import {
  createContext,
  Fragment,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
} from "react";
import coverTruck from "@/assets/cover-truck.jpg.asset.json";
import { isRegulatoryAuthority } from "@/lib/legal/topic-classifier";
import {
  buildAuthorityLineage,
  persistedTextOr,
  mapVerificationStatus,
  safeSourceUrl,
  LINEAGE_UNAVAILABLE,
  type LegalAuthorityLineage,
} from "@/lib/legal/authority-lineage";
import { LegalLineageStrip } from "@/components/report/legal-lineage";
import { ReportAccordion } from "@/components/report/report-accordion";
import {
  VerificationBadge,
  VerificationStatusBanner,
  type ReportVerificationState,
} from "@/components/report/verification-status";
import {
  selectOwnershipAuthorities,
  isFederalAuthority,
} from "@/lib/legal/authority-jurisdiction";
import { NOT_AVAILABLE } from "@/lib/financial/surface-financials";
import { createFileRoute, Link, useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ArrowRight,
  Download,
  Printer,
  ShieldCheck,
  CheckCircle2,
  MinusCircle,
  AlertTriangle,
  FileText,
  Scale,
  Gauge,
  Handshake,
  Sparkles,
  TrendingDown,
  MapPin,
  Building2,
  CalendarDays,
  Hash,
  Landmark,
  BookOpen,
  ClipboardCheck,
  Users,
  Info,
  ChevronRight,
  Truck,
  Upload,
  Receipt,
  Warehouse,
  BadgeCheck,
  FileCheck,
  Mail,
  Phone,
  Send,
  Image as ImageIcon,
  Paperclip,
  Wrench,
  Search,
  Clock,
  Star,
  Database,
  BarChart3,
  DollarSign,
  TrendingUp,
  GitCompareArrows,
  Flag,
  PieChart,
  LineChart,
  Target,
  Waves,
  FileText as FileTextIcon,
  Lightbulb,
  Circle,
  FileSearch,
  type LucideIcon,
} from "lucide-react";
import { Tooltip, TooltipProvider, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { fileService } from "@/lib/services/fileService";
import { buildChapter08MarketIntel } from "@/lib/vendor-intel/chapter08-intel";
import { SiteHeader } from "@/components/site-header";
import { SiteFooter } from "@/components/site-footer";
import { Breadcrumbs } from "@/components/breadcrumbs";
import { StatusChip, type SubmissionStatus } from "@/components/status-chip";
import { AuthGate } from "@/components/auth-gate";
import { useCase, type Case } from "@/lib/cases";
import {
  buildReport,
  currency,
  extractInvoiceReportFields,
  MISSING_FIELD,
  NOT_EXTRACTED,
} from "@/lib/report";
import {
  ANALYSIS_CONFIDENCE_LABEL,
  NOT_CALCULATED as CONF_NOT_CALCULATED,
  confidenceBand,
  evidenceStrength as canonicalEvidenceStrength,
  evidenceStrengthFromFraction,
  toConfidencePct,
} from "@/lib/confidence/confidence-vocabulary";
import { readReportMetadata } from "@/lib/report/metadata";
import { resolveTimelineDatesISO, parseTowReleaseFromRawText } from "@/lib/report/date-sources";
import {
  getStatutoryAuthorities,
  type StatutoryAuthorityBundle,
} from "@/lib/report/statutory-authorities";
import { useCaseFacts } from "@/hooks/use-case-facts";
import {
  hasVendorEvidence,
  hasLawEvidence,
  hasRegulationEvidence,
  hasCarrierEvidence,
  hasDocumentEvidence,
  hasTimelineEvidence,
  hasPhotoEvidence,
  hasGpsEvidence,
  hasNovaEvidence,
} from "@/lib/report/evidence-predicates";
import { useAuth } from "@/hooks/use-auth";
import {
  useReportPdfActions,
  useSectionPdfActions,
  registerPdfTreeMount,
  preloadPdfLibraries,
  chaptersForPackage,
  type PdfTreeHandle,
} from "@/hooks/use-report-pdf";
import {
  useFullWorkspaceBundle,
  bundleToCase,
  type FullWorkspaceBundle,
} from "@/hooks/use-real-reviews";
import type {
  ReviewFile,
  LegalResearch,
  MarketComparison as MarketComparisonRow,
  MarketResearch,
  FindingItem,
} from "@/lib/db/models";
import {
  certifyDocument,
  formatConsistencyReport,
} from "@/lib/report/consistency-certification";
import {
  buildCoverageReport,
  formatCoverageReport,
} from "@/lib/report/coverage-report";
import {
  ReportSummaryProvider,
  useReportSummary,
  summaryField,
  summarySection,
  chapterMetric,
} from "@/lib/report/report-summary-context";
import { buildReportSummary } from "@/lib/report/report-summary";
import {
  buildChargeInventory,
  normalizeChargeKey,
  parseChargeDescription,
  type InventoryCharge,
} from "@/lib/report/charge-inventory";
import {
  resolveReusedEvidence,
  type ReuseCandidate,
  type ReuseRejectionReason,
} from "@/lib/report/benchmark-reuse";
import {
  CANONICAL_STATUS_LABEL,
  findInventoryChargeForMarketRow,
  resolveChargeBenchmarkStatuses,
  type CanonicalBenchmarkStatus,
  type CanonicalChargeStatus,
} from "@/lib/report/charge-benchmark-status";
import { computeDeterministicRecovery } from "@/lib/report/deterministic-recovery";
import { useScopedExportActive } from "@/lib/report/export-mode";
import {
  buildChargeDisclosure,
  unsupportedExplanation,
  auditChapter02Totals,
  type DisclosureField,
} from "@/lib/report/chapter02-presentation";

import {
  SOURCE_CLASSIFICATION_LABEL,
  type SourceClassification,
} from "@/lib/report/market-research";
import { toast } from "sonner";

import {
  buildMarketRows,
  classifyVariance,
  type MarketRow,
} from "@/lib/report/market-evidence";
import {
  buildDocumentationReviews,
  documentationMatchKey as normalizeDocDescription,
  documentationOutcomeLabel,
  indexReviewsByDescription,
  toDocumentationLines,
} from "@/lib/report/documentation-review";

import { classifyVehicle } from "@/lib/report/vehicle-classification";
import type { VehicleClass } from "@/lib/report/vehicle-classification";
import { classifyBenchmarkFamily } from "@/lib/report/benchmark-families";

/**
 * Section 6 transparency: the classified vehicle for this claim, so every
 * benchmarked charge can name the market family it was compared against.
 */
const ClaimVehicleClassContext = createContext<VehicleClass>("unknown");
import {
  isStorageDescription,
  normalizeStorageLine,
  selectStorageBenchmark,
} from "@/lib/report/storage-benchmark";
import { resolveStorageTimeline } from "@/lib/report/storage-timeline";

import { downloadOriginalFile } from "@/lib/download-file";
import { Briefcase, ChevronDown, Maximize2 } from "lucide-react";
import { InvoiceViewer, type ViewerDocument } from "@/components/report/invoice-viewer";
import {
  ChapterNavDrawer,
  ChapterNavList,
  useChapterNavState,
} from "@/components/report/chapter-nav";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  CarrierGuidelineAnalysis,
  type CarrierGuideline,
  VendorIntelligence,
  EvidenceIndexTiles,
  VersionHistory,
  type VersionEntry,
  HowToUsePackage,
  MethodologyPanel,
  CoverageNote,
  type ExecutiveVerdictData,
  type EvidenceCounts,
} from "@/components/report/claimguard-sections";
import { selectSupportingDocuments } from "@/lib/supporting-documents";
import { CustomerContextPanel } from "@/components/report/customer-context-panel";
import { DossierChapter } from "@/components/report/dossier-chapter";
import {
  CHAPTERS,
  TOTAL_CHAPTERS,
  getChapter,
  getChapterByInternalId,
  chapterRef,
  chapterLongRef,
  chapterHref,
  nextChapter,
  nextChapterLabel,
  partsGrouped,
  type ChapterEntry,
} from "@/lib/report/chapter-registry";
import { absenceLabel } from "@/lib/report/status-vocabulary";
import { analystNoteFor } from "@/lib/report/analyst-notes";
import { OwnershipLienAnalysis } from "@/components/report/ownership-lien-analysis";
import { useVendorSnapshot } from "@/hooks/use-vendor-snapshot";
import { useSosSnapshot } from "@/hooks/use-sos-snapshot";
import { useLicensingSnapshot } from "@/hooks/use-licensing-snapshot";
import { useBBBSnapshot } from "@/hooks/use-bbb-snapshot";
import { useStateLicensingSnapshot } from "@/hooks/use-state-licensing-snapshot";
import { DownloadPackageMenu, autoDownloadKey } from "@/components/report/download-package-menu";


export const Route = createFileRoute("/report/$id")({
  /* `download` lets the Dashboard row action hand a chosen package to the one
     canonical generator that lives on this page — no second download system. */
  validateSearch: (search: Record<string, unknown>) => {
    const raw = typeof search["download"] === "string" ? (search["download"] as string) : undefined;
    const download =
      raw === "complete" || raw === "review" || raw === "negotiation" ? raw : undefined;
    return download ? { download } : {};
  },
  head: ({ params }) => ({

    meta: [
      { title: `ClaimGuard | Professional Findings Report — ${params.id}` },
      {
        name: "description",
        content:
          "A comprehensive review of your invoice with supporting research, pricing comparisons, and negotiation opportunities.",
      },
    ],
  }),
  component: () => (
    <AuthGate allowDemo>
      <ReportPage />
    </AuthGate>
  ),
});

/* ─────────────────────────────────────────────────────────── */
/* Render-mode context                                          */
/*                                                              */
/* The Claim Intelligence Package™ has two render paths that   */
/* share the SAME chapter components:                           */
/*   • "interactive" — full website experience with export     */
/*     bars, floating TOC, accordions, and action cards.       */
/*   • "pdf" — off-screen dedicated publication tree used by    */
/*     the exporter. All interactive chrome is structurally    */
/*     omitted here so the exported document reads like a      */
/*     professionally typeset consulting dossier.              */
/*                                                              */
/* All future report enhancements are built once in the shared */
/* chapter components (ChapterStack) and automatically appear  */
/* in both render modes.                                        */
/* ─────────────────────────────────────────────────────────── */

export type RenderMode = "interactive" | "pdf";
export const RenderModeContext = createContext<RenderMode>("interactive");
export function useRenderMode(): RenderMode {
  return useContext(RenderModeContext);
}

/* Package identity — governs branding of cover, TOC intro, and closing
   page. In interactive mode we always show the flagship Claim
   Intelligence Package identity. During a PDF export the PdfPublicationHost
   re-mounts the tree inside a provider set to the chosen package so the
   cover, TOC, and closing pages match the downloaded product. */
import {
  getPackageIdentity as _getPackageIdentity,
  type PackageIdentity as _PackageIdentity,
  type ReportPackage as _ReportPackage,
} from "@/lib/report/package-identity";
export {
  PdfPackageContext,
  usePdfPackageIdentity,
} from "@/lib/report/package-context";
import {
  PdfPackageContext,
  usePdfPackageIdentity,
} from "@/lib/report/package-context";


/* Deterministic date formatting — uses UTC so server/client match. */
function formatReportDate(
  iso: string,
  opts: { month: "long" | "short"; day: "numeric" | "2-digit" },
): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    year: "numeric",
    month: opts.month,
    day: opts.day,
  });
}

/* Deterministic time formatting — U.S. 12-hour clock, fixed to the report's
   completion timestamp so it is stable across viewers and re-opens. */
function formatReportTime(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleTimeString("en-US", {
    timeZone: "UTC",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
}

/* ─────────────────────────────────────────────────────────── */

function demoCase(id: string): Case {
  // For the public sample at /report/demo we render a realistic, production-
  // style file number rather than the raw URL slug ("demo") so the Case Brief
  // masthead and File Number row read like a real case. Values are aligned
  // with the Case Brief reference design.
  const displayId = id === "demo" ? "INV-2026-168690" : id;
  return {
    id: displayId,
    createdAt: "2026-07-07T15:00:00.000Z",
    method: "upload",
    fastTrack: true,
    files: [{ name: "invoice.pdf", size: 218_431, type: "application/pdf" }],
    details: {
      vendor: "ABC Recovery & Storage, LLC",
      invoiceDate: "2026-07-10",
      invoiceType: "towing_storage",
      totalAmount: "2884.09",
      urgency: "high",
      timeframe: "24-48 hours",
      zip: "78539",
    },
    status: "report_ready",
    negotiationChosen: false,
  };
}

/* Truthful shell for a production claim with nothing persisted yet: no
   fabricated vendor, totals, dates, or ZIP. */
function emptyCase(id: string): Case {
  return {
    id,
    createdAt: new Date().toISOString(),
    method: "upload",
    fastTrack: false,
    files: [],
    details: {
      vendor: "",
      invoiceDate: "",
      invoiceType: "towing_storage",
      totalAmount: "",
      urgency: "",
      timeframe: "",
      zip: "",
    },
    status: "received",
    negotiationChosen: false,
  } as unknown as Case;
}


const DEMO_INVOICE_EXTRACTED: Record<string, unknown> = {
  invoice_number: "INV-2026-08157",
  invoice_date: "2026-07-10",
  storage_start_date: "2026-06-18",
  storage_end_date: "2026-07-04",
  location: {
    verified: false,
    city: "Edinburg",
    state: "TX",
    zip5: "78539",
  },
};

const DEMO_MARKET_COMPARISONS: MarketComparisonRow[] = [
  {
    id: "demo-storage",
    review_id: "demo",
    line_item: "Outdoor Storage (Daily Rate)",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "storage",
    invoice_amount: 1088,
    invoice_rate: 68,
    market_low: 15,
    market_median: 21,
    market_high: 30,
    local_average: 21,
    typical_low: 15,
    typical_high: 30,
    difference_amount: 752,
    difference_percent: 223.8,
    assessment: "significantly_high",
    confidence: 0.94,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-initial-tow",
    review_id: "demo",
    line_item: "Initial Tow (14 miles)",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "tow",
    invoice_amount: 285,
    invoice_rate: 285,
    market_low: 175,
    market_median: 190,
    market_high: 275,
    local_average: 190,
    typical_low: 175,
    typical_high: 275,
    difference_amount: 95,
    difference_percent: 50,
    assessment: "significantly_high",
    confidence: 0.9,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-winch",
    review_id: "demo",
    line_item: "Winch Recovery",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "recovery",
    invoice_amount: 195,
    invoice_rate: 195,
    market_low: 100,
    market_median: 150,
    market_high: 250,
    local_average: 150,
    typical_low: 100,
    typical_high: 250,
    difference_amount: 45,
    difference_percent: 30,
    assessment: "high",
    confidence: 0.86,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-lien",
    review_id: "demo",
    line_item: "Lien Processing",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "administrative",
    invoice_amount: 185,
    invoice_rate: 185,
    market_low: 50,
    market_median: 50,
    market_high: 50,
    local_average: 50,
    typical_low: 50,
    typical_high: 50,
    difference_amount: 135,
    difference_percent: 270,
    assessment: "significantly_high",
    confidence: 0.88,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-after-hours",
    review_id: "demo",
    line_item: "After-Hours Release",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "release",
    invoice_amount: 150,
    invoice_rate: 150,
    market_low: 35,
    market_median: 50,
    market_high: 100,
    local_average: 50,
    typical_low: 35,
    typical_high: 100,
    difference_amount: 100,
    difference_percent: 200,
    assessment: "significantly_high",
    confidence: 0.84,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-admin",
    review_id: "demo",
    line_item: "Administrative Fee",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "administrative",
    invoice_amount: 125,
    invoice_rate: 125,
    market_low: 45,
    market_median: 50,
    market_high: 75,
    local_average: 50,
    typical_low: 45,
    typical_high: 75,
    difference_amount: 75,
    difference_percent: 150,
    assessment: "significantly_high",
    confidence: 0.88,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-inventory",
    review_id: "demo",
    line_item: "Inventory Documentation",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "documentation",
    invoice_amount: 95,
    invoice_rate: 95,
    market_low: 0,
    market_median: 0,
    market_high: 25,
    local_average: 0,
    typical_low: 0,
    typical_high: 25,
    difference_amount: 95,
    difference_percent: null,
    assessment: "unsupported",
    confidence: 0.78,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-gate",
    review_id: "demo",
    line_item: "Gate Access Fee",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "access",
    invoice_amount: 80,
    invoice_rate: 80,
    market_low: 30,
    market_median: 35,
    market_high: 50,
    local_average: 35,
    typical_low: 30,
    typical_high: 50,
    difference_amount: 45,
    difference_percent: 128.6,
    assessment: "significantly_high",
    confidence: 0.82,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
  {
    id: "demo-card",
    review_id: "demo",
    line_item: "Credit Card Convenience Fee",
    zip: "78539",
    zip_code: "78539",
    state: "TX",
    service_type: "convenience_fee",
    invoice_amount: 76.84,
    invoice_rate: 76.84,
    market_low: 15,
    market_median: 30,
    market_high: 45,
    local_average: 30,
    typical_low: 15,
    typical_high: 45,
    difference_amount: 46.84,
    difference_percent: 156.1,
    assessment: "significantly_high",
    confidence: 0.76,
    source: "demo benchmark",
    created_at: "2026-07-01T00:00:00.000Z",
  },
];

function ReportPage() {
  const { id } = useParams({ from: "/report/$id" });
  /* PRODUCTION INTEGRITY: demo datasets may render only on the dedicated demo
     route. Missing/empty production data must never fall back to demo values. */
  const isDemoRoute = id === "demo";
  const { isStaff, isAdmin, user, loading: authLoading } = useAuth();
  const bundle = useFullWorkspaceBundle(user && id !== "demo" ? id : undefined);
  const localCase = useCase(id);
  const realCase = bundleToCase(bundle);
  const hasReal = !!bundle.review;


  useEffect(() => {
    if (id === "demo") return;
    import("@/lib/analytics").then(({ trackEvent }) => {
      void trackEvent({
        event: "report_opened",
        userId: user?.id,
        properties: { review_id: id },
      });
    });
  }, [id, user?.id]);

  const loading = user && id !== "demo" && (authLoading || bundle.loading);
  /* The demo case shell may only be used on the demo route; production claims
     with nothing persisted render an empty (truthful) shell instead. */
  const c = realCase ?? localCase ?? (isDemoRoute ? demoCase(id) : emptyCase(id));

  const baseReport = buildReport(c);

  /* PHASE 6.4 — REPAIR 1: ONE CANONICAL REPORT OBJECT PER RENDER.
     The Report Summary is built here, once, and handed to the provider. The
     canonical report it produced is the ONLY report object the page consumes,
     so no chapter can read a second independently built instance. */
  const summaryInputs = useMemo(
    () => ({
      review: hasReal ? bundle.review : null,
      invoiceData: hasReal ? bundle.invoiceData : null,
      findings: hasReal ? bundle.findings : null,
      findingItems: hasReal ? bundle.findingItems : [],
      legalResearch: hasReal ? bundle.legalResearch : [],
      marketComparisons: hasReal ? bundle.marketComparisons : [],
      marketResearch: hasReal ? bundle.marketResearch : [],
      marketResearchSources: hasReal ? bundle.marketResearchSources : [],
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      hasReal,
      bundle.review?.id,
      bundle.review?.updated_at,
      bundle.invoiceData?.id,
      bundle.findings?.id,
      bundle.findingItems?.length,
      bundle.legalResearch?.length,
      bundle.marketComparisons?.length,
      bundle.marketResearch?.length,
      bundle.marketResearchSources?.length,
    ],
  );
  const reportSummary = useMemo(() => buildReportSummary(summaryInputs), [summaryInputs]);
  /* Single canonical report instance. `buildReportFromBundle` ignores its
     fallback entirely whenever a review is present, so the summary's build is
     value-identical to the removed second invocation. */
  const r = hasReal ? reportSummary.canonicalReport : baseReport;

  const invoiceFields = hasReal
    ? extractInvoiceReportFields(bundle.invoiceData, bundle.review)
    : null;
  // Canonical Case Facts — single source of truth for all chapters.
  const caseFacts = useCaseFacts(hasReal ? bundle : null);
  // Google Places snapshot for Chapter 08. Resolves server-side, frozen
  // once the report reaches a completed state so PDF exports and re-opens
  // reuse the same evidence without re-hitting Google.
  const vendorSnapshot = useVendorSnapshot(hasReal ? bundle.review?.id : undefined);
  // Secretary of State snapshot for Chapter 08. Frozen per review;
  // completed reports read reviews.sos_snapshot and never hit Middesk.
  const sosSnapshot = useSosSnapshot(hasReal ? bundle.review?.id : undefined);
  // Licensing & Regulatory snapshot (FMCSA) for Chapter 08. Frozen per
  // review; completed reports read reviews.licensing_snapshot and never
  // hit FMCSA or the licensing cache during render.
  const licensingSnapshot = useLicensingSnapshot(hasReal ? bundle.review?.id : undefined);
  // Better Business Bureau snapshot for Chapter 08. Frozen per review;
  // completed reports read reviews.bbb_snapshot and never hit the BBB
  // cache or provider during render.
  const bbbSnapshot = useBBBSnapshot(hasReal ? bundle.review?.id : undefined);
  // State Licensing snapshot for Chapter 08. Frozen per review;
  // completed reports read reviews.state_licensing_snapshot and never
  // hit the state licensing provider, registry, or cache during render.
  const stateLicensingSnapshot = useStateLicensingSnapshot(hasReal ? bundle.review?.id : undefined);

  const canOpenWorkspace = hasReal && (isStaff || isAdmin);
  const reportMetadata = readReportMetadata(
    (bundle.report?.report_payload as Record<string, unknown> | null) ?? null,
  );
  useEffect(() => {
    if (typeof window === "undefined") return;
    const w = window as unknown as { __CLAIMGUARD_REPORT_METADATA__?: unknown };
    if (reportMetadata) w.__CLAIMGUARD_REPORT_METADATA__ = reportMetadata;
    else delete w.__CLAIMGUARD_REPORT_METADATA__;
    return () => {
      delete w.__CLAIMGUARD_REPORT_METADATA__;
    };
  }, [reportMetadata]);

  // Warm the PDF engine (html2canvas-pro, jsPDF, pdf-lib) during idle time
  // so the first "Download Report" click doesn't pay the dynamic-import cost.
  useEffect(() => {
    if (typeof window === "undefined") return;
    type IdleWindow = Window & {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
    };
    const w = window as IdleWindow;
    const run = () => preloadPdfLibraries();
    const handle: number =
      typeof w.requestIdleCallback === "function"
        ? w.requestIdleCallback(run, { timeout: 4000 })
        : (window.setTimeout(run, 2000) as unknown as number);
    return () => {
      type IdleCancelWindow = Window & { cancelIdleCallback?: (h: number) => void };
      const cw = window as IdleCancelWindow;
      if (typeof cw.cancelIdleCallback === "function") cw.cancelIdleCallback(handle);
      else window.clearTimeout(handle);
    };
  }, []);



  if (loading) {
    return (
      <div className="min-h-dvh bg-background">
        <SiteHeader />
        <div className="container-page py-8">
          <div className="animate-pulse space-y-6">
            <div className="h-8 w-64 rounded bg-secondary" />
            <div className="h-64 rounded-3xl bg-secondary" />
            <div className="h-40 rounded-2xl bg-secondary" />
            <div className="h-40 rounded-2xl bg-secondary" />
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  /* PAYMENT GATE — a report only exists after payment queues the pipeline.
     Customers who open /report/:id before then see a pending notice instead
     of an empty shell. Staff and admins keep full access for operations. */
  const pipelineStarted =
    hasReal &&
    !!bundle.review &&
    bundle.review.status !== "received" &&
    bundle.review.status !== "submitted";
  /* INTEGRITY GATE — a real claim with nothing persisted must never render
     report conclusions (they would be defaults, not findings). */
  const hasProcessedData =
    !!bundle.invoiceData ||
    !!bundle.findings ||
    bundle.findingItems.length > 0 ||
    bundle.marketComparisons.length > 0 ||
    bundle.marketResearch.length > 0 ||
    bundle.legalResearch.length > 0 ||
    !!bundle.report;
  if (!isDemoRoute && !hasProcessedData) {
    return (
      <div className="min-h-dvh bg-background">
        <SiteHeader />
        <div className="container-page py-16">
          <div className="mx-auto max-w-xl rounded-3xl border border-border bg-card p-8 text-center">
            <h1 className="text-2xl font-semibold text-navy">Awaiting Analysis</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              This report will become available after invoice processing completes. No invoice
              data, market analysis, or legal analysis has been produced for this claim yet.
            </p>
            <div className="mt-6">
              <Link to="/dashboard" className="btn-primary inline-flex">
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }
  if (hasReal && !pipelineStarted && !isStaff && !isAdmin) {

    return (
      <div className="min-h-dvh bg-background">
        <SiteHeader />
        <div className="container-page py-16">
          <div className="mx-auto max-w-xl rounded-3xl border border-border bg-card p-8 text-center">
            <h1 className="text-2xl font-semibold text-navy">Review Pending</h1>
            <p className="mt-3 text-sm text-muted-foreground">
              Your review will begin immediately after payment. As soon as it is complete,
              your Professional Findings Report will appear here.
            </p>
            <div className="mt-6">
              <Link to="/dashboard" className="btn-primary inline-flex">
                Back to Dashboard
              </Link>
            </div>
          </div>
        </div>
        <SiteFooter />
      </div>
    );
  }

  const invNumber =
    invoiceFields?.invoiceNumber ??
    (isDemoRoute ? (DEMO_INVOICE_EXTRACTED.invoice_number as string | undefined) : undefined) ??
    c.id;

  const customerName =
    (user?.user_metadata as { full_name?: string; name?: string } | undefined)?.full_name ??
    (user?.user_metadata as { name?: string } | undefined)?.name ??
    user?.email ??
    "Confidential Client";

  return (
    <ReportSummaryProvider inputs={summaryInputs} summary={reportSummary}>
    <div className="min-h-dvh bg-background">
      <SiteHeader />
      <main id="main-content" className="container-page py-8">
        <div className="mb-4 no-print">
          <Breadcrumbs
            items={[{ label: "Dashboard", to: "/dashboard" }, { label: "Professional Review" }]}
          />
        </div>
        <div className="no-print report-toolbar-sticky" data-report-toolbar>
          <ReportToolbar
            caseId={c.id}
            workspaceHref={canOpenWorkspace ? c.id : null}
            invoiceNumber={invNumber}
            fileNumber={c.id}
            invoiceFiles={hasReal ? bundle.files : undefined}
          />
          <MobileChapterJump />
        </div>
        <SectionExportProvider fileNumber={c.id}>
          <div className="xl:grid xl:grid-cols-[minmax(0,1fr)_clamp(14rem,17vw,17.5rem)] xl:gap-8 2xl:gap-10">
            <div id="report-interactive-root" className="min-w-0">

              <RenderModeContext.Provider value="interactive">
                <ChapterStack
                  mode="interactive"
                  c={c}
                  r={r}
                  hasReal={hasReal}
                  bundle={bundle}
                  invoiceFields={invoiceFields}
                  caseFacts={caseFacts}
                  customerName={customerName}
                  invNumber={invNumber}
                  vendorSnapshot={vendorSnapshot.data ?? null}
                  sosSnapshot={sosSnapshot.data ?? null}
                  licensingSnapshot={licensingSnapshot.data ?? null}
                  bbbSnapshot={bbbSnapshot.data ?? null}
                  stateLicensingSnapshot={stateLicensingSnapshot.data ?? null}
                />
              </RenderModeContext.Provider>
            </div>
            <FloatingTOC />
          </div>
          <ChapterNavDrawer />
          <BackToTop />


          {/* Dedicated PDF publication render path.

              Previously this tree was always mounted at page load so the
              exporter could find it. That kept two full copies of the
              report resident in memory (interactive + hidden) plus a
              third clone during export.

              PdfPublicationHost registers an on-demand mount function
              with the PDF engine. When "Download Report" is clicked the
              engine mounts this tree, captures it directly (no clone),
              and immediately unmounts it. During ordinary viewing this
              subtree does not exist in the DOM. */}
          <PdfPublicationHost
            renderTree={(pkg) => (
              <RenderModeContext.Provider value="pdf">
                <PdfPackageContext.Provider value={_getPackageIdentity(pkg)}>
                  <ChapterStack
                    mode="pdf"
                    c={c}
                    r={r}
                    hasReal={hasReal}
                    bundle={bundle}
                    invoiceFields={invoiceFields}
                    caseFacts={caseFacts}
                    customerName={customerName}
                    invNumber={invNumber}
                    vendorSnapshot={vendorSnapshot.data ?? null}
                    sosSnapshot={sosSnapshot.data ?? null}
                    licensingSnapshot={licensingSnapshot.data ?? null}
                    bbbSnapshot={bbbSnapshot.data ?? null}
                    stateLicensingSnapshot={stateLicensingSnapshot.data ?? null}
                  />
                </PdfPackageContext.Provider>
              </RenderModeContext.Provider>
            )}
          />
        </SectionExportProvider>

      </main>

      <SiteFooter />
      <ConsistencyCertificationProbe />
    </div>
    </ReportSummaryProvider>
  );
}

/**
 * Global Consistency Certification probe.
 *
 * Walks every node tagged with `summaryField(...)` and compares the rendered
 * value against the immutable Report Summary. Failures are reported with
 * Page / Section / Field / Expected / Actual. Exposed on `window` so QA and
 * the production certification protocol can run it against a live claim.
 */
function ConsistencyCertificationProbe() {
  const summary = useReportSummary();
  useEffect(() => {
    if (typeof window === "undefined") return;
    const run = () => {
      const result = certifyDocument(summary, document, "report");
      if (!result.certified) console.warn(formatConsistencyReport(result));
      return result;
    };
    const coverage = () => {
      const report = buildCoverageReport(summary, document, "report");
      console.info(formatCoverageReport(report));
      return report;
    };
    /**
     * Stage 9 per-chapter certification. Scopes discovery to one chapter
     * anchor so a chapter can be certified independently of the rest of the
     * package (Certification Pipeline v3.0).
     */
    const chapterCoverage = (anchorId: string) => {
      const root = document.getElementById(anchorId);
      if (!root) return null;
      const report = buildCoverageReport(summary, root, anchorId);
      console.info(formatCoverageReport(report));
      return report;
    };
    (window as unknown as Record<string, unknown>).__claimguardCertifyConsistency = run;
    (window as unknown as Record<string, unknown>).__claimguardCoverageReport = coverage;
    (window as unknown as Record<string, unknown>).__claimguardChapterCoverage = chapterCoverage;
    (window as unknown as Record<string, unknown>).__claimguardReportSummary = summary;
    const t = setTimeout(run, 1200);
    return () => clearTimeout(t);
  }, [summary]);
  return null;
}

/* ─────────────────────────────────────────────────────────── */
/* ChapterStack — shared chapter tree for both render paths     */
/*                                                              */
/* This is the single source of truth for the Claim            */
/* Intelligence Package™ report content. It is mounted twice   */
/* by ReportPage:                                               */
/*   • Once in the visible layout with mode="interactive" —    */
/*     export bars, chapter IDs for the floating TOC, and any  */
/*     .no-print helper widgets are rendered.                  */
/*   • Once inside a hidden off-screen [data-pdf-root]         */
/*     container with mode="pdf" — the PDF engine captures     */
/*     this tree exclusively; interactive chrome is omitted    */
/*     by construction so it can never leak into exports.      */
/* ─────────────────────────────────────────────────────────── */

/**
 * PdfPublicationHost — on-demand PDF publication tree.
 *
 * Registers a mount function with the PDF engine (`registerPdfTreeMount`).
 * While the user browses the report interactively this component renders
 * nothing, so the browser only pays for the interactive tree — the hidden
 * publication tree (formerly always mounted) is gone.
 *
 * When the engine calls the registered mount function it:
 *   1. Sets `active=true` so React renders the pdf-mode ChapterStack into
 *      an off-screen absolute-positioned container.
 *   2. Waits two RAFs (mount + layout settle) then resolves with the live
 *      element so html2canvas can capture it directly — no cloneNode step.
 *   3. Exposes a `release()` handle that flips active back to false,
 *      immediately unmounting the tree and freeing memory.
 */
function PdfPublicationHost({
  renderTree,
}: {
  renderTree: (pkg: _ReportPackage) => React.ReactNode;
}) {
  const [activePkg, setActivePkg] = useState<_ReportPackage | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const pendingRef = useRef<((h: PdfTreeHandle) => void) | null>(null);
  const renderTreeRef = useRef(renderTree);
  renderTreeRef.current = renderTree;

  useEffect(() => {
    registerPdfTreeMount(
      (pkg) =>
        new Promise<PdfTreeHandle>((resolve) => {
          pendingRef.current = resolve;
          setActivePkg(pkg);
        }),
    );
    return () => {
      registerPdfTreeMount(null);
    };
  }, []);

  useEffect(() => {
    if (!activePkg) return;
    const el = rootRef.current;
    const cb = pendingRef.current;
    if (!el || !cb) return;
    pendingRef.current = null;
    // Two RAFs: first commits the mount, second lets layout settle.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        cb({
          root: el,
          release: () => setActivePkg(null),
        });
      });
    });
  }, [activePkg]);


  if (!activePkg) return null;
  return (
    <div
      ref={rootRef}
      aria-hidden
      data-pdf-root
      data-pdf-publication="true"
      style={{
        position: "absolute",
        left: "-10000px",
        top: 0,
        width: "1180px",
        pointerEvents: "none",
        background: "#ffffff",
      }}
    >
      {renderTreeRef.current(activePkg)}
    </div>
  );
}



/**
 * Shared chapter-level export actions. Rendered once per chapter, directly
 * below the navy banner and above the chapter's first content section.
 * Visual style intentionally mirrors <SectionActions /> (bordered white
 * buttons + muted helper copy) so per-chapter and per-section exports feel
 * like a single, cohesive control family. Hidden from PDF captures via the
 * shared `.no-print` / `data-pdf-exclude` conventions.
 */
function ChapterActionButtons({
  sectionId,
  title,
  chapterEntry,
  onPrint,
  onDownload,
  busy,
}: {
  sectionId: string;
  title: string;
  chapterEntry: ChapterEntry;
  onPrint: (id: string, title: string) => void;
  onDownload: (id: string, title: string, entry: ChapterEntry) => Promise<void> | void;
  busy: boolean;
}) {
  return (
    <div className="no-print flex shrink-0 flex-col items-end gap-1.5" data-pdf-exclude="true">
      <div className="flex items-center gap-1.5">
        <button
          type="button"
          onClick={() => onPrint(sectionId, title)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-navy disabled:opacity-60"
          aria-label={`Print this chapter only: ${title}`}
          title={`Print this chapter only (${title})`}
        >
          <Printer className="h-3.5 w-3.5" /> Print Chapter
        </button>
        <button
          type="button"
          onClick={() => onDownload(sectionId, title, chapterEntry)}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded-md border border-border bg-white px-2.5 py-1 text-xs font-medium text-muted-foreground hover:bg-secondary hover:text-navy disabled:opacity-60"
          aria-label={`Download this chapter as a standalone PDF: ${title}`}
          title={`Download this chapter as a standalone PDF (${title})`}
        >
          <Download className="h-3.5 w-3.5" />
          {busy ? "Preparing PDF…" : "Download Chapter PDF"}
        </button>
      </div>
      <div className="text-right text-[10px] leading-tight text-muted-foreground/60">
        <div className="font-semibold uppercase tracking-[0.14em]">Standalone Chapter Export</div>
        <div className="text-muted-foreground/50">Contains only this chapter.</div>
      </div>
    </div>
  );
}

function ChapterStack({
  mode,
  c,
  r,
  hasReal,
  bundle,
  invoiceFields,
  caseFacts,
  customerName,
  invNumber,
  vendorSnapshot,
  sosSnapshot,
  licensingSnapshot,
  bbbSnapshot,
  stateLicensingSnapshot,
}: {
  mode: RenderMode;
  c: Case;
  r: ReturnType<typeof buildReport>;
  hasReal: boolean;
  bundle: FullWorkspaceBundle;
  invoiceFields: ReturnType<typeof extractInvoiceReportFields> | null;
  caseFacts: import("@/lib/report/case-facts").CaseFacts;
  customerName: string;
  invNumber: string;
  vendorSnapshot: import("@/hooks/use-vendor-snapshot").VendorSnapshot | null;
  sosSnapshot: import("@/hooks/use-sos-snapshot").SosSnapshot | null;
  licensingSnapshot: import("@/hooks/use-licensing-snapshot").LicensingSnapshot | null;
  bbbSnapshot: import("@/hooks/use-bbb-snapshot").BBBSnapshot | null;
  stateLicensingSnapshot:
    | import("@/hooks/use-state-licensing-snapshot").StateLicensingSnapshot
    | null;
}) {
  /* Demo datasets require the dedicated demo route. */
  const { id: routeId } = useParams({ from: "/report/$id" });
  const isDemoRoute = routeId === "demo";
  const dateLabel = formatReportDate(c.createdAt, { month: "short", day: "2-digit" });


  const { downloadSection, printSection, busyId } = useSectionPdfActions(c.id);
  const packageIdentity = usePdfPackageIdentity();


  // Registry-only chapter renderer. Callers may NOT pass a chapter
  // number, title, or variant — every customer-facing field is resolved
  // from the Master Chapter Registry via the anchor id. If you change
  // chapter numbering, edit `src/lib/report/chapter-registry.ts` only;
  // every surface in the report picks up the change automatically.
  // Registry-only chapter renderer. Callers may NOT pass a chapter
  // number, title, or variant — every customer-facing field is resolved
  // from the Master Chapter Registry via the anchor id. If you change
  // chapter numbering, edit `src/lib/report/chapter-registry.ts` only;
  // every surface in the report picks up the change automatically.
  //
  // The closing Analyst Note is applied here, in one place, so every
  // chapter ends with the same structure: content → findings → Analyst
  // Note → chapter footer. Note copy lives in
  // `src/lib/report/analyst-notes.ts`; the final chapter has none and
  // closes with the Professional Review Conclusion.
  const chapter = (
    id: string,
    intent: string,
    body: React.ReactNode,
    headerAccessory?: React.ReactNode,
    empty?: { isEmpty: boolean; badge?: string; watermark?: string; message?: string },
  ) => {
    const analystNote = analystNoteFor(id, { packageKey: packageIdentity.key });
    const entry = getChapter(id);
    return (
      <DossierChapter
        variant={entry.variant}
        chapterNumber={entry.paddedDisplayNumber}
        title={entry.title}
        caseId={c.id}
        fileNumber={invNumber}
        dateLabel={dateLabel}
        intent={intent}
        id={entry.anchorId}
        headerAccessory={headerAccessory}
        icon={entry.icon}
        partRoman={entry.partRoman}
        partName={entry.partName}
        chapterOfLabel={`of ${entry.totalChapters}`}
        category={entry.category}
        isEmpty={empty?.isEmpty}
        emptyBadge={empty?.badge}
        emptyWatermark={empty?.watermark}
        emptyMessage={empty?.message}
        actions={
          mode === "interactive" ? (
            <ChapterActionButtons
              sectionId={entry.anchorId}
              title={entry.title}
              chapterEntry={entry}
              onPrint={(sid, t) => printSection(sid, t, { kind: "chapter" })}
              onDownload={(sid, t, ent) =>

                downloadSection(sid, t, { kind: "chapter", chapter: ent })
              }
              busy={busyId === entry.anchorId}
            />
          ) : undefined
        }
      >
        <ChapterNumberContext.Provider value={entry.paddedDisplayNumber}>
          {body}
          {/* Standard chapter close. Suppressed when the chapter rendered
              an empty state, since there are no findings to summarize. */}
          {analystNote && !empty?.isEmpty ? <AnalystNote>{analystNote}</AnalystNote> : null}
        </ChapterNumberContext.Provider>
      </DossierChapter>
    );
  };

  // Canonical recovery — bound to the finalized AI Findings record (r.estimatedSavings).
  // Never a midpoint of a locally recomputed band.
  const savingsMid = r.estimatedSavings;
  const recommended = Math.max(0, r.invoiceTotal - savingsMid);
  const pctHigh = r.savingsPct[1] ?? 0;
  const risk: ExecutiveVerdictData["financialRisk"] =
    pctHigh >= 15 ? "High" : pctHigh >= 6 ? "Medium" : pctHigh > 0 ? "Low" : "Unknown";
  const recText =
    pctHigh >= 15
      ? "Negotiate before payment"
      : pctHigh >= 6
        ? "Negotiate recommended"
        : pctHigh > 0
          ? "Minor adjustments"
          : "Under review";
  const verdict: ExecutiveVerdictData = {
    invoiceSubmitted: currency(r.invoiceTotal),
    recommendedPayment: currency(recommended),
    potentialSavings: `${currency(r.potentialSavings[0])} – ${currency(r.potentialSavings[1])}`,
    savingsPct: pctHigh > 0 ? `Up to ${pctHigh}% of invoice` : undefined,
    confidence: r.confidence ?? 0,
    financialRisk: risk,
    recommendation: recText,
  };
  const impactCount = (level: "High" | "Medium" | "Low") =>
    r.findings.filter((f) => f.impact === level).length;
  const topIssues = [...r.findings]
    .sort((a, b) => b.savings[1] - a.savings[1])
    .slice(0, 3)
    .map((f) => ({
      label: f.item,
      savings: Math.round((f.savings[0] + f.savings[1]) / 2),
      impact: f.impact,
    }));
  const laws = hasReal
    ? bundle.legalResearch.filter((l) => !isRegulatoryAuthority(l))
        .length
    : 0;
  const regs = hasReal
    ? bundle.legalResearch.filter((l) => isRegulatoryAuthority(l))
        .length
    : 0;
  /* Demo datasets require an explicit demo context (demo route). Real claims
     with no processed data render truthful empty states instead. */
  const isDemoReport = isDemoRoute;
  const overviewMarketComparisons = isDemoReport
    ? DEMO_MARKET_COMPARISONS
    : hasReal
      ? bundle.marketComparisons
      : [];
  const overviewExtracted = isDemoReport
    ? DEMO_INVOICE_EXTRACTED
    : ((bundle.invoiceData?.extracted as Record<string, unknown> | undefined) ?? {});
  const overviewLineItems = isDemoReport
    ? []
    : getNormalizedInvoiceLineItems(bundle.invoiceData?.line_items, overviewExtracted);

  const storageSummary = getStorageSummaryData(
    overviewExtracted,
    overviewMarketComparisons,
    overviewLineItems,
  );

  const evidenceCounts: EvidenceCounts = {
    stateLaws: laws,
    regulations: regs,
    carrierGuidelines: 0,
    vendorHistory: 0,
    marketData: hasReal ? bundle.marketComparisons.length : 0,
    photos: hasReal
      ? bundle.files.filter((f) => (f.mime_type ?? "").toLowerCase().startsWith("image/")).length
      : 0,
    gpsEvidence: 0,
    communications: 0,
    aiAnalysis: hasReal ? bundle.findingItems.length : 0,
    humanReview:
      hasReal && (bundle.review as { assigned_staff_id?: string | null } | null)?.assigned_staff_id
        ? 1
        : 0,
    missingDocs: 0,
    timeline: hasReal ? bundle.timeline.length : 0,
  };

  // ─── Empty-chapter detection ──────────────────────────────────────────
  // Presentation-only: these flags trigger the standardized "evaluated, no
  // applicable information" state (muted badge, dimmed content, diagonal
  // watermark). No calculation, evidence, or AI output is altered.
  //
  // CRITICAL: every predicate below MUST derive from the current report's
  // own bundle. A chapter is never empty just because of its number; if
  // real source data appears later, the chapter automatically returns to
  // its active presentation.
  const findingEvidences: Array<Record<string, unknown> | null> = hasReal
    ? (bundle.findingItems ?? []).map(
        (f) => (f as { evidence?: Record<string, unknown> }).evidence ?? null,
      )
    : [];

  const anyCarrierEvidence = findingEvidences.some(hasCarrierEvidence);

  // ─── Chapter 07 (Carrier Guideline Analysis) — chapter-local binding ───
  // Carrier identity is only ever reported from submitted materials; it is
  // never inferred. Guideline cards are built from persisted finding evidence
  // that carries a carrier/insurer key. Nothing here is fabricated or shared.
  const carrierIdentity = ((): { name: string | null; source: string | null } => {
    if (!hasReal) return { name: null, source: null };
    const ex = (bundle.invoiceData?.extracted ?? null) as Record<string, unknown> | null;
    const pick = (o: Record<string, unknown> | null, keys: string[]): string | null => {
      if (!o) return null;
      for (const k of Object.keys(o)) {
        if (keys.includes(k.toLowerCase().replace(/[\s-]+/g, "_"))) {
          const v = o[k];
          if (typeof v === "string" && v.trim()) return v.trim();
        }
      }
      return null;
    };
    const fromInvoice = pick(ex, ["carrier", "carrier_name", "insurer", "insurance_company"]);
    if (fromInvoice)
      return { name: fromInvoice, source: "Named on the submitted invoice document." };
    const ctxRaw = (bundle.review as { customer_context?: unknown } | null)?.customer_context;
    let ctx: Record<string, unknown> | null = null;
    if (ctxRaw && typeof ctxRaw === "object") ctx = ctxRaw as Record<string, unknown>;
    else if (typeof ctxRaw === "string") {
      try {
        const parsed: unknown = JSON.parse(ctxRaw);
        if (parsed && typeof parsed === "object") ctx = parsed as Record<string, unknown>;
      } catch {
        ctx = null;
      }
    }
    const fromCtx = pick(ctx, ["carrier", "carrier_name", "insurer", "insurance_company"]);
    if (fromCtx) return { name: fromCtx, source: "Provided by the customer at claim submission." };
    return { name: null, source: null };
  })();

  const carrierGuidelineCards: CarrierGuideline[] = hasReal
    ? (bundle.findingItems ?? []).flatMap((f) => {
        const item = f as {
          title?: string | null;
          description?: string | null;
          evidence?: Record<string, unknown> | null;
        };
        const ev = item.evidence ?? null;
        if (!ev || !hasCarrierEvidence(ev)) return [];
        const raw = Object.entries(ev).find(([k]) =>
          /^(carrier|carrier_guideline|insurer_guideline|insurance_guideline)$/i.test(k),
        )?.[1];
        const detail =
          typeof raw === "string"
            ? raw
            : raw && typeof raw === "object"
              ? String(
                  (raw as Record<string, unknown>).requirement ??
                    (raw as Record<string, unknown>).text ??
                    (raw as Record<string, unknown>).label ??
                    "",
                )
              : "";
        if (!detail.trim()) return [];
        return [
          {
            carrier: carrierIdentity.name ?? "Carrier of record",
            guideline: item.title?.trim() || "Carrier guideline",
            requirement: detail.trim(),
            supportsFinding: item.title?.trim() || undefined,
          } satisfies CarrierGuideline,
        ];
      })
    : [];
  void hasVendorEvidence;

  const carrierGuidelinesEmpty = hasReal ? !anyCarrierEvidence : false;
  // Chapter 08 (Vendor Intelligence) is ALWAYS an active chapter: it renders
  // its own investigation record (vendor snapshot, sources reviewed, coverage,
  // and impact) regardless of whether adverse findings exist. "No adverse
  // findings" is a result, not an absent chapter — so it never receives the
  // muted/watermarked empty presentation. The status is communicated by the
  // chapter's own result badge only.
  const vendorIntelEmpty = false;
  const timelineEmpty = hasReal ? bundle.timeline.length === 0 : false;
  const stateLawEmpty = hasReal
    ? bundle.legalResearch.filter((l) => !isRegulatoryAuthority(l))
        .length === 0
    : false;
  const regulationEmpty = hasReal
    ? bundle.legalResearch.filter((l) => isRegulatoryAuthority(l))
        .length === 0
    : false;
  const supportingEvidenceEmpty =
    hasReal &&
    evidenceCounts.marketData === 0 &&
    evidenceCounts.stateLaws === 0 &&
    evidenceCounts.regulations === 0 &&
    evidenceCounts.photos === 0 &&
    evidenceCounts.timeline === 0 &&
    evidenceCounts.aiAnalysis === 0;

  // Single source of truth: every consumer (DossierChapter, PDF watermark,
  // FloatingTOC opacity/N-A pill, chapter Print & Download actions) reads
  // from this map. No component re-derives availability on its own.
  const chapterStatus: Record<
    "chapter-04" | "chapter-06" | "chapter-07" | "chapter-08" | "chapter-09" | "chapter-11",
    { isEmpty: boolean; reason: string }
  > = {
    "chapter-04": {
      isEmpty: stateLawEmpty,
      reason: "no state statute or law findings",
    },
    "chapter-06": {
      isEmpty: regulationEmpty,
      reason: "no regulation or administrative-rule findings",
    },
    "chapter-07": {
      isEmpty: carrierGuidelinesEmpty,
      reason: "no carrier or insurer guideline records",
    },
    "chapter-08": {
      isEmpty: vendorIntelEmpty,
      reason: "no vendor-history, licensing, complaint, or enforcement records",
    },
    "chapter-09": {
      isEmpty: timelineEmpty,
      reason: "no timeline events or dated activity records",
    },
    "chapter-11": {
      isEmpty: supportingEvidenceEmpty,
      reason: "no supporting documents, references, or attached evidence",
    },
  };
  const emptyChapterIds = new Set<string>(
    Object.entries(chapterStatus)
      .filter(([, v]) => v.isEmpty)
      .map(([k]) => k),
  );
  // Expose to the FloatingTOC via a document-level attribute so we don't
  // have to thread state through the whole tree. TOC reads this on mount.
  if (typeof document !== "undefined" && mode === "interactive") {
    document.documentElement.dataset.claimguardEmptyChapters =
      Array.from(emptyChapterIds).join(",");
  }

  const claimVehicleClass = classifyVehicle({
    rawText: typeof overviewExtracted?.raw_text === "string" ? overviewExtracted.raw_text : "",
    lineItems: overviewLineItems.map((li) => ({
      description: typeof li?.description === "string" ? li.description : "",
    })),
  }).vehicleClass;

  return (
    <ClaimVehicleClassContext.Provider value={claimVehicleClass}>
      {/* Cover — its own PDF page, no chapter chrome above it */}

      <div data-pdf-include="true">
        <ReportCover
          id={c.id}
          type={r.invoiceType}
          createdAt={c.createdAt}
          completedAt={
            (bundle.report?.generated_at as string | null | undefined) ??
            (bundle.report?.created_at as string | null | undefined) ??
            c.createdAt
          }
          customerName={customerName}
          invoiceNumber={invNumber}
          hasSpecialist={
            !!(bundle.review as { assigned_staff_id?: string | null } | null)?.assigned_staff_id
          }
          r={r}
        />
      </div>

      {/* AI provider transparency banner — surfaced only when the report
          generator (or upstream AI-findings step) used a deterministic /
          stub / fallback provider. Purely informational, not printed. */}
      {hasReal &&
        (() => {
          const reportProv =
            (bundle.report?.provider as string | null | undefined) ??
            bundle.jobs
              .filter((j) => j.job_type === "report_generation" && j.status === "completed")
              .at(-1)?.provider ??
            null;
          const findingsProv =
            bundle.jobs
              .filter((j) => j.job_type === "ai_findings" && j.status === "completed")
              .at(-1)?.provider ?? null;
          const isFallback = (p: string | null) =>
            !!p &&
            (p.startsWith("stub-") ||
              p.endsWith(":fallback") ||
              p.includes("provider_unavailable"));
          if (!isFallback(reportProv) && !isFallback(findingsProv)) return null;
          return (
            <div
              role="note"
              className="no-print mx-auto mb-6 max-w-4xl rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
            >
              <p className="font-semibold">Notice: fallback processing used</p>
              <p className="mt-1 text-amber-800">
                Some AI-assisted sections were generated using fallback processing because one or
                more AI services were unavailable. Report findings are limited to the available
                evidence.
              </p>
            </div>
          );
        })()}

      {/* Table of Contents — sits immediately after the cover so readers see
          the entire deliverable structure before diving in. Suppressed for the
          Negotiation Package, which is intentionally a concise executive
          deliverable; Complete and Professional Review packages retain it. */}
      <PackageContentsSlot files={hasReal ? bundle.files : null} />




      {/* Original Submitted Invoice — source document that immediately follows
          the Table of Contents. Not a chapter; carries no chapter export
          controls and no analytical overlays. The PDF engine renders the
          scanned invoice at this same position via pdf-lib front matter. */}
      {mode === "interactive" && (
        <div id="original-invoice" className="no-print">
          <OriginalSubmittedInvoice files={hasReal ? bundle.files : null} />
        </div>
      )}

      {/* Case Brief & Invoice Overview — introduced BEFORE the Executive
          Dashboard so the reader first understands the case (vendor, invoice,
          jurisdiction) before seeing executive conclusions. Chapter numbering
          and anchors are preserved per the Report Lock Manifest. */}
      {chapter(
        "chapter-02",
        "The situation, the vendor, and the specific invoice under review — self-contained context for anyone reading only this chapter.",
        // Case Brief reads as one continuous chapter, so per-section export
        // controls are suppressed here; the chapter-level Print / Download
        // Chapter PDF pair in the header remains the only export surface.
        <SectionExportEnabled value={false}>
          <InvoiceOverview
            c={c}
            r={r}
            bundle={hasReal ? bundle : null}
            invoiceFields={invoiceFields}
            invoiceTotal={r.invoiceTotal}
            fairLow={r.fairRange[0]}
            fairHigh={r.fairRange[1]}
            demoExtracted={isDemoRoute ? DEMO_INVOICE_EXTRACTED : undefined}
            demoMarketComparisons={isDemoRoute ? DEMO_MARKET_COMPARISONS : undefined}
          />
          {hasReal && (
            <CustomerContextPanel
              review={bundle.review as never}
              extracted={{
                vehicleDescription:
                  ((bundle.invoiceData?.extracted as Record<string, unknown> | undefined)
                    ?.vehicle_description as string | undefined) ?? null,
                claimNumber:
                  ((bundle.invoiceData?.extracted as Record<string, unknown> | undefined)
                    ?.claim_number as string | undefined) ?? null,
              }}
            />
          )}
        </SectionExportEnabled>,
        storageSummary ? <HeaderStoragePeriodCard summary={storageSummary} /> : undefined,
      )}

      {chapter(
        "chapter-03",
        "A single continuous analysis: how this invoice measures against local benchmarks, how each individual charge holds up under forensic review, and what the combined evidence recommends.",

        <>
          
          <MarketConclusion
            marketComparisons={hasReal ? bundle.marketComparisons : []}
            findingItems={hasReal ? bundle.findingItems : []}
            chargeData={
              hasReal
                ? {
                    lineItems: invoiceFields?.lineItems ?? [],
                    fees: ((bundle.invoiceData?.extracted as { fees?: unknown[] } | undefined)
                      ?.fees ?? []) as Array<Record<string, unknown>>,
                    findingItems: bundle.findingItems,
                    marketComparisons: bundle.marketComparisons,
                    marketResearch: bundle.marketResearch,
                    legalResearchCount: bundle.legalResearch.length,
                    legalResearch: bundle.legalResearch,
                  }
                : null
            }
            zip={bundle.marketComparisons[0]?.zip_code ?? bundle.marketComparisons[0]?.zip ?? r.zip}
            state={bundle.marketComparisons[0]?.state ?? r.state}
            variant="overview"
          />

          <MarketStage
            index="02"
            label="Executive Analyst Summary"
            description="Professional interpretation of the benchmark evidence, pricing trends, and applicable legal findings."
          >
            <div className="grid gap-5 lg:grid-cols-[minmax(0,1.6fr)_minmax(0,1fr)] lg:items-start">
              <AnalystSummaryCard
                marketComparisons={
                  hasReal
                    ? bundle.marketComparisons
                    : isDemoRoute
                      ? DEMO_MARKET_COMPARISONS
                      : []
                }
                findingItems={hasReal ? bundle.findingItems : []}
                chargeData={
                  hasReal
                    ? {
                        lineItems: invoiceFields?.lineItems ?? [],
                        fees: ((bundle.invoiceData?.extracted as { fees?: unknown[] } | undefined)
                          ?.fees ?? []) as Array<Record<string, unknown>>,
                        findingItems: bundle.findingItems,
                        marketComparisons: bundle.marketComparisons,
                        marketResearch: bundle.marketResearch,
                        legalResearchCount: bundle.legalResearch.length,
                        legalResearch: bundle.legalResearch,
                      }
                    : null
                }
              />
              <KeyFindingsCard
                legalResearch={hasReal ? bundle.legalResearch : []}
                marketComparisons={hasReal ? bundle.marketComparisons : []}
              />
            </div>
          </MarketStage>

          <MarketStage
            index="03"
            label="Market Evidence Review"
            description="Review each benchmark finding to understand how the invoiced charges compare against local market benchmarks. Focus on one finding at a time or expand the complete benchmark review."
          >
            <InterleavedChargeAnalysis
              pending={
                hasReal &&
                (bundle.marketComparisons.length === 0 || bundle.findingItems.length === 0)
              }
              real={
                hasReal
                  ? {
                      lineItems: invoiceFields?.lineItems ?? [],
                      fees: ((bundle.invoiceData?.extracted as { fees?: unknown[] } | undefined)
                        ?.fees ?? []) as Array<Record<string, unknown>>,
                      findingItems: bundle.findingItems,
                      marketComparisons: bundle.marketComparisons,
                      marketResearch: bundle.marketResearch,
                      legalResearchCount: bundle.legalResearch.length,
                      legalResearch: bundle.legalResearch,
                    }
                  : null
              }
              findingItems={hasReal ? bundle.findingItems : undefined}
            />
          </MarketStage>

        </>,

      )}

      {chapter(
        "chapter-04",
        "Applicable statutes with citations, relevance, and how each supports the negotiation position.",

        <ApplicableLaws
          pending={hasReal && bundle.legalResearch.length === 0}
          real={
            hasReal
              ? bundle.legalResearch.filter(
                  (l) => !isRegulatoryAuthority(l),
                )
              : undefined
          }
          findingItems={hasReal ? bundle.findingItems : undefined}
          invoiceLineItems={invoiceFields?.lineItems}
        />,
        undefined,

        stateLawEmpty
          ? {
              isEmpty: true,
              badge: "No Findings",
              watermark: "NO APPLICABLE STATUTES",
              message:
                "This chapter was evaluated as part of the ClaimGuard review. No applicable state statutes were identified for the specific charges on this invoice.",
            }
          : undefined,
      )}

      {chapter(
        "chapter-06",
        "Regulatory rules and agency guidance that constrain what the vendor may charge.",
        <ApplicableRegulations
          pending={hasReal && bundle.legalResearch.length === 0}
          real={
            hasReal
              ? bundle.legalResearch.filter(
                  (l) => isRegulatoryAuthority(l),
                )
              : undefined
          }
          findingItems={hasReal ? bundle.findingItems : undefined}
          invoiceLineItems={invoiceFields?.lineItems}
        />,
        undefined,
        regulationEmpty
          ? {
              isEmpty: true,
              badge: "No Findings",
              watermark: "NO APPLICABLE REGULATIONS",
              message:
                "This chapter was evaluated as part of the ClaimGuard review. No applicable agency regulations or bulletins were identified for the specific charges on this invoice.",
            }
          : undefined,
      )}

      {chapter(
        "chapter-05",
        "Professional analysis of statutory lien procedures, abandonment timelines, owner rights, and vendor legal obligations applicable to this claim.",
        <OwnershipLienAnalysis
          vendor={caseFacts.parties.towCompany ?? r.vendor}
          state={caseFacts.location.state ?? invoiceFields?.state ?? null}
          currentOwner={caseFacts.parties.owner}
          lienholder={caseFacts.parties.lienholder}
          vin={caseFacts.vehicle.vin}
          licensePlate={caseFacts.vehicle.licensePlate}
          vehicleDescription={
            [caseFacts.vehicle.year, caseFacts.vehicle.make, caseFacts.vehicle.model]
              .filter(Boolean)
              .join(" ") || null
          }
          vehicleStatus={caseFacts.vehicle.status}
          storageDays={caseFacts.dates.storageDaysBilled}
          towDate={caseFacts.dates.towDate}
          storageStart={caseFacts.dates.storageStart}
          invoiceDate={caseFacts.dates.invoiceDate ?? invoiceFields?.invoiceDate ?? null}
          extracted={
            (bundle.invoiceData?.extracted as Record<string, unknown> | null | undefined) ?? null
          }
          lineItems={
            caseFacts.invoice.lineItems.length
              ? caseFacts.invoice.lineItems
              : (invoiceFields?.lineItems ?? null)
          }
          // SAB-002b — Chapter 05 receives ONLY authorities that pass the
          // canonical jurisdiction filter AND are relevant to ownership,
          // lien, notice, title, abandonment, release or disposition. The
          // full authority array is never handed to presentation code.
          legalResearch={
            hasReal
              ? selectOwnershipAuthorities(bundle.legalResearch, bundle.legalJurisdiction)
              : undefined
          }
          nextChapterLabel={nextChapterLabelAfter("chapter-05")}
        />,
      )}

      {chapter(
        "chapter-07",
        "Policy compliance review against the carrier's own bulletins, fee schedules, and adjuster guidelines.",
        <CarrierGuidelineAnalysis
          guidelines={carrierGuidelineCards}
          carrierName={carrierIdentity.name}
          carrierSource={carrierIdentity.source}
          number="01"
          bare
        />,
        undefined,
        carrierGuidelinesEmpty
          ? {
              isEmpty: true,
              badge: "Carrier Guidance Reviewed",
              watermark: "CARRIER LAYER EVALUATED",
              message:
                "Carrier Policy Review Completed. This claim was evaluated for an applicable carrier policy layer. No carrier-specific bulletin, fee schedule, or adjuster guideline applies to the charges on this invoice, so the report relies on statutory, regulatory, and independent market evidence.",
            }
          : undefined,
      )}

      {chapter(
        "chapter-08",
        "Investigative profile of the vendor — history, market position, and risk indicators material to this claim.",
        <VendorIntelligence
          number="01"
          bare
          data={{
            vendor: r.vendor,
            riskScore: null,
            overchargeFrequencyPct: null,
            averageSavings: null,
            previousReviews: null,
            marketPosition: null,
            invoiceType: r.invoiceType,
            serviceAddress: invoiceFields?.serviceAddress ?? null,
          }}
          googleProfile={vendorSnapshot}
          sosProfile={sosSnapshot}
          licensingProfile={licensingSnapshot}
          bbbProfile={bbbSnapshot}
          stateLicensingProfile={stateLicensingSnapshot}
          market={buildChapter08MarketIntel(overviewMarketComparisons)}
        />,
        undefined,
        vendorIntelEmpty
          ? {
              isEmpty: true,
              badge: "No Evidence",
              watermark: "NO VENDOR INTELLIGENCE",
              message:
                "This chapter was evaluated as part of the ClaimGuard review. No vendor intelligence records were applicable to this invoice.",
            }
          : undefined,
      )}

      {chapter(
        "chapter-09",
        "Chronology of the vehicle, storage, statutory notices, and invoice — reconstructed from the documents on file.",
        <TimelineAnalysis
          bundle={hasReal ? bundle : null}
          invoiceFields={invoiceFields}
          state={caseFacts.location.state ?? invoiceFields?.state ?? null}
        />,
        undefined,
        timelineEmpty
          ? {
              isEmpty: true,
              badge: "No Events Recorded",
              watermark: "NO TIMELINE EVENTS",
              message:
                "This chapter was evaluated as part of the ClaimGuard review. No timeline events, statutory notices, or dated activity records were available for reconstruction from the documents on file.",
            }
          : undefined,
      )}

      {chapter(
        "chapter-11",
        `Every verified evidence source, reference material, and case file underpinning this ${packageIdentity.title}.`,
        <SupportingEvidenceAndReferences
          counts={evidenceCounts}
          files={hasReal ? bundle.files : null}
          hasReal={hasReal}
          bundle={bundle}
          vendor={r.vendor}
          r={r}
        />,
        undefined,
        supportingEvidenceEmpty
          ? {
              isEmpty: true,
              badge: "No Evidence",
              watermark: "NO SUPPORTING EVIDENCE",
              message:
                "This chapter was evaluated as part of the ClaimGuard review. No supporting evidence, reference material, or case files were identified for citation in this package.",
            }
          : undefined,
      )}

      {/* Executive Dashboard — Chapter 01. Uses the standard DossierChapter
          opener so it visually matches every other chapter (badge, number,
          title, subtitle, File/Version/Issued meta row). Absorbs the former
          standalone "Consultant's Assessment" and "Executive Decision Brief"
          chapters so a single page carries the executive decision surface. */}
      {(() => {
        const isNegotiable = r.potentialSavings[1] > 0 && r.savingsPct[1] >= 3;
        const findingItemsCount = hasReal ? bundle.findingItems.length : r.findings.length;
        // Statute-labeled copy on this chapter must count STATUTES ONLY.
        // `laws` already excludes rows classified as regulations, which are
        // surfaced separately by the Regulations tile — using the full
        // legalResearch length here double-counted regulation rows.
        const legalCount = hasReal ? laws : 4;
        const marketCount = hasReal ? bundle.marketComparisons.length : 3;
        const narrative = isNegotiable
          ? `Our review indicates this invoice is negotiable. We documented ${findingItemsCount} finding item${findingItemsCount === 1 ? "" : "s"} across ${legalCount} applicable statute${legalCount === 1 ? "" : "s"} and ${marketCount} local market benchmark${marketCount === 1 ? "" : "s"}, supporting a credible reduction of approximately ${currency(savingsMid)}.`
          : `Our review indicates this invoice is largely aligned with market and regulatory expectations. No material overcharges were identified across the ${legalCount} statute${legalCount === 1 ? "" : "s"} and ${marketCount} market benchmark${marketCount === 1 ? "" : "s"} reviewed.`;
        const sortedFindings = [...r.findings].sort((a, b) => b.savings[1] - a.savings[1]);
        const top = sortedFindings[0];
        const topFive = sortedFindings.slice(0, 5).map((f) => ({
          label: f.item,
          impact: f.impact,
        }));

        // Intelligence Status tiles — what ClaimGuard actually investigated.
        // Never fabricate: tiles switch to a gray "unavailable" state when
        // no supporting evidence exists in the bundle.
        const filesList = hasReal ? bundle.files : [];
        const hasContract = filesList.some((f) => {
          const n = (f.file_name ?? "").toLowerCase();
          const k = (f.kind ?? "").toLowerCase();
          return (
            k === "contract" || /contract|agreement|authorization|estimate\s*agreement/.test(n)
          );
        });
        const hasCarrierDoc = filesList.some((f) => {
          const n = (f.file_name ?? "").toLowerCase();
          const k = (f.kind ?? "").toLowerCase();
          return /carrier|policy|guideline|insurer/.test(`${k} ${n}`);
        });
        const extracted =
          (bundle.invoiceData?.extracted as Record<string, unknown> | undefined) ?? {};
        const ownershipConfirmed =
          hasReal &&
          !!(
            extracted.owner_name ||
            extracted.registered_owner ||
            extracted.vin ||
            extracted.plate ||
            extracted.license_plate ||
            extracted.vehicle_vin
          );
        // Vendor Intelligence is only "reviewed" when we actually have
        // vendor-specific evidence (public records, rate history, or a
        // vendor profile attached to at least one finding). A vendor name
        // alone is NOT vendor intelligence.
        const hasVendorEv = hasReal
          ? (bundle.findingItems ?? []).some((f) =>
              hasVendorEvidence((f as { evidence?: Record<string, unknown> }).evidence ?? null),
            )
          : false;
        const vendorIntelPresent = hasVendorEv;
        const supportingDocsCount = filesList.length;

        const intelligence: IntelligenceTile[] = [
          {
            label: "Market Benchmarking",
            status: marketCount > 0 ? "verified" : "not_applicable",
            value: marketCount > 0 ? "Reviewed" : absenceLabel("COVERAGE_BOUNDARY"),
            detail:
              marketCount > 0
                ? `${marketCount} ZIP-level benchmarks analyzed`
                : "No benchmark coverage for this service and ZIP",
            iconKind: "market",
          },
          {
            label: "State Law Review",
            status: legalCount > 0 ? "verified" : "not_applicable",
            value: legalCount > 0 ? "Reviewed" : absenceLabel("COVERAGE_BOUNDARY"),
            detail:
              legalCount > 0
                ? `${legalCount} applicable statute${legalCount === 1 ? "" : "s"} identified`
                : "No statute in the connected sources applied to these charges",
            iconKind: "law",
          },
          {
            label: "Regulations",
            status: regs > 0 ? "verified" : "not_applicable",
            value: regs > 0 ? "Reviewed" : absenceLabel("COVERAGE_BOUNDARY"),
            detail:
              regs > 0
                ? `${regs} agency regulation${regs === 1 ? "" : "s"} reviewed`
                : "No agency regulation in the connected sources applied",
            iconKind: "regulation",
          },
          {
            label: "Vendor Intelligence",
            status: vendorIntelPresent ? "verified" : "not_provided",
            value: vendorIntelPresent
              ? "Reviewed"
              : absenceLabel("COVERAGE_BOUNDARY"),
            detail: vendorIntelPresent
              ? "Rate history and vendor profile analyzed; no risk indicators identified"
              : "No vendor-specific record was returned by a connected source",
            iconKind: "vendor",
          },
          {
            label: "Carrier Guidelines",
            status: hasCarrierDoc ? "reviewed" : "not_provided",
            value: hasCarrierDoc ? "Reviewed" : absenceLabel("NOT_EVIDENCED"),
            detail: hasCarrierDoc
              ? "Carrier policies analyzed against invoice"
              : "No carrier guideline document was submitted with this claim",
            iconKind: "carrier",
          },
          {
            label: "Contract Review",
            status: hasContract ? "verified" : "not_provided",
            value: hasContract ? "Reviewed" : absenceLabel("NOT_EVIDENCED"),
            detail: hasContract
              ? "Agreement reviewed against charges"
              : "No service agreement or contract was submitted with this claim",
            iconKind: "contract",
          },
          {
            label: "Vehicle Ownership",
            status: ownershipConfirmed ? "reviewed" : "not_provided",
            value: ownershipConfirmed ? "Reviewed" : absenceLabel("NOT_EVIDENCED"),
            detail: ownershipConfirmed
              ? "Ownership and lien rights analyzed"
              : "No title, registration, or lien document was submitted with this claim",
            iconKind: "ownership",
          },
          {
            label: "Supporting Documentation",
            status: supportingDocsCount > 0 ? "verified" : "not_provided",
            value: supportingDocsCount > 0 ? "Reviewed" : absenceLabel("NOT_EVIDENCED"),
            detail:
              supportingDocsCount > 0
                ? `${supportingDocsCount} submitted document${supportingDocsCount === 1 ? "" : "s"} analyzed`
                : "No supporting documents were submitted beyond the invoice",
            iconKind: "docs",
          },
        ];

        // Strongest negotiation points — evidence-backed arguments derived
        // directly from the report's findings, market data, and statutes.
        // Never fabricated: each bullet is gated by a real signal.
        const negotiationPoints: string[] = [];
        const findingsText = sortedFindings
          .map((f) => `${f.item} ${f.issue}`.toLowerCase())
          .join(" | ");

        // Market overage — quantify when we can measure it against benchmarks.
        let aboveMarketCharges = 0;
        const topOverPct = (() => {
          if (!hasReal) return 0;
          let best = 0;
          for (const m of bundle.marketComparisons ?? []) {
            const inv = Number((m as { invoice_amount?: number }).invoice_amount ?? 0);
            const mkt = Number(
              (m as { local_average?: number }).local_average ??
                (m as { market_median?: number }).market_median ??
                (m as { median?: number }).median ??
                0,
            );
            if (inv > 0 && mkt > 0 && inv > mkt) {
              const pct = Math.round(((inv - mkt) / mkt) * 100);
              if (pct >= 8) aboveMarketCharges += 1;
              if (pct > best) best = pct;
            }
          }
          return best;
        })();
        /* Never generalise beyond what the benchmarks actually show: the
           claim is scoped to the number of charges measured above market. */
        if (topOverPct >= 25 && aboveMarketCharges > 0) {
          negotiationPoints.push(
            aboveMarketCharges === 1
              ? `One benchmarked charge exceeds verified local market pricing by ${topOverPct}%.`
              : `${aboveMarketCharges} benchmarked charges exceed verified local market pricing, the highest by ${topOverPct}%.`,
          );
        } else if (aboveMarketCharges > 0) {
          negotiationPoints.push(
            aboveMarketCharges === 1
              ? "One benchmarked charge is priced above the local market range."
              : `${aboveMarketCharges} benchmarked charges are priced above the local market range.`,
          );
        } else if (marketCount > 0) {
          negotiationPoints.push(
            "Local market benchmarks were retrieved for every billed charge and are documented in this report.",
          );
        }

        if (legalCount > 0) {
          negotiationPoints.push(
            legalCount >= 2
              ? `${legalCount} fee categories are supported by applicable statutes.`
              : "Applicable law supports challenging billed charges.",
          );
        }
        if (regs > 0) {
          negotiationPoints.push(
            regs === 1
              ? "An agency regulation applies to a charge on this invoice."
              : `${regs} agency regulations apply to charges on this invoice.`,
          );
        }
        if (/duplicate|repeated|double\s*bill/i.test(findingsText)) {
          negotiationPoints.push("Duplicate administrative billing identified.");
        }
        if (/admin|administrative/i.test(findingsText)) {
          negotiationPoints.push("Administrative fees exceed statutory limits.");
        }
        if (/storage|per[-\s]?day/i.test(findingsText) && topOverPct < 25) {
          negotiationPoints.push("Storage charges are not supported by verified local rates.");
        }
        if (
          /unsupported|no support|missing|undocumented|no receipt|not itemi/i.test(findingsText)
        ) {
          negotiationPoints.push("Vendor documentation does not substantiate every billed charge.");
        }
        if (findingItemsCount >= 2 && negotiationPoints.length < 3) {
          negotiationPoints.push("Industry benchmarks support a materially lower charge.");
        }
        if (r.potentialSavings[1] > 0 && negotiationPoints.length < 3) {
          negotiationPoints.push("Invoice contains unsupported or excessive fees.");
        }
        // Dedupe + cap at 6.
        const seen = new Set<string>();
        const dedupedPoints = negotiationPoints
          .filter((p) => {
            const k = p.toLowerCase();
            if (seen.has(k)) return false;
            seen.add(k);
            return true;
          })
          .slice(0, 6);
        negotiationPoints.length = 0;
        negotiationPoints.push(...dedupedPoints);

        return chapter(
          "chapter-01",
          "A single-page executive briefing — findings, financial impact, and recommended action distilled for rapid decision-making.",
          <ExecutiveDashboard
            invoiceTotal={r.invoiceTotal}
            fairLow={r.fairRange[0]}
            fairHigh={r.fairRange[1]}
            savingsLow={r.potentialSavings[0]}
            savingsHigh={r.potentialSavings[1]}
            savingsMid={savingsMid}
            highestRecovery={r.estimatedSavings}
            negotiationStrength={c.ai?.negotiationStrength ?? null}
            negotiationPct={Math.max(0, Math.min(100, Math.round(pctHigh * 4)))}
            reviewConfidence={r.confidence ?? 0}
            questionableCount={r.findings.length}
            lawsCount={laws + regs}
            topFinding={topFive[0]?.label ?? null}
            topFindingImpact={topFive[0]?.impact ?? null}
            risk={risk}
            consultantNarrative={narrative}
            invoiceHealthLabel={invoiceHealth(r).label}
            negotiationPriorityLabel={negotiationPriority(r).label}
            topOpportunity={
              top
                ? {
                    label: `${top.item} — ${top.issue.toLowerCase()}`,
                    savingsLow: top.savings[0],
                    savingsHigh: top.savings[1],
                  }
                : null
            }
            topFindings={topFive}
            negotiationPoints={negotiationPoints}
            intelligence={intelligence}
            isNegotiable={isNegotiable}
            marketBenchmarkCount={marketCount}
          />,
        );
      })()}

      {chapter(
        "chapter-10",
        "Prioritized negotiation playbook — what to say, in what order, and the escalation path for each position.",
        <>
          <NegotiationOpportunities
            pending={hasReal && bundle.findingItems.length === 0}
            real={hasReal ? bundle.findingItems : null}
            reportFindings={hasReal ? r.findings : undefined}
            marketComparisons={hasReal ? bundle.marketComparisons : undefined}
            legalResearch={hasReal ? bundle.legalResearch : undefined}
            vendor={r.vendor}
            report={r}
          />
          <div className="mt-10">
            <SavingsAndNegotiationStrategy r={r} />
          </div>
        </>,
      )}

      {/* Version History has been relocated into Chapter 12 as
          "Publication Record" (immediately below the Executive Verdict),
          so publication metadata reinforces the certification instead
          of interrupting the Chapter 11 → Chapter 12 transition. */}

      {chapter(
        "chapter-12",
        "Signed certification of the review, methodology, and reviewer accountability.",
        <>
          <ProfessionalReviewConclusion
            r={r}
            caseId={c.id}
            versionEntries={
              mode === "interactive"
                ? [
                    {
                      version: "1.0",
                      date: formatReportDate(c.createdAt, { month: "short", day: "numeric" }),
                      changes: "Initial Claim Intelligence Package™ published.",
                      publishedBy: "ClaimGuard Professional Review",
                    },
                  ]
                : null
            }
          />
          <div className="mt-16">
            <ClosingPage mode={mode} />
          </div>
        </>,
      )}

      {mode === "interactive" && (
        <>
          {/* Web-only customer action cards — placed directly after the
              closing page so "Your Next Actions" follows immediately. */}
          <div className="no-print mt-10">
            <RecommendationCard caseId={c.id} />
          </div>

          {/* Case File Appendices — web-only widget. The PDF engine
              appends the actual invoice pages as Appendix A directly. */}
          <div className="no-print mt-10">
            <CaseFileAppendices files={hasReal ? bundle.files : null} />
          </div>
          <ReportFooter caseId={c.id} real={hasReal} />
        </>
      )}
    </ClaimVehicleClassContext.Provider>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Toolbar & Cover                                             */
/* ─────────────────────────────────────────────────────────── */

function ReportToolbar({
  caseId,
  workspaceHref,
  invoiceNumber,
  fileNumber,
  invoiceFiles,
}: {
  caseId: string;
  workspaceHref?: string | null;
  invoiceNumber?: string;
  fileNumber?: string;
  invoiceFiles?: ReviewFile[];
}) {
  const { downloadOrGenerate, busy } = useReportPdfActions(
    caseId,
    invoiceNumber,
    fileNumber,
    invoiceFiles,
  );
  /* Dashboard hand-off: the row action stores the chosen package and opens the
     report, which runs the one canonical generator once its DOM is mounted. */
  const search = Route.useSearch();
  const navigate = useNavigate();
  const autoRanRef = useRef(false);
  useEffect(() => {
    if (autoRanRef.current) return;
    let pkg = (search as { download?: _ReportPackage }).download;
    if (!pkg && typeof window !== "undefined") {
      const stored = window.sessionStorage.getItem(autoDownloadKey(caseId));
      if (stored === "complete" || stored === "review" || stored === "negotiation") pkg = stored;
    }
    if (!pkg) return;
    autoRanRef.current = true;
    if (typeof window !== "undefined") window.sessionStorage.removeItem(autoDownloadKey(caseId));
    const chosen = pkg;
    /* Start the download first; clearing the search param must not cancel it. */
    window.setTimeout(() => {
      void downloadOrGenerate(chosen);
      if ((search as { download?: string }).download) {
        void navigate({ to: "/report/$id", params: { id: caseId }, search: {}, replace: true });
      }
    }, 900);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, caseId]);



  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-4 w-4" /> Return to Dashboard
      </Link>
      <div className="flex flex-wrap gap-2">
        {workspaceHref && (
          <Link
            to="/workspace/$id"
            params={{ id: workspaceHref }}
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium hover:bg-secondary"
          >
            <Briefcase className="h-4 w-4" /> Open Workspace
          </Link>
        )}
        <button
          onClick={() => typeof window !== "undefined" && window.print()}
          className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-2 text-sm font-medium hover:bg-secondary"
        >
          <Printer className="h-4 w-4" /> Print
        </button>
        <DownloadPackageMenu onSelect={(pkg) => downloadOrGenerate(pkg)} busy={busy} />

      </div>
    </div>
  );
}

/* The three download package options now live in one canonical place:
   @/components/report/download-package-menu — shared with the Dashboard. */





function PackageContentsIntro() {
  const identity = usePdfPackageIdentity();
  const intro =
    identity.key === "negotiation"
      ? `This ${identity.title} equips you to negotiate with confidence — the executive decision, the negotiation strategy playbook, and the professional review conclusion that seals your position.`
      : identity.key === "review"
        ? `This ${identity.title} documents the market analysis, legal review, and supporting evidence assembled by ClaimGuard Professional Review Services — the complete investigative record for your invoice.`
        : `This ${identity.title} is organized into five professional review parts that guide you from the original invoice through executive findings, detailed evidence, negotiation strategy, and supporting documentation.`;
  return (
    <>
      <h2 className="font-display text-[38px] leading-[1.05] text-navy md:text-[52px]">
        Contents
      </h2>
      <p className="mt-3 max-w-2xl text-pretty text-[14px] leading-relaxed text-muted-foreground">
        {intro}
      </p>
    </>
  );
}

function PackageCoverTitle() {
  const identity = usePdfPackageIdentity();
  return (
    <div className="relative mt-10 px-10 md:mt-14 md:px-16">
      <h1 className="font-display text-[62px] font-bold leading-[0.82] tracking-tight text-white md:text-[92px] lg:text-[104px]">
        {identity.coverTitleLines.map((line, i) => (
          <Fragment key={line}>
            {i > 0 ? <br /> : null}
            {line}
          </Fragment>
        ))}
        <span className="align-super text-[26px] font-semibold text-white/80 md:text-[40px] lg:text-[46px]">
          ™
        </span>
      </h1>

      <div className="mt-10 inline-block bg-fasttrack px-7 py-3 md:mt-11 md:px-8 md:py-3.5">
        <div className="text-[14px] font-bold uppercase tracking-[0.26em] text-navy md:text-[17px]">
          {identity.ribbonPrimary}
        </div>
        <div className="mt-0.5 text-[12px] font-semibold uppercase tracking-[0.22em] text-navy/75 md:text-[14px]">
          {identity.ribbonSecondary}
        </div>
      </div>
    </div>
  );
}

function ReportCover({
  id,
  type,
  createdAt,
  completedAt,
  customerName,
  invoiceNumber,
  hasSpecialist = false,
  r,
}: {
  id: string;
  type: string;
  createdAt: string;
  completedAt: string;
  customerName: string;
  invoiceNumber: string;
  hasSpecialist?: boolean;
  r: ReturnType<typeof buildReport>;
}) {
  const preparedBy = hasSpecialist
    ? "ClaimGuard Expert Review Team"
    : "ClaimGuard Professional Review Services";
  const receivedStr = formatReportDate(createdAt, { month: "long", day: "numeric" });
  const completedStr = formatReportDate(completedAt, { month: "long", day: "numeric" });
  const completedTimeStr = formatReportTime(completedAt);

  const shortCustomer = customerName.split("@")[0];
  return (
    <div className="relative mt-6 overflow-hidden rounded-[28px] border border-white/10 bg-navy text-navy-foreground shadow-elevated print-avoid-break aspect-[3/4] md:aspect-[4/5]">
      {/* Right-side truck panel — enlarged hero for stronger cinematic anchor */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-y-0 right-0 w-[68%] overflow-hidden"
        style={{ clipPath: "polygon(22% 0, 100% 0, 100% 100%, 0 100%)" }}
      >
        <img
          src={coverTruck.url}
          alt=""
          crossOrigin="anonymous"
          className="h-full w-full object-cover object-[center_60%] scale-[1.12]"
        />
        {/* Single unified navy scrim — cleaner than stacked gradients */}
        <div className="absolute inset-0 bg-[linear-gradient(150deg,rgba(9,17,40,0.94)_0%,rgba(9,17,40,0.42)_42%,rgba(9,17,40,0)_68%)]" />
        <div className="absolute inset-x-0 bottom-0 h-1/3 bg-[linear-gradient(to_top,rgba(9,17,40,0.6),rgba(9,17,40,0))]" />
      </div>

      {/* Single quiet diagonal — one gold edge, nothing competing */}
      <svg
        aria-hidden
        viewBox="0 0 100 130"
        preserveAspectRatio="none"
        className="pointer-events-none absolute inset-0 h-full w-full"
      >
        <path d="M 56,0 L 42,130" stroke="#d4a24a" strokeWidth="0.4" opacity="0.75" />
      </svg>

      {/* 1) Brand — top anchor */}
      <div className="relative flex items-center gap-3 px-10 pt-14 md:px-16 md:pt-16">
        <ShieldCheck className="h-9 w-9 text-fasttrack md:h-11 md:w-11" />
        <span className="font-display text-[42px] font-bold tracking-tight text-white md:text-[52px]">
          CLAIMGUARD
        </span>
        <span className="align-super text-[13px] font-semibold text-white/70 md:text-[15px]">
          ™
        </span>
      </div>

      {/* Thin gold rule separates identity from title — quiet hierarchy cue */}
      <div
        aria-hidden
        className="relative mx-10 mt-8 h-px w-24 bg-fasttrack/70 md:mx-16 md:mt-10 md:w-32"
      />

      {/* 2) Title block */}
      <PackageCoverTitle />


      {/* 4) Claim information — even rhythm, clears the confidentiality footer */}
      <div className="relative mt-10 grid max-w-[50%] grid-cols-1 gap-y-[1.4rem] px-10 pb-[104px] md:mt-12 md:px-16 md:pb-[112px]">
        {[
          { l: "Report ID", v: id, mono: true },
          { l: "Date Received", v: receivedStr },
          { l: "Review Completed", v: completedStr },
          { l: "Review Completed Time", v: completedTimeStr },
          { l: "Prepared By", v: preparedBy },
        ].map((m) => (
          <div key={m.l} className="flex flex-col justify-start border-l-[2px] border-fasttrack/80 pl-4">
            <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-white/55 md:text-[11px]">
              {m.l}
            </div>
            <div
              className={
                "mt-1.5 text-[19px] font-bold leading-tight text-white md:text-[22px] " +
                (m.mono ? "font-mono tracking-tight" : "")
              }
            >
              {m.v}
            </div>
          </div>
        ))}
      </div>


      {/* 5) Certification seal — anchored lower-right, overlapping the diagonal */}
      <div className="absolute bottom-[8%] left-[60%] md:bottom-[7%] md:left-[64%]">
        <div className="relative h-40 w-40 rounded-full bg-fasttrack p-2 shadow-[0_18px_44px_rgba(0,0,0,0.55)] md:h-52 md:w-52 md:p-2.5 lg:h-60 lg:w-60">
          <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-fasttrack bg-navy">
            <div
              aria-hidden
              className="absolute inset-5 rounded-full border border-fasttrack/60 md:inset-7"
              style={{
                background:
                  "repeating-conic-gradient(from 0deg, rgba(212,162,74,0.32) 0deg 1deg, rgba(212,162,74,0.06) 1deg 7deg)",
              }}
            />
            <div aria-hidden className="absolute inset-[22%] rounded-full bg-navy/90" />

            <svg
              aria-hidden
              className="absolute inset-0 h-full w-full"
              viewBox="0 0 200 200"
              preserveAspectRatio="xMidYMid meet"
            >
              <defs>
                <path id="seal-top-arc" d="M 28,100 A 72,72 0 0,1 172,100" fill="none" />
                <path id="seal-bottom-arc" d="M 32,100 A 68,68 0 0,0 168,100" fill="none" />
              </defs>
              <text
                fill="#ffffff"
                fontSize="14"
                fontWeight="800"
                letterSpacing="2.4"
                textAnchor="middle"
                fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              >
                <textPath href="#seal-top-arc" startOffset="50%">
                  CERTIFIED REVIEW
                </textPath>
              </text>
              <text
                fill="#ffffff"
                fontSize="14"
                fontWeight="800"
                letterSpacing="2.4"
                textAnchor="middle"
                fontFamily="system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif"
              >
                <textPath href="#seal-bottom-arc" startOffset="50%">
                  CLAIMGUARD
                </textPath>
              </text>
            </svg>

            <div className="absolute left-[12%] top-1/2 -translate-y-1/2 text-[17px] font-black text-white md:text-[22px] lg:text-[26px]">
              ★
            </div>
            <div className="absolute right-[12%] top-1/2 -translate-y-1/2 text-[17px] font-black text-white md:text-[22px] lg:text-[26px]">
              ★
            </div>
            <div className="relative flex h-[34%] w-[34%] items-center justify-center rounded-[28%] bg-fasttrack shadow-inner">
              <div className="absolute inset-[13%] rounded-[24%] bg-navy" />
              <svg aria-hidden viewBox="0 0 40 40" className="relative h-[70%] w-[70%]">
                <path
                  d="M 10 20 L 17 28 L 31 12"
                  fill="none"
                  stroke="#ffffff"
                  strokeWidth="5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Confidential strip */}
      <div className="absolute inset-x-0 bottom-0 flex items-center justify-center border-t-[3px] border-fasttrack/70 bg-white px-10 py-4 md:px-14 md:py-4">
        <span className="text-[13px] font-bold uppercase tracking-[0.36em] text-navy md:text-[16px]">
          Confidential — Prepared for the Named Recipient
        </span>
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* ─────────────────────────────────────────────────────────── */
/* Executive Dashboard                                         */
/* The single executive summary of the Claim Intelligence      */
/* Package™. Printed immediately after the cover and before    */
/* the Table of Contents. Designed to answer, in under 30      */
/* seconds: should this invoice be negotiated, how much can    */
/* likely be recovered, how confident is the analysis, and     */
/* what should the customer do next.                           */
/* ─────────────────────────────────────────────────────────── */
export type IntelligenceTileStatus = "verified" | "reviewed" | "not_applicable" | "not_provided";

export interface IntelligenceTile {
  label: string;
  status: IntelligenceTileStatus;
  value: string;
  detail: string;
  iconKind?:
    | "market"
    | "law"
    | "regulation"
    | "vendor"
    | "carrier"
    | "contract"
    | "ownership"
    | "docs";
}

export function ExecutiveDashboard(props: {
  invoiceTotal: number;
  fairLow: number;
  fairHigh: number;
  savingsLow: number;
  savingsHigh: number;
  savingsMid: number;
  highestRecovery: number;
  negotiationStrength: string | null;
  negotiationPct: number;
  reviewConfidence: number;
  questionableCount: number;
  lawsCount: number;
  topFinding: string | null;
  topFindingImpact: "High" | "Medium" | "Low" | null;
  risk: "High" | "Medium" | "Low" | "Unknown";
  consultantNarrative?: string;
  invoiceHealthLabel?: "Excellent" | "Good" | "Moderate" | "High Risk";
  negotiationPriorityLabel?: "Critical" | "High" | "Moderate" | "Low";
  topOpportunity?: { label: string; savingsLow: number; savingsHigh: number } | null;
  topFindings?: Array<{ label: string; impact: "High" | "Medium" | "Low" }>;
  negotiationPoints?: string[];
  intelligence?: IntelligenceTile[];
  isNegotiable?: boolean;
  marketBenchmarkCount?: number;
}) {
  const {
    invoiceTotal,
    savingsLow,
    savingsHigh,
    savingsMid,
    // highestRecovery intentionally unused: it duplicated Total Supported
    // Recovery on this chapter and produced two conflicting KPI labels.

    negotiationStrength,
    reviewConfidence,
    questionableCount,
    lawsCount,
    topFinding,
    consultantNarrative,
    negotiationPriorityLabel,
    topFindings = [],
    negotiationPoints,
    intelligence = [],
    isNegotiable = savingsHigh > 0,
    marketBenchmarkCount = 0,
  } = props;
  const identity = usePdfPackageIdentity();
  /* Canonical Report Summary — the only source for report-wide financials
     surfaced on this summary chapter. Nothing here is recalculated. */
  const rs = useReportSummary();
  /* SAB-001: canonical only — no derived fallback. */
  const settlementGoal = rs.financials.settlementGoal;





  const impactCount = (level: "High" | "Medium" | "Low") =>
    topFindings.filter((f) => f.impact === level).length;

  // Evidence strength — derived from provided negotiation strength when
  // available; otherwise mapped from review confidence. No new logic —
  // presentational label only.
  const evidenceStrengthLabel = ((): "VERY STRONG" | "STRONG" | "MODERATE" | "LIMITED" => {
    const raw = (negotiationStrength ?? "").toLowerCase();
    if (raw.includes("very")) return "VERY STRONG";
    if (raw.includes("strong")) return "STRONG";
    if (raw.includes("moder")) return "MODERATE";
    if (raw.includes("weak") || raw.includes("limit")) return "LIMITED";
    // SAB-004: single canonical ladder. No local cut points.
    const canonical = canonicalEvidenceStrength(reviewConfidence);
    if (canonical === "Very Strong") return "VERY STRONG";
    if (canonical === "Strong") return "STRONG";
    if (canonical === "Moderate") return "MODERATE";
    return "LIMITED";
  })();

  /* Effort/complexity forecasting removed: this summary chapter may not
     invent negotiation-effort metrics that no certified chapter establishes. */


  const recommendationHeadline = isNegotiable
    ? "NEGOTIATE BEFORE PAYMENT"
    : "PAYMENT ALIGNED — NO NEGOTIATION REQUIRED";

  // Executive statement — audit-conclusion tone, evidence-based.
  const executiveStatement =
    consultantNarrative && consultantNarrative.trim().length > 0
      ? consultantNarrative
      : isNegotiable
        ? `Based on ${questionableCount} analysis finding${questionableCount === 1 ? "" : "s"}, ${marketBenchmarkCount} market benchmark${marketBenchmarkCount === 1 ? "" : "s"}, and ${lawsCount} applicable legal reference${lawsCount === 1 ? "" : "s"}, ClaimGuard concludes the invoice contains evidence-supported grounds for negotiation prior to payment.`
        : `ClaimGuard's forensic review found no material overcharges. The invoice is consistent with prevailing market rates and applicable regulation.`;

  // Recommended next steps — ordered as a logical negotiation sequence.
  const nextSteps: string[] = [];
  if (isNegotiable) nextSteps.push("Withhold payment pending vendor response");
  if (topFinding) nextSteps.push(`Open with the strongest finding: ${topFinding.toLowerCase()}`);
  if (impactCount("High") >= 2)
    nextSteps.push("Address remaining high-impact findings in priority order");
  if (marketBenchmarkCount > 0)
    nextSteps.push("Support each challenge with documented market benchmarks");
  if (lawsCount > 0) nextSteps.push("Cite applicable statutes and regulations where relevant");
  if (settlementGoal > 0)
    nextSteps.push(`Anchor the discussion on the Settlement Goal of ${currency(settlementGoal)}`);
  const steps = nextSteps.slice(0, 6);

  // Retain negotiation points for internal use; not surfaced in the new
  // hierarchy to avoid duplication with next steps.
  void negotiationPoints;
  void savingsLow;
  void savingsHigh;

  /* ── Analysis Confidence foundation ──
     Each pillar mirrors the Evidence Reviewed tiles supplied by the report
     builder; this chapter performs no independent evaluation. */
  const tileActive = (label: string) =>
    intelligence.some(
      (t) => t.label === label && (t.status === "verified" || t.status === "reviewed"),
    );
  const foundation: Array<{ label: string; present: boolean }> = [
    { label: "Invoice Analysis", present: true },
    { label: "Market Benchmarking", present: tileActive("Market Benchmarking") },
    { label: "State Law Review", present: tileActive("State Law Review") },
    { label: "Regulations", present: tileActive("Regulations") },
    { label: "Supporting Documentation", present: tileActive("Supporting Documentation") },
  ];
  const foundationCount = foundation.filter((f) => f.present).length;


  return (
    <div className="px-8 py-14 md:px-14 md:py-16">
      {/* ─────────── SECTION 1 · PRIMARY DECISION PANEL ─────────── */}
      <div className="relative overflow-hidden rounded-3xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-50 via-white to-amber-50/40 px-8 py-14 text-center shadow-card md:px-14 md:py-20 print-avoid-break">
        <div className="mx-auto flex max-w-3xl flex-col items-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-600 text-white shadow-md">
            {isNegotiable ? <Handshake className="h-8 w-8" /> : <ShieldCheck className="h-8 w-8" />}
          </div>
          <div className="mt-6 font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/70">
            ClaimGuard Recommendation
          </div>
          <h2 className="mt-3 font-display text-[40px] font-bold leading-[1.02] text-emerald-900 md:text-[64px]">
            {recommendationHeadline}
          </h2>
          <div className="mt-8 grid w-full grid-cols-1 gap-4 sm:grid-cols-2 sm:max-w-xl">
            <div className="rounded-xl border border-emerald-200 bg-white/80 px-5 py-4">
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-navy/60">
                Evidence Strength
              </div>
              <div className="mt-2 font-display text-[22px] font-bold text-emerald-800 md:text-[26px]">
                {evidenceStrengthLabel}
              </div>
            </div>
            <div className="rounded-xl border border-rose-200 bg-white/80 px-5 py-4">
              <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-navy/60">
                Negotiation Priority
              </div>
              <div className="mt-2 font-display text-[22px] font-bold text-rose-700 md:text-[26px]">
                {(negotiationPriorityLabel ?? "Moderate").toUpperCase()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ─────── SECTION 2 · ANALYSIS CONFIDENCE + NEGOTIATION EFFORT ─────── */}
      <SectionEyebrow>Why You Can Trust This Recommendation</SectionEyebrow>
      <div className="mt-5 grid grid-cols-1 gap-4 md:grid-cols-2 print-avoid-break">
        {/* SAB-004: canonical Analysis Confidence — same number and name as
            the review page, dashboard and workspace. */}
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
          <div className="flex items-baseline justify-between gap-3">
            <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-navy/70">
              {ANALYSIS_CONFIDENCE_LABEL}
            </div>
            <div className="font-display text-[32px] leading-none text-emerald-800 tabular-nums md:text-[38px]">
              {confidenceBand(reviewConfidence) ? `${Math.round(reviewConfidence)}%` : CONF_NOT_CALCULATED}
            </div>
          </div>
          <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-emerald-600"
              style={{ width: `${Math.max(6, Math.min(100, Math.round(reviewConfidence)))}%` }}
            />
          </div>
          <div className="mt-5 text-[11px] font-semibold uppercase tracking-[0.18em] text-navy/60">
            Supported by {foundationCount} of {foundation.length} evidence pillars
          </div>
          <ul className="mt-3 grid grid-cols-1 gap-1.5 sm:grid-cols-2">
            {foundation.map((f) => (
              <li key={f.label} className="flex min-w-0 items-center gap-2 text-[13px]">
                {f.present ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" aria-hidden="true" />
                ) : (
                  <Circle className="h-4 w-4 shrink-0 text-navy/25" aria-hidden="true" />
                )}
                <span
                  className={`min-w-0 [overflow-wrap:anywhere] ${f.present ? "text-navy" : "text-navy/40 line-through"}`}
                >
                  {f.label}
                </span>
              </li>
            ))}
          </ul>
        </div>

        {/* Evidence Basis — canonical counts only; no effort forecasting. */}
        <div className="rounded-2xl border border-border bg-white p-6 shadow-card">
          <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-navy/70">
            Evidence Basis
          </div>
          <div className="mt-4 grid grid-cols-2 gap-x-6 gap-y-4">
            <EffortRow label="Negotiation Strength" value={evidenceStrengthLabel} />
            <EffortRow label="Negotiation Priority" value={negotiationPriorityLabel ?? "Moderate"} />
            <EffortRow label="Analysis Findings" value={String(questionableCount)} />
            <EffortRow label="Market Benchmarks" value={String(marketBenchmarkCount)} />
          </div>
          <p className="mt-4 text-[11.5px] leading-snug text-muted-foreground">
            Each value is carried forward from the chapter that established it; nothing on this page
            is recalculated.
          </p>
        </div>
      </div>

      {/* ─────────── SECTION 3 · FINANCIAL IMPACT ─────────── */}
      <SectionEyebrow>Financial Impact</SectionEyebrow>
      <div className="mt-5 rounded-2xl border border-emerald-200 bg-gradient-to-br from-emerald-50/70 to-white p-8 shadow-card md:p-10 print-avoid-break">
        <div className="grid grid-cols-1 gap-6 md:grid-cols-[minmax(0,1fr)_1px_minmax(0,1.5fr)_1px_minmax(0,1fr)] md:items-center">
          <div>
            <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-navy/70">
              Invoice Total
            </div>
            <div
              className="mt-2 font-display text-[26px] leading-none text-navy/80 tabular-nums md:text-[30px]"
              {...summaryField("invoiceTotal", rs.financials.invoiceTotal || invoiceTotal)}
            >
              {currency(rs.financials.invoiceTotal || invoiceTotal)}
            </div>
          </div>
          <div className="hidden h-20 w-px bg-border md:block" />
          <div className="md:px-4">
            <div className="flex items-center gap-2">
              <span
                className="inline-block h-1.5 w-1.5 rounded-full bg-emerald-600"
                aria-hidden="true"
              />
              <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-emerald-800">
                Total Supported Recovery
              </div>
            </div>
            <div
              className="mt-2 font-display text-[46px] font-bold leading-none text-emerald-800 tabular-nums md:text-[64px]"
              {...summaryField("potentialSavings", savingsMid)}
            >
              {currency(savingsMid)}
            </div>
          </div>
          <div className="hidden h-20 w-px bg-border md:block" />
          <div>
            <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-navy/70">
              Settlement Goal
            </div>
            <div
              className="mt-2 font-display text-[26px] leading-none text-navy/80 tabular-nums md:text-[30px]"
              {...summaryField("settlementGoal", settlementGoal)}
            >
              {settlementGoal > 0 ? currency(settlementGoal) : NOT_AVAILABLE}
            </div>
          </div>
        </div>
      </div>


      {/* ─────────── SECTION 4 · WHY WE RECOMMEND NEGOTIATION ─────────── */}
      <SectionEyebrow>
        {isNegotiable ? "Why We Recommend Negotiation" : "Why No Negotiation Is Needed"}
      </SectionEyebrow>
      <div className="mt-5 rounded-2xl border border-navy/15 bg-navy/[0.03] p-8 md:p-10 print-avoid-break">
        <p className="max-w-3xl text-[15.5px] leading-relaxed text-navy/85 md:text-[16.5px]">
          {executiveStatement}
        </p>
      </div>

      {/* ─────────── SECTION 5 · WHERE TO START ─────────── */}
      <SectionEyebrow>Where To Start</SectionEyebrow>
      <ol className="mt-5 space-y-3">
        {topFindings.length === 0 && (
          <li className="rounded-2xl border border-border bg-white p-6 text-sm text-muted-foreground">
            No material findings identified for prioritization.
          </li>
        )}
        {topFindings.slice(0, 5).map((f, i) => {
          return (
            <li
              key={`${f.label}-${i}`}
              className="flex flex-col items-start gap-3 rounded-2xl border border-border bg-white p-4 shadow-card print-avoid-break sm:flex-row sm:items-center sm:gap-5 sm:p-5"
            >
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-navy text-[15px] font-bold text-white tabular-nums">
                {i + 1}
              </div>
              <div className="w-full min-w-0 flex-1">
                <div className="text-[15.5px] font-semibold leading-snug text-navy [overflow-wrap:anywhere]">
                  {f.label}
                </div>
                <div className="mt-1.5 text-[12px] text-muted-foreground">
                  Detailed in the Invoice &amp; Charge Analysis chapter.
                </div>
              </div>
              <div className="shrink-0 self-start sm:self-center">
                <ImpactBadge level={f.impact} />
              </div>
            </li>
          );
        })}

      </ol>

      {/* ─────────── SECTION 6 · RECOMMENDED NEXT STEPS ─────────── */}
      <SectionEyebrow>Recommended Next Steps</SectionEyebrow>
      <ol className="mt-5 space-y-2.5 rounded-2xl border border-amber-200 bg-amber-50/60 p-6 md:p-8 print-avoid-break">
        {steps.length === 0 && (
          <li className="text-sm text-amber-900/80">No immediate actions recommended.</li>
        )}
        {steps.map((s, i) => (
          <li key={i} className="flex items-start gap-3">
            <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-500 text-[11px] font-bold text-white tabular-nums">
              {i + 1}
            </span>
            <span className="text-[14px] font-medium leading-snug text-amber-900">{s}</span>
          </li>
        ))}
      </ol>

      {/* ─────────── SECTION 7 · EVIDENCE REVIEWED ─────────── */}
      <SectionEyebrow>Evidence Reviewed</SectionEyebrow>
      <div
        className="mt-5 grid grid-cols-1 gap-3 print-avoid-break sm:[grid-template-columns:repeat(auto-fit,minmax(13rem,1fr))]"
        data-pdf-keep-together="true"
      >
        {intelligence.length === 0 && (
          <div className="col-span-full rounded-xl border border-border/70 bg-secondary/20 p-5 text-sm text-muted-foreground">
            Investigation modules not yet reported.
          </div>
        )}
        {[...intelligence]
          .sort((a, b) => {
            const rank = (s: string) => (s === "not_applicable" || s === "not_provided" ? 1 : 0);
            return rank(a.status) - rank(b.status);
          })
          .map((t, i) => (
            <IntelligenceTileCard key={i} tile={t} />
          ))}
      </div>

      {/* ─────────── EXECUTIVE FOOTER ─────────── */}
      {identity.key === "negotiation" ? (
        <p className="mt-12 border-t border-border/60 pt-5 text-center text-[11.5px] leading-relaxed text-muted-foreground">
          The Executive Decision Cheat Sheet highlights the most important findings and priorities
          identified during the review. This Negotiation Package provides the professional
          negotiation strategy, recommended sequencing, conversation guidance, and settlement
          playbook needed to approach negotiations with confidence from beginning to end. If a
          future situation requires expanded legal research, comprehensive market benchmarking, or
          detailed supporting documentation, additional comprehensive investigative review services
          are available separately.
        </p>
      ) : (
        <p className="mt-12 border-t border-border/60 pt-5 text-center text-[11.5px] leading-relaxed text-muted-foreground">
          This Executive Decision Cheat Sheet summarizes the most important findings from the
          complete Claim Intelligence Package. Detailed evidence, analysis, and supporting
          documentation are provided in the chapters that follow.
        </p>
      )}

    </div>
  );
}

function EffortRow({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <div className="font-mono text-[9.5px] font-semibold uppercase tracking-[0.22em] text-navy/55">
        {label}
      </div>
      <div className="mt-1 text-[15px] font-semibold text-navy [overflow-wrap:anywhere]">
        {value}
      </div>
    </div>
  );
}

function BottomLineKpi({
  label,
  value,
  hint,
  icon,
  tone,
  primary = false,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ReactNode;
  tone: "finance" | "navy";
  primary?: boolean;
}) {
  const toneMap =
    tone === "finance"
      ? {
          border: "border-emerald-200",
          iconBg: "bg-emerald-50 text-emerald-700",
          value: "text-emerald-800",
          heroColor: "var(--color-savings, #047857)",
        }
      : {
          border: "border-navy/15",
          iconBg: "bg-navy/5 text-navy",
          value: "text-navy",
          heroColor: "var(--color-navy)",
        };
  return (
    <div
      className={`relative flex h-full min-h-[170px] flex-col rounded-2xl border bg-white p-6 print-avoid-break ${toneMap.border} ${primary ? "ring-1 ring-emerald-200/70" : ""}`}
    >
      <div className="flex h-full flex-1 flex-col justify-between">
        <div className="flex items-start justify-between gap-3">
          {primary ? (
            <span className="metric-label leading-[1.4]">{label}</span>
          ) : (
            <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-navy/70 leading-[1.4]">
              {label}
            </div>
          )}
          <div
            className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full ${toneMap.iconBg}`}
          >
            {icon}
          </div>
        </div>
        {primary ? (
          <span className="kpi-hero mt-5 whitespace-nowrap" style={{ color: toneMap.heroColor }}>
            {value}
          </span>
        ) : (
          <div
            className={`mt-5 font-display text-[26px] leading-[1.02] tabular-nums whitespace-nowrap md:text-[30px] ${toneMap.value}`}
          >
            {value}
          </div>
        )}
        <div className="mt-2 text-[11.5px] leading-snug text-muted-foreground">{hint}</div>
      </div>
    </div>
  );
}

function SectionEyebrow({ children }: { children: React.ReactNode }) {
  return (
    <div className="mt-14 flex items-center gap-3 first:mt-0">
      <span className="h-[2px] w-8 bg-navy/40" />
      <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-navy/70">
        {children}
      </span>
    </div>
  );
}

function ImpactPill({ level, count }: { level: "High" | "Medium" | "Low"; count: number }) {
  const map = {
    High: { text: "text-rose-800", dot: "bg-rose-600", label: "High" },
    Medium: { text: "text-amber-800", dot: "bg-amber-500", label: "Moderate" },
    Low: { text: "text-slate-700", dot: "bg-slate-500", label: "Low" },
  }[level];
  return (
    <div className="flex items-center gap-2">
      <span
        className={`flex h-5 w-5 items-center justify-center rounded-full ${map.dot} text-[11px] font-semibold text-white tabular-nums`}
      >
        {count}
      </span>
      <span className={`text-[12.5px] font-medium ${map.text}`}>{map.label} impact</span>
    </div>
  );
}

function ImpactBadge({ level }: { level: "High" | "Medium" | "Low" }) {
  const map = {
    High: "border-rose-300 bg-rose-50 text-rose-800",
    Medium: "border-amber-300 bg-amber-50 text-amber-800",
    Low: "border-slate-300 bg-slate-50 text-slate-700",
  }[level];
  const label = level === "Medium" ? "Moderate" : level;
  return (
    <span
      className={`shrink-0 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${map}`}
    >
      {label}
    </span>
  );
}

function FinancialRow({
  label,
  value,
  valueTone = "default",
  emphasize = false,
}: {
  label: string;
  value: string;
  valueTone?: "default" | "navy" | "finance";
  emphasize?: boolean;
}) {
  const tone =
    valueTone === "finance"
      ? "text-emerald-800"
      : valueTone === "navy"
        ? "text-navy"
        : "text-navy/90";
  return (
    <div
      className={`flex items-baseline justify-between gap-4 ${emphasize ? "pt-5 pb-1" : "py-3"}`}
    >
      <dt className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-navy/70">
        {label}
      </dt>
      <dd
        className={`font-display tabular-nums whitespace-nowrap ${emphasize ? "text-[24px] md:text-[26px]" : "text-[17px] md:text-[18px]"} ${tone}`}
      >
        {value}
      </dd>
    </div>
  );
}

function IntelligenceTileCard({ tile }: { tile: IntelligenceTile }) {
  const map: Record<
    IntelligenceTileStatus,
    {
      border: string;
      bg: string;
      iconBg: string;
      iconColor: string;
      valueTone: string;
    }
  > = {
    verified: {
      border: "border-emerald-200",
      bg: "bg-emerald-50/50",
      iconBg: "bg-emerald-600",
      iconColor: "text-white",
      valueTone: "text-emerald-900",
    },
    reviewed: {
      border: "border-trust/25",
      bg: "bg-trust-soft/60",
      iconBg: "bg-trust",
      iconColor: "text-white",
      valueTone: "text-navy",
    },
    not_applicable: {
      border: "border-slate-200",
      bg: "bg-slate-50/80",
      iconBg: "bg-slate-200",
      iconColor: "text-slate-500",
      valueTone: "text-slate-500",
    },
    not_provided: {
      border: "border-slate-200",
      bg: "bg-slate-50/80",
      iconBg: "bg-slate-200",
      iconColor: "text-slate-500",
      valueTone: "text-slate-500",
    },
  };
  const s = map[tile.status];
  const isMuted = tile.status === "not_applicable" || tile.status === "not_provided";
  const domainIconMap: Record<NonNullable<IntelligenceTile["iconKind"]>, React.ReactNode> = {
    market: <TrendingUp className="h-4 w-4" />,
    law: <Scale className="h-4 w-4" />,
    regulation: <Landmark className="h-4 w-4" />,
    vendor: <Building2 className="h-4 w-4" />,
    carrier: <ShieldCheck className="h-4 w-4" />,
    contract: <FileText className="h-4 w-4" />,
    ownership: <Truck className="h-4 w-4" />,
    docs: <BookOpen className="h-4 w-4" />,
  };
  const domainIcon = tile.iconKind ? domainIconMap[tile.iconKind] : <Info className="h-4 w-4" />;
  return (
    <div
      className={`relative flex h-full min-h-[76px] rounded-xl border p-4 print-avoid-break ${s.border} ${s.bg} ${isMuted ? "opacity-70 border-dashed" : "shadow-sm"}`}
    >
      <div className="flex items-start gap-3 w-full">
        <div
          className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${s.iconBg} ${s.iconColor}`}
        >
          {domainIcon}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className={`font-mono text-[9.5px] font-semibold uppercase tracking-[0.18em] leading-[1.4] [overflow-wrap:anywhere] ${isMuted ? "text-slate-500" : "text-navy/70"}`}
          >
            {tile.label}
          </div>
          <div className={`mt-1 font-display text-[15px] leading-[1.15] ${s.valueTone}`}>
            Status: {tile.value}
          </div>
          <div
            className={`mt-0.5 text-[11px] leading-snug [overflow-wrap:anywhere] ${isMuted ? "text-slate-500" : "text-muted-foreground"}`}
          >
            {tile.detail}
          </div>
        </div>
      </div>
    </div>
  );
}

function KpiCard({
  tone,
  label,
  value,
  valueNode,
  hint,
  emphasize = false,
  wrap = false,
  hero = false,
  field,
  fieldValue,
}: {
  tone: "finance" | "navy" | "amber" | "benchmark";
  label: string;
  value: string;
  /**
   * Optional rendered value. Used when a single KPI shows more than one
   * canonical figure (e.g. a low–high band) and each half must carry its own
   * `summaryField()` tag for Stage 9 certification.
   */
  valueNode?: ReactNode;
  hint?: string;
  emphasize?: boolean;
  wrap?: boolean;
  hero?: boolean;
  /** Canonical Report Summary field this KPI renders (enables certification). */
  field?: string;
  fieldValue?: number | string | null;
}) {
  const toneMap = {
    benchmark: {
      border: "border-sky-200",
      bg: "bg-sky-50/70",
      accent: "bg-sky-600",
      label: "text-sky-900",
      value: "text-executive-blue",
    },
    finance: {
      border: "border-emerald-200",
      bg: "bg-emerald-50/60",
      accent: "bg-emerald-600",
      label: "text-emerald-800",
      value: "text-emerald-900",
    },
    navy: {
      border: "border-navy/15",
      bg: "bg-navy/[0.03]",
      accent: "bg-navy",
      label: "text-navy/80",
      value: "text-navy",
    },
    amber: {
      border: "border-amber-200",
      bg: "bg-amber-50/70",
      accent: "bg-amber-500",
      label: "text-amber-800",
      value: "text-executive-red",
    },
  }[tone];


  return (
    <div
      data-metric-card
      data-summary-component={field || valueNode ? `KpiCard:${label}` : undefined}
      className={[
        "relative overflow-hidden rounded-2xl border print-avoid-break",
        hero ? "p-6 md:p-7" : "p-5 md:p-6",
        toneMap.border,
        toneMap.bg,
      ].join(" ")}
    >
      <span aria-hidden className={`absolute left-0 top-0 h-full w-[3px] ${toneMap.accent}`} />
      <div
        className={[
          "font-mono text-[10.5px] uppercase tracking-[0.24em]",
          hero ? "font-medium opacity-80" : "font-semibold",
          toneMap.label,
        ].join(" ")}
      >
        {label}
      </div>
      <div
        className={[
          "font-display tabular-nums leading-[1.05]",
          toneMap.value,
          hero
            ? "mt-4 text-[26px] font-bold md:text-[32px]"
            : emphasize
              ? "mt-3 text-[30px] md:text-[36px]"
              : "mt-3 text-[20px] md:text-[24px]",
          wrap ? "" : "truncate",
        ].join(" ")}
        title={value}
        {...(field ? summaryField(field, fieldValue ?? value) : {})}
      >
        {valueNode ?? value}
      </div>
      {hint ? (
        <div
          className={[
            "leading-snug text-muted-foreground",
            hero ? "mt-3 text-[11px] opacity-70" : "mt-2 text-[12px]",
          ].join(" ")}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}

/* Package table of contents — printed right after the cover so the deliverable
   feels like a complete case file, not just a scrollable web report. */

/* Slot wrapper: gates the Table of Contents by active package identity. The
   Negotiation Package intentionally omits the TOC (executive brevity); the
   Complete Claim Intelligence Package™ and Professional Review Report keep it. */
function PackageContentsSlot({ files }: { files: ReviewFile[] | null }) {
  const identity = usePdfPackageIdentity();
  if (identity.key === "negotiation") return null;
  return (
    <div id="package-contents" data-pdf-include="true" data-pdf-page-break="before">
      <PackageContents files={files} />
    </div>
  );
}


function PackageContents({ files }: { files: ReviewFile[] | null }) {
  const identity = usePdfPackageIdentity();
  // Supplementary appendices only. The Original Submitted Invoice is now
  // presented as a source-document section immediately after this Contents
  // page, not as an appendix.
  const groups = classifyAppendixFiles(files ?? []).filter((g) => g.letter !== "A");

  // Registry-driven parts, filtered to the chapters actually included in
  // this package export. `chaptersForPackage` is the same helper the PDF
  // engine uses, so the printed TOC can never advertise a chapter that
  // isn't in the delivered document.
  type PrintedSection = {
    label: string;
    displayNumber?: number;
    anchor?: string;
    icon?: LucideIcon;
  };
  type PrintedPart = { partLabel: string; partTitle: string; sections: PrintedSection[] };
  const allowedAnchors = chaptersForPackage(identity.key);
  const isAllowed = (anchor: string) =>
    !allowedAnchors || allowedAnchors.includes(anchor);
  // Part headings must describe only what the package actually contains.
  // The "Executive" part heading is accurate for the Complete package (which
  // carries the Executive Decision Cheat Sheet); in the Professional Review
  // Report it would advertise chapters the deliverable excludes.
  const partTitleFor = (name: string) =>
    identity.key === "review" && name === "Executive" ? "Professional Findings" : name;
  const registryParts: PrintedPart[] = partsGrouped()
    .map((g) => ({
      partLabel: `Part ${g.partRoman} · ${partTitleFor(g.partName)}`,
      partTitle: partTitleFor(g.partName),

      sections: g.chapters
        .filter((c) => isAllowed(c.anchorId))
        .map((c) => ({
          label: c.shortTitle,
          displayNumber: c.displayNumber,
          anchor: c.anchorId,
          icon: c.icon,
        })),
    }))
    .filter((p) => p.sections.length > 0);
  const parts: PrintedPart[] = [
    {
      partLabel: "Source Document",
      partTitle: "Original Submitted Invoice",
      sections: [{ label: "Original Submitted Invoice", anchor: "section-original-invoice" }],
    },
    ...registryParts,
  ];

  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-border bg-card shadow-card print-avoid-break">
      {/* Header band */}
      <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-8 py-5 md:px-14">
        <div className="flex items-center gap-3">
          <span className="h-[2px] w-8 bg-fasttrack" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-fasttrack">
            Table of Contents
          </span>
        </div>
        <div className="hidden font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground md:block">
          Volume I of I
        </div>
      </div>

      <div className="px-8 py-10 md:px-14 md:py-12">
        <PackageContentsIntro />


        <div className="mt-9 grid items-start gap-x-16 gap-y-10 md:grid-cols-2">
          {parts.map((p, partIndex) => {
            // Reading order is the point of this page: the source document,
            // the executive part, and the investigation part each take a full
            // row so the eye steps top-to-bottom instead of zig-zagging.
            const isSource = p.partLabel === "Source Document";
            const isExecutive = partIndex === 1;
            const isInvestigation =
              p.partTitle === "Investigation" || p.sections.length > 4;
            const fullWidth = isSource || isExecutive || isInvestigation;

            return (
            <div
              key={p.partLabel}
              // Part blocks share one spacing recipe (header → rule → list).
              // The Executive part gets a hair more air above and below so it
              // reads as the first destination without changing its scale.
              className={[fullWidth ? "md:col-span-2" : "", isExecutive ? "md:py-1" : ""]
                .filter(Boolean)
                .join(" ")}
            >
              <div className="flex items-baseline justify-between gap-4 border-b-2 border-navy pb-2">
                <div>
                  <div className="font-mono text-[10px] font-semibold uppercase tracking-[0.24em] text-fasttrack">
                    {p.partLabel}
                  </div>
                  <div className="mt-1 font-display text-xl leading-tight text-navy">
                    {p.partTitle}
                  </div>
                </div>
              </div>
              <ul
                className={[
                  "mt-4",
                  // Two columns must read top-to-bottom: CSS multi-column
                  // fills the left column completely before the right, so the
                  // chapter numbers always increase vertically.
                  isInvestigation
                    ? "space-y-3.5 md:columns-2 md:gap-x-16 md:space-y-0 md:[&>li]:mb-3.5 md:[&>li]:break-inside-avoid"
                    : "space-y-3.5",
                ].join(" ")}
              >
                {p.sections.map((s) => {
                  const num =
                    typeof s.displayNumber === "number"
                      ? String(s.displayNumber).padStart(2, "0")
                      : null;
                  const TocIcon = s.icon;
                  // Fixed-width number and icon slots keep every label,
                  // leader and row edge on the same vertical rails, whether
                  // or not a section carries a number or icon.
                  const inner = (
                    <>
                      <span className="w-6 shrink-0 text-left font-mono text-[11px] font-semibold tabular-nums tracking-[0.14em] text-muted-foreground">
                        {num ?? ""}
                      </span>
                      <span className="flex h-4 w-4 shrink-0 items-center justify-center text-muted-foreground">
                        {TocIcon ? (
                          <TocIcon aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                        ) : null}
                      </span>
                      <span className="min-w-0 truncate text-[14px] leading-6 text-navy">
                        {s.label}
                      </span>
                      <span className="ml-3 flex-1 border-b border-dotted border-border" />
                    </>
                  );
                  return (
                    <li key={`${p.partLabel}-${s.anchor ?? s.label}`}>
                      {s.anchor ? (
                        <a
                          href={`#${s.anchor}`}
                          className="flex items-center gap-3 no-underline transition hover:text-trust"
                        >
                          {inner}
                        </a>
                      ) : (
                        <div className="flex items-center gap-3">{inner}</div>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
            );
          })}
        </div>

        {/* How to Read This Report — orientation callout */}
        <div className="mt-10 overflow-hidden rounded-2xl border border-border bg-secondary/40 print-avoid-break">
          <div className="flex items-center gap-3 border-b border-border px-6 py-3">
            <span className="h-[2px] w-6 bg-fasttrack" />
            <span className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-fasttrack">
              How to Read This Report
            </span>
          </div>
          <ul className="grid gap-x-12 gap-y-5 px-6 py-5 text-[13.5px] leading-relaxed md:grid-cols-3">
            <li className="min-w-0">
              <div className="font-semibold text-navy">Start Here</div>
              <div className="mt-1 text-pretty text-muted-foreground">
                Review the Original Submitted Invoice before reading the findings.
              </div>
            </li>
            <li className="min-w-0">
              <div className="font-semibold text-navy">Executive Review</div>
              <div className="mt-1 text-pretty text-muted-foreground">
                {isAllowed(getChapterByInternalId("executive-decision").anchorId)
                  ? "Understand the major findings, total supported recovery, and recommended actions."
                  : "Understand the major findings, the charges under review, and how they compare to the market."}
              </div>
            </li>
            <li className="min-w-0">
              <div className="font-semibold text-navy">Detailed Analysis</div>
              <div className="mt-1 text-pretty text-muted-foreground">
                {isAllowed(getChapterByInternalId("negotiation-strategy").anchorId)
                  ? "Review benchmark data, legal findings, supporting evidence, and negotiation strategy."
                  : "Review benchmark data, legal findings, vendor intelligence, and supporting evidence."}
              </div>
            </li>
          </ul>
        </div>



        {/* Appendix drawer — only when supplementary appendices exist */}
        {groups.length > 0 && (
          <div className="mt-8 overflow-hidden rounded-2xl border border-border">
            <div className="flex items-center justify-between border-b border-border bg-navy px-6 py-4 text-white">
              <div className="flex items-center gap-3">
                <Paperclip className="h-4 w-4 text-fasttrack" />
                <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-fasttrack">
                  Appendices
                </span>
              </div>
              <div className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-white/60">
                {groups.length} attached
              </div>
            </div>
            <ul className="divide-y divide-border bg-card">
              {groups.map((g) => (
                <li key={g.letter} className="flex items-baseline gap-4 px-6 py-4">
                  <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-fasttrack">
                    Appendix {g.letter}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-[14px] text-navy">{g.title}</span>
                  <span className="font-mono text-[11px] tabular-nums text-muted-foreground">
                    {g.files.length} document{g.files.length === 1 ? "" : "s"}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Executive Decision Brief                                    */
/*                                                              */
/* Companion page to the Executive Dashboard. Explains WHY the */
/* dashboard reached its recommendation. Deliberately excludes */
/* invoice total, savings estimates, confidence %, fair market */
/* range, recovery range, and negotiation status — those live  */
/* on the dashboard. This page is a reasoning surface, not a   */
/* second KPI grid.                                             */
/* ─────────────────────────────────────────────────────────── */

function ExecutiveDecisionBrief(props: {
  hasReal: boolean;
  invoiceReady: boolean;
  aiReady: boolean;
  legalReady: boolean;
  marketReady: boolean;
  evidenceCount: number;
  timelineCount: number;
  findingsCount: number;
  lawsCount: number;
  topFindings: { label: string; savings: number; impact: "High" | "Medium" | "Low" }[];
  topLaw: { title: string; citation: string | null; topic: string | null } | null;
  recommendation: string;
  risk: "High" | "Medium" | "Low" | "Unknown";
}) {
  const {
    invoiceReady,
    aiReady,
    legalReady,
    marketReady,
    evidenceCount,
    timelineCount,
    findingsCount,
    lawsCount,
    topFindings,
    topLaw,
    recommendation,
    risk,
  } = props;

  const checklist: { label: string; done: boolean; note: string }[] = [
    {
      label: "Invoice OCR & normalization",
      done: invoiceReady,
      note: "Line items and totals extracted from the submitted invoice.",
    },
    {
      label: "AI forensic review",
      done: aiReady,
      note: `${findingsCount} finding${findingsCount === 1 ? "" : "s"} raised for review.`,
    },
    {
      label: "State law & regulation research",
      done: legalReady,
      note: `${lawsCount} statute${lawsCount === 1 ? "" : "s"} and regulation${lawsCount === 1 ? "" : "s"} matched to the file.`,
    },
    {
      label: "Local market benchmark",
      done: marketReady,
      note: "Comparable-rate analysis against verified local vendors.",
    },
  ];

  const biggest = topFindings[0] ?? null;
  const topThree = topFindings.slice(0, 5);

  return (
    <section className="mt-8 overflow-hidden rounded-3xl border border-border bg-card shadow-card print-avoid-break">
      {/* Header band */}
      <div className="flex items-center justify-between border-b border-border bg-secondary/50 px-8 py-5 md:px-14">
        <div className="flex items-center gap-3">
          <span className="h-[2px] w-8 bg-fasttrack" />
          <span className="font-mono text-[11px] font-semibold uppercase tracking-[0.28em] text-fasttrack">
            Executive · Decision Brief
          </span>
        </div>
        <div className="hidden font-mono text-[11px] font-semibold uppercase tracking-[0.24em] text-muted-foreground md:block">
          Why the dashboard reached this recommendation
        </div>
      </div>

      <div className="px-8 py-12 md:px-14 md:py-14">
        <h2 className="font-display text-[38px] leading-[1.05] text-navy md:text-[52px]">
          Executive Decision Brief
        </h2>
        <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-muted-foreground md:text-[16px]">
          The dashboard on the previous page states the recommendation. This page explains the
          reasoning behind it — the work completed, the evidence carrying the most weight, and the
          single most consequential finding driving the position.
        </p>
        <div className="mt-6 h-px w-16 bg-fasttrack" />

        {/* 1 — Investigation Completed */}
        <SectionEyebrow>1 · Investigation Completed</SectionEyebrow>
        <ul className="mt-5 grid grid-cols-1 gap-3 md:grid-cols-2">
          {checklist.map((item) => (
            <li
              key={item.label}
              className="flex items-start gap-3 rounded-2xl border border-border bg-secondary/30 p-5 print-avoid-break"
            >
              <span
                aria-hidden
                className={[
                  "mt-[3px] inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full border text-[11px] font-semibold",
                  item.done
                    ? "border-emerald-600 bg-emerald-600 text-white"
                    : "border-muted-foreground/40 bg-white text-muted-foreground",
                ].join(" ")}
              >
                {item.done ? "✓" : "•"}
              </span>
              <div className="min-w-0">
                <div className="text-[14px] font-semibold text-navy">{item.label}</div>
                <div className="mt-1 text-[13px] leading-snug text-muted-foreground">
                  {item.note}
                </div>
              </div>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-[13px] leading-relaxed text-muted-foreground">
          {evidenceCount} evidence file{evidenceCount === 1 ? "" : "s"} reviewed · {timelineCount}{" "}
          timeline event{timelineCount === 1 ? "" : "s"} reconstructed for this case.
        </p>

        {/* 2 — Top Findings */}
        <SectionEyebrow>2 · Top Findings</SectionEyebrow>
        {topThree.length === 0 ? (
          <p className="mt-5 text-[14px] text-muted-foreground">
            No material findings were raised during the review.
          </p>
        ) : (
          <ol className="mt-5 space-y-3">
            {topThree.map((f, i) => (
              <li
                key={`${f.label}-${i}`}
                className="flex items-start gap-4 rounded-2xl border border-border bg-card p-5 print-avoid-break"
              >
                <span className="font-mono text-[13px] tabular-nums text-fasttrack">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-semibold text-navy">{f.label}</div>
                  <div className="mt-1 text-[13px] leading-snug text-muted-foreground">
                    Impact: {f.impact}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}

        {/* 3 — Strongest Evidence & Highest Legal Leverage */}
        <SectionEyebrow>3 · Strongest Supporting Evidence</SectionEyebrow>
        <div className="mt-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          <div className="rounded-2xl border border-navy/15 bg-navy/[0.03] p-6 print-avoid-break">
            <span aria-hidden className="block h-[2px] w-8 bg-navy" />
            <div className="mt-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-navy/80">
              Strongest Supporting Evidence
            </div>
            <div className="mt-3 font-display text-[22px] leading-[1.15] text-navy">
              {biggest ? biggest.label : "Line-item audit and market benchmark set"}
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
              {biggest
                ? `This finding is corroborated across the invoice audit, market benchmark, and applicable-law layers, giving it the highest evidentiary weight in the file.`
                : `The strongest evidence in this file is the aggregate of independent OCR, market, and legal signals converging on the same conclusion.`}
            </p>
          </div>
          <div className="rounded-2xl border border-navy/15 bg-navy/[0.03] p-6 print-avoid-break">
            <span aria-hidden className="block h-[2px] w-8 bg-fasttrack" />
            <div className="mt-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-navy/80">
              Highest Legal Leverage
            </div>
            <div className="mt-3 font-display text-[22px] leading-[1.15] text-navy">
              {topLaw ? topLaw.title : "See State Law Analysis"}
            </div>
            <p className="mt-3 text-[13.5px] leading-relaxed text-muted-foreground">
              {topLaw
                ? `${topLaw.citation ? topLaw.citation + " — " : ""}${topLaw.topic ? "Topic: " + topLaw.topic + ". " : ""}This citation anchors the negotiation position and is discussed in full in the State Law Analysis chapter.`
                : `Applicable statutes and regulations for this jurisdiction are catalogued and cited in the State Law Analysis chapter.`}
            </p>
          </div>
        </div>

        {/* 4 — Biggest Financial Opportunity */}
        <SectionEyebrow>4 · Biggest Financial Opportunity</SectionEyebrow>
        <div className="mt-5 rounded-2xl border border-emerald-200 bg-emerald-50/60 p-6 print-avoid-break">
          <span aria-hidden className="block h-[2px] w-8 bg-emerald-600" />
          <div className="mt-3 font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-emerald-800">
            Where the recovery is concentrated
          </div>
          <div className="mt-3 font-display text-[24px] leading-[1.15] text-emerald-900 md:text-[28px]">
            {biggest ? biggest.label : "Distributed across multiple charges"}
          </div>
          <p className="mt-3 max-w-3xl text-[13.5px] leading-relaxed text-emerald-900/80">
            {biggest
              ? `This charge represents the largest single lever in the negotiation. Line-item breakdowns, benchmark comparisons, and the dollar ranges tied to it are shown in the Invoice Charge Analysis and Market Benchmark chapters.`
              : `Recovery for this file is not concentrated in one line — see the Invoice Charge Analysis chapter for the full distribution across items.`}
          </p>
        </div>

        {/* 5 — Final Recommendation */}
        <SectionEyebrow>5 · Final Recommendation</SectionEyebrow>
        <div className="mt-5 relative overflow-hidden rounded-2xl border border-amber-200 bg-amber-50/70 p-8 md:p-10 print-avoid-break">
          <span aria-hidden className="absolute left-0 top-0 h-full w-[3px] bg-amber-500" />
          <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.24em] text-amber-800">
            One Final Recommendation
          </div>
          <div className="mt-4 font-display text-[26px] leading-[1.15] text-amber-900 md:text-[32px]">
            {recommendation}
          </div>
          <p className="mt-4 max-w-3xl text-[14.5px] leading-relaxed text-amber-900/80">
            The dashboard quantifies the outcome; this brief explains the reasoning. Risk profile
            assessed as {risk}. Full findings, citations, and vendor-facing negotiation language
            follow in the chapters ahead.
          </p>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Section shell                                               */
/* ─────────────────────────────────────────────────────────── */

const SectionExportContext = createContext<{ fileNumber?: string } | null>(null);

/**
 * Chapter-level switch for per-section export controls. Chapters that read as
 * one continuous brief (e.g. Case Brief & Invoice Overview) opt out by
 * wrapping their body in `<SectionExportEnabled value={false}>`, which removes
 * the Print Section / Download Section PDF pair and its helper copy entirely —
 * no reserved space, no CSS text matching. Chapter-level exports are
 * unaffected.
 */
const SectionExportEnabledContext = createContext<boolean>(true);

export function SectionExportEnabled({
  value,
  children,
}: {
  value: boolean;
  children: React.ReactNode;
}) {
  return (
    <SectionExportEnabledContext.Provider value={value}>
      {children}
    </SectionExportEnabledContext.Provider>
  );
}

/**
 * Chapter-scoped context that carries the current chapter number to every
 * SectionActions nested inside a chapter body. Used to build section
 * filenames of the form ClaimGuard_<File>_Chapter_<NN>_Section_<Title>.pdf.
 */
export const ChapterNumberContext = createContext<string | null>(null);

export function SectionExportProvider({
  fileNumber,
  children,
}: {
  fileNumber?: string;
  children: React.ReactNode;
}) {
  return (
    <SectionExportContext.Provider value={{ fileNumber }}>{children}</SectionExportContext.Provider>
  );
}

function SectionActions({ sectionId, title }: { sectionId: string; title: string }) {
  const mode = useRenderMode();
  const ctx = useContext(SectionExportContext);
  const sectionExportEnabled = useContext(SectionExportEnabledContext);
  const chapterNumber = useContext(ChapterNumberContext) ?? undefined;
  const { downloadSection, printSection, busyId } = useSectionPdfActions(ctx?.fileNumber);
  if (mode === "pdf") return null;
  if (!sectionExportEnabled) return null;
  const busy = busyId === sectionId;
  return (
    <div className="no-print flex shrink-0 flex-col items-end gap-1" data-pdf-exclude="true">
      <div className="flex items-center gap-1">
        <button
          type="button"
          onClick={() => printSection(sectionId, title)}
          className="inline-flex items-center gap-1 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-[10.5px] font-medium text-muted-foreground/80 hover:border-border hover:bg-secondary/60 hover:text-navy"
          aria-label={`Print this section only: ${title}`}
          title={`Print this section only (${title})`}
        >
          <Printer className="h-3 w-3" /> Print Section
        </button>
        <button
          type="button"
          onClick={() => downloadSection(sectionId, title, { kind: "section", chapterNumber })}
          disabled={busy}
          className="inline-flex items-center gap-1 rounded border border-transparent bg-transparent px-1.5 py-0.5 text-[10.5px] font-medium text-muted-foreground/80 hover:border-border hover:bg-secondary/60 hover:text-navy disabled:opacity-60"
          aria-label={`Download this section as a standalone PDF: ${title}`}
          title={`Download this section as a standalone PDF (${title})`}
        >
          <Download className="h-3 w-3" />
          {busy ? "Preparing…" : "Download Section PDF"}
        </button>
      </div>
      <div className="text-right text-[9px] leading-tight text-muted-foreground/50">
        <div className="font-semibold uppercase tracking-[0.14em]">Section Export</div>
        <div className="text-muted-foreground/40">Contains only this section.</div>
      </div>
    </div>
  );
}

function sectionTitleText(title: React.ReactNode, fallback: string): string {
  if (typeof title === "string") return title;
  if (typeof title === "number") return String(title);
  return fallback;
}

function Section({
  id,
  number,
  eyebrow,
  title,
  intro,
  children,
  exportable = true,
  exportTitle,
  bare = false,
}: {
  id?: string;
  /** Optional per-chapter section number (e.g., "01"). Omit for
   *  standalone/interactive-only cards outside the chapter sequence. */
  number?: string;
  eyebrow?: string;
  title: React.ReactNode;
  intro?: string;
  children: React.ReactNode;
  exportable?: boolean;
  exportTitle?: string;
  /** When true, suppress the eyebrow/number/title/intro and per-section
   *  export actions — used to eliminate first-section titles that merely
   *  restate the chapter header above. The <section id> is preserved so
   *  anchors, TOC links, and PDF bookmarks continue to resolve. */
  bare?: boolean;
}) {
  // React.useId gives us a stable, collision-free fallback so multiple
  // sections that legitimately share number="01" (one per chapter) don't
  // produce duplicate DOM ids.
  const autoId = useId();
  const sectionId = id ?? (number ? `section-${number}-${autoId}` : autoId);
  const titleText =
    exportTitle ?? sectionTitleText(title, number ? `Section ${number}` : "Section");
  if (bare) {
    return (
      <section id={sectionId} className="scroll-mt-24 report-rise print-avoid-break">
        {children}
      </section>
    );
  }
  return (
    <section id={sectionId} className="scroll-mt-24 report-rise print-avoid-break">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-baseline gap-4">
          {number && (
            <div className="section-number font-display text-2xl font-light text-trust/60 tabular-nums">
              {number}
            </div>
          )}
          <div>
            {eyebrow && (
              <div className="section-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] text-trust">
                {eyebrow}
              </div>
            )}
            <h2 className="section-title mt-1 font-display text-3xl text-navy md:text-4xl">
              {title}
            </h2>
          </div>
        </div>
        {exportable && <SectionActions sectionId={sectionId} title={titleText} />}
      </div>
      {intro && (
        <p className="section-subtitle mt-2 max-w-3xl text-sm text-muted-foreground">{intro}</p>
      )}
      <div className="mt-6">{children}</div>
    </section>
  );
}

function PendingBadge({ label = "Pending analysis" }: { label?: string }) {
  return (
    <span className="ml-3 inline-flex items-center gap-1.5 rounded-full border border-trust/25 bg-trust-soft px-2.5 py-0.5 align-middle text-[10px] font-semibold uppercase tracking-wider text-trust">
      <Sparkles className="h-3 w-3" /> {label}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 1. Executive Summary                                        */
/* ─────────────────────────────────────────────────────────── */

function invoiceHealth(r: ReturnType<typeof buildReport>): {
  label: "Excellent" | "Good" | "Moderate" | "High Risk";
  tone: "savings" | "trust" | "warn" | "danger";
  sentence: string;
} {
  const pctHigh = r.savingsPct[1];
  if (pctHigh >= 25)
    return {
      label: "High Risk",
      tone: "danger",
      sentence: "This invoice shows significant overcharges and warrants immediate negotiation.",
    };
  if (pctHigh >= 15)
    return {
      label: "Moderate",
      tone: "warn",
      sentence: "This invoice has meaningful issues worth negotiating before payment.",
    };
  if (pctHigh >= 5)
    return {
      label: "Good",
      tone: "trust",
      sentence: "This invoice is largely reasonable with a few refinable line items.",
    };
  return {
    label: "Excellent",
    tone: "savings",
    sentence: "This invoice appears well-aligned with market and regulatory expectations.",
  };
}

function negotiationPriority(r: ReturnType<typeof buildReport>): {
  label: "Critical" | "High" | "Moderate" | "Low";
  tone: "danger" | "warn" | "trust" | "savings";
  copy: string;
} {
  const p = r.savingsPct[1];
  if (p >= 25)
    return { label: "Critical", tone: "danger", copy: "Immediate negotiation strongly advised." };
  if (p >= 15)
    return { label: "High", tone: "warn", copy: "Negotiate before payment for material recovery." };
  if (p >= 5)
    return { label: "Moderate", tone: "trust", copy: "Selective negotiation is worthwhile." };
  return { label: "Low", tone: "savings", copy: "Minor refinements; payment appears reasonable." };
}

function recommendedAction(r: ReturnType<typeof buildReport>): string {
  const p = r.savingsPct[1];
  if (p >= 25) return "Authorize negotiation on your behalf — do not pay this invoice as issued.";
  if (p >= 15) return "Engage the vendor with our talking points before remitting payment.";
  if (p >= 5) return "Request itemized adjustments on the flagged charges, then pay the balance.";
  return "Pay as invoiced. No material overcharges were identified.";
}

function ExecutiveSummary({
  r,
  pending,
  counts,
  ocrComplete,
}: {
  r: ReturnType<typeof buildReport>;
  pending?: boolean;
  counts: {
    findingItems: number;
    legalResearch: number;
    marketComparisons: number;
    evidenceFiles: number;
  };
  ocrComplete: boolean;
}) {
  const health = invoiceHealth(r);
  const priority = negotiationPriority(r);
  const top = [...r.findings].sort((a, b) => b.savings[1] - a.savings[1])[0];
  // Canonical recovery — finalized AI Findings record.
  const savingsMid = r.estimatedSavings;
  const isNegotiable = r.potentialSavings[1] > 0 && r.savingsPct[1] >= 3;

  // SAB-004: canonical analysis confidence only. No fabricated likelihood metric.
  const analysisConfidencePct = toConfidencePct(r.confidence);
  const analysisConfidenceChip =
    analysisConfidencePct != null ? `${analysisConfidencePct}%` : null;

  const priorityCls =
    priority.tone === "danger"
      ? "border-destructive/40 bg-destructive/10 text-destructive"
      : priority.tone === "warn"
        ? "border-warn/40 bg-warn/10 text-warn"
        : priority.tone === "trust"
          ? "border-trust/40 bg-trust-soft text-trust"
          : "border-savings/40 bg-savings-soft text-savings";

  const healthCls =
    health.tone === "savings"
      ? "border-savings/30 bg-savings-soft text-savings"
      : health.tone === "trust"
        ? "border-trust/30 bg-trust-soft text-trust"
        : health.tone === "warn"
          ? "border-warn/30 bg-warn/10 text-warn"
          : "border-destructive/30 bg-destructive/10 text-destructive";

  const assessment = isNegotiable
    ? `Our review indicates this invoice is negotiable. We documented ${counts.findingItems} finding item${counts.findingItems === 1 ? "" : "s"} across ${counts.legalResearch} applicable statute${counts.legalResearch === 1 ? "" : "s"} and ${counts.marketComparisons} local market benchmark${counts.marketComparisons === 1 ? "" : "s"}, supporting a credible reduction of approximately ${currency(savingsMid)}.`
    : `Our review indicates this invoice is largely aligned with market and regulatory expectations. No material overcharges were identified across the ${counts.legalResearch} statute${counts.legalResearch === 1 ? "" : "s"} and ${counts.marketComparisons} market benchmark${counts.marketComparisons === 1 ? "" : "s"} reviewed.`;

  const briefAnswers: {
    label: string;
    value: string;
    hint: string;
    tone: "savings" | "trust" | "warn" | "navy";
  }[] = [
    {
      label: "Is this invoice negotiable?",
      value: isNegotiable ? "Yes" : "Not materially",
      hint: isNegotiable
        ? `Supported by ${counts.findingItems} finding${counts.findingItems === 1 ? "" : "s"}.`
        : "Charges align with fair range.",
      tone: isNegotiable ? "savings" : "trust",
    },
    {
      label: "How much could you save?",
      value: `${currency(r.potentialSavings[0])} – ${currency(r.potentialSavings[1])}`,
      hint: `Up to ${r.savingsPct[1]}% of the invoiced total.`,
      tone: "savings",
    },
    {
      label: "How serious are the issues?",
      value: health.label,
      hint: health.sentence,
      tone: health.tone === "danger" ? "warn" : health.tone === "warn" ? "warn" : "trust",
    },
    {
      label: "What should you do next?",
      value: priority.label + " priority",
      hint: recommendedAction(r),
      tone: priority.tone === "danger" ? "warn" : priority.tone === "warn" ? "warn" : "trust",
    },
  ];

  const toneText = (t: "savings" | "trust" | "warn" | "navy") =>
    t === "savings"
      ? "text-savings"
      : t === "warn"
        ? "text-warn"
        : t === "trust"
          ? "text-trust"
          : "text-navy";

  return (
    <Section
      number="02"
      eyebrow="Consultant Memo"
      title={<>Consultant's Assessment{pending && <PendingBadge />}</>}
      intro="A concise professional summary of what we found, how confident we are, and what we recommend."
    >
      {/* Consultant memo */}
      <div className="rounded-3xl border border-border bg-card p-8 shadow-card md:p-10">
        <div className="flex flex-wrap items-center gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${healthCls}`}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> Invoice Health: {health.label}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${priorityCls}`}
          >
            <AlertTriangle className="h-3.5 w-3.5" /> Negotiation Priority: {priority.label}
          </span>
          {analysisConfidenceChip && (
            <span className="inline-flex items-center gap-1.5 rounded-full border border-trust/25 bg-trust-soft px-3 py-1 text-xs font-semibold text-trust">
              <Gauge className="h-3.5 w-3.5" /> {ANALYSIS_CONFIDENCE_LABEL}: {analysisConfidenceChip}
            </span>
          )}
        </div>

        <p className="mt-6 max-w-3xl font-display text-2xl leading-snug text-navy md:text-[26px]">
          {assessment}
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {briefAnswers.map((a) => (
            <div
              key={a.label}
              className="rounded-2xl border border-border/70 bg-white p-5 transition hover:-translate-y-0.5 hover:shadow-card"
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {a.label}
              </div>
              <div className={`mt-3 font-display text-2xl leading-tight ${toneText(a.tone)}`}>
                {a.value}
              </div>
              <div className="mt-2 text-xs leading-relaxed text-muted-foreground">{a.hint}</div>
            </div>
          ))}
        </div>

        {top && (
          <div className="mt-8 flex flex-col gap-4 rounded-2xl border border-warn/30 bg-warn/5 p-5 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warn/15 text-warn">
                <AlertTriangle className="h-5 w-5" />
              </div>
              <div className="min-w-0">
                <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-warn">
                  Highest-value opportunity
                </div>
                <div className="mt-1 font-display text-lg text-navy md:text-xl">
                  {top.item} — {top.issue.toLowerCase()}
                </div>
              </div>
            </div>
            <div className="md:text-right">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-savings/80">
                Supported recovery
              </div>
              <div className="font-display text-2xl text-savings md:text-3xl">
                {currency(top.savings[0])} – {currency(top.savings[1])}
              </div>
            </div>
          </div>
        )}

        <div className="mt-8 flex items-start gap-3 rounded-2xl border-l-4 border-trust bg-trust-soft/60 p-5">
          <ClipboardCheck className="mt-0.5 h-5 w-5 shrink-0 text-trust" />
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-trust">
              Recommended next action
            </div>
            <div className="mt-1 text-sm font-semibold leading-relaxed text-navy">
              {recommendedAction(r)}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function BiggestOpportunity({ r }: { r: ReturnType<typeof buildReport> }) {
  const top = [...r.findings].sort((a, b) => b.savings[1] - a.savings[1])[0];
  if (!top) return null;
  return (
    <div className="-mt-4 rounded-2xl border border-warn/30 bg-warn/5 p-5 shadow-card md:p-6">
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div className="flex items-start gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-warn/15 text-warn">
            <AlertTriangle className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[11px] font-semibold uppercase tracking-wider text-warn">
              Biggest opportunity
            </div>
            <div className="mt-1 font-display text-lg text-navy md:text-xl">
              {top.item} — {top.issue.toLowerCase()}
            </div>
            <div className="mt-1 text-sm text-muted-foreground">
              This is where you can save the most money on this invoice.
            </div>
          </div>
        </div>
        <div className="text-left md:text-right">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-savings/80">
            Supported recovery
          </div>
          <div className="font-display text-2xl text-savings md:text-3xl">
            {currency(top.savings[0])} – {currency(top.savings[1])}
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  hint,
  icon: Icon,
  tone,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
  tone: "navy" | "trust" | "savings";
}) {
  const toneCls =
    tone === "savings"
      ? "text-savings bg-savings-soft"
      : tone === "trust"
        ? "text-trust bg-trust-soft"
        : "text-navy bg-secondary";
  const valueCls =
    tone === "savings" ? "text-savings" : tone === "trust" ? "text-trust" : "text-navy";
  return (
    <div className="group rounded-2xl border border-border bg-card p-6 shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated">
      <div className="flex items-center gap-3">
        <div className={`flex h-10 w-10 items-center justify-center rounded-xl ${toneCls}`}>
          <Icon className="h-5 w-5" />
        </div>
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          {label}
        </div>
      </div>
      <div className={`mt-5 font-display text-3xl leading-none ${valueCls}`}>{value}</div>
      <div className="mt-3 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 2. Invoice Overview                                         */
/* ─────────────────────────────────────────────────────────── */

function LocationBadge({ verified }: { verified: boolean }) {
  return (
    <span
      className={
        verified
          ? "inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-emerald-700"
          : "inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wider text-amber-700"
      }
    >
      <MapPin className="h-3 w-3" />
      {verified ? "Location verified" : "Location estimated"}
    </span>
  );
}

function InvoiceOverview({
  c,
  r,
  bundle,
  invoiceFields,
  invoiceTotal,
  fairLow,
  fairHigh,
  demoExtracted,
  demoMarketComparisons,
}: {
  c: Case;
  r: ReturnType<typeof buildReport>;
  bundle?: FullWorkspaceBundle | null;
  invoiceFields?: ReturnType<typeof extractInvoiceReportFields> | null;
  invoiceTotal?: number;
  fairLow?: number;
  fairHigh?: number;
  demoExtracted?: Record<string, unknown>;
  demoMarketComparisons?: MarketComparisonRow[];
}) {
  /* Stage 9 (Certification Pipeline v3.0): every report-wide statistic shown
     in Chapter 01 is read from the immutable Report Summary and tagged, so it
     can never drift from the rest of the package. */
  const rs = useReportSummary();
  const inv =
    (bundle?.invoiceData?.extracted as Record<string, unknown> | undefined) ??
    demoExtracted ??
    {};
  const marketComparisons =
    bundle?.marketComparisons && bundle.marketComparisons.length > 0
      ? bundle.marketComparisons
      : (demoMarketComparisons ?? []);
  const lineItems = getNormalizedInvoiceLineItems(bundle?.invoiceData?.line_items, inv);
  const invoiceNumber =
    invoiceFields?.invoiceNumber ??
    (inv?.invoice_number as string | undefined) ??
    (inv?.invoiceNumber as string | undefined) ??
    null;
  const serviceDate = (inv?.service_date as string | undefined) ?? null;
  const claimRef = (inv?.claim_number as string | undefined) ?? null;

  // Storage Period reads the canonical timeline from the immutable Report
  // Summary, the same source as the masthead card and the Storage Summary.
  const storageSummary = getStorageSummaryData(inv, marketComparisons, lineItems);
  const fmtStorageDate = (d: Date | null) => (d ? fmtLongDate(d) : "Pending");
  const storagePeriodStart = rs.dates.storageStart
    ? new Date(rs.dates.storageStart)
    : (storageSummary?.start ?? null);
  const storagePeriodEnd = rs.dates.storageEnd
    ? new Date(rs.dates.storageEnd)
    : (storageSummary?.end ?? null);
  const storagePeriod = `${fmtStorageDate(storagePeriodStart)} — ${fmtStorageDate(storagePeriodEnd)}`;

  const loc = (inv?.location ?? undefined) as
    | {
        verified?: boolean;
        city?: string;
        state?: string;
        zip5?: string;
        zip4?: string;
        market?: string;
        normalized_address?: string;
      }
    | undefined;
  const zipDisplay = loc?.zip5
    ? loc.zip4
      ? `${loc.zip5}-${loc.zip4}`
      : loc.zip5
    : (invoiceFields?.zip ?? r.zip);
  const cityDisplay = invoiceFields?.city ?? loc?.city ?? null;
  const marketDisplay = loc?.market ?? null;
  // Presentation-only normalizers: the underlying values are never changed,
  // they are only rendered in the report's human-readable house style.
  const prettyInvoiceType = (value: string | null | undefined): string | null => {
    if (!value) return null;
    const cleaned = value.replace(/[_-]+/g, " ").trim();
    if (!cleaned) return null;
    if (/[a-z]/.test(cleaned) && /[A-Z]/.test(cleaned)) return cleaned;
    return cleaned
      .split(/\s+/)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" & ");
  };
  const prettyDate = (value: string | null | undefined): string | null => {
    if (!value) return null;
    if (!/^\d{4}-\d{2}-\d{2}/.test(value.trim())) return value;
    const d = new Date(`${value.trim().slice(0, 10)}T00:00:00Z`);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString("en-US", {
      timeZone: "UTC",
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };
  const allRows: {
    icon: React.ComponentType<{ className?: string }>;
    label: string;
    value: string | null;
    alwaysShow?: boolean;
    /** Canonical Report Summary field, when this row renders one. */
    field?: string;
    fieldValue?: string | number | null;
  }[] = [
    {
      icon: Hash,
      label: "File Number",
      value: rs.fileNumber ?? c.id,
      alwaysShow: true,
      ...(rs.fileNumber ? { field: "fileNumber", fieldValue: rs.fileNumber } : {}),
    },
    {
      icon: Building2,
      label: "Vendor",
      value: rs.vendor ?? invoiceFields?.vendorName ?? r.vendor,
      alwaysShow: true,
      ...(rs.vendor ? { field: "vendor", fieldValue: rs.vendor } : {}),
    },
    {
      icon: Hash,
      label: "Invoice Number",
      value: rs.invoiceNumber ?? invoiceNumber,
      ...(rs.invoiceNumber ? { field: "invoiceNumber", fieldValue: rs.invoiceNumber } : {}),
    },
    {
      icon: FileText,
      label: "Invoice Type",
      value: prettyInvoiceType(rs.invoiceType ?? invoiceFields?.invoiceType ?? r.invoiceType),
      alwaysShow: true,
    },
    {
      icon: CalendarDays,
      label: "Invoice Date",
      value: prettyDate(rs.dates.invoiceDate ?? invoiceFields?.invoiceDate ?? r.invoiceDate),
      ...(rs.dates.invoiceDate
        ? { field: "invoiceDate", fieldValue: rs.dates.invoiceDate }
        : {}),
    },

    { icon: CalendarDays, label: "Service Date", value: serviceDate },
    { icon: CalendarDays, label: "Storage Period", value: storagePeriod, alwaysShow: true },
    { icon: MapPin, label: "ZIP Code", value: rs.zip ?? zipDisplay },
    { icon: MapPin, label: "City", value: cityDisplay },
    {
      icon: Landmark,
      label: "State",
      value: rs.state ?? invoiceFields?.state ?? loc?.state ?? r.state,
    },
    { icon: Landmark, label: "Market", value: marketDisplay },
    { icon: Hash, label: "Claim / Reference Number", value: claimRef },
    {
      icon: ClipboardCheck,
      label: "Submission Method",
      value: methodLabel(c.method),
      alwaysShow: true,
    },
  ];
  const rows = allRows.filter((r) => r.alwaysShow || (r.value && r.value.trim().length > 0));
  const hiddenCount = allRows.length - rows.length;
  return (
    <Section
      id="section-02-01-case-summary"
      number="01"
      eyebrow="Case Summary"
      title="Case Summary"
      exportTitle="Case Summary"
      bare
    >
      <div
        {...summarySection("Chapter 01 — Case Brief & Invoice Overview")}
        data-summary-component="Chapter01:CaseSummary"
      >
      {/* Chapter 02 refinement: the chapter header ("Case Brief & Invoice
          Overview") already conveys the title, so we render only the
          numbered marker to avoid a redundant large heading. Scoped to
          this section only — other chapters keep their standard <Section>
          header. */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-baseline gap-4">
          <div className="section-number font-display text-2xl font-light text-trust/60 tabular-nums">
            01
          </div>
          <div className="section-eyebrow text-[11px] font-semibold uppercase tracking-[0.18em] text-trust">
            Case Summary
          </div>
        </div>
        <SectionActions sectionId="section-02-01-case-summary" title="Case Summary" />
      </div>
      <div className="mt-6" />
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <LocationBadge verified={loc?.verified === true} />
        {!loc?.verified && (
          <span className="text-xs text-muted-foreground">
            Location estimated from submitted invoice.
          </span>
        )}
      </div>
      {(invoiceTotal !== undefined || (fairLow !== undefined && fairHigh !== undefined)) && (
        <div className="mb-5 grid grid-cols-1 gap-5 md:grid-cols-2">
          {invoiceTotal !== undefined && (
            <KpiCard
              tone="amber"
              label="Invoice Total"
              value={currency(rs.financials.invoiceTotal || invoiceTotal)}
              hint="Amount billed by vendor"
              field="invoiceTotal"
              fieldValue={rs.financials.invoiceTotal || invoiceTotal}
              hero
            />
          )}
          {fairLow !== undefined && fairHigh !== undefined && (
            /* Estimated Fair Payment is a single canonical figure
               (Report Summary `supportedAmount`), identical to the value shown
               in Chapters 10, 11 and 12. The earlier low/high range published
               a second, conflicting definition of the same frozen term. */
            <KpiCard
              tone="benchmark"
              label="Estimated Fair Payment"
              value={currency(rs.financials.supportedAmount)}
              field="supportedAmount"
              fieldValue={rs.financials.supportedAmount}
              hint="Independent benchmark for a reasonable settlement"
              hero
            />
          )}
        </div>
      )}
      <div className="grid overflow-hidden rounded-xl border border-trust/15 bg-card shadow-card sm:grid-cols-2 lg:grid-cols-3">
        {rows.map((row) => (
          <div
            key={row.label}
            className="flex min-h-[58px] items-start gap-3 border-b border-trust/10 bg-white p-3 last:border-b-0 sm:[&:nth-last-child(-n+2)]:border-b-0 lg:border-r lg:[&:nth-child(3n)]:border-r-0 lg:[&:nth-last-child(-n+3)]:border-b-0"
          >
            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-trust-soft text-navy">
              <row.icon className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-[9px] font-bold uppercase tracking-[0.12em] text-navy/70">
                {row.label}
              </div>
              <div
                className="mt-0.5 text-[12px] font-bold leading-tight text-navy [overflow-wrap:anywhere]"
                {...(row.field ? summaryField(row.field, row.fieldValue ?? null) : {})}
              >
                {row.value && row.value.trim().length > 0 ? row.value : NOT_EXTRACTED}
              </div>
            </div>
          </div>
        ))}
      </div>
      <StorageSummaryCard
        extracted={inv}
        marketComparisons={marketComparisons}
        lineItems={lineItems}
        marketResearch={bundle?.marketResearch ?? []}
      />

      <InvoiceChargesSummary
        marketComparisons={marketComparisons}
        extracted={inv}
        lineItems={lineItems}
        invoiceTotal={invoiceTotal}
        marketResearch={bundle?.marketResearch ?? []}
      />

      {c.details.notes && (
        <div className="mt-4 rounded-2xl border border-border bg-white p-5 shadow-card">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Customer notes
          </div>
          <p className="mt-2 text-sm text-navy whitespace-pre-wrap">{c.details.notes}</p>
        </div>
      )}
      </div>
    </Section>
  );
}

function methodLabel(m: string) {
  return m === "photo" ? "Photo" : m === "email" ? "Email forwarding" : "File upload";
}

/* ─────────────────────────────────────────────────────────── */
/* 2b. Invoice Charges Summary (executive comparison table)    */
/* ─────────────────────────────────────────────────────────── */

type ChargeStatusKey = "within" | "slightly" | "high" | "significantly" | "unsupported";

function classifyChargeStatus(
  invoiceAmount: number | null,
  expectedAmount: number | null,
  description: string,
): { key: ChargeStatusKey; label: string } {
  const desc = (description ?? "").toLowerCase();
  if (/\bduplicate\b/.test(desc)) return { key: "unsupported", label: "Duplicate" };
  /* No benchmark retrieved is NOT an adverse finding — it means the charge
     could not be compared, so it must never read as "Unsupported". */
  if (invoiceAmount === null || expectedAmount === null || expectedAmount <= 0) {
    return { key: "unsupported", label: "Requires Additional Market Support" };
  }
  /* Same thresholds and same vocabulary as Market Benchmark Analysis
     (classifyVariance) so one charge can never read "High" here and
     "Above Market" there. */
  const pct = ((invoiceAmount - expectedAmount) / expectedAmount) * 100;
  if (pct >= 25) return { key: "significantly", label: "Materially Above Market" };
  if (pct >= 8) return { key: "high", label: "Above Market" };
  if (pct <= -8) return { key: "within", label: "Below Market" };
  return { key: "within", label: "Within Market" };
}

function chargeStatusCls(k: ChargeStatusKey): string {
  switch (k) {
    case "within":
      return "border-savings/25 bg-savings-soft/60 text-savings";
    case "slightly":
      return "border-amber-300/40 bg-amber-50/70 text-amber-700";
    case "high":
      return "border-warn/30 bg-warn/[0.07] text-warn";
    case "significantly":
      return "border-destructive/30 bg-destructive/[0.06] text-destructive";
    case "unsupported":
      return "border-border bg-secondary/60 text-muted-foreground";
  }
}


interface ChargeSummaryRow {
  description: string;
  /** Service family alone — used for charts and grouping labels. */
  shortLabel: string;
  qty: number | null;
  invoiceRate: number | null;
  invoiceAmount: number | null;
  marketLow: number | null;
  marketHigh: number | null;
  marketAverage: number | null;
  expectedAmount: number | null;
  difference: number | null;
  status: { key: ChargeStatusKey; label: string };
  isStorage: boolean;
  /** Customer-facing pricing basis for this row. */
  sourceLabel: string;
  /** Plain-English basis note, shown only for researched pricing. */
  researchNote: string | null;
  /** Normalized key of the invoice charge this row was built from, if any. */
  chargeKey?: string | null;
  /** Inventory charge id — the join key across chapters. */
  chargeId?: string | null;
  /** Canonical charge-level disposition from the single resolver. */
  canonicalStatus?: CanonicalBenchmarkStatus;
  /**
   * Internal audit trail: id of the invoice charge whose verified market
   * evidence this row borrowed. Never rendered to customers.
   */
  benchmarkReusedFromLineId?: string | null;
  /**
   * Professional sentence explaining why an unsupported charge could not be
   * benchmarked. Presentation only — never implies or suggests a price.
   */
  unsupportedReason?: string | null;
  /** Collapsed-by-default evidence disclosure for a supported charge. */
  disclosure?: DisclosureField[];
}





/* Accepts 0 and negative values so zero-dollar charges and credits /
   negative adjustments survive extraction instead of silently vanishing
   from the totals. */
function getLineItemNumber(
  item: Record<string, unknown> | null | undefined,
  keys: string[],
): number | null {
  if (!item) return null;
  for (const k of keys) {
    const v = item[k];
    if (typeof v === "number" && Number.isFinite(v)) return v;
    if (typeof v === "string" && v.trim()) {
      const negative = /^\s*[(-]/.test(v) || /\bcredit\b/i.test(v);
      const n = Number(v.replace(/[^0-9.]/g, ""));
      if (Number.isFinite(n) && v.replace(/[^0-9]/g, "").length > 0) {
        return negative ? -n : n;
      }
    }
  }
  return null;
}

function getNormalizedInvoiceLineItems(
  directLineItems: unknown,
  extracted?: Record<string, unknown>,
): Array<Record<string, unknown>> {
  if (Array.isArray(directLineItems)) return directLineItems as Array<Record<string, unknown>>;
  if (Array.isArray(extracted?.line_items))
    return extracted.line_items as Array<Record<string, unknown>>;
  if (Array.isArray(extracted?.lineItems))
    return extracted.lineItems as Array<Record<string, unknown>>;
  return [];
}

/* Benchmark→inventory binding and the qualifier vocabulary now live in
   `@/lib/report/charge-benchmark-status`, which is the single canonical
   resolver shared by Chapter 01, Chapter 02 and the Report Summary. */




/**
 * Builds one table row per BILLED INVOICE CHARGE.
 *
 * The invoice inventory is the sole source of the row set and of every
 * invoice-side value (description, quantity, rate, amount). Benchmark rows
 * only ATTACH market data (range, average, expected amount, variance,
 * status) to a charge — they can never create one. A benchmark that cannot
 * be bound to a charge is logged and dropped. Benchmark math is unchanged.
 */
function buildChargeSummaryRows(
  markets: MarketComparisonRow[],
  lineItems: Array<Record<string, unknown>> = [],
  research: MarketResearch[] = [],
): ChargeSummaryRow[] {
  const { canonical: charges } = buildChargeInventory(lineItems);

  /* Legacy/preview reports carry no captured invoice lines at all. There is no
     invoice to be the source of truth for, so the benchmark rows are shown as
     they always were. Real claims always have captured lines. */
  if (charges.length === 0) {
    return markets.map((m) => {
      const description = m.line_item ?? m.service_type ?? "Service";
      const invoiceAmount = m.invoice_amount ?? null;
      const invoiceRate = m.invoice_rate ?? null;
      const qty =
        invoiceAmount !== null && invoiceRate && invoiceRate > 0
          ? Math.round((invoiceAmount / invoiceRate) * 100) / 100
          : null;
      const q = qty !== null && qty > 0 ? qty : 1;
      const marketAverage = m.local_average ?? m.market_median ?? null;
      const expectedAmount =
        marketAverage !== null ? Math.round(marketAverage * q * 100) / 100 : null;
      return {
        description,
        shortLabel: description,
        qty,
        invoiceRate,
        invoiceAmount,
        marketLow: m.typical_low ?? m.market_low ?? null,
        marketHigh: m.typical_high ?? m.market_high ?? null,
        marketAverage,
        expectedAmount,
        difference:
          invoiceAmount !== null && expectedAmount !== null
            ? Math.round((invoiceAmount - expectedAmount) * 100) / 100
            : null,
        status: classifyChargeStatus(invoiceAmount, expectedAmount, description),
        isStorage: /storage/i.test(description),
        sourceLabel:
          expectedAmount !== null
            ? SOURCE_CLASSIFICATION_LABEL.verified_market
            : SOURCE_CLASSIFICATION_LABEL.insufficient_pricing_data,
        researchNote: null,
        unsupportedReason: expectedAmount !== null ? null : unsupportedExplanation({}),
        disclosure:
          expectedAmount !== null
            ? buildChargeDisclosure({
                marketBasis: SOURCE_CLASSIFICATION_LABEL.verified_market,
                billingUnit: null,
                jurisdiction: [m.zip_code ?? m.zip, m.state].filter(Boolean).join(", ") || null,
              })
            : undefined,
      };
    });
  }

  /* CANONICAL COVERAGE. Binding, research fallback and benchmark reuse are all
     resolved by the shared resolver, so this table and the Report Summary
     (Chapter 02, dashboards, PDF) can never classify a charge differently. */
  const resolution = resolveChargeBenchmarkStatuses({
    lineItems,
    marketComparisons: markets,
    marketResearch: research,
  });

  const sourceLabelFor = (s: CanonicalChargeStatus): string => {
    switch (s.evidenceBasis) {
      case "verified_benchmark":
        return SOURCE_CLASSIFICATION_LABEL.verified_market;
      case "researched":
        return (
          SOURCE_CLASSIFICATION_LABEL[
            s.research?.source_classification as SourceClassification
          ] ?? SOURCE_CLASSIFICATION_LABEL.industry_estimate
        );
      case "reused": {
        const donor = s.reusedFromChargeId
          ? resolution.byChargeId.get(s.reusedFromChargeId)
          : null;
        if (donor?.evidenceBasis === "verified_benchmark")
          return SOURCE_CLASSIFICATION_LABEL.verified_market;
        return (
          SOURCE_CLASSIFICATION_LABEL[
            donor?.research?.source_classification as SourceClassification
          ] ?? SOURCE_CLASSIFICATION_LABEL.industry_estimate
        );
      }
      default:
        return SOURCE_CLASSIFICATION_LABEL.insufficient_pricing_data;
    }
  };

  return resolution.statuses.map((s) => {
    const charge = s.charge;
    const priced = s.expectedAmount !== null;
    const sourceLabel = sourceLabelFor(s);
    const donor = s.reusedFromChargeId ? resolution.byChargeId.get(s.reusedFromChargeId) : null;
    const researchNote = priced
      ? (s.research?.methodology_note ?? donor?.research?.methodology_note ?? null)
      : null;

    return {
      description: charge.displayName,
      shortLabel: charge.familyName,
      qty: charge.quantity,
      invoiceRate: charge.rate,
      invoiceAmount: s.invoiceAmount,
      marketLow: s.marketLow,
      marketHigh: s.marketHigh,
      marketAverage: s.marketAverage,
      expectedAmount: s.expectedAmount,
      difference: s.difference,
      status: priced
        ? { key: CANONICAL_STATUS_KEY[s.status], label: CANONICAL_STATUS_LABEL[s.status] }
        : {
            key: "unsupported" as const,
            /* A repeated printed line is held for vendor clarification — it is
               NOT the same state as "no comparable pricing found". */
            label: CANONICAL_STATUS_LABEL[
              s.status === "clarification" ? "clarification" : "pending"
            ],
          },
      isStorage: /storage/i.test(charge.familyName),
      sourceLabel,
      researchNote,
      chargeKey: charge.normalized,
      chargeId: charge.id,
      canonicalStatus: s.status,
      benchmarkReusedFromLineId: s.reusedFromChargeId,
      unsupportedReason: priced
        ? null
        : s.status === "clarification"
          ? "This printed line repeats an earlier printed charge. It remains on the invoice inventory and is excluded from quantified recovery until the vendor confirms whether it is a separate, legitimate charge."
        : unsupportedExplanation({
            researchFailureReason: s.research?.failure_reason ?? null,
            reuseRejectionReason: s.reuseRejectionReason,
          }),
      disclosure: priced
        ? buildChargeDisclosure({
            marketBasis: sourceLabel,
            billingUnit:
              s.research?.invoice_unit ??
              s.research?.resolved_unit ??
              donor?.research?.invoice_unit ??
              null,
            jurisdiction:
              s.research?.geography ??
              ([s.comparison?.zip_code ?? s.comparison?.zip, s.comparison?.state]
                .filter(Boolean)
                .join(", ") ||
                null),
            sourceClassificationLabel: sourceLabel,
            calculationMethod: s.research?.calculation_method ?? null,
            evidenceCount: s.research?.accepted_source_count ?? null,
            reused: s.reusedFromChargeId !== null,
          })
        : undefined,
    };
  });
}

/** Canonical status → the table's legacy style key. One mapping only. */
const CANONICAL_STATUS_KEY: Record<CanonicalBenchmarkStatus, ChargeStatusKey> = {
  "materially-above": "significantly",
  above: "high",
  within: "within",
  below: "within",
  pending: "unsupported",
  clarification: "unsupported",
};




export interface Chapter02Reconciliation {
  pass: boolean;
  rowCount: number;
  invoiceLineCount: number;
  displayedTotal: number;
  invoiceSubtotal: number | null;
  variance: number | null;
  /** Billed lines present on the invoice but absent from the rendered table. */
  missingRows: string[];
  /** Rendered rows that could not be traced back to a billed invoice line. */
  phantomRows: string[];
  /** Invoice lines collapsed into a single rendered row. */
  duplicatedRows: string[];
}

/**
 * Financial integrity gate for Chapter 02.
 *
 * Verifies that the sum of every displayed invoice amount equals the invoice
 * subtotal, and that every rendered row traces back to a billed invoice line.
 * Failures are logged with the offending rows — never silently swallowed.
 */
function reconcileChapter02(
  rows: ChargeSummaryRow[],
  lineItems: Array<Record<string, unknown>> = [],
  invoiceStatedTotal?: number | null,
): Chapter02Reconciliation {
  const round2 = (n: number) => Math.round(n * 100) / 100;
  const displayedTotal = round2(rows.reduce((s, r) => s + (r.invoiceAmount ?? 0), 0));
  const { canonical: charges, duplicateExtractions } = buildChargeInventory(lineItems);


  /* Nothing to reconcile against on legacy/preview reports with no captured
     invoice lines. */
  if (charges.length === 0) {
    return {
      pass: true,
      rowCount: rows.length,
      invoiceLineCount: 0,
      displayedTotal,
      invoiceSubtotal: null,
      variance: null,
      missingRows: [],
      phantomRows: [],
      duplicatedRows: [],
    };
  }

  /* Rows are traced by the invoice charge they were built from, so a benchmark
     label that differs from the invoice wording is never mistaken for a
     phantom charge. */
  const rowKeys = new Map<string, number>();
  for (const r of rows) {
    const k = `${r.chargeKey ?? ""}|${r.invoiceAmount ?? ""}`;
    rowKeys.set(k, (rowKeys.get(k) ?? 0) + 1);
  }
  const chargeKeys = new Map<string, number>();
  for (const c of charges) {
    const k = `${c.normalized}|${c.amount ?? ""}`;
    chargeKeys.set(k, (chargeKeys.get(k) ?? 0) + 1);
  }

  const missingRows: string[] = [];
  for (const c of charges) {
    const k = `${c.normalized}|${c.amount ?? ""}`;
    if ((rowKeys.get(k) ?? 0) < (chargeKeys.get(k) ?? 0)) missingRows.push(c.displayName);
  }
  const phantomRows: string[] = [];
  for (const r of rows) {
    const k = `${r.chargeKey ?? ""}|${r.invoiceAmount ?? ""}`;
    if (!r.chargeKey || !chargeKeys.has(k)) phantomRows.push(r.description);
  }
  const duplicatedRows = duplicateExtractions.map(
    (c) =>
      `${c.displayName} (repeated extraction of an earlier printed line — held out of the certified charge inventory)`,
  );




  /* The authoritative subtotal is the sum of the billed invoice lines. The
     invoice's stated total is only informational here because it may also
     carry tax and other non-line amounts. */
  const subtotal = round2(charges.reduce((s, c) => s + (c.amount ?? 0), 0));
  const variance = round2(displayedTotal - subtotal);
  const pass =
    phantomRows.length === 0 && missingRows.length === 0 && Math.abs(variance) < 0.01;

  if (!pass) {
    console.error("[chapter02] financial integrity validation FAILED", {
      rowCount: rows.length,
      invoiceLineCount: charges.length,
      displayedTotal,
      invoiceSubtotal: subtotal,
      variance,
      missingRows,
      phantomRows,
      duplicatedRows,
    });
  }

  if (
    typeof invoiceStatedTotal === "number" &&
    Number.isFinite(invoiceStatedTotal) &&
    displayedTotal - round2(invoiceStatedTotal) > 0.01
  ) {
    console.error("[chapter02] displayed charges exceed the invoice total", {
      displayedTotal,
      invoiceStatedTotal: round2(invoiceStatedTotal),
    });
  }


  return {
    pass,
    rowCount: rows.length,
    invoiceLineCount: charges.length,
    displayedTotal,
    invoiceSubtotal: subtotal,
    variance,
    missingRows,
    phantomRows,
    duplicatedRows,
  };
}



function fmtMoney(n: number | null): string {
  return typeof n === "number" ? currency(n) : "—";
}

function fmtMoneyFixed(n: number | null): string {
  return typeof n === "number"
    ? n.toLocaleString("en-US", {
        style: "currency",
        currency: "USD",
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : "—";
}

function fmtRange(low: number | null, high: number | null): string {
  if (low === null && high === null) return "—";
  // Non-breaking space keeps the dash attached to the low bound so a narrow
  // column never wraps the separator onto a line of its own.
  if (low !== null && high !== null) return `${currency(low)}\u00A0– ${currency(high)}`;

  return currency((low ?? high) as number);
}

function parseAnyDate(v: unknown): Date | null {
  if (!v) return null;
  if (v instanceof Date) return Number.isNaN(v.getTime()) ? null : v;
  if (typeof v !== "string" && typeof v !== "number") return null;
  const d = new Date(v as string | number);
  return Number.isNaN(d.getTime()) ? null : d;
}

function fmtLongDate(d: Date): string {
  return d.toLocaleDateString("en-US", {
    month: "long",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  });
}

interface StorageSummaryData {
  start: Date | null;
  end: Date | null;
  days: number | null;
  dailyRate: number | null;
  /** How the duration was established. Never lowers confidence on its own. */
  daysSource: "extracted" | "dates" | "billed" | null;
  /** True when billed storage = daily rate x duration to the cent. */
  reconciles: boolean | null;
}

function getStorageSummaryData(
  extracted: Record<string, unknown> | undefined,
  marketComparisons: MarketComparisonRow[],
  lineItems: Array<Record<string, unknown>> = [],
): StorageSummaryData | null {
  const storageRow =
    buildChargeSummaryRows(marketComparisons, lineItems).find((r) => r.isStorage) ?? null;



  /* Start Date, End Date and Duration all come from the ONE canonical storage
     timeline resolver (see src/lib/report/storage-timeline.ts), which is the
     same resolver that feeds ReportSummary.storageDays. Nothing here recomputes
     a duration, so the three values can never contradict each other. */
  const timeline = resolveStorageTimeline({ extracted, lineItems });
  const start = timeline.start;
  const end = timeline.end;

  const dailyRate = storageRow?.invoiceRate ?? null;
  const storageAmount = storageRow?.invoiceAmount ?? null;

  let days = timeline.days;
  let daysSource: StorageSummaryData["daysSource"] =
    timeline.basis === "extracted_storage_days"
      ? "extracted"
      : timeline.basis !== null
        ? "dates"
        : null;

  /* Duration implied by the invoice itself: billed storage / daily rate. It is
     only allowed to settle the duration when NO date evidence exists, because a
     documented storage period outranks a per-period billing quantity. */
  const billedDays =
    storageAmount !== null && dailyRate !== null && dailyRate > 0
      ? Math.round((storageAmount / dailyRate) * 100) / 100
      : null;

  if (days === null && billedDays !== null && billedDays > 0) {
    days = billedDays;
    daysSource = "billed";
  } else if (days === null && storageRow?.qty != null) {
    days = storageRow.qty;
    daysSource = "billed";
  }

  const reconciles =
    days !== null && dailyRate !== null && storageAmount !== null
      ? Math.abs(dailyRate * days - storageAmount) <= 0.01
      : null;

  if (!start && !end && !days && dailyRate === null) return null;
  return { start, end, days, dailyRate, daysSource, reconciles };
}




function StorageDurationLabel({ days }: { days: number | null }) {
  /* No silent fallback: an absent or invalid canonical duration renders an
     explicit unavailable state and flags a consistency defect rather than
     quietly displaying "1 Day". */
  if (days === null || !Number.isFinite(days) || days <= 0) {
    return (
      <span
        data-consistency-defect="storageDays-unavailable"
        title="Storage duration could not be established from the invoice."
      >
        Duration unavailable
      </span>
    );
  }
  return (
    <>
      <span {...summaryField("storageDays", days)}>{days}</span> Total{" "}
      {days === 1 ? "Day" : "Days"}
    </>
  );
}

function HeaderStoragePeriodCard({ summary }: { summary: StorageSummaryData }) {
  // Only render the masthead badge when we have a real date range OR a
  // meaningful duration. Otherwise the header reads as "— — N Days" which
  // looks broken.
  const rs = useReportSummary();
  /* Storage start, end and duration are report-wide: all three read from the
     immutable Report Summary so the range and the duration can never disagree.
     No local date arithmetic and no fallback duration live in this component. */
  const days = rs.storageDays;
  const start = rs.dates.storageStart ? new Date(rs.dates.storageStart) : summary.start;
  const end = rs.dates.storageEnd ? new Date(rs.dates.storageEnd) : summary.end;
  const hasRange = !!(start && end);
  const hasDays = days !== null && days > 0;
  if (!hasRange && !hasDays) return null;

  return (
    <div className="hidden min-w-[260px] rounded-xl border border-white/25 bg-white px-5 py-4 text-navy shadow-elevated md:flex md:items-center md:gap-4">
      <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-trust-soft text-trust ring-1 ring-trust/20">
        <CalendarDays className="h-8 w-8" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-extrabold uppercase tracking-[0.16em] text-trust">
          Storage Period
        </div>
        {hasRange ? (
          <>
            <div className="mt-1 text-[16px] font-extrabold leading-tight text-navy tabular-nums">
              {fmtLongDate(start!).replace(/, \d{4}$/, "")}
              <span className="mx-1.5 text-navy/45">–</span>
              {fmtLongDate(end!)}
            </div>
            <div className="mt-0.5 text-[12px] font-bold text-navy/70">
              <StorageDurationLabel days={days} />
            </div>
          </>
        ) : (
          <div className="mt-1 text-[16px] font-extrabold leading-tight text-navy tabular-nums">
            <StorageDurationLabel days={days} />
          </div>
        )}
      </div>
    </div>
  );
}


function StorageSummaryCard({
  extracted,
  marketComparisons,
  lineItems,
  marketResearch = [],
}: {
  extracted?: Record<string, unknown>;
  marketComparisons: MarketComparisonRow[];
  lineItems?: Array<Record<string, unknown>>;
  marketResearch?: MarketResearch[];
}) {

  const rs = useReportSummary();
  const summary = getStorageSummaryData(extracted, marketComparisons, lineItems ?? []);
  /* Start Date, End Date and Duration are report-wide and all read from the
     immutable Report Summary, which owns the canonical storage timeline. */
  const start = rs.dates.storageStart ? new Date(rs.dates.storageStart) : (summary?.start ?? null);
  const end = rs.dates.storageEnd ? new Date(rs.dates.storageEnd) : (summary?.end ?? null);
  const days = rs.storageDays;

  // `summary.dailyRate` is still computed by getStorageSummaryData() and used
  // for benchmarking, reconciliation and audit — it is simply not displayed.


  // The card is a storage *timeline*; the derived daily rate alone is no longer
  // rendered, so it can no longer keep the card alive on its own.
  if (!start && !end && days === null) return null;


  const hasStart = !!start;
  const hasEnd = !!end;
  const hasAnyDate = hasStart || hasEnd;

  return (
    <div
      className="mt-5 overflow-x-hidden rounded-xl border border-trust/25 bg-card p-4 shadow-card"
      data-pdf-keep-together="true"
    >
      <div className="grid min-w-0 items-center gap-5 lg:grid-cols-[minmax(0,1fr)_minmax(0,2fr)]">
        <div className="min-w-0">
          <div className="text-[13px] font-extrabold uppercase tracking-[0.14em] text-trust">
            Storage Summary
          </div>
          <div className="mt-1.5 text-[11px] font-medium leading-snug text-navy/70">
            {hasAnyDate
              ? summary?.daysSource === "billed"
                ? "Duration derived from the billed storage amount and daily rate on the invoice."
                : "Based on invoice storage charges and benchmark data."
              : "Storage dates were not extracted from the submitted invoice — duration is derived from the storage line item."}
            {summary?.reconciles === false
              ? " Billed storage does not equal daily rate × duration on the submitted invoice; both figures are reported exactly as billed."
              : ""}
          </div>
        </div>

        {/* One unified timeline: a single hairline rail joins the milestones,
            the values carry the emphasis and the icons stay supporting. */}
        <div className="relative flex min-w-0 flex-col items-stretch gap-4 rounded-lg border border-trust/12 bg-trust-soft/25 px-4 py-3 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between sm:gap-x-5 sm:gap-y-3">
          {hasStart && (
            <div className="flex shrink-0 items-center gap-2.5">
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-trust/20 bg-card text-trust/80">
                <CalendarDays className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[8px] font-semibold uppercase tracking-[0.12em] text-navy/45">
                  Start Date
                </div>
                <div className="mt-0.5 whitespace-nowrap text-[14px] font-extrabold leading-tight text-navy tabular-nums">
                  {fmtLongDate(start!)}
                </div>
              </div>
            </div>
          )}

          {hasEnd && (
            <div className="flex shrink-0 items-center gap-2.5">
              {hasStart && (
                <span
                  aria-hidden="true"
                  className="hidden h-px w-5 shrink-0 bg-trust/25 lg:block"
                />
              )}
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-trust/20 bg-card text-trust/80">
                <CalendarDays className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[8px] font-semibold uppercase tracking-[0.12em] text-navy/45">
                  End Date
                </div>
                <div className="mt-0.5 whitespace-nowrap text-[14px] font-extrabold leading-tight text-navy tabular-nums">
                  {fmtLongDate(end!)}
                </div>
              </div>
            </div>
          )}

          {days !== null && (
            <div className="flex shrink-0 items-center gap-2.5">
              {hasAnyDate && (
                <span
                  aria-hidden="true"
                  className="hidden h-px w-5 shrink-0 bg-trust/25 lg:block"
                />
              )}
              <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-trust/20 bg-card text-trust/80">
                <Star className="h-4 w-4" />
              </div>
              <div className="min-w-0">
                <div className="text-[8px] font-semibold uppercase tracking-[0.12em] text-navy/45">
                  Duration
                </div>
                <div className="mt-0.5 whitespace-nowrap font-mono font-extrabold leading-tight text-navy tabular-nums">
                  <span className="text-[20px]" {...summaryField("storageDays", days)}>
                    {days}
                  </span>
                  <span className="ml-1 align-baseline text-[11px] font-bold text-navy/65">
                    {days === 1 ? "Day" : "Days"}
                  </span>
                </div>
              </div>
            </div>
          )}

          {/* The normalized daily storage rate is intentionally NOT displayed:
              this invoice was billed as a per-period charge, and showing a
              derived per-day figure would imply daily billing. The value is
              still computed above and used for benchmarking and audit. */}

        </div>
      </div>


      <StorageBenchmarkClassNote
        extracted={extracted}
        lineItems={lineItems}
        marketComparisons={marketComparisons}
        marketResearch={marketResearch}
      />


    </div>
  );
}

/**
 * Phase 7 transparency: state which storage market the charges were compared
 * against and why that market was selected. A storage benchmark is only valid
 * when the benchmark class matches the classified vehicle.
 */
function StorageBenchmarkClassNote({
  extracted,
  lineItems,
  marketComparisons = [],
  marketResearch = [],
}: {
  extracted?: Record<string, unknown>;
  lineItems?: Array<Record<string, unknown>>;
  marketComparisons?: MarketComparisonRow[];
  marketResearch?: MarketResearch[];
}) {

  const str = (v: unknown) => (typeof v === "string" ? v : "");
  const classification = classifyVehicle({
    rawText: str(extracted?.raw_text),
    lineItems: (lineItems ?? []).map((li) => ({ description: str(li?.description) })),
  });
  const benchmark = selectStorageBenchmark(classification, {
    zip: str(extracted?.zip_code) || null,
    state: str(extracted?.state) || null,
  });
  const region = benchmark
    ? benchmark.zip
      ? `ZIP ${benchmark.zip}`
      : benchmark.state
        ? benchmark.state
        : "National"
    : null;

  /* Presentation-only truth check: the chapter's own storage charge may already
     carry verified market evidence even when the vehicle class (and therefore a
     class-specific storage band) could not be established. In that case the note
     must NOT claim that storage was "reported exactly as billed" — that would
     contradict the benchmarked storage row shown directly above it. No value is
     computed or altered here; the applied evidence is only described. */
  const appliedStorageRow =
    buildChargeSummaryRows(marketComparisons, lineItems ?? [], marketResearch).find(
      (row) => row.isStorage && row.marketAverage !== null && row.marketAverage > 0,
    ) ?? null;

  return (
    <div className="mt-4 border-t border-trust/15 pt-3">
      <div className="text-[9px] font-semibold uppercase tracking-[0.12em] text-navy/45">
        Storage Benchmark
      </div>
      <div className="mt-0.5 text-[13px] font-extrabold leading-tight text-navy">
        {benchmark
          ? benchmark.benchmarkLabel
          : appliedStorageRow
            ? "Verified market pricing applied"
            : "Not established"}
      </div>
      <div className="mt-1 flex flex-wrap items-baseline gap-x-4 gap-y-0.5 text-[10px] font-semibold text-navy/60">
        <span>Vehicle Class: {classification.label}</span>
        {region ? <span>Market Region: {region}</span> : null}
      </div>
      <p className="mt-2 text-[11px] leading-snug text-navy/70">
        {benchmark
          ? `This vehicle is a ${classification.label}, so storage was compared only against the ${benchmark.benchmarkLabel} market for this region. Where a state-published storage cap or a local commercial rate applies, that published rate is used in place of the general market band. The per-day rate band applied to each storage charge is shown with that charge.`
          : appliedStorageRow
            ? "The vehicle class could not be confirmed from the submitted invoice, so no class-specific storage band was applied. The storage charge was instead compared against verified market pricing for this service and region; the rate band used is shown with that charge in the table above."
            : "The vehicle class could not be established from the submitted invoice, so no storage market benchmark was applied. Storage charges are reported exactly as billed."}
      </p>

    </div>

  );
}




function conciseChargeLabel(label: string): string {
  // Keep the Other count; otherwise strip parenthetical detail for the legend.
  if (/^other\s*\(/i.test(label)) return label;
  return label.replace(/\s*\([^)]*\)/g, "").trim() || label;
}

/**
 * Ranked contribution of each billed charge to the invoice total. Presentation
 * only — every amount comes straight from the reconciled charge rows.
 */
function ChargeContributionRanking({
  rows,
  invoiceSum,
}: {
  rows: ReturnType<typeof buildChargeSummaryRows>;
  invoiceSum: number;
}) {
  const items = rows
    .map((r) => ({
      label: conciseChargeLabel(r.shortLabel || r.description),
      value: Math.max(0, r.invoiceAmount ?? 0),
    }))
    .filter((s) => s.value > 0)
    .sort((a, b) => b.value - a.value);
  const total = items.reduce((s, r) => s + r.value, 0) || invoiceSum || 1;

  if (items.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-trust/20 bg-card p-5 text-center text-xs text-muted-foreground shadow-card">
        No charge data available.
      </div>
    );
  }

  const TOP_N = 4;
  const top = items.slice(0, TOP_N).map((s) => ({ ...s, sub: null as string | null }));
  const rest = items.slice(TOP_N);
  const restTotal = rest.reduce((s, r) => s + r.value, 0);
  const base =
    rest.length > 0
      ? [
          ...top,
          {
            label: "Remaining Charges",
            sub: `(${rest.length} Item${rest.length === 1 ? "" : "s"})`,
            value: restTotal,
          },
        ]
      : top;

  /* Largest-remainder apportionment so displayed shares sum to exactly 100.0%. */
  const raw = base.map((s) => (s.value / total) * 1000);
  const floors = raw.map((v) => Math.floor(v));
  let remainder = 1000 - floors.reduce((a, b) => a + b, 0);
  const order = raw
    .map((v, i) => ({ i, frac: v - Math.floor(v) }))
    .sort((a, b) => b.frac - a.frac);
  const tenths = [...floors];
  for (const { i } of order) {
    if (remainder <= 0) break;
    tenths[i] += 1;
    remainder -= 1;
  }
  const ranked = base.map((s, i) => ({ ...s, pct: tenths[i] / 10 }));
  const maxPct = Math.max(...ranked.map((r) => r.pct), 1);

  return (
    <div className="flex h-full flex-col rounded-xl border border-trust/20 bg-card p-5 shadow-card">
      <div className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-trust">
        Largest Invoice Charges
      </div>
      <div className="mt-1 text-[10px] font-semibold text-navy/60">
        Where the {currency(invoiceSum || total)} printed charge subtotal originates.
      </div>
      <ul className="mt-4 flex-1 space-y-2.5">
        {ranked.map((s, i) => (
          <li key={`${s.label}-${i}`} className="min-w-0">
            <div className="flex items-baseline justify-between gap-3">
              <span className="min-w-0 flex-1 text-[11px] font-semibold leading-tight text-navy">
                <span className="block truncate" title={s.label}>
                  {s.label}
                </span>
                {s.sub ? (
                  <span className="block text-[9.5px] font-semibold text-navy/55">{s.sub}</span>
                ) : null}
              </span>
              <span className="shrink-0 font-mono text-[11px] font-extrabold tabular-nums text-navy">
                {s.pct.toFixed(1)}%
              </span>
            </div>
            <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-executive-red/10">
              <div
                className={`h-full rounded-full ${i === ranked.length - 1 && rest.length > 0 ? "bg-navy/25" : "bg-executive-red/70"}`}
                style={{ width: `${Math.max(2, (s.pct / maxPct) * 100)}%` }}
              />
            </div>
            <div className="mt-0.5 font-mono text-[9.5px] font-semibold tabular-nums text-navy/55">
              {currency(s.value)}
            </div>
          </li>
        ))}
      </ul>

    </div>
  );
}


/**
 * Full-width benchmark basis panel. Rendered inside a colSpan row so the
 * charge rows themselves stay compact and no cell is stretched.
 */
function DisclosurePanel({ fields }: { fields: DisclosureField[] }) {
  return (
    <div className="rounded-sm border border-trust/15 bg-trust-soft/25 px-3 py-2.5">
      <div className="mb-2 text-[8.5px] font-extrabold uppercase tracking-[0.1em] text-trust">
        Benchmark Basis
      </div>
      <dl className="grid gap-x-6 gap-y-2 [grid-template-columns:repeat(auto-fit,minmax(13rem,1fr))]">
        {fields.map((f) => (
          <div key={f.label} className="min-w-0">
            <dt className="text-[8px] font-extrabold uppercase tracking-[0.08em] text-navy/50">
              {f.label}
            </dt>
            <dd className="m-0 mt-0.5 min-w-0 text-[9px] font-medium leading-snug text-navy/80 [overflow-wrap:anywhere]">
              {f.value}
            </dd>
          </div>
        ))}
      </dl>
    </div>
  );
}


function InvoiceChargesSummary({
  marketComparisons,
  lineItems,
  invoiceTotal,
  marketResearch,
  extracted,
}: {
  marketComparisons: MarketComparisonRow[];
  lineItems?: Array<Record<string, unknown>>;
  invoiceTotal?: number;
  marketResearch?: MarketResearch[];
  /** Canonical persisted invoice record (Layer 1 printed reconciliation). */
  extracted?: Record<string, unknown> | null;
}) {
  const [openDisclosure, setOpenDisclosure] = useState<number | null>(null);
  const rs = useReportSummary();
  const rows = buildChargeSummaryRows(
    marketComparisons,
    lineItems ?? [],
    marketResearch ?? [],
  );
  /* Financial integrity gate — logs missing / duplicated / phantom rows and
     any variance against the invoice subtotal instead of continuing silently. */
  reconcileChapter02(rows, lineItems ?? [], invoiceTotal ?? null);
  if (rows.length === 0) return null;




  const round2 = (n: number) => Math.round(n * 100) / 100;

  /* Full invoice — every billed charge, priced or not. */
  const invoiceSum = round2(rows.reduce((s, r) => s + (r.invoiceAmount ?? 0), 0));

  /* Market-supported scope — only the charges that carry a published market
     basis. A charge with no established pricing is NEVER carried at its
     invoiced amount into the Expected column, because doing so would imply
     the invoiced figure is market-supported when it is not. */
  const supportedRows = rows.filter((r) => r.expectedAmount !== null);
  const supportedInvoiceSum = round2(
    supportedRows.reduce((s, r) => s + (r.invoiceAmount ?? 0), 0),
  );
  const supportedExpectedSum = round2(
    supportedRows.reduce((s, r) => s + (r.expectedAmount ?? 0), 0),
  );
  const diffSum = round2(supportedInvoiceSum - supportedExpectedSum);
  /* Unsupported scope is reported in its two distinct states — a repeated
     printed line awaiting vendor clarification is not the same thing as a
     charge with no comparable pricing. Both remain visible above. */
  const clarificationRows = rows.filter((r) => r.canonicalStatus === "clarification");
  const pendingEvidenceRows = rows.filter(
    (r) => r.expectedAmount === null && r.canonicalStatus !== "clarification",
  );
  const clarificationCount = clarificationRows.length;
  const clarificationAmount = round2(
    clarificationRows.reduce((s, r) => s + (r.invoiceAmount ?? 0), 0),
  );
  const pendingEvidenceCount = pendingEvidenceRows.length;
  const pendingEvidenceAmount = round2(
    pendingEvidenceRows.reduce((s, r) => s + (r.invoiceAmount ?? 0), 0),
  );

  /* Printed invoice financials come ONLY from the canonical Layer 1
     reconciliation persisted on invoice_data.extracted. Nothing here is
     recomputed from the analysis scope. */
  const printedFinancials = (() => {
    const e = extracted ?? {};
    const num = (v: unknown) => (typeof v === "number" && Number.isFinite(v) ? v : null);
    const subtotal = num(e.printed_subtotal);
    const lineCount = num(e.printed_line_count);
    if (subtotal === null || lineCount === null) return null;
    return {
      lineCount,
      subtotal,
      tax: num(e.printed_tax),
      total: num(e.printed_total),
      taxSource: typeof e.printed_tax_source === "string" ? e.printed_tax_source : null,
    };
  })();

  /* INVENTORY INTEGRITY GATE. A certified charge inventory is only published
     when the charges it lists can be reconciled to the printed invoice total.
     If the captured charges still exceed the printed total after repeated
     extractions are removed, the chapter refuses to present figures. */
  const integrityBlock = (() => {
    const printedTotal =
      printedFinancials?.total ??
      (typeof invoiceTotal === "number" && Number.isFinite(invoiceTotal) ? invoiceTotal : null);
    if (printedTotal === null || printedTotal <= 0) return null;
    const tolerance = Math.max(0.01, round2(printedTotal * 0.005));
    const overage = round2(invoiceSum - printedTotal);
    if (overage <= tolerance) return null;
    return { printedTotal, overage };
  })();
  if (integrityBlock) {
    console.error("[chapter02] inventory integrity gate blocked publication", integrityBlock);
    return (
      <div className="mt-5" data-pdf-keep-together="true">
        <div className="rounded-xl border border-alert/30 bg-alert/5 p-5">
          <h2 className="section-eyebrow m-0 text-[11px] font-semibold uppercase tracking-[0.18em] text-alert">
            02 — Invoice Charges Held for Verification
          </h2>
          <p className="mt-3 text-sm leading-relaxed text-foreground">
            The charges captured from the uploaded invoice total{" "}
            {fmtMoneyFixed(invoiceSum)}, which exceeds the printed invoice total of{" "}
            {fmtMoneyFixed(integrityBlock.printedTotal)} by{" "}
            {fmtMoneyFixed(integrityBlock.overage)}. A certified charge inventory is not
            published until that difference is resolved, so no charge-level figures are shown
            for this invoice.
          </p>
          <p className="mt-2 text-xs leading-relaxed text-muted-foreground">
            This claim is flagged for document verification. Re-uploading clean, single copies
            of each invoice page resolves it in most cases.
          </p>
        </div>
      </div>
    );
  }


  /* Totals integrity gate. Every displayed total must satisfy its own
     identity before it is rendered; a failure is logged and the affected
     figures are withheld rather than shown incorrectly. */
  const totalsAudit = auditChapter02Totals({
    invoiceSum,
    rowInvoiceAmounts: rows.map((r) => r.invoiceAmount),
    supportedInvoiceSum,
    supportedInvoiceAmounts: supportedRows.map((r) => r.invoiceAmount),
    supportedExpectedSum,
    supportedExpectedAmounts: supportedRows.map((r) => r.expectedAmount),
    diffSum,
  });
  if (!totalsAudit.pass) {
    console.error("[chapter02] totals integrity gate failed", totalsAudit.failures);
  }
  const showTotals = totalsAudit.pass;
  const WITHHELD = "—";

  /* Invoice Total is report-wide — it comes from the Report Summary. */
  const totalBilled = rs.financials.invoiceTotal || invoiceTotal || invoiceSum;
  /* The savings card renders the canonical Report Summary value only — no
     locally recomputed overcharge total or percentage is displayed. */


  const storageRow = rows.find((r) => r.isStorage) ?? null;

  const chargeSummaryColumns = [
    "charge-col-description",
    "charge-col-qty",
    "charge-col-invoice-rate",
    "charge-col-invoice-amount",
    "charge-col-market-range",
    "charge-col-market-average",
    "charge-col-expected-amount",
    "charge-col-difference",
    "charge-col-status",
  ];

  const headerLabel = (
    label: ReactNode,
    className = "",
  ) => (
    <span className={`charge-header-label ${className}`}>
      {typeof label === "string" || typeof label === "number" ? <span>{label}</span> : label}
    </span>
  );

  return (
    <div className="mt-5 space-y-4" data-pdf-keep-together="true">
      <div className="chapter02-charge-summary-card rounded-xl border border-trust/15 bg-card p-4 shadow-card md:p-5">
        <div className="mb-3 w-full min-w-0 border-b border-trust/10 pb-3">
          <div className="flex w-full min-w-0 items-baseline gap-3">
            <div className="section-number shrink-0 font-display text-2xl font-light text-trust/60 tabular-nums">
              02
            </div>
            <h2 className="section-eyebrow m-0 min-w-0 flex-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-trust">
              Invoice Charges vs. Market Benchmarks
            </h2>
          </div>
          <p className="mt-2 w-full text-[10px] font-semibold leading-snug text-muted-foreground">
            {rows.length} Charge{rows.length === 1 ? "" : "s"} Reviewed • {supportedRows.length}{" "}
            Market Supported
            {pendingEvidenceCount > 0
              ? ` • ${pendingEvidenceCount} Requiring Additional Market Support`
              : ""}
            {clarificationCount > 0
              ? ` • ${clarificationCount} Vendor Clarification Required`
              : ""}
          </p>
        </div>

        <p className="-mt-1 mb-3 text-[10px] leading-snug text-muted-foreground">
          Each billed charge has been reviewed against available market pricing for the same service,
          vehicle class, billing unit, and geographic market. Charges requiring additional market
          support remain included in this report and are evaluated through documentation, mathematical
          validation, and forensic review.
        </p>

        {/* Financial guide — plain-language key to the three money columns */}
        <div className="mb-3 rounded-md border border-trust/12 bg-trust-soft/25 px-3 py-2">
          <div className="text-[8.5px] font-semibold uppercase tracking-[0.18em] text-navy/60">
            Financial Guide
          </div>
          <div className="mt-1.5 grid gap-1.5 md:grid-cols-3">
            {[
              {
                dot: "bg-executive-red",
                label: "Vendor Amount",
                text: "Amount billed by the towing/storage company.",
              },
              {
                dot: "bg-executive-blue",
                label: "Supported Amount",
                text: "Amount supported by verified market pricing.",
              },
              {
                dot: "bg-savings",
                label: "Total Supported Recovery",
                text: "Potential amount that may be recoverable through negotiation.",
              },
            ].map((k) => (
              <div key={k.label} className="flex items-start gap-1.5">
                <span className={`mt-[3px] h-1.5 w-1.5 shrink-0 rounded-full ${k.dot}`} />
                <div className="min-w-0 text-[9px] leading-snug text-navy/70">
                  <span className="font-semibold text-navy">{k.label}</span> — {k.text}
                </div>
              </div>
            ))}
          </div>
        </div>


        <div className="charge-summary-scroll rounded-sm border border-trust/10">
          <table className="charge-summary-table table-fixed border-collapse text-[11px]">
            <colgroup>
              {chargeSummaryColumns.map((column) => (
                <col key={column} className={column} />
              ))}
            </colgroup>





            <thead>
              <tr className="border-b border-trust/15 bg-trust-soft/50 text-navy">
                <th className="charge-header-cell charge-header-description border-r border-trust/10 text-left">
                  {headerLabel("Charge Description", "items-start text-left")}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel("QTY")}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel(<><span>INVOICE</span><span>RATE</span></>)}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel(<><span>VENDOR</span><span>AMOUNT</span></>)}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel(<><span>MARKET</span><span>RANGE</span></>)}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel(<><span>MARKET</span><span>AVERAGE</span></>)}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel(<><span>SUPPORTED</span><span>AMOUNT</span></>)}
                </th>
                <th className="charge-header-cell border-r border-trust/10 text-center">
                  {headerLabel(<><span>POTENTIAL</span><span>SAVINGS</span><span className="charge-header-note">(Vendor − Supported)</span></>)}
                </th>
                <th className="charge-header-cell text-center">{headerLabel("STATUS")}</th>

              </tr>
            </thead>


            <tbody>
              {rows.map((row, i) => {
                const zebra = i % 2 === 0 ? "bg-white" : "bg-trust-soft/20";
                const notes: DisclosureField[] = [
                  ...(row.unsupportedReason
                    ? [{ label: "Evidence Status", value: row.unsupportedReason }]
                    : []),
                  ...(row.researchNote ? [{ label: "Research Note", value: row.researchNote }] : []),
                  ...(row.disclosure ?? []),
                ];
                const open = openDisclosure === i;
                return (
                  <Fragment key={`${row.description}-${i}`}>
                    <tr className={`border-b border-trust/10 ${zebra}`}>
                      <td className="border-r border-trust/10 px-4 py-2.5 text-left align-top font-bold text-navy">
                        <div className="text-[11.5px] leading-snug [overflow-wrap:break-word]">
                          {row.description}
                        </div>
                        <div className="mt-1 text-[8.5px] font-semibold uppercase tracking-[0.06em] text-navy/45">
                          {notes.length > 0 ? (
                            <button
                              type="button"
                              onClick={() => setOpenDisclosure(open ? null : i)}
                              aria-expanded={open}
                              aria-label={
                                open
                                  ? `Hide benchmark basis for ${row.description}`
                                  : `Show benchmark basis for ${row.description}`
                              }
                              className={`no-print text-left underline-offset-2 transition-colors hover:text-trust hover:underline ${open ? "text-trust" : ""}`}
                            >
                              {row.sourceLabel}
                            </button>
                          ) : (
                            <span>{row.sourceLabel}</span>
                          )}
                        </div>
                      </td>


                      <td className="border-r border-trust/10 px-2 py-2.5 text-center align-top tabular-nums text-navy">
                        {row.qty !== null ? row.qty : "—"}
                      </td>
                      <td className="border-r border-trust/10 px-3 py-2.5 text-right align-top tabular-nums font-semibold text-navy/75">
                        {fmtMoney(row.invoiceRate)}
                      </td>
                      <td className="border-r border-trust/10 px-3.5 py-2.5 text-right align-top tabular-nums font-bold text-executive-red">
                        {fmtMoney(row.invoiceAmount)}
                      </td>
                      <td className="border-r border-trust/10 px-3.5 py-2.5 text-right align-top tabular-nums font-semibold text-navy/70">
                        {fmtRange(row.marketLow, row.marketHigh)}
                      </td>
                      <td className="border-r border-trust/10 px-3.5 py-2.5 text-right align-top tabular-nums font-semibold text-executive-blue/85">
                        {fmtMoney(row.marketAverage)}
                      </td>
                      <td className="border-r border-trust/10 px-3.5 py-2.5 text-right align-top tabular-nums font-extrabold text-executive-blue">

                        {row.expectedAmount !== null ? (
                          fmtMoney(row.expectedAmount)
                        ) : (
                          <span className="block break-normal py-1 text-[8px] font-semibold uppercase leading-[1.5] tracking-normal [word-break:keep-all] text-navy/40">
                            Not Available
                          </span>
                        )}
                      </td>
                      <td
                        className={`border-r border-trust/10 px-3.5 py-2.5 text-right align-top text-[11.5px] tabular-nums font-extrabold ${
                          row.difference !== null && row.difference > 0
                            ? "text-savings"
                            : row.difference !== null && row.difference < 0
                              ? "text-navy/60"
                              : "text-muted-foreground"
                        }`}
                      >
                        {row.difference !== null ? (
                          `${row.difference > 0 ? "+" : ""}${fmtMoney(row.difference)}`
                        ) : (
                          <span className="block break-normal py-1 text-[7.5px] font-semibold uppercase leading-[1.5] tracking-normal [word-break:keep-all] text-navy/40">
                            Pending Comparable Pricing
                          </span>
                        )}
                      </td>
                      <td className="px-2.5 py-3 align-middle">
                        <span
                          className={`flex h-[42px] w-full items-center justify-center whitespace-normal rounded-full border px-3 py-1 text-center font-bold uppercase leading-[1.25] tracking-normal ${row.status.label.length > 24 ? "text-[7.5px]" : "text-[8.5px]"} ${chargeStatusCls(row.status.key)}`}
                        >
                          {row.status.label}
                        </span>
                      </td>




                    </tr>

                    {notes.length > 0 && open ? (
                      <tr className={`no-print border-b border-trust/10 ${zebra}`}>
                        <td colSpan={9} className="px-2.5 pb-2.5 pt-0">
                          <DisclosurePanel fields={notes} />
                        </td>
                      </tr>
                    ) : null}

                    {/* Static, print-only rendition of the same basis so the
                        exported document is complete without any interaction. */}
                    {notes.length > 0 ? (
                      <tr className={`pdf-only border-b border-trust/10 ${zebra}`}>
                        <td colSpan={9} className="px-2.5 pb-2.5 pt-0">
                          <DisclosurePanel fields={notes} />
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}

              <tr className="charge-summary-totals-row border-t-[5px] border-trust/70 bg-trust-soft align-top text-navy">
                <td className="border-r border-trust/10 px-4 py-5 text-left align-top">
                  <div className="text-[16px] font-black uppercase tracking-[0.16em]">Totals</div>
                  <div className="mt-1.5 text-[8px] font-medium leading-tight text-navy/55">
                    Full invoice reviewed vs. market-supported scope
                  </div>
                </td>
                <td className="border-r border-trust/10 px-2 py-5 align-top" />
                <td className="border-r border-trust/10 px-3 py-5 align-top" />
                <td className="border-r border-trust/10 px-2 py-5 text-right align-top">
                  <div
                    className="charge-total-value font-black tabular-nums text-executive-red"
                    {...summaryField("chargesReviewedTotal", invoiceSum)}
                  >
                    {showTotals ? currency(invoiceSum) : WITHHELD}
                  </div>
                  <div className="mt-2 min-h-[2.5em] text-[7.5px] font-medium leading-tight text-navy/45">
                    Charges Reviewed Subtotal
                  </div>
                </td>
                <td className="border-r border-trust/10 px-3.5 py-5 align-top" />
                <td className="border-r border-trust/10 px-2 py-5 text-right align-top">
                  <div
                    className="charge-total-value font-black tabular-nums text-executive-red"
                    /* Scope-local total: the vendor amount for charges priced
                       by the Chapter 02 benchmark + AI research scope. It is a
                       different (wider) scope than
                       ReportSummary.vendorTotalWithinMarketScope, so it is
                       neither tagged nor treated as a report-wide duplicate. */
                    data-coverage-ignore="chapter02-supported-scope-subtotal"
                  >
                    {showTotals ? currency(supportedInvoiceSum) : WITHHELD}
                  </div>
                  <div className="mt-2 min-h-[2.5em] text-[7.5px] font-medium leading-tight text-navy/45">
                    Market-Supported Vendor Amount
                  </div>
                </td>
                <td className="border-r border-trust/10 px-2 py-5 text-right align-top">
                  <div className="charge-total-value font-black tabular-nums text-executive-blue">
                    {showTotals ? currency(supportedExpectedSum) : WITHHELD}
                  </div>
                  <div className="mt-2 min-h-[2.5em] text-[7.5px] font-medium leading-tight text-navy/45">
                    Market-Supported Supported Amount
                  </div>
                </td>
                <td className="border-r border-trust/10 px-2 py-5 text-right align-top">
                  <div
                    className={`charge-total-value font-black tabular-nums ${
                      diffSum > 0 ? "text-savings" : diffSum < 0 ? "text-navy/60" : ""
                    }`}
                  >
                    {showTotals ? `${diffSum > 0 ? "+" : ""}${currency(diffSum)}` : WITHHELD}
                  </div>
                  <div className="mt-2 min-h-[2.5em] text-[7.5px] font-medium leading-tight text-navy/45">
                    Total Supported Recovery Within Supported Scope
                  </div>

                </td>
                <td className="px-2.5 py-5" />
              </tr>


            </tbody>
          </table>
        </div>

        {pendingEvidenceCount > 0 ? (
          <p className="mt-2.5 text-[10px] leading-relaxed text-navy/70">
            {pendingEvidenceCount} charge{pendingEvidenceCount === 1 ? "" : "s"} (
            {currency(pendingEvidenceAmount)}
            {pendingEvidenceCount === 1 ? ") lacks" : ") lack"} sufficient comparable pricing and{" "}
            {pendingEvidenceCount === 1 ? "is" : "are"} excluded from benchmark totals pending
            defensible evidence. {pendingEvidenceCount === 1 ? "It remains" : "They remain"} under
            review in this report.
          </p>
        ) : null}

        {clarificationCount > 0 ? (
          <p className="mt-2 text-[10px] leading-relaxed text-navy/70">
            {clarificationCount} printed line{clarificationCount === 1 ? "" : "s"} (
            {currency(clarificationAmount)}
            {clarificationCount === 1 ? ") repeats" : ") repeat"} an earlier printed charge and{" "}
            {clarificationCount === 1 ? "is" : "are"} classified{" "}
            <span className="font-semibold text-navy">
              Possible Duplicate Billing — Vendor Clarification Required
            </span>
            . {clarificationCount === 1 ? "It remains" : "They remain"} listed in full above and{" "}
            {clarificationCount === 1 ? "is" : "are"} excluded from quantified recovery until the
            vendor confirms {clarificationCount === 1 ? "it" : "them"}.
          </p>
        ) : null}


        {printedFinancials ? (
          <div
            className="mt-3 max-w-[68ch] rounded-lg border border-trust/20 bg-trust-soft/40 px-3 py-2 text-[10px] leading-relaxed text-navy/75"
            data-coverage-ignore="chapter02-printed-invoice-reconciliation"
          >
            <div className="font-semibold uppercase tracking-[0.08em] text-navy/70">
              Printed Invoice Reconciliation
            </div>
            <span className="mt-1 block">
              {printedFinancials.lineCount} printed charge
              {printedFinancials.lineCount === 1 ? "" : "s"} ·{" "}
              {currency(printedFinancials.subtotal)} subtotal
              {printedFinancials.tax !== null ? (
                <> + {currency(printedFinancials.tax)} tax</>
              ) : null}
              {printedFinancials.total !== null ? (
                <> = {currency(printedFinancials.total)} invoice total</>
              ) : null}
              {printedFinancials.taxSource === "derived"
                ? " (tax derived from the printed total)"
                : ""}
              . Printed invoice values are reported separately from analysis scope.
            </span>
          </div>
        ) : null}

        {/* Presentation-only disclosure. Every figure below is already
            established elsewhere in the report: the two charge-scope subtotals
            come from the Chapter 02 table above, the tax comes from the Layer 1
            printed reconciliation, and the total is the canonical Report
            Summary Estimated Fair Payment. Nothing is recalculated here. */}
        {showTotals && printedFinancials && printedFinancials.tax !== null ? (
          <div
            className="mt-3 max-w-[68ch] rounded-xl border border-trust/20 bg-card px-4 py-3.5 shadow-card"
            data-pdf-keep-together="true"
          >
            <div className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-trust">
              Estimated Fair Payment Breakdown
            </div>
            <dl className="mt-3 space-y-1.5 text-[11px] leading-relaxed text-navy/80">
              <div className="flex items-baseline justify-between gap-4">
                <dt>Market-Supported Charges</dt>
                <dd
                  className="m-0 font-mono font-semibold tabular-nums text-navy"
                  {...chapterMetric("chapter-02", "fairPaymentSupportedCharges", supportedExpectedSum)}
                >
                  {fmtMoneyFixed(supportedExpectedSum)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt>Charge{pendingEvidenceCount === 1 ? "" : "s"} Pending Additional Market Support</dt>
                <dd
                  className="m-0 font-mono font-semibold tabular-nums text-navy"
                  {...chapterMetric("chapter-02", "fairPaymentPendingCharges", pendingEvidenceAmount)}
                >
                  {fmtMoneyFixed(pendingEvidenceAmount)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4">
                <dt>Printed Invoice Tax</dt>
                <dd
                  className="m-0 font-mono font-semibold tabular-nums text-navy"
                  {...chapterMetric("chapter-02", "fairPaymentPrintedTax", printedFinancials.tax)}
                >
                  {fmtMoneyFixed(printedFinancials.tax)}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-4 border-t border-trust/25 pt-2 text-[12px]">
                <dt className="font-bold uppercase tracking-[0.06em] text-navy">
                  Estimated Fair Payment
                </dt>
                <dd
                  className="m-0 font-mono font-black tabular-nums text-executive-blue"
                  {...summaryField("supportedAmount", rs.financials.supportedAmount)}
                >
                  {fmtMoneyFixed(rs.financials.supportedAmount)}
                </dd>
              </div>
            </dl>
            <p className="mt-2.5 text-[10px] leading-relaxed text-navy/65">
              The Estimated Fair Payment combines the verified market-supported charges, any
              charges still pending additional market support, and the tax reported on the
              printed invoice. Taxes are reported from the invoice and are not evaluated against
              market pricing.
            </p>
          </div>
        ) : null}

        <div className="mt-2 max-w-[68ch] space-y-1.5 text-[10px] leading-relaxed text-navy/70">
          <p>
            Total Supported Recovery compares the {currency(supportedInvoiceSum)} Vendor Amount with the{" "}
            {currency(supportedExpectedSum)} Supported Amount, within market-supported scope only.
          </p>
          <p>
            The {currency(invoiceSum)} Charges Reviewed Subtotal is shown for context. Charge-level
            evidence appears in{" "}
            <span className="font-medium text-navy">Market Benchmark Analysis</span> (
            {chapterRef("chapter-03")}).
          </p>
        </div>




      </div>

      <div
        className="grid items-stretch gap-4 [grid-template-columns:repeat(auto-fit,minmax(16rem,1fr))]"
        data-pdf-keep-together="true"
      >
        <ChargeContributionRanking rows={rows} invoiceSum={invoiceSum} />

        {/* Rendered only when a verified storage overcharge is objectively
            calculable: a storage row exists, it carries a benchmarked expected
            amount, and the difference is above $0. Within-market, below-market,
            $0-recovery, missing-storage and insufficient-data cases render
            nothing at all — the row then collapses to two even columns via the
            auto-fit grid above, with no reserved or hidden space. The storage
            result itself is already stated in the benchmark table. */}
        {storageRow &&
        storageRow.expectedAmount !== null &&
        storageRow.difference !== null &&
        storageRow.difference > 0 ? (
          <div className="flex h-full flex-col rounded-xl border border-trust/20 bg-card p-5 shadow-card">
            <div className="text-center text-[11px] font-extrabold uppercase tracking-[0.08em] text-trust">
              Storage Overcharge (Total Storage)
            </div>
            <div className="mt-4 flex flex-1 flex-col items-center justify-center">
              <div className="w-full max-w-[220px] space-y-1 text-center">
                <div className="text-[9px] font-extrabold uppercase tracking-[0.08em] text-navy/60">
                  Invoice Storage
                </div>
                <div className="font-mono text-[18px] font-bold text-navy tabular-nums">
                  {currency(storageRow.invoiceAmount ?? 0)}
                </div>
              </div>

              <div className="my-1 flex items-center justify-center">
                <span className="text-[18px] font-bold text-navy/45">−</span>
              </div>

              <div className="w-full max-w-[220px] space-y-1 text-center">
                <div className="text-[9px] font-extrabold uppercase tracking-[0.08em] text-navy/60">
                  Expected Market
                </div>
                <div className="font-mono text-[18px] font-bold text-savings tabular-nums">
                  {currency(storageRow.expectedAmount ?? 0)}
                </div>
              </div>

              <div className="my-2 w-full max-w-[220px]">
                <div className="h-px bg-navy/20" />
              </div>

              <div className="w-full max-w-[220px] space-y-1 text-center">
                <div className="text-[9px] font-extrabold uppercase tracking-[0.08em] text-destructive">
                  Storage Overcharge
                </div>
                <div className="font-mono text-[26px] font-extrabold text-destructive tabular-nums leading-none">
                  {currency(storageRow.difference)}
                </div>
              </div>
            </div>
            {storageRow.qty !== null && (
              <div className="mt-2 text-center text-[11px] font-bold text-navy/65">
                For {storageRow.qty} {storageRow.qty === 1 ? "day" : "days"} of storage
              </div>
            )}
          </div>
        ) : null}


        <div className="flex h-full flex-col rounded-xl border-2 border-savings/40 bg-savings/[0.06] p-5 shadow-card">
          <div className="text-center text-[11px] font-extrabold uppercase tracking-[0.08em] text-trust">
            Total Supported Recovery Identified
          </div>
          <div className="mt-1 text-center text-[10px] font-semibold text-trust/80">
            (Based on Verified Market Analysis)
          </div>
          <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-2">
            <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-savings/25 bg-savings/10 text-savings">
              <Scale className="h-7 w-7" />
            </div>
            <div className="text-center">
              <div
                className="font-mono text-[40px] font-extrabold leading-none text-savings tabular-nums"
                {...summaryField("potentialSavings", rs.financials.potentialSavings)}
              >
                {currency(rs.financials.potentialSavings)}
              </div>
              <div className="mt-1.5 text-[13px] font-bold text-navy/70">
                Total supported recovery based on verified market analysis.
              </div>
            </div>
          </div>

          <dl className="mt-4">
            <div className="flex items-baseline justify-between gap-3 rounded-lg border border-savings/30 bg-savings/[0.1] px-3 py-2.5">
              <dt className="text-[10px] font-extrabold uppercase tracking-[0.06em] text-navy/80">
                Total Supported Recovery
              </dt>
              <dd
                className="m-0 font-mono text-[17px] font-extrabold tabular-nums text-savings"
                {...summaryField("potentialSavings", rs.financials.potentialSavings)}
              >
                {currency(rs.financials.potentialSavings)}
              </dd>
            </div>
            <div className="mt-3 space-y-1.5 border-t border-savings/20 pt-3">

              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[9px] font-extrabold uppercase tracking-[0.06em] text-navy/55">
                  Market-Supported Charges
                </dt>
                <dd className="m-0 font-mono text-[11px] font-bold tabular-nums text-navy/85">
                  {supportedRows.length} of {rows.length}
                </dd>
              </div>
              <div className="flex items-baseline justify-between gap-3">
                <dt className="text-[9px] font-extrabold uppercase tracking-[0.06em] text-navy/55">
                  Evidence Coverage
                </dt>
                <dd className="m-0 font-mono text-[11px] font-bold tabular-nums text-navy/85">
                  {invoiceSum > 0
                    ? `${Math.round((supportedInvoiceSum / invoiceSum) * 100)}% of invoice value`
                    : "—"}
                </dd>
              </div>
            </div>
          </dl>
        </div>

      </div>
    </div>
  );
}

/* 3. Charge Breakdown                                         */
/* ─────────────────────────────────────────────────────────── */

type Assessment =
  | "Within Market"
  | "Above Market"
  | "Materially Above Market"
  | "Fair"
  | "Within Expected Range"
  | "Negotiable"
  | "Questionable"
  | "Excessive"
  | "Duplicate"
  | "Unsupported"
  | "Needs Documentation";

interface ChargeRow {
  description: string;
  category: string;
  amount: number;
  assessment: Assessment;
  severity: "none" | "low" | "medium" | "high" | "critical";
  chip: SubmissionStatus;
  whyItMatters: string;
  evidence: string | null;
  marketNote: string | null;
  legalNote: string | null;
  recommendedAction: string;
  savingsImpact: [number, number] | null;
}

const DEMO_CHARGE_ROWS: ChargeRow[] = [
  {
    description: "Storage Rate — 28 days",
    category: "Storage",
    amount: 4185,
    assessment: "Excessive",
    severity: "critical",
    chip: "findings_ready",
    whyItMatters: "Daily rate is materially above local ZIP averages, inflating the invoice total.",
    evidence: "12 local facility comparisons within the same ZIP code.",
    marketNote: "~43% above local median daily rate.",
    legalNote: "Reasonableness standard applies under state code.",
    recommendedAction: "Request rate adjustment to local market median.",
    savingsImpact: [820, 1120],
  },
  {
    description: "Administrative Fee",
    category: "Fee",
    amount: 125,
    assessment: "Unsupported",
    severity: "high",
    chip: "findings_ready",
    whyItMatters: "No authorization or itemization is on file to justify this charge.",
    evidence: "Invoice contains no supporting contract clause.",
    marketNote: null,
    legalNote: "Undisclosed fees are frequently unenforceable.",
    recommendedAction: "Demand documentation or removal of the fee.",
    savingsImpact: [100, 125],
  },
  {
    description: "Environmental Fee",
    category: "Fee",
    amount: 75,
    assessment: "Negotiable",
    severity: "medium",
    chip: "ai_review_in_progress",
    whyItMatters: "Undocumented pass-through fee commonly waived on request.",
    evidence: "Fee not itemized against a specific service.",
    marketNote: null,
    legalNote: null,
    recommendedAction: "Negotiate reduction or waiver.",
    savingsImpact: [40, 75],
  },
  {
    description: "Lien Fee",
    category: "Fee",
    amount: 15,
    assessment: "Fair",
    severity: "none",
    chip: "completed",
    whyItMatters: "Consistent with standard industry practice.",
    evidence: null,
    marketNote: null,
    legalNote: null,
    recommendedAction: "No action required.",
    savingsImpact: null,
  },
  {
    description: "Release Fee",
    category: "Fee",
    amount: 45,
    assessment: "Within Expected Range",
    severity: "low",
    chip: "completed",
    whyItMatters: "Typical release processing charge; within regional norms.",
    evidence: null,
    marketNote: null,
    legalNote: null,
    recommendedAction: "No action required.",
    savingsImpact: null,
  },
];

function pickString(o: Record<string, unknown>, keys: string[]): string | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return undefined;
}
function pickNumber(o: Record<string, unknown>, keys: string[]): number | undefined {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === "number" && !Number.isNaN(v)) return v;
    if (typeof v === "string") {
      const n = Number(v.replace(/[^0-9.\-]/g, ""));
      if (!Number.isNaN(n) && n !== 0) return n;
    }
  }
  return undefined;
}

function normalizeSeverity(sev: string | null | undefined): ChargeRow["severity"] {
  const v = (sev ?? "").toLowerCase();
  if (v === "critical") return "critical";
  if (v === "high") return "high";
  if (v === "medium" || v === "moderate") return "medium";
  if (v === "low") return "low";
  return "none";
}

function classifyAssessment(
  finding: FindingItem | undefined,
  severity: ChargeRow["severity"],
): Assessment {
  if (!finding) {
    return severity === "low" ? "Within Market" : "Within Market";
  }
  const blob =
    `${finding.title ?? ""} ${finding.description ?? ""} ${finding.category ?? ""}`.toLowerCase();
  if (
    blob.includes("duplicate") ||
    blob.includes("duplicated") ||
    blob.includes("double-charged") ||
    blob.includes("double charged")
  )
    return "Duplicate";
  if (
    blob.includes("unauthoriz") ||
    blob.includes("unsupported") ||
    blob.includes("no authorization") ||
    blob.includes("not authorized")
  )
    return "Unsupported";
  if (
    blob.includes("documentation") ||
    blob.includes("undocumented") ||
    blob.includes("missing invoice") ||
    blob.includes("no receipt")
  )
    return "Needs Documentation";
  if (severity === "critical" || severity === "high") {
    if (blob.includes("above market") || blob.includes("excess") || blob.includes("overcharge"))
      return "Materially Above Market";
    return "Above Market";
  }
  if (severity === "medium") return "Above Market";
  if (severity === "low") return "Within Market";
  return "Within Market";
}

function severityToChip(sev: ChargeRow["severity"]): SubmissionStatus {
  if (sev === "critical" || sev === "high") return "findings_ready";
  if (sev === "medium") return "ai_review_in_progress";
  return "completed";
}

function recommendActionFor(a: Assessment): string {
  switch (a) {
    case "Materially Above Market":
      return "Adjustment recommended";
    case "Above Market":
      return "Adjustment recommended";
    case "Excessive":
      return "Adjustment recommended";
    case "Duplicate":
      return "Remove duplicate charge";
    case "Unsupported":
      return "Documentation required";
    case "Needs Documentation":
      return "Documentation required";
    case "Questionable":
      return "Adjustment recommended";
    case "Negotiable":
      return "Adjustment recommended";
    case "Within Market":
    case "Within Expected Range":
    case "Fair":
    default:
      return "No adjustment recommended";
  }
}

/** True when the assessment implies a real recovery opportunity. */
function assessmentImpliesAdjustment(a: Assessment): boolean {
  return (
    a === "Materially Above Market" ||
    a === "Above Market" ||
    a === "Excessive" ||
    a === "Duplicate" ||
    a === "Unsupported" ||
    a === "Needs Documentation" ||
    a === "Questionable" ||
    a === "Negotiable"
  );
}

function findMarketNote(
  description: string,
  markets: MarketComparisonRow[] | undefined,
): string | null {
  if (!markets || markets.length === 0) return null;
  const key = description.toLowerCase();
  const m = markets.find((x) => (x.line_item ?? "").toLowerCase().includes(key.slice(0, 12)));
  if (!m) return null;
  if (typeof m.difference_percent === "number" && Math.abs(m.difference_percent) >= 1) {
    const sign = m.difference_percent > 0 ? "above" : "below";
    return `${Math.abs(Math.round(m.difference_percent))}% ${sign} local ${m.zip ?? m.zip_code ?? "market"} median.`;
  }
  if (typeof m.market_low === "number" && typeof m.market_high === "number") {
    return `Local range: ${currency(m.market_low)} – ${currency(m.market_high)}.`;
  }
  return m.assessment ?? null;
}

/**
 * FINANCIAL DETERMINISM — per-charge recoverable dollars.
 *
 * Charge-level savings shown in the report come exclusively from the
 * deterministic recovery engine (canonical invoice × benchmark snapshot).
 * Neither AI severity heuristics nor `evidence.estimated_savings` may
 * produce a dollar figure here.
 */
function deterministicChargeRecovery(real: {
  lineItems: Array<Record<string, unknown>>;
  marketComparisons?: MarketComparisonRow[];
  marketResearch?: MarketResearch[];
}): Map<string, number[]> {
  const byDescription = new Map<string, number[]>();
  const lineItems = real.lineItems ?? [];
  if (lineItems.length === 0) return byDescription;
  const detail = computeDeterministicRecovery({
    lineItems,
    marketComparisons: (real.marketComparisons ?? []) as unknown as MarketComparisonRow[],
    marketResearch: real.marketResearch ?? [],
  });
  for (const line of detail.lines) {
    const key = line.printedDescription.trim().toLowerCase();
    const bucket = byDescription.get(key) ?? [];
    bucket.push(line.eligible ? Math.max(0, Math.round(line.recoveryAmount)) : 0);
    byDescription.set(key, bucket);
  }
  return byDescription;
}

/** Pops the next deterministic recovery for a printed description (duplicate-safe). */
function takeChargeRecovery(
  map: Map<string, number[]>,
  description: string,
): [number, number] | null {
  const key = description.trim().toLowerCase();
  const bucket = map.get(key);
  if (!bucket || bucket.length === 0) return null;
  const value = bucket.shift() ?? 0;
  if (value <= 0) return null;
  return [value, value];
}


function evidenceLine(finding: FindingItem | undefined): string | null {
  if (!finding) return null;
  const ev = finding.evidence as Record<string, unknown> | undefined;
  if (ev && typeof ev === "object") {
    const source = pickString(ev, ["source", "reference", "citation"]);
    const count = pickNumber(ev, ["comparisons", "sources_count", "references"]);
    if (source) return source;
    if (count) return `${count} supporting reference${count === 1 ? "" : "s"} on file.`;
  }
  return finding.description ? "Analyst notes on file." : null;
}





function MarketStage({
  index,
  label,
  description,
  children,
  terminal = false,
}: {
  index: string;
  label: string;
  description: string;
  children: React.ReactNode;
  terminal?: boolean;
}) {
  return (
    <section
      className={
        terminal
          ? "pt-14 mt-14 border-t border-border"
          : "pt-14 mt-14 border-t border-border first:mt-0 first:border-t-0 first:pt-0"
      }
    >
      <div className="mb-6 flex items-start gap-4">
        <div className="flex h-11 w-11 flex-none items-center justify-center rounded-md border border-navy/20 bg-navy text-[12px] font-semibold tracking-[0.16em] text-navy-foreground shadow-card">
          {index}
        </div>
        <div className="min-w-0 flex-1 pt-0.5">
          <h3 className="font-display text-2xl font-semibold leading-tight text-navy md:text-[1.7rem]">
            {label}
          </h3>
          <p className="mt-1.5 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {description}
          </p>
        </div>
      </div>

      <div>{children}</div>
    </section>
  );
}

/**
 * Shared market summary source of truth for Chapter 03.
 *
 * Both the Executive Market Overview and the Executive Analyst Summary
 * MUST derive their numbers from this helper. Do not recalculate
 * above-market counts, largest variance, average variance, recovery
 * range, or negotiation strength locally in either section.
 *
 * "Above market" is normalized via the same assessment logic used by
 * the charge cards (`buildMarketRows` → `classifyVariance`). A row with
 * `difference_percent > 0` but a within-tolerance assessment does NOT
 * count as above market.
 */
export interface MarketSummary {
  aboveCount: number;
  totalReviewed: number;
  largestVarianceCharge: string | null;
  largestVariancePercent: number | null;
  averageAboveMarketVariance: number | null;
  recoveryLow: number;
  recoveryHigh: number;
  negotiationStrength: "Strong" | "Moderate" | "Limited" | "Minimal";
  assessmentSummary: string;
}

export function deriveMarketSummary(
  marketComparisons: MarketComparisonRow[],
  findingItems: FindingItem[],
  chargeData: {
    lineItems: Array<Record<string, unknown>>;
    fees: Array<Record<string, unknown>>;
    findingItems: FindingItem[];
    marketComparisons?: MarketComparisonRow[];
    marketResearch?: MarketResearch[];
    legalResearchCount?: number;
    legalResearch?: LegalResearch[];
  } | null,
): MarketSummary {
  const marketRows = buildMarketRows(marketComparisons ?? [], findingItems);
  const flagged = marketRows.filter(
    (row) => row.assessment === "Above Market" || row.assessment === "Materially Above Market",
  );
  const marketLow = flagged.reduce((s, x) => s + x.savingsLow, 0);
  const marketHigh = flagged.reduce((s, x) => s + x.savingsHigh, 0);

  const chargeRows: ChargeRow[] = chargeData ? buildRealChargeRows(chargeData) : [];
  const questionable = chargeRows.filter(
    (r) => r.severity === "critical" || r.severity === "high" || r.severity === "medium",
  );
  const chargeLow = questionable.reduce(
    (s, r) => s + (r.savingsImpact ? r.savingsImpact[0] : 0),
    0,
  );
  const chargeHigh = questionable.reduce(
    (s, r) => s + (r.savingsImpact ? r.savingsImpact[1] : 0),
    0,
  );
  // NOTE: This is NOT true deduplication of overlapping charges — it is the
  // higher of the two independently computed ranges. Copy that describes
  // this metric must not claim mathematical deduplication.
  const recoveryLow = Math.max(marketLow, chargeLow);
  const recoveryHigh = Math.max(marketHigh, chargeHigh);

  const worst = [...marketRows].sort(
    (a, b) => (b.variancePct ?? -Infinity) - (a.variancePct ?? -Infinity),
  )[0];
  const largestVariancePercent =
    worst && typeof worst.variancePct === "number" && worst.variancePct > 0
      ? Math.round(worst.variancePct)
      : null;
  const largestVarianceCharge = largestVariancePercent !== null && worst ? worst.service : null;

  const positiveVariances = flagged
    .map((r) => r.variancePct)
    .filter((v): v is number => typeof v === "number" && v > 0);
  const averageAboveMarketVariance =
    positiveVariances.length > 0
      ? Math.round(positiveVariances.reduce((s, v) => s + v, 0) / positiveVariances.length)
      : null;

  const avg = averageAboveMarketVariance ?? 0;
  const forensicSupport = questionable.length > 0;
  let negotiationStrength: MarketSummary["negotiationStrength"];
  if (avg >= 50 && flagged.length >= 2 && forensicSupport) {
    negotiationStrength = "Strong";
  } else if (avg >= 25 && flagged.length >= 1) {
    negotiationStrength = "Moderate";
  } else if (flagged.length > 0 || forensicSupport) {
    negotiationStrength = "Limited";
  } else {
    negotiationStrength = "Minimal";
  }

  const assessmentSummary =
    negotiationStrength === "Strong"
      ? "Evidence supports a strong market-based negotiation position."
      : negotiationStrength === "Moderate"
        ? "Evidence supports a credible negotiation position, although some findings carry limited support."
        : negotiationStrength === "Limited"
          ? "The available benchmark evidence identifies selected concerns but does not support a broad adjustment."
          : "The available evidence does not currently support a material market-based adjustment.";

  return {
    aboveCount: flagged.length,
    totalReviewed: marketRows.length,
    largestVarianceCharge,
    largestVariancePercent,
    averageAboveMarketVariance,
    recoveryLow,
    recoveryHigh,
    negotiationStrength,
    assessmentSummary,
  };
}

/**
 * Overall Market Conclusion — deterministic synthesis of the market and
 * forensic evidence surfaced above. Intentionally consumes the same inputs
 * already used by MarketComparison and ChargeBreakdown so numbers never drift.
 */
function MarketConclusion({
  marketComparisons,
  findingItems,
  chargeData,
  zip,
  state,
  variant = "conclusion",
}: {
  marketComparisons: MarketComparisonRow[];
  findingItems: FindingItem[];
  chargeData: {
    lineItems: Array<Record<string, unknown>>;
    fees: Array<Record<string, unknown>>;
    findingItems: FindingItem[];
    marketComparisons?: MarketComparisonRow[];
    marketResearch?: MarketResearch[];
    legalResearchCount?: number;
    legalResearch?: LegalResearch[];
  } | null;
  zip?: string | null;
  state?: string | null;
  variant?: "conclusion" | "overview";
}) {
  // Report-wide statistics come from the immutable Report Summary only.
  const rs = useReportSummary();
  const combinedLow = rs.financials.recoveryLow;
  const combinedHigh = rs.financials.recoveryHigh;

  // Chapter-scoped benchmark statistics — never promoted to ReportSummary.
  const marketRows = buildMarketRows(marketComparisons, findingItems);
  const flaggedMarket = marketRows.filter(
    (row) => row.assessment === "Above Market" || row.assessment === "Materially Above Market",
  );

  const chargeRows: ChargeRow[] = chargeData ? buildRealChargeRows(chargeData) : [];
  const questionable = chargeRows.filter(
    (r) => r.severity === "critical" || r.severity === "high" || r.severity === "medium",
  );


  const topCharge = [...chargeRows].sort(
    (a, b) => (b.savingsImpact?.[1] ?? 0) - (a.savingsImpact?.[1] ?? 0),
  )[0];
  const worstVariance = [...marketRows].sort(
    (a, b) => (b.variancePct ?? -Infinity) - (a.variancePct ?? -Infinity),
  )[0];

  const stance: "leverage" | "neutral" | "fair" =
    combinedHigh >= 500 || flaggedMarket.length >= 2 || questionable.length >= 3
      ? "leverage"
      : combinedHigh > 0 || questionable.length > 0
        ? "neutral"
        : "fair";

  const worstPct =
    worstVariance && typeof worstVariance.variancePct === "number" && worstVariance.variancePct > 0
      ? Math.round(worstVariance.variancePct)
      : null;
  /* Report-level financial consistency: the narrative quotes the ONE canonical
     Total Supported Recovery published by the Report Summary. It never restates
     a locally combined low/high range, which contradicted Chapters 10-12. */
  const rangeText =
    rs.financials.potentialSavings > 0 ? currency(rs.financials.potentialSavings) : null;
  /* Executive scope sentence — every count is read from the immutable Report
     Summary so the prose can never disagree with the Review Scope tiles.
     "Reviewed" = every billed charge; "benchmarked" = charges carrying usable
     market evidence; the remainder is pending comparable pricing. */
  const reviewedCount = rs.counts.totalInvoiceCharges;
  const benchmarkedCount = rs.counts.marketSupportedCharges;
  const aboveCanonical = rs.counts.chargesAboveMarket;
  const pendingCount = rs.counts.pendingComparablePricing;
  const scopeSentence =
    `${reviewedCount} invoice charge${reviewedCount === 1 ? " was" : "s were"} reviewed. ` +
    `${benchmarkedCount} could be benchmarked against verified local market data` +
    (pendingCount > 0
      ? `; the remaining ${pendingCount} require additional comparable market evidence.`
      : `.`);
  const verdict =
    stance === "leverage"
      ? `${scopeSentence} ${aboveCanonical} benchmarked charge${aboveCanonical === 1 ? "" : "s"} exceed${aboveCanonical === 1 ? "s" : ""} local pricing${worstPct !== null && worstVariance ? `. ${worstVariance.service} is the single largest pricing variance at +${worstPct}% above market` : ""}. Total Supported Recovery${rangeText ? ` of ${rangeText}` : ""} represents the combined financial impact of the verified findings summarized throughout this chapter. It is calculated using verified market pricing, duplicate billing findings, and other supported evidence contained throughout this report. This is not a guaranteed settlement amount.`
      : stance === "neutral"
        ? `${scopeSentence} The benchmarked charges are largely consistent with local pricing, though ${questionable.length} charge${questionable.length === 1 ? "" : "s"} warrant targeted adjustment${worstPct !== null && worstVariance ? `. ${worstVariance.service} is the single largest pricing variance at +${worstPct}% above market` : ""}. Total Supported Recovery${rangeText ? ` of ${rangeText}` : ""} represents the combined financial impact of the verified findings summarized throughout this chapter. It is calculated using verified market pricing, duplicate billing findings, and other supported evidence contained throughout this report. This is not a guaranteed settlement amount.`
        : `${scopeSentence} The benchmarked charges are broadly consistent with local benchmarks and forensic standards. No supported recovery identified across the reviewed charges.`;



  const stanceTone =
    stance === "leverage"
      ? "border-savings/30 bg-savings-soft text-savings"
      : stance === "neutral"
        ? "border-warn/30 bg-warn/10 text-warn"
        : "border-trust/30 bg-trust-soft text-trust";
  const stanceLabel =
    stance === "leverage"
      ? "Negotiation Recommended"
      : stance === "neutral"
        ? "Targeted Adjustments Recommended"
        : "Aligned with Market";

  const isOverview = variant === "overview";
  const tileBase = isOverview
    ? "flex h-full min-w-0 flex-col rounded-xl border border-border/60 bg-card p-5 shadow-sm"
    : "flex h-full min-w-0 flex-col rounded-xl border border-white/10 bg-white/5 p-4";
  const eyebrowCls = isOverview
    ? "min-w-0 text-[9.5px] font-semibold uppercase leading-[1.35] tracking-[0.12em] text-trust [overflow-wrap:anywhere]"
    : "text-[10px] font-semibold uppercase tracking-[0.16em] text-navy-foreground/60";
  const subCls = isOverview
    ? "mt-2 max-w-full text-xs leading-relaxed text-muted-foreground [overflow-wrap:anywhere]"
    : "mt-1 text-xs text-navy-foreground/70";

  const IconBadge = ({
    icon: Icon,
    tone,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    tone: "trust" | "savings";
  }) => (
    <span
      className={`flex h-9 w-9 flex-none items-center justify-center rounded-full ${
        tone === "savings" ? "bg-savings/15 text-savings" : "bg-trust/15 text-trust"
      }`}
    >
      <Icon className="h-4 w-4" />
    </span>
  );

  /**
   * Overview tiles put the icon on the eyebrow row so the metric value and
   * its supporting caption get the full tile width. Presentation only.
   */
  const TileHead = ({
    icon,
    tone = "trust",
    label,
    labelCls,
    tooltip,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    tone?: "trust" | "savings";
    label: string;
    labelCls?: string;
    tooltip?: string;
  }) => (
    <div className="flex items-center gap-2.5">
      <IconBadge icon={icon} tone={tone} />
      {tooltip ? (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className={`flex items-center gap-1.5 cursor-help ${labelCls ?? eyebrowCls}`}>
                {label}
                <Info className="h-3.5 w-3.5 opacity-70" />
              </div>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs">
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        <div className={labelCls ?? eyebrowCls}>{label}</div>
      )}
    </div>
  );

  return (
    <div
      {...summarySection("Chapter 02 — Market Benchmark Analysis")}
      className={`rounded-2xl border p-6 md:p-8 ${isOverview ? "mb-8 border-trust/25 bg-trust-soft/30 text-navy shadow-card" : "border-navy/15 bg-navy text-navy-foreground shadow-elevated"}`}
    >
      <div className="flex flex-wrap items-start gap-3">
        {isOverview && (
          <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-trust text-white">
            <BarChart3 className="h-5 w-5" />
          </span>
        )}
        <div className="min-w-0 flex-1">
          <div
            className={`flex flex-wrap items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.18em] ${isOverview ? "text-trust" : "text-navy-foreground/70"}`}
          >
            {!isOverview && <Scale className="h-3.5 w-3.5" />}
            {isOverview ? "Executive Market Overview" : "Executive Market Assessment"}
          </div>
          {isOverview && (
            <h3 className="mt-2 font-display text-[1.75rem] leading-tight tracking-tight text-navy md:text-[1.9rem]">
              Executive Market Assessment
            </h3>
          )}
        </div>
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${stanceTone}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {stanceLabel}
        </span>
      </div>
      <p
        className={`mt-5 max-w-3xl text-[15px] leading-relaxed ${isOverview ? "text-muted-foreground" : "font-display text-2xl leading-snug md:text-[1.65rem] text-navy-foreground"}`}
        style={isOverview ? undefined : { color: "var(--color-navy-foreground)" }}
      >
        {verdict}
      </p>

      <div className={`mt-6 grid grid-cols-1 items-stretch gap-4 sm:grid-cols-2 ${isOverview ? "xl:grid-cols-[1fr_1fr_1.15fr_1fr]" : "xl:grid-cols-4"}`}>
        <div className={tileBase}>
          {isOverview ? (
            <div className="flex h-full min-w-0 flex-col">
              <TileHead icon={BarChart3} label="Market variance" />
              <div className="metric-value-fit mt-3 max-w-full text-navy">
                <span><span {...summaryField("chargesAboveMarket", rs.counts.chargesAboveMarket)}>{rs.counts.chargesAboveMarket}</span>{" of "}<span {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}>{rs.counts.marketSupportedCharges}</span>{" benchmarked above market"}</span>
              </div>
              <div className={subCls}>
                {worstVariance &&
                worstVariance.variancePct !== null &&
                worstVariance.variancePct > 0 ? (
                  <>
                    <span className="block font-semibold">Largest single pricing outlier</span>
                    <span className="block">
                      {worstVariance.service} +{Math.round(worstVariance.variancePct)}% above market
                    </span>
                  </>
                ) : (
                  "No material variances flagged."
                )}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className={eyebrowCls}>Market variance</div>
                <div className="metric-value-fit mt-1 max-w-full text-navy">
                  <span><span {...summaryField("chargesAboveMarket", rs.counts.chargesAboveMarket)}>{rs.counts.chargesAboveMarket}</span>{" of "}<span {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}>{rs.counts.marketSupportedCharges}</span>{" benchmarked above market"}</span>
                </div>
                <div className={subCls}>
                  {worstVariance &&
                  worstVariance.variancePct !== null &&
                  worstVariance.variancePct > 0 ? (
                    <>
                      <span className="block font-semibold">Largest single pricing outlier</span>
                      <span className="block">
                        {worstVariance.service} +{Math.round(worstVariance.variancePct)}% above
                        market
                      </span>
                    </>
                  ) : (
                    "No material variances flagged."
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <div className={tileBase}>
          {isOverview ? (
            <div className="flex h-full min-w-0 flex-col">
              <TileHead icon={Flag} label="Forensic findings" />
              <div className="metric-value-fit mt-3 max-w-full text-navy">
                <span {...chapterMetric("chapter-03", "forensicChargesFlagged", questionable.length)}>{questionable.length} charge{questionable.length === 1 ? "" : "s"} flagged</span>
              </div>
              <div className={subCls}>
                {topCharge && topCharge.savingsImpact
                  ? `Top impact: ${topCharge.description}`
                  : "No material charge-level impacts."}
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className={eyebrowCls}>Forensic findings</div>
                <div className="metric-value-fit mt-1 max-w-full text-navy">
                  <span {...chapterMetric("chapter-03", "forensicChargesFlagged", questionable.length)}>{questionable.length} charge{questionable.length === 1 ? "" : "s"} flagged</span>
                </div>
                <div className={subCls}>
                  {topCharge && topCharge.savingsImpact
                    ? `Top impact: ${topCharge.description}`
                    : "No material charge-level impacts."}
                </div>
              </div>
            </div>
          )}
        </div>
        <div
          className={
            isOverview
              ? "flex h-full min-w-0 flex-col rounded-xl border border-savings/50 bg-savings/[0.11] p-5"
              : "flex h-full min-w-0 flex-col rounded-xl border border-savings/40 bg-savings/10 p-4"
          }
        >
          {isOverview ? (
            <div className="flex h-full min-w-0 flex-col">
              <TileHead
                icon={DollarSign}
                tone="savings"
                label="Total Supported Recovery"
                labelCls="min-w-0 text-[9.5px] font-semibold uppercase leading-[1.35] tracking-[0.12em] text-savings/90 [overflow-wrap:anywhere]"
              />
              <div className="mt-3 w-full max-w-full text-[clamp(1.25rem,2.1vw,1.875rem)] font-bold leading-[1.1] tracking-[-0.02em] text-savings tabular-nums">
                <span {...summaryField("potentialSavings", rs.financials.potentialSavings)}>
                  {currency(rs.financials.potentialSavings)}
                </span>
              </div>
              <div className="mt-1.5 max-w-full text-xs font-semibold leading-snug text-savings/90 [overflow-wrap:anywhere]">
                Source: Market Benchmark Analysis
              </div>
              <div className="mt-2 max-w-full text-[11px] leading-relaxed text-muted-foreground/80 [overflow-wrap:anywhere]">
                Based on verified market pricing and supported findings.
              </div>
            </div>
          ) : (

            <div className="flex h-full items-start gap-3">
              <IconBadge icon={DollarSign} tone="savings" />
              <div className="flex min-w-0 flex-1 flex-col">
                <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-savings">
                  Total supported recovery
                </div>
                <div className="kpi-range mt-1 text-savings tabular-nums">
                  <span {...summaryField("potentialSavings", rs.financials.potentialSavings)}>
                    {currency(rs.financials.potentialSavings)}
                  </span>
                </div>
                <div className={subCls}>
                  Recovery supported by all benchmarked charges and forensic findings together,
                  after duplicate adjustments.
                </div>
              </div>
            </div>
          )}
        </div>

        <div className={tileBase}>
          {isOverview ? (
            <div className="flex h-full min-w-0 flex-col">
              <TileHead
                icon={Target}
                label="Market evidence"
                tooltip="Regional Pricing Records are verified market pricing references collected for this claim. A single pricing record may support the evaluation of multiple invoice charges."
              />
              <div
                className="metric-value-fit mt-3 max-w-full text-navy"
                {...chapterMetric("chapter-02", "verifiedBenchmarkRecords", marketRows.length)}
              >
                {marketRows.length} regional pricing record
                {marketRows.length === 1 ? "" : "s"}
              </div>
              <div className={subCls}>
                {zip ? `ZIP ${zip}` : "Invoice region"}
                {state ? `, ${state}` : ""}. Used to evaluate{" "}
                <span {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}>
                  {rs.counts.marketSupportedCharges}
                </span>{" "}
                benchmarked invoice charge
                {rs.counts.marketSupportedCharges === 1 ? "" : "s"}.
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-3">
              <div className="min-w-0 flex-1">
                <div className={eyebrowCls}>Market evidence</div>
                <div
                  className="metric-value-fit mt-1 max-w-full text-navy"
                  {...chapterMetric("chapter-02", "verifiedBenchmarkRecords", marketRows.length)}
                >
                  {marketRows.length} regional pricing record
                  {marketRows.length === 1 ? "" : "s"}
                </div>
                <div className={subCls}>
                  {zip ? `ZIP ${zip}` : "Invoice region"}
                  {state ? `, ${state}` : ""}. Used to evaluate{" "}
                  <span
                    {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}
                  >
                    {rs.counts.marketSupportedCharges}
                  </span>{" "}
                  benchmarked invoice charge
                  {rs.counts.marketSupportedCharges === 1 ? "" : "s"}.
                </div>
              </div>
            </div>
          )}
        </div>

      </div>
      {isOverview && (
        <div className="mt-4 rounded-xl border border-slate-200 bg-slate-50/80 p-5 text-slate-800 md:p-6">
          <div className="flex items-start gap-3">
            <span className="flex h-8 w-8 flex-none items-center justify-center rounded-full bg-slate-200/80 text-slate-700">
              <Info className="h-4 w-4" />
            </span>
            <div className="min-w-0 flex-1">
              <h4 className="text-sm font-semibold text-slate-900">How Total Supported Recovery Was Calculated</h4>
              <p className="mt-2 text-[13px] leading-relaxed text-slate-700">
                The Total Supported Recovery figure represents the amount that may be recoverable through negotiation based on the supported findings identified throughout this report.
              </p>
              <div className="mt-3 grid gap-4 sm:grid-cols-2">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">This estimate includes</div>
                  <ul className="mt-1.5 list-disc space-y-1 pl-4 text-[12px] leading-relaxed text-slate-700">
                    <li>Verified market pricing differences</li>
                    <li>Duplicate billing findings</li>
                    <li>Unsupported or excessive charges</li>
                    <li>Other supported forensic findings</li>
                  </ul>
                </div>
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">This estimate excludes</div>
                  <ul className="mt-1.5 list-disc space-y-1 pl-4 text-[12px] leading-relaxed text-slate-700">
                    <li>Charges without sufficient comparable market evidence</li>
                    <li>Charges pending additional market support</li>
                  </ul>
                </div>
              </div>
              <p className="mt-4 text-[12px] leading-relaxed text-slate-600">
                This is an evidence-supported estimate intended to assist negotiations and should not be interpreted as a guaranteed settlement amount.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Currency range that never clips and never splits a numeral.
 * Each amount is an unbreakable unit; the range may wrap to a second line at
 * the separator when the card is too narrow to hold it on one.
 */
function CurrencyRange({ low, high, noWrap }: { low: number; high: number; noWrap?: boolean }) {
  if (!(high > 0)) return <>—</>;
  return (
    <span className={`inline-flex max-w-full items-baseline justify-center gap-x-1.5 ${noWrap ? "flex-nowrap" : "flex-wrap"}`}>
      <span className="whitespace-nowrap">{currency(low)}</span>
      <span className="whitespace-nowrap">{`\u2013\u00a0${currency(high)}`}</span>
    </span>
  );
}

/**
 * Stage 03 — closing synthesis of the market benchmark chapter. Consumes the
 * same inputs already used by the opening Executive Market Overview so the
 * narrative closes on numbers the reader has already seen.
 */
function MarketOverallConclusion({
  marketComparisons,
  findingItems,
  chargeData,
}: {
  marketComparisons: MarketComparisonRow[];
  findingItems: FindingItem[];
  chargeData: {
    lineItems: Array<Record<string, unknown>>;
    fees: Array<Record<string, unknown>>;
    findingItems: FindingItem[];
    marketComparisons?: MarketComparisonRow[];
    marketResearch?: MarketResearch[];
    legalResearchCount?: number;
    legalResearch?: LegalResearch[];
  } | null;
}) {
  // Report-wide recovery comes from the immutable Report Summary only.
  const rs = useReportSummary();
  const combinedLow = rs.financials.recoveryLow;
  const combinedHigh = rs.financials.recoveryHigh;

  // Chapter-scoped benchmark statistics.
  const marketRows = buildMarketRows(marketComparisons, findingItems);
  const flagged = marketRows.filter(
    (row) => row.assessment === "Above Market" || row.assessment === "Materially Above Market",
  );
  const chargeRows: ChargeRow[] = chargeData ? buildRealChargeRows(chargeData) : [];
  const questionable = chargeRows.filter(
    (r) => r.severity === "critical" || r.severity === "high" || r.severity === "medium",
  );
  const worst = [...marketRows].sort(
    (a, b) => (b.variancePct ?? -Infinity) - (a.variancePct ?? -Infinity),
  )[0];

  const stance: "leverage" | "neutral" | "fair" =
    combinedHigh >= 500 || flagged.length >= 2 || questionable.length >= 3
      ? "leverage"
      : combinedHigh > 0 || questionable.length > 0
        ? "neutral"
        : "fair";
  const stanceLabel =
    stance === "leverage"
      ? "Negotiation Recommended"
      : stance === "neutral"
        ? "Targeted Adjustments Recommended"
        : "Aligned with Market";
  const stanceTone =
    stance === "leverage"
      ? "border-savings/30 bg-savings-soft text-savings"
      : stance === "neutral"
        ? "border-warn/30 bg-warn/10 text-warn"
        : "border-trust/30 bg-trust-soft text-trust";
  const conclusion =
    stance === "leverage"
      ? "The evidence across the benchmark review supports a documented negotiation. Present the flagged charges together with the local comparisons and the total supported recovery."
      : stance === "neutral"
        ? "The invoice is broadly consistent with local pricing on most lines. Focus the negotiation on the specific charges called out above rather than a wholesale challenge."
        : "No material variances were identified. The invoice can be treated as aligned with prevailing local pricing.";

  const Tile = ({
    label,
    value,
    sub,
  }: {
    label: string;
    value: React.ReactNode;
    sub?: React.ReactNode;
  }) => (
    <div className="rounded-xl border border-navy/10 bg-white/60 p-4 shadow-sm">
      <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-trust">
        {label}
      </div>
      <div className="mt-1 font-display text-lg leading-snug text-navy tabular-nums">{value}</div>
      {sub && <div className="mt-1 text-xs text-muted-foreground">{sub}</div>}
    </div>
  );

  return (
    <div
      {...summarySection("Chapter 02 — Market Benchmark Analysis")}
      className="rounded-2xl border border-trust/25 bg-trust-soft/25 p-6 shadow-card md:p-8"
    >
      <div className="flex flex-wrap items-start gap-3">
        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-full bg-trust text-white">
          <Target className="h-5 w-5" />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-trust">
            Closing Synthesis
          </div>
          <h3 className="mt-1 font-display text-2xl leading-tight text-navy md:text-[1.7rem]">
            Overall Market Conclusion
          </h3>
        </div>
        <span
          className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[11px] font-semibold ${stanceTone}`}
        >
          <span className="h-1.5 w-1.5 rounded-full bg-current" />
          {stanceLabel}
        </span>
      </div>

      <p className="mt-4 max-w-3xl text-[15px] leading-relaxed text-muted-foreground">
        {conclusion}
      </p>

      {/* Review Scope — states the relationship between the charge counts used
          throughout this chapter. All values read from the Report Summary. */}
      <dl className="mt-5 max-w-xl rounded-lg border border-border/60 bg-muted/30 p-4">
        <p className="mb-3 text-[11px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          Review Scope
        </p>
        {[
          {
            label: "Invoice Charges Reviewed",
            field: "totalInvoiceCharges",
            value: rs.counts.totalInvoiceCharges,
            note: "every billed charge on the invoice",
          },
          {
            label: "Charges Benchmarked",
            field: "marketSupportedCharges",
            value: rs.counts.marketSupportedCharges,
            note: "charges with verified local market data",
          },
          {
            label: "Charges Above Market",
            field: "chargesAboveMarket",
            value: rs.counts.chargesAboveMarket,
            note: "benchmarked charges priced above local pricing",
          },
          {
            label: "Pending Comparable Pricing",
            field: "pendingComparablePricing",
            value: rs.counts.pendingComparablePricing,
            note: "no comparable market established yet",
          },
          {
            label: "Supporting Market Sources",
            field: "supportingMarketSources",
            value: rs.counts.supportingMarketSources,
            note: "accepted comparables backing the analysis",
          },
        ]
          /* The canonical Report Summary counts only accepted research sources.
             When that register is empty the row would print a bare "0" beside
             statements that cite the benchmark evidence actually used, which
             reads as a contradiction. The row is omitted rather than restated
             from another source — no count is recomputed here. */
          .filter((row) => row.field !== "supportingMarketSources" || row.value > 0)
          .map((row) => (
          <div
            key={row.field}
            className="flex items-baseline justify-between gap-3 border-b border-border/40 py-1.5 last:border-0"
          >
            <dt className="text-[13px] text-muted-foreground">
              {row.label}
              <span className="ml-2 hidden text-[11px] text-muted-foreground/70 sm:inline">
                {row.note}
              </span>
            </dt>
            <dd className="text-[15px] font-semibold tabular-nums text-foreground">
              <span {...summaryField(row.field, row.value)}>{row.value}</span>
            </dd>
          </div>
          ))}
        <p className="mt-3 text-[11px] leading-relaxed text-muted-foreground/80">
          Charges Reviewed = Charges Benchmarked + Pending Comparable Pricing. A charge is
          benchmarked only when comparable local pricing exists for that service; administrative,
          pass-through and documentation-only charges are reviewed but cannot be benchmarked.
        </p>
      </dl>

      <div className="mt-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="Invoice Charges Reviewed"
          value={
            <span {...summaryField("totalInvoiceCharges", rs.counts.totalInvoiceCharges)}>
              {rs.counts.totalInvoiceCharges}
            </span>
          }
          sub={
            <span {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}>
              {rs.counts.marketSupportedCharges} benchmarked against local market data
            </span>
          }
        />

        <Tile
          label="Above market"
          value={
            <span>
              <span {...summaryField("chargesAboveMarket", rs.counts.chargesAboveMarket)}>
                {rs.counts.chargesAboveMarket}
              </span>
              {" of "}
              <span {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}>
                {rs.counts.marketSupportedCharges}
              </span>
              {" benchmarked"}
            </span>
          }
          sub={
            rs.counts.chargesAboveMarket > 0
              ? "Documented variance vs. local median"
              : "No material variances flagged"
          }
        />

        <Tile
          label="Largest variance"
          value={
            worst && typeof worst.variancePct === "number" && worst.variancePct > 0
              ? `+${Math.round(worst.variancePct)}%`
              : "—"
          }
          sub={worst && worst.service ? worst.service : undefined}
        />
        <div className="rounded-xl border border-savings/30 bg-savings/[0.06] p-4 shadow-sm">
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-savings">
            Total Supported Recovery
          </div>
          <div
            className="mt-1 font-display text-2xl leading-snug text-savings tabular-nums whitespace-nowrap"
            {...summaryField("potentialSavings", rs.financials.potentialSavings)}
          >
            {rs.financials.potentialSavings > 0
              ? currency(rs.financials.potentialSavings)
              : "None identified"}
          </div>
          <div className="mt-1 text-xs text-muted-foreground">
            Market-Based Recovery combined with Non-Market Recovery, taken as the stronger of the
            two supported positions rather than a sum
          </div>
        </div>
      </div>

    </div>
  );
}

function AnalystSummaryCard({
  marketComparisons,
  findingItems,
  chargeData,
}: {
  marketComparisons: MarketComparisonRow[];
  findingItems: FindingItem[];
  chargeData: {
    lineItems: Array<Record<string, unknown>>;
    fees: Array<Record<string, unknown>>;
    findingItems: FindingItem[];
    marketComparisons?: MarketComparisonRow[];
    marketResearch?: MarketResearch[];
    legalResearchCount?: number;
    legalResearch?: LegalResearch[];
  } | null;
}) {
  // Report-wide financials come from the immutable Report Summary.
  const rs = useReportSummary();
  // Chapter-scoped benchmark statistics — shared with Executive Market Overview.
  const summary = deriveMarketSummary(marketComparisons ?? [], findingItems ?? [], chargeData);

  const largestPct = summary.largestVariancePercent;
  const largestName = summary.largestVarianceCharge;
  const avgAbove = summary.averageAboveMarketVariance;

  const items: Array<{
    icon: React.ComponentType<{ className?: string }>;
    tone: string;
    bg: string;
    label: string;
    text: React.ReactNode;
  }> = [
    {
      icon: TrendingUp,
      tone: "text-warn",
      bg: "bg-warn/10",
      label: "Largest Variance",
      text:
        largestPct !== null && largestName ? (
          <>
            {largestName} at <span className="font-semibold text-navy">+{largestPct}%</span> above
            local median
          </>
        ) : (
          <span className="text-navy/60">No above-market variance identified</span>
        ),
    },
    {
      icon: PieChart,
      tone: "text-destructive",
      bg: "bg-destructive/10",
      label: "Charges Above Market",
      text: (
        <>
          <span className="font-semibold text-navy">
            <span {...summaryField("chargesAboveMarket", rs.counts.chargesAboveMarket)}>
              {rs.counts.chargesAboveMarket}
            </span>
            {" of "}
            <span {...summaryField("marketSupportedCharges", rs.counts.marketSupportedCharges)}>
              {rs.counts.marketSupportedCharges}
            </span>
          </span>{" "}
          benchmarked charges exceed local pricing benchmarks
        </>
      ),
    },

    {
      icon: Waves,
      tone: "text-trust",
      bg: "bg-trust/10",
      label: "Average Variance",
      text:
        avgAbove !== null ? (
          <>
            <span className="font-semibold text-navy">+{avgAbove}%</span> across above-market
            charges
          </>
        ) : (
          <span className="text-navy/60">Not applicable</span>
        ),
    },
    {
      icon: DollarSign,
      tone: "text-savings",
      bg: "bg-savings/10",
      label: "Total Supported Recovery",
      text:
        rs.financials.potentialSavings > 0 ? (
          <>
            <span
              className="font-semibold text-navy"
              {...summaryField("potentialSavings", rs.financials.potentialSavings)}
            >
              {fmtMoney(rs.financials.potentialSavings)}
            </span>{" "}
            Market-Based Recovery combined with Non-Market Recovery, taken as the stronger of the
            two supported positions rather than a sum
          </>
        ) : (
          <span className="text-navy/60">No supported recovery identified</span>
        ),
    },
    {
      icon: ShieldCheck,
      tone: "text-navy",
      bg: "bg-navy/10",
      label: "Negotiation Strength",
      text: (
        <>
          <span className="font-semibold text-navy">{summary.negotiationStrength}</span>
        </>
      ),
    },
  ];

  return (
    <aside
      {...summarySection("Chapter 02 — Market Benchmark Analysis")}
      className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-7"
    >
      <ul className="space-y-4">
        {items.map((it, i) => {
          const Icon = it.icon;
          return (
            <li key={i} className="flex items-start gap-3.5">
              <span
                className={`mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full ${it.bg}`}
              >
                <Icon className={`h-4 w-4 ${it.tone}`} />
              </span>
              <div className="min-w-0 pt-0.5">
                <div className="font-mono text-[10.5px] font-semibold uppercase tracking-[0.18em] text-navy/70">
                  {it.label}
                </div>
                <p className="mt-0.5 text-[14px] leading-relaxed text-navy/85">{it.text}</p>
              </div>
            </li>
          );
        })}
      </ul>
      <div className="mt-5 border-t border-border pt-4">
        <div className="font-mono text-[11.5px] font-bold uppercase tracking-[0.18em] text-navy/60">
          Overall Assessment
        </div>
        <p className="mt-1.5 text-[14.5px] leading-[1.65] text-navy/85">
          {summary.assessmentSummary}
        </p>
      </div>
      <EvidenceUsedSummary
        marketComparisons={marketComparisons ?? []}
        findingItems={findingItems ?? []}
        legalResearch={chargeData?.legalResearch ?? []}
      />
    </aside>
  );
}

// Report-specific evidence summary rendered inside AnalystSummaryCard (Stage
// 02). Unlike a static legend, this component only lists evidence categories
// that were ACTUALLY used while generating THIS report. Counts are derived
// from live data (market comparisons, legal research, finding evidence keys).
// Categories with zero backing evidence are omitted entirely.
function EvidenceUsedSummary({
  marketComparisons,
  findingItems,
  legalResearch,
}: {
  marketComparisons: MarketComparisonRow[];
  findingItems: FindingItem[];
  legalResearch: LegalResearch[];
}) {
  // SAB-002: single canonical statute/regulation classifier.
  const isRegulation = (l: LegalResearch) => isRegulatoryAuthority(l);
  const marketCount = marketComparisons.length;
  const regs = legalResearch.filter(isRegulation);
  const laws = legalResearch.filter((l) => !isRegulation(l));
  const lawCount = laws.length;
  const regCount = regs.length;

  const evidences = findingItems.map(
    (f) =>
      ((f as { evidence?: Record<string, unknown> }).evidence ?? {}) as Record<string, unknown>,
  );
  const anyDocs = evidences.some(hasDocumentEvidence);
  const anyPhoto = evidences.some(hasPhotoEvidence);
  const anyGps = evidences.some(hasGpsEvidence);
  const anyTimeline = evidences.some(hasTimelineEvidence);
  const anyNova = evidences.some(hasNovaEvidence);
  const anyVendor = evidences.some(hasVendorEvidence);
  const anyCarrier = evidences.some(hasCarrierEvidence);
  const anyHuman = findingItems.some((f) => {
    const v = f as { verified?: boolean; reviewed?: boolean };
    return v.verified === true || v.reviewed === true;
  });

  type Row = { key: EvidenceKind; label: string; desc: string };
  const rows: Row[] = [];
  if (marketCount > 0)
    rows.push({
      key: "market",
      label: "Market Benchmark",
      desc: `Verified against ${marketCount} regional benchmark${marketCount === 1 ? "" : "s"}.`,
    });
  if (lawCount > 0)
    rows.push({
      key: "law",
      label: "Applicable Law",
      desc: `${lawCount} statute${lawCount === 1 ? "" : "s"} referenced.`,
    });
  if (regCount > 0)
    rows.push({
      key: "regulation",
      label: "Regulations",
      desc: `${regCount} regulation${regCount === 1 ? "" : "s"} applied.`,
    });
  if (anyDocs)
    rows.push({
      key: "docs",
      label: "Supporting Documents",
      desc: "Invoice and supporting records reviewed.",
    });
  if (anyPhoto)
    rows.push({ key: "photo", label: "Photo Evidence", desc: "Photographic evidence reviewed." });
  if (anyGps)
    rows.push({ key: "gps", label: "GPS / Distance", desc: "Location and mileage validated." });
  if (anyTimeline)
    rows.push({ key: "timeline", label: "Timeline", desc: "Chronological evidence reviewed." });
  if (anyVendor)
    rows.push({
      key: "vendor",
      label: "Vendor Intelligence",
      desc: "Vendor records and public filings reviewed.",
    });
  if (anyCarrier)
    rows.push({
      key: "carrier",
      label: "Carrier Guidelines",
      desc: "Carrier guidelines applied.",
    });
  if (anyNova)
    rows.push({ key: "nova", label: "AI Analysis", desc: "AI-assisted analytical findings." });
  if (anyHuman)
    rows.push({
      key: "human",
      label: "Human Review",
      desc: "Reviewed by ClaimGuard analysts.",
    });

  if (rows.length === 0) return null;
  return (
    <div className="mt-6 border-t border-border pt-5">
      <div className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-navy/75">
        Evidence Used For This Review
      </div>
      <ul className="mt-4 grid grid-cols-1 gap-x-6 gap-y-3.5 sm:grid-cols-2">
        {rows.map((e) => {
          const meta = EVIDENCE_META[e.key];
          const Icon = meta.icon;
          return (
            <li key={e.key} className="flex items-start gap-3">
              <span
                className={`mt-0.5 inline-flex h-7 w-7 flex-none items-center justify-center rounded-full border ${meta.colorClass}`}
                aria-hidden="true"
              >
                <Icon className="h-3.5 w-3.5" />
              </span>
              <div className="min-w-0 leading-snug">
                <div className="text-[13.5px] font-semibold text-navy">{e.label}</div>
                <div className="mt-0.5 text-[12.5px] leading-[1.55] text-muted-foreground">
                  {e.desc}
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      <EvidenceIconLegend />
    </div>
  );
}

// Compact icon legend rendered directly beneath "Evidence Used For This
// Review". Reuses the same EVIDENCE_META badges as the charge cards so
// readers can decode any icon they encounter later in the chapter.
// Excludes Vendor Intelligence and Carrier Guidelines per report spec.
function EvidenceIconLegend() {
  const keys: EvidenceKind[] = [
    "market",
    "law",
    "regulation",
    "docs",
    "photo",
    "gps",
    "timeline",
    "nova",
    "human",
  ];
  return (
    <div className="mt-6 border-t border-border pt-4">
      <div className="font-mono text-[12px] font-bold uppercase tracking-[0.2em] text-navy/75">
        Evidence Icon Legend
      </div>
      <ul className="mt-3.5 grid grid-cols-2 gap-x-5 gap-y-2.5 sm:grid-cols-3">
        {keys.map((k) => {
          const meta = EVIDENCE_META[k];
          if (!meta) return null;
          const Icon = meta.icon;
          return (
            <li key={k} className="flex items-center gap-2.5">
              <span
                className={`inline-flex h-6 w-6 flex-none items-center justify-center rounded-full border ${meta.colorClass}`}
                aria-hidden="true"
              >
                <Icon className="h-3 w-3" />
              </span>
              <span className="text-[13px] font-semibold text-navy">{meta.label}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function KeyFindingsCard({
  legalResearch,
  marketComparisons = [],
}: {
  legalResearch: LegalResearch[];
  marketComparisons?: MarketComparisonRow[];
}) {
  // SAB-002: single canonical statute/regulation classifier.
  const isRegulation = (l: LegalResearch) => isRegulatoryAuthority(l);
  const toItem = (l: LegalResearch): string | null => {
    const label = l.statute || l.citation || l.title;
    if (!label) return null;
    const detail =
      (typeof l.why_it_matters === "string" && l.why_it_matters.trim()) ||
      (typeof l.summary === "string" && l.summary.trim()) ||
      (typeof l.impact === "string" && l.impact.trim()) ||
      "";
    return detail ? `${label} — ${detail}` : `${label} may apply.`;
  };
  const seen = new Set<string>();
  const laws: string[] = [];
  const regs: string[] = [];
  for (const l of legalResearch) {
    const item = toItem(l);
    if (!item || seen.has(item)) continue;
    seen.add(item);
    if (isRegulation(l)) regs.push(item);
    else laws.push(item);
  }

  // Market findings — deterministic summary of the strongest above-market variances
  const marketFindings: string[] = [];
  const sortedAbove = [...marketComparisons]
    .filter((m) => Number(m.difference_percent ?? 0) > 0)
    .sort((a, b) => Number(b.difference_percent ?? 0) - Number(a.difference_percent ?? 0))
    .slice(0, 4);
  for (const m of sortedAbove) {
    const name = m.line_item ?? m.service_type;
    const pct = Math.round(Number(m.difference_percent ?? 0));
    if (!name) continue;
    marketFindings.push(`${name} runs +${pct}% above local benchmarks.`);
  }

  const showLaws = laws.length > 0;
  const showRegs = regs.length > 0;
  const showMarket = marketFindings.length > 0;
  if (!showLaws && !showRegs && !showMarket) return null;

  // Presentation-only certainty wording for the authorities listed here.
  const verifiedAuthorities = officiallyVerifiedCount(legalResearch);
  const authorityState: ReportVerificationState =
    legalResearch.length > 0 && verifiedAuthorities === legalResearch.length
      ? "verified"
      : "analyzed";



  const Section = ({
    icon: Icon,
    title,
    items,
  }: {
    icon: React.ComponentType<{ className?: string }>;
    title: string;
    items: string[];
  }) => (
    <div>
      <div className="flex items-center gap-2 text-[12.5px] font-bold uppercase tracking-[0.18em] text-trust">
        <Icon className="h-3.5 w-3.5" /> {title}
      </div>
      <ul className="mt-2 space-y-1.5 text-[14px] leading-[1.55] text-navy">
        {items.slice(0, 4).map((it, i) => (
          <li key={i} className="flex gap-2">
            <span className="mt-1.5 h-1 w-1 shrink-0 rounded-full bg-trust" />
            <span>{it}</span>
          </li>
        ))}
      </ul>
    </div>
  );

  const sections: React.ReactNode[] = [];
  if (showLaws) sections.push(<Section key="laws" icon={Scale} title="Laws" items={laws} />);
  if (showRegs)
    sections.push(<Section key="regs" icon={ShieldCheck} title="Regulations" items={regs} />);
  if (showMarket)
    sections.push(
      <Section key="mkt" icon={BarChart3} title="Market Findings" items={marketFindings} />,
    );

  return (
    <aside className="rounded-2xl border border-border bg-card p-5 shadow-card md:p-6">
      <div className="flex items-center gap-2 text-[12.5px] font-bold uppercase tracking-[0.16em] text-trust">
        <Landmark className="h-3.5 w-3.5" /> Applicable Legal Authorities Identified
      </div>
      <p className="mt-1.5 text-[12px] leading-relaxed text-muted-foreground">
        Authorities and market observations identified during this review. These are the
        authorities ClaimGuard applied to the invoice — not confirmed violations.
      </p>
      <VerificationStatusBanner
        className="mt-4"
        state={authorityState}
        badgeLabel={authorityState === "verified" ? "Verified" : "Analyzed"}
        title={
          authorityState === "verified"
            ? `Confirmed against official published sources${verifiedAuthorities > 0 ? ` (${verifiedAuthorities})` : ""}`
            : "Identified and analyzed during review"
        }
      />

      <div className="mt-4 space-y-4 divide-y divide-border [&>*+*]:pt-4">{sections}</div>
    </aside>
  );
}


function buildRealChargeRows(real: {
  lineItems: Array<Record<string, unknown>>;
  fees: Array<Record<string, unknown>>;
  findingItems: FindingItem[];
  marketComparisons?: MarketComparisonRow[];
  marketResearch?: MarketResearch[];
  legalResearchCount?: number;
  legalResearch?: LegalResearch[];
}): ChargeRow[] {
  /* Deterministic per-charge recovery ledger — the only source of the
     dollar figures rendered in the charge breakdown. */
  const recoveryLedger = deterministicChargeRecovery(real);
  const findingByKey = new Map<string, FindingItem>();
  for (const f of real.findingItems) {
    if (f.title) findingByKey.set(f.title.toLowerCase(), f);
  }

  const findMatch = (description: string): FindingItem | undefined => {
    const key = description.toLowerCase();
    if (findingByKey.has(key)) return findingByKey.get(key);
    for (const [k, v] of findingByKey) {
      if (k.includes(key.slice(0, 10)) || key.includes(k.slice(0, 10))) return v;
    }
    return undefined;
  };

  const hasLegal = (real.legalResearchCount ?? 0) > 0;
  const legalItems = real.legalResearch ?? [];
  const pickLegalNote = (description: string, sev: ChargeRow["severity"]): string | null => {
    if (!hasLegal || (sev !== "critical" && sev !== "high")) return null;
    const desc = description.toLowerCase();
    const tokens = desc.split(/[^a-z0-9]+/).filter((t) => t.length >= 4);
    const scoreOf = (l: LegalResearch) => {
      const hay =
        `${l.title ?? ""} ${l.topic ?? ""} ${l.summary ?? ""} ${l.impact ?? ""}`.toLowerCase();
      let s = 0;
      for (const t of tokens) if (hay.includes(t)) s += 1;
      return s;
    };
    let best: LegalResearch | undefined;
    let bestScore = 0;
    for (const l of legalItems) {
      const s = scoreOf(l);
      if (s > bestScore) {
        bestScore = s;
        best = l;
      }
    }
    const chosen = best ?? legalItems[0];
    if (!chosen) return null;
    const cite = chosen.statute || chosen.citation || chosen.title;
    if (!cite) return null;
    return `${cite} may apply.`;
  };

  /** Match this description to a MarketComparison row (variance-based override). */
  const findMarketMatch = (description: string): MarketComparisonRow | undefined => {
    if (!real.marketComparisons?.length) return undefined;
    const key = description.toLowerCase();
    return real.marketComparisons.find((m) => {
      const k = (m.line_item ?? m.service_type ?? "").toLowerCase();
      if (!k) return false;
      return k.includes(key.slice(0, 10)) || key.includes(k.slice(0, 10));
    });
  };

  /** Overlay market variance on the finding-based assessment. */
  const applyMarketVariance = (
    base: Assessment,
    mkt: MarketComparisonRow | undefined,
  ): Assessment => {
    // Keep forensic-strong classifications intact.
    if (base === "Duplicate" || base === "Unsupported" || base === "Needs Documentation")
      return base;
    if (!mkt) return base;
    let pct: number | null = null;
    if (typeof mkt.difference_percent === "number") pct = mkt.difference_percent;
    else if (
      typeof mkt.local_average === "number" &&
      typeof mkt.invoice_amount === "number" &&
      mkt.local_average > 0
    ) {
      pct = Math.round(((mkt.invoice_amount - mkt.local_average) / mkt.local_average) * 100);
    }
    if (pct === null) return base;
    if (pct >= 25) return "Materially Above Market";
    if (pct >= 8) return "Above Market";
    return "Within Market";
  };

  const deriveWhyItMatters = (
    category: string,
    assessment: Assessment,
    mkt: MarketComparisonRow | undefined,
    finding: FindingItem | undefined,
  ): string => {
    // Prefer real analyst description if it isn't generic template text.
    const d = finding?.description?.trim();
    if (d && !/reviewed against local benchmarks and standards\.?/i.test(d)) return d;
    const cat = category.toLowerCase();
    if (assessment === "Duplicate") return `Duplicate ${cat} charge identified.`;
    if (assessment === "Unsupported") return `${category} lacks supporting authorization.`;
    if (assessment === "Needs Documentation")
      return `${category} requires supporting documentation.`;
    if (assessment === "Materially Above Market")
      return `${category} materially exceeds the local benchmark range.`;
    if (assessment === "Above Market" || assessment === "Excessive")
      return `${category} exceeds regional pricing.`;
    if (assessment === "Questionable" || assessment === "Negotiable")
      return `${category} is a candidate for negotiation.`;
    /* Only claim alignment when the billed amount actually sits at or below the
       benchmark ceiling. A charge priced above the local range must never be
       described as "aligned", which would contradict the outcome badge shown
       directly above this sentence. No value is computed or altered here. */
    if (mkt) {
      const ceiling =
        typeof mkt.market_high === "number"
          ? mkt.market_high
          : typeof mkt.typical_high === "number"
            ? mkt.typical_high
            : typeof mkt.local_average === "number"
              ? mkt.local_average
              : null;
      const billed = typeof mkt.invoice_amount === "number" ? mkt.invoice_amount : null;
      const aligned =
        ceiling !== null && ceiling > 0 && billed !== null ? billed <= ceiling : ceiling !== null && ceiling > 0;
      if (aligned) return `${category} aligns with prevailing market pricing.`;
    }
    return `${category} reviewed against local benchmarks.`;
  };

  const toRow = (o: Record<string, unknown>, category: string): ChargeRow | null => {
    const description = pickString(o, ["description", "name", "label", "item"]) ?? "Line item";
    const amount =
      pickNumber(o, ["total", "amount", "line_total", "extended", "price"]) ??
      (pickNumber(o, ["quantity", "qty"]) ?? 0) *
        (pickNumber(o, ["unitPrice", "unit_price", "rate"]) ?? 0);
    if (!description && !amount) return null;
    const match = findMatch(description);
    const mkt = findMarketMatch(description);
    const severity = normalizeSeverity(match?.severity);
    const assessment = applyMarketVariance(classifyAssessment(match, severity), mkt);
    const marketNote = findMarketNote(description, real.marketComparisons);
    const finalCategory = match?.category ?? category;
    return {
      description,
      category: finalCategory,
      amount: amount || 0,
      assessment,
      severity,
      chip: severityToChip(severity),
      whyItMatters: deriveWhyItMatters(finalCategory, assessment, mkt, match),
      evidence: evidenceLine(match),
      marketNote,
      legalNote: pickLegalNote(description, severity),
      recommendedAction: recommendActionFor(assessment),
      savingsImpact: takeChargeRecovery(recoveryLedger, description),
    };
  };

  const lineRows = real.lineItems
    .map((li) => toRow(li, "Line item"))
    .filter((r): r is ChargeRow => !!r);
  const feeRows = real.fees.map((f) => toRow(f, "Fee")).filter((r): r is ChargeRow => !!r);
  const rows = [...lineRows, ...feeRows];
  if (rows.length > 0) return rows;

  // No structured line items — derive rows straight from finding items.
  return real.findingItems.map((f) => {
    const severity = normalizeSeverity(f.severity);
    const assessment = classifyAssessment(f, severity);
    const amount = typeof f.amount === "number" ? f.amount : 0;
    return {
      description: f.title,
      category: f.category ?? "Charge",
      amount,
      assessment,
      severity,
      chip: severityToChip(severity),
      whyItMatters: f.description ?? "Flagged during forensic review.",
      evidence: evidenceLine(f),
      marketNote: findMarketNote(f.title, real.marketComparisons),
      legalNote: pickLegalNote(f.title, severity),
      recommendedAction: recommendActionFor(assessment),
      /* No canonical invoice inventory ⇒ no deterministic benchmark scope ⇒
         no recoverable dollars may be published for this line. */
      savingsImpact: null,
    };
  });
}

function ChargeBreakdown({
  pending,
  real,
  embedded = false,
  allowDemo = false,
}: {
  pending?: boolean;
  /** Demo charge rows render only with an explicit demo context. */
  allowDemo?: boolean;
  real?: {
    lineItems: Array<Record<string, unknown>>;
    fees: Array<Record<string, unknown>>;
    findingItems: FindingItem[];
    marketComparisons?: MarketComparisonRow[];
    marketResearch?: MarketResearch[];
    legalResearchCount?: number;
    legalResearch?: LegalResearch[];
  } | null;
  /** When true, renders without the standalone Section wrapper so the parent chapter can compose it. */
  embedded?: boolean;
} = {}) {
  const rows: ChargeRow[] = real ? buildRealChargeRows(real) : allowDemo ? DEMO_CHARGE_ROWS : [];

  const questionable = rows.filter(
    (r) => r.severity === "critical" || r.severity === "high" || r.severity === "medium",
  );
  const totalReviewed = rows.length;
  const totalSavingsLow = questionable.reduce(
    (s, r) => s + (r.savingsImpact ? r.savingsImpact[0] : 0),
    0,
  );
  const totalSavingsHigh = questionable.reduce(
    (s, r) => s + (r.savingsImpact ? r.savingsImpact[1] : 0),
    0,
  );
  const highestImpact = [...rows].sort(
    (a, b) => (b.savingsImpact?.[1] ?? 0) - (a.savingsImpact?.[1] ?? 0),
  )[0];

  const critCount = rows.filter((r) => r.severity === "critical").length;
  const highCount = rows.filter((r) => r.severity === "high").length;
  const medCount = rows.filter((r) => r.severity === "medium").length;

  const body = (
    <>
      {/* Summary strip */}
      <div className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <SummaryTile
          label="Charges reviewed"
          value={String(totalReviewed)}
          hint="Total line items and fees analyzed."
          icon={ClipboardCheck}
        />
        <SummaryTile
          label="Charges flagged for review"
          value={String(questionable.length)}
          hint={
            questionable.length > 0 ? "Flagged for negotiation or documentation." : "None flagged."
          }
          icon={AlertTriangle}
          tone={questionable.length > 0 ? "warn" : "trust"}
        />
        <SummaryTile
          label="Highest-impact charge"
          value={highestImpact?.description ?? "—"}
          hint={
            highestImpact?.savingsImpact
              ? `Up to ${currency(highestImpact.savingsImpact[1])} recoverable.`
              : "No material findings."
          }
          icon={TrendingDown}
          tone="warn"
          compact
        />
        <SummaryTile
          label="Charge-level savings"
          value={
            totalSavingsHigh > 0
              ? `${currency(totalSavingsLow)} – ${currency(totalSavingsHigh)}`
              : "—"
          }
          hint="Cumulative savings from flagged charges."
          icon={Sparkles}
          tone="savings"
        />
      </div>

      {/* Key Findings severity distribution */}
      {questionable.length > 0 && (
        <div className="mb-6 rounded-2xl border border-border bg-secondary/40 p-4 md:p-5">
          <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
            <div className="eyebrow text-navy">Key findings by severity</div>
            <div className="text-[11px] text-muted-foreground">
              Detailed evidence for each flagged charge appears on its card below.
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <SeverityChip label="Critical" count={critCount} tone="destructive" />
            <SeverityChip label="High" count={highCount} tone="warn" />
            <SeverityChip label="Medium" count={medCount} tone="fasttrack" />
          </div>
        </div>
      )}

      {/* Charge cards */}
      <div className="space-y-5">
        {rows.map((row, i) => (
          <ChargeCard key={`${row.description}-${i}`} row={row} />
        ))}
      </div>
    </>
  );

  if (embedded) return <div>{body}</div>;
  return (
    <Section
      bare
      number="01"
      eyebrow="Forensic Audit · Key Findings"
      title={
        <>
          Invoice Charge Analysis &amp; Key Findings
          {pending && <PendingBadge label="Prepared during review" />}
        </>
      }
      intro="Every line item is independently reviewed against four dimensions — local rates, contract standards, documentation, and applicable law. Flagged charges carry inline evidence, market context, and a recommended negotiation action."
    >
      {body}
    </Section>
  );
}

function SeverityChip({
  label,
  count,
  tone,
}: {
  label: string;
  count: number;
  tone: "destructive" | "warn" | "fasttrack";
}) {
  const cls =
    tone === "destructive"
      ? "border-destructive/30 bg-destructive/10 text-destructive"
      : tone === "warn"
        ? "border-warn/30 bg-warn/10 text-warn"
        : "border-fasttrack/30 bg-fasttrack-soft text-fasttrack";
  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs font-semibold ${cls} ${count === 0 ? "opacity-40" : ""}`}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-current" />
      {label}
      <span className="tabular-nums">{count}</span>
    </span>
  );
}

function SummaryTile({
  label,
  value,
  hint,
  icon: Icon,
  tone = "navy",
  compact = false,
}: {
  label: string;
  value: string;
  hint: string;
  icon: React.ComponentType<{ className?: string }>;
  tone?: "navy" | "trust" | "warn" | "savings";
  compact?: boolean;
}) {
  const iconCls =
    tone === "savings"
      ? "bg-savings-soft text-savings"
      : tone === "warn"
        ? "bg-warn/10 text-warn"
        : tone === "trust"
          ? "bg-trust-soft text-trust"
          : "bg-secondary text-navy";
  const valueCls =
    tone === "savings" ? "text-savings" : tone === "warn" ? "text-warn" : "text-navy";
  return (
    <div className="rounded-2xl border border-border bg-card p-5 shadow-card">
      <div className="flex items-center gap-3">
        <div className={`flex h-9 w-9 items-center justify-center rounded-lg ${iconCls}`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {label}
        </div>
      </div>
      <div
        className={`mt-4 font-display leading-tight ${valueCls} ${compact ? "text-lg truncate" : "text-2xl"}`}
        title={compact ? value : undefined}
      >
        {value}
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground">{hint}</div>
    </div>
  );
}

function accentForSeverity(sev: ChargeRow["severity"]): string {
  if (sev === "critical") return "border-l-4 border-l-destructive";
  if (sev === "high") return "border-l-4 border-l-warn";
  if (sev === "medium") return "border-l-4 border-l-fasttrack";
  return "border-l-4 border-l-border";
}

// Shared classification for a charge's legal note. The Law / Regulation icon
// in the Evidence header and the Law / Regulation row in the charge body MUST
// consume the same result — a body row without a matching header icon (or
// vice versa) is a reconciliation defect.
function classifyLegalNote(legal: string | null | undefined): {
  showLaw: boolean;
  showReg: boolean;
} {
  const s = (legal ?? "").trim();
  if (!s) return { showLaw: false, showReg: false };
  if (/^applicable statute( or regulation)?( identified)?\.?$/i.test(s)) {
    return { showLaw: false, showReg: false };
  }
  const isReg =
    /rule|regulation|ordinance|administrative code|municipal|c\.f\.r\.|\btex\.?\s*admin/i.test(s);
  return { showLaw: !isReg, showReg: isReg };
}

/**
 * Presentation-only trim: keep the first N sentences of an existing narrative
 * so every "Why it matters" paragraph reads at the same length. No wording is
 * generated or altered.
 */
function firstSentences(text: string | null | undefined, max = 2): string {
  const s = (text ?? "").trim();
  if (!s) return s;
  const parts = s.match(/[^.!?]+[.!?]+(\s|$)/g);
  if (!parts || parts.length <= max) return s;
  return parts.slice(0, max).join("").trim();
}

function ChargeCard({
  row,
  embedded = false,
  evidence,
  hideIdentity = false,
  hideMetrics = false,
}: {
  row: ChargeRow;
  embedded?: boolean;
  evidence?: NormalizedChargeEvidence;
  /** Suppress badge + charge name when the parent header already states them. */
  hideIdentity?: boolean;
  /** Suppress the right-hand amount column when the parent header already shows it. */
  hideMetrics?: boolean;
}) {
  const isFlagged = row.severity !== "none" && row.severity !== "low";
  // When embedded inside InterleavedChargeAnalysis, the parent container owns
  // emphasis styling. Standalone cards keep their own severity accent so every
  // container reads at the same visual weight regardless of context.
  const containerCls = embedded
    ? ""
    : `rounded-2xl border-t border-r border-b bg-trust-soft/25 border-t-navy/15 border-r-navy/15 border-b-navy/15 shadow-card transition hover:shadow-elevated ${accentForSeverity(row.severity)} ${
        isFlagged ? "ring-1 ring-warn/10" : ""
      }`;
  return (
    <div className={containerCls}>
      <div
        className={`grid gap-2.5 ${hideMetrics ? "" : "lg:grid-cols-[minmax(0,1fr)_180px] lg:gap-6"} lg:items-start ${embedded ? "p-2.5 lg:p-2.5" : "p-3.5 lg:p-3.5"}`}
      >
        {/* Left column: identity + narrative */}
        <div className="min-w-0">
          {!hideIdentity && (
            <>
              <h4
                className={`font-display font-semibold leading-snug tracking-tight text-navy ${embedded ? "text-[17px]" : "text-xl"}`}
              >
                {row.description}
              </h4>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <AssessmentBadge value={row.assessment} severity={row.severity} />
                {/* Embedded cards sit under a header that already names the charge
                    family — the category label would only repeat it. */}
                {!embedded && (
                  <span className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-muted-foreground/70">
                    {row.category}
                  </span>
                )}
              </div>
            </>
          )}
          <p className="mt-1.5 text-[14.5px] leading-relaxed text-navy/85">
            <span className="font-semibold text-navy">Why it matters — </span>
            {firstSentences(row.whyItMatters, 2)}
          </p>



          {row.evidence && (
            <div className="mt-2">
              <MetaRow icon={BookOpen} label="Evidence" value={row.evidence} />
            </div>
          )}

          {(() => {
            // Evidence body rows MUST reconcile with the header Evidence icons.
            // When the parent has computed a NormalizedChargeEvidence for this
            // charge, consume it directly. Otherwise fall back to classifying
            // row.legalNote alone (standalone cards without finding context).
            const legal = row.legalNote?.trim() ?? "";
            const norm = evidence ?? deriveChargeEvidence(row, null, undefined);
            const marketText = norm.market.text ?? (row.marketNote?.trim() || null);
            const lawText =
              norm.law.text ??
              (norm.law.present && legal ? legal : null) ??
              (norm.law.present ? `Applicable law cited — see ${chapterRef("chapter-04")}.` : null);
            const regText =
              norm.regulation.text ??
              (norm.regulation.present && legal ? legal : null) ??
              (norm.regulation.present
                ? `Applicable regulation cited — see ${chapterRef("chapter-06")}.`
                : null);

            const rows: Array<{ label: string; icon: typeof Scale; value: string }> = [];
            if (marketText) rows.push({ label: "Market", icon: Scale, value: marketText });
            if (lawText) rows.push({ label: "Law", icon: Scale, value: lawText });
            if (regText) rows.push({ label: "Regulation", icon: ShieldCheck, value: regText });
            return (
              <div className="mt-2 space-y-1.5">
                {rows.map((r) => {
                  const Icon = r.icon;
                  return (
                    <div
                      key={r.label}
                      className="flex items-start gap-2 rounded-lg border border-trust/25 bg-trust-soft/40 px-3 py-1.5 text-left"
                    >
                      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0 text-trust" />
                      <div className="text-xs leading-snug">
                        <span className="font-semibold uppercase tracking-[0.14em] text-trust">
                          {r.label}
                        </span>{" "}
                        <span className="text-navy">{r.value}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </div>

        {/* Right column: fixed slots so every card aligns identically.
            Suppressed when the parent header already presents these figures. */}
        {!hideMetrics && (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:flex lg:flex-col lg:items-end lg:gap-3 lg:pl-2 lg:pt-0 lg:w-[180px]">
          <div className="min-w-0 lg:text-right">
            <div className="text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
              Amount billed
            </div>
            <div className="mt-0.5 font-display text-lg tabular-nums text-muted-foreground whitespace-nowrap">
              {currency(row.amount)}
            </div>
          </div>
          {row.savingsImpact ? (
            <div className="min-w-0 sm:text-right lg:text-right">
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-savings/80">
                Supported Adjustment Range
              </div>
              <div className="mt-0.5 font-display text-xl tabular-nums text-savings sm:whitespace-nowrap">
                {currency(row.savingsImpact[0])} – {currency(row.savingsImpact[1])}
              </div>
            </div>
          ) : (
            <div className="min-w-0 sm:text-right lg:text-right">
              <div
                className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${
                  assessmentImpliesAdjustment(row.assessment)
                    ? "text-warn/80"
                    : "text-muted-foreground"
                }`}
              >
                Impact
              </div>
              <div
                className={`mt-0.5 text-sm ${
                  assessmentImpliesAdjustment(row.assessment)
                    ? "text-warn font-semibold"
                    : "text-muted-foreground"
                }`}
              >
                {assessmentImpliesAdjustment(row.assessment)
                  ? "Adjustment Recommended"
                  : "No Adjustment Recommended"}
              </div>
            </div>
          )}

        </div>
        )}
      </div>
    </div>
  );
}

function MetaRow({
  icon: Icon,
  label,
  value,
  tone = "muted",
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string;
  tone?: "muted" | "trust";
}) {
  const cls = tone === "trust" ? "text-trust" : "text-muted-foreground";
  return (
    <div className="flex h-full min-w-0 items-start gap-2 py-1.5">
      <Icon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${cls}`} />
      <div className="min-w-0">
        <div className={`text-[10px] font-semibold uppercase tracking-[0.14em] ${cls}`}>
          {label}
        </div>
        <div className="mt-0.5 text-xs leading-snug text-navy">{value}</div>
      </div>
    </div>
  );
}

function AssessmentBadge({
  value,
  severity,
}: {
  value: Assessment;
  severity?: ChargeRow["severity"];
}) {
  const cls =
    value === "Within Market" || value === "Fair" || value === "Within Expected Range"
      ? "bg-savings-soft text-savings border-savings/25"
      : value === "Materially Above Market" || value === "Excessive"
        ? "bg-destructive/10 text-destructive border-destructive/30"
        : value === "Duplicate"
          ? "bg-destructive/10 text-destructive border-destructive/30"
          : value === "Unsupported"
            ? "bg-warn/10 text-warn border-warn/30"
            : value === "Needs Documentation"
              ? "bg-fasttrack-soft text-fasttrack border-fasttrack/40"
              : value === "Above Market" || value === "Negotiable" || value === "Questionable"
                ? "bg-warn/10 text-warn border-warn/30"
                : "bg-warn/10 text-warn border-warn/30";
  const showDot =
    severity === "critical" ||
    severity === "high" ||
    value === "Materially Above Market" ||
    value === "Above Market" ||
    value === "Duplicate" ||
    value === "Unsupported";
  return (
    <span
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-[0.08em] ${cls}`}
    >
      {showDot && <span className="h-1.5 w-1.5 rounded-full bg-current" />}
      {value}
    </span>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 4. Key Findings                                             */
/* ─────────────────────────────────────────────────────────── */

type Severity = "Low" | "Medium" | "High" | "Critical";

// FINDINGS fixture removed — fabricating dollar values or sample conclusions
// in a production customer-facing report is forbidden. When no real findings
// exist, KeyFindings renders an explicit empty state below.
const FINDINGS: {
  title: string;
  explanation: string;
  why: string;
  value: string;
  evidence: string;
  severity: Severity;
  confidence: number;
}[] = [];

const SEVERITY_STYLE: Record<
  Severity,
  { border: string; chipBg: string; chipText: string; dot: string }
> = {
  Low: {
    border: "border-l-savings",
    chipBg: "bg-savings-soft",
    chipText: "text-savings",
    dot: "bg-savings",
  },
  Medium: {
    border: "border-l-fasttrack",
    chipBg: "bg-fasttrack-soft",
    chipText: "text-fasttrack",
    dot: "bg-fasttrack",
  },
  High: {
    border: "border-l-warn",
    chipBg: "bg-warn/15",
    chipText: "text-warn",
    dot: "bg-warn",
  },
  Critical: {
    border: "border-l-destructive",
    chipBg: "bg-destructive/10",
    chipText: "text-destructive",
    dot: "bg-destructive",
  },
};

function severityLabel(s: string | null | undefined): Severity {
  const v = (s ?? "").toLowerCase();
  if (v === "critical") return "Critical";
  if (v === "high") return "High";
  if (v === "medium" || v === "moderate") return "Medium";
  return "Low";
}

interface FindingCardData {
  title: string;
  explanation: string;
  why: string;
  invoiceEvidence: string;
  marketEvidence: string | null;
  legalEvidence: string | null;
  aiReasoning: string;
  action: string;
  value: string;
  severity: Severity;
  /** SAB-004: report-wide analysis confidence, or null when not calculated. */
  confidence: number | null;
}

function findMarketEvidence(
  title: string,
  markets: MarketComparisonRow[] | undefined,
): string | null {
  if (!markets || markets.length === 0) return null;
  const key = title.toLowerCase();
  const m =
    markets.find((x) => (x.line_item ?? "").toLowerCase().includes(key.slice(0, 10))) ?? markets[0];
  if (!m) return null;
  if (typeof m.difference_percent === "number" && Math.abs(m.difference_percent) >= 1) {
    const sign = m.difference_percent > 0 ? "above" : "below";
    return `Invoiced rate is ${Math.abs(Math.round(m.difference_percent))}% ${sign} the local ${m.zip ?? m.zip_code ?? "market"} median (${
      typeof m.market_low === "number" && typeof m.market_high === "number"
        ? `${currency(m.market_low)} – ${currency(m.market_high)}`
        : "benchmark on file"
    }).`;
  }
  if (typeof m.market_low === "number" && typeof m.market_high === "number") {
    return `Local benchmark range: ${currency(m.market_low)} – ${currency(m.market_high)}${
      m.zip || m.zip_code ? ` in ZIP ${m.zip ?? m.zip_code}` : ""
    }.`;
  }
  return m.assessment ?? null;
}

function findLegalEvidence(title: string, legal: LegalResearch[] | undefined): string | null {
  if (!legal || legal.length === 0) return null;
  const key = title.toLowerCase();
  const l =
    legal.find((x) => (x.topic ?? "").toLowerCase().includes(key.slice(0, 10))) ??
    legal.find((x) => !!x.statute) ??
    legal[0];
  if (!l) return null;
  const cite = l.statute ?? l.citation ?? l.title ?? "Statute on file";
  const short = l.summary ?? l.why_it_matters ?? l.impact ?? "Supports negotiation basis.";
  return `${cite} — ${short}`;
}

function recommendedFor(sev: Severity): string {
  switch (sev) {
    case "Critical":
      return "Formally dispute this charge before payment and demand a revised invoice.";
    case "High":
      return "Present written objection with citations and negotiate a reduction.";
    case "Medium":
      return "Request adjustment or waiver with documented rationale.";
    case "Low":
    default:
      return "Note for future reference; no immediate action required.";
  }
}

function KeyFindings({
  pending,
  real,
  reportFindings,
  confidenceHint,
  marketComparisons,
  legalResearch,
}: {
  pending?: boolean;
  real?: FindingItem[] | null;
  reportFindings?: ReturnType<typeof buildReport>["findings"];
  confidenceHint?: number;
  marketComparisons?: MarketComparisonRow[];
  legalResearch?: LegalResearch[];
} = {}) {
  const items: FindingCardData[] = reportFindings
    ? reportFindings.map((f) => ({
        title: f.item,
        explanation: f.issue,
        why: "This charge affects the amount owed and should be supported by the submitted invoice.",
        invoiceEvidence: `Extracted charge on invoice: ${currency(f.charge)}.`,
        marketEvidence: findMarketEvidence(f.item, marketComparisons),
        legalEvidence: findLegalEvidence(f.item, legalResearch),
        aiReasoning:
          "Cross-referenced against local benchmarks, contract standards, and applicable statutes.",
        action: recommendedFor(
          f.impact === "High" ? "High" : f.impact === "Medium" ? "Medium" : "Low",
        ),
        value: `${currency(f.savings[0])} – ${currency(f.savings[1])}`,
        severity: f.impact === "High" ? "High" : f.impact === "Medium" ? "Medium" : "Low",
        confidence: toConfidencePct(confidenceHint),
      }))
    : real
      ? real.length
        ? real.map((f) => {
            const evidence = (f.evidence ?? {}) as Record<string, unknown>;
            const why =
              (typeof evidence.why_it_matters === "string" && evidence.why_it_matters) ||
              (typeof evidence.rationale === "string" && evidence.rationale) ||
              "This finding directly affects the amount owed and the invoice's defensibility.";
            const invoiceEv =
              (typeof evidence.source === "string" && evidence.source) ||
              (typeof evidence.reference === "string" && evidence.reference) ||
              (typeof evidence.note === "string" && evidence.note) ||
              (typeof f.amount === "number" && f.amount > 0
                ? `Extracted charge on invoice: ${currency(f.amount)}.`
                : "Extracted from the submitted invoice line items.");
            const reasoning =
              (typeof evidence.reasoning === "string" && evidence.reasoning) ||
              (typeof evidence.analysis === "string" && evidence.analysis) ||
              "Cross-referenced against local market rates, documentation standards, and applicable regulations.";
            const sev = severityLabel(f.severity);
            return {
              title: f.title,
              explanation: f.description ?? "",
              why,
              invoiceEvidence: invoiceEv,
              marketEvidence: findMarketEvidence(f.title, marketComparisons),
              legalEvidence: findLegalEvidence(f.title, legalResearch),
              aiReasoning: reasoning,
              action: recommendedFor(sev),
              value: typeof f.amount === "number" && f.amount > 0 ? currency(f.amount) : "Variable",
              severity: sev,
              confidence: toConfidencePct(confidenceHint),
            } satisfies FindingCardData;
          })
        : []
      : FINDINGS.map((f) => ({
          title: f.title,
          explanation: f.explanation,
          why: f.why,
          invoiceEvidence: f.evidence,
          marketEvidence:
            f.severity === "Critical" || f.severity === "High"
              ? "Local ZIP benchmarks reviewed."
              : null,
          legalEvidence:
            f.severity === "Critical" || f.severity === "High"
              ? "Applicable statute identified in Section 06."
              : null,
          aiReasoning:
            "Cross-referenced against local market rates, documentation standards, and applicable regulations.",
          action: recommendedFor(f.severity),
          value: f.value,
          severity: f.severity,
          confidence: f.confidence,
        }));

  return (
    <Section
      number="01"
      eyebrow="Forensic conclusions"
      title={<>Key Findings{pending && <PendingBadge />}</>}
      intro="Each finding is presented as a defensible audit conclusion with a full chain of supporting evidence."
    >
      {items.length === 0 ? (
        <EmptyRealState label="No findings have been extracted for this review." />
      ) : (
        <div className="space-y-5">
          {items.map((f, i) => (
            <FindingCard key={`${f.title}-${i}`} f={f} index={i} />
          ))}
        </div>
      )}
    </Section>
  );
}

function FindingCard({ f, index }: { f: FindingCardData; index: number }) {
  const s = SEVERITY_STYLE[f.severity];
  return (
    <article
      className={`rounded-2xl border border-border border-l-4 ${s.border} bg-card shadow-card transition hover:shadow-elevated print-avoid-break`}
    >
      {/* Header */}
      <header className="grid gap-4 border-b border-border p-6 md:grid-cols-[minmax(0,1fr)_auto] md:items-start md:p-7">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Finding {String(index + 1).padStart(2, "0")}
            </span>
            <span
              className={`inline-flex items-center gap-1.5 rounded-full ${s.chipBg} ${s.chipText} px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider`}
            >
              <span className={`h-1.5 w-1.5 rounded-full ${s.dot}`} />
              {f.severity} severity
            </span>
          </div>
          <h3 className="mt-3 font-display text-2xl leading-snug text-navy md:text-[26px]">
            {f.title}
          </h3>
          <p className="mt-3 max-w-3xl text-sm leading-relaxed text-muted-foreground">
            {f.explanation}
          </p>
        </div>
        <div className="flex flex-col gap-3 md:min-w-[200px] md:items-end">
          <div className="w-full rounded-xl border border-savings/25 bg-savings-soft px-4 py-3 md:text-right">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-savings/80">
              Supported recovery
            </div>
            <div className="mt-1 font-display text-xl tabular-nums text-savings">{f.value}</div>
          </div>
          {/* SAB-004: this is the report-wide analysis confidence, shown under
              its canonical name. It is never fabricated per finding. */}
          {f.confidence != null && (
            <div className="w-full">
              <div className="mb-1 flex items-center justify-between text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                <span>{ANALYSIS_CONFIDENCE_LABEL}</span>
                <span className="text-navy tabular-nums">{f.confidence}%</span>
              </div>
              <div className="h-1.5 overflow-hidden rounded-full bg-secondary">
                <div
                  className="h-full rounded-full bg-trust transition-all"
                  style={{ width: `${f.confidence}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </header>

      {/* Why + Action callouts */}
      <div className="grid gap-4 p-6 md:grid-cols-2 md:p-7">
        <div className="rounded-xl border-l-2 border-warn bg-warn/5 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-warn">
            Why this matters
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-navy">{f.why}</p>
        </div>
        <div className="rounded-xl border-l-2 border-trust bg-trust-soft/60 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-trust">
            Recommended negotiation action
          </div>
          <p className="mt-1.5 text-sm font-semibold leading-relaxed text-navy">{f.action}</p>
        </div>
      </div>

      {/* Evidence chain */}
      <details className="group border-t border-border">
        <summary className="flex cursor-pointer items-center justify-between gap-4 px-6 py-4 text-sm font-semibold text-navy hover:bg-secondary/40 md:px-7">
          <span className="flex items-center gap-2">
            <BookOpen className="h-4 w-4 text-trust" />
            Evidence Chain
            <span className="rounded-full bg-trust-soft px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-trust">
              6 steps
            </span>
          </span>
          <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
        </summary>
        <div className="px-6 pb-6 md:px-7">
          <EvidenceChain f={f} />
        </div>
      </details>
    </article>
  );
}

function EvidenceChain({ f }: { f: FindingCardData }) {
  const steps: {
    label: string;
    body: string;
    icon: React.ComponentType<{ className?: string }>;
    tone: "navy" | "trust" | "warn" | "savings";
    fallback?: boolean;
  }[] = [
    {
      label: "Invoice Evidence",
      body: f.invoiceEvidence,
      icon: FileText,
      tone: "navy",
    },
    {
      label: "Market Evidence",
      body: f.marketEvidence ?? "No matching local market benchmark for this line item.",
      icon: Scale,
      tone: "trust",
      fallback: !f.marketEvidence,
    },
    {
      label: "Legal Evidence",
      body: f.legalEvidence ?? "No matching statute or regulation identified for this line item.",
      icon: Landmark,
      tone: "trust",
      fallback: !f.legalEvidence,
    },
    {
      label: "AI Reasoning",
      body: f.aiReasoning,
      icon: Sparkles,
      tone: "trust",
    },
    {
      label: "Recommended Action",
      body: f.action,
      icon: ClipboardCheck,
      tone: "warn",
    },
    {
      label: "Supported Adjustment Range",
      body: `Supported adjustment range from this finding: ${f.value}.`,
      icon: TrendingDown,
      tone: "savings",
    },
  ];

  const toneCls = (t: "navy" | "trust" | "warn" | "savings") =>
    t === "savings"
      ? "text-savings bg-savings-soft border-savings/30"
      : t === "warn"
        ? "text-warn bg-warn/10 border-warn/30"
        : t === "trust"
          ? "text-trust bg-trust-soft border-trust/25"
          : "text-navy bg-secondary border-border";

  return (
    <ol className="relative mt-4 space-y-3 border-l border-dashed border-border pl-6">
      {steps.map((step, i) => (
        <li key={step.label} className="relative">
          <span
            className={`absolute -left-[33px] flex h-7 w-7 items-center justify-center rounded-full border ${toneCls(step.tone)} font-display text-xs font-bold`}
          >
            {i + 1}
          </span>
          <div className="rounded-xl border border-border bg-white p-4">
            <div className="flex items-center gap-2">
              <step.icon className={`h-4 w-4 ${toneCls(step.tone).split(" ")[0]}`} />
              <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                {step.label}
              </div>
            </div>
            <p
              className={`mt-1.5 text-sm leading-relaxed ${
                step.fallback ? "text-muted-foreground italic" : "text-navy"
              }`}
            >
              {step.body}
            </p>
          </div>
        </li>
      ))}
    </ol>
  );
}

function EmptyRealState({ label }: { label: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card p-6 text-sm font-medium text-muted-foreground shadow-card">
      {label}
    </div>
  );
}

function MetaBlock({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-white p-3">
      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-1 text-sm text-navy">{value}</dd>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 5. Market Comparison                                        */
/* ─────────────────────────────────────────────────────────── */

function marketAssessmentCls(a: MarketRow["assessment"]): string {
  switch (a) {
    case "Materially Above Market":
      return "border-destructive/40 bg-destructive/10 text-destructive";
    case "Above Market":
      return "border-warn/40 bg-warn/10 text-warn";
    case "Below Market":
      return "border-savings/30 bg-savings-soft text-savings";
    case "Within Market":
      return "border-savings/30 bg-savings-soft text-savings";
    default:
      return "border-border bg-secondary text-muted-foreground";
  }
}

function formatMoney(v: number | null): string {
  return typeof v === "number" ? currency(v) : NOT_EXTRACTED;
}

/**
 * Interleaved presentation of three existing components (unchanged):
 *   1. MarketRowLine (benchmark row)
 *   2. ChargeCard    (invoice charge analysis card)
 *   3. MarketBar     (visualization graph)
 *
 * Components are moved, not redesigned. For each invoice charge we render its
 * benchmark row, then its matching charge analysis card, then its matching
 * visualization graph. Unmatched charge rows still render their card so no
 * evidence is dropped.
 */
/**
 * How a charge earned (or failed to earn) its market support in Chapter 02.
 *  - "verified": its own independently retrieved benchmark evidence.
 *  - "reused":   a compatible verified benchmark applied from a sibling line.
 *  - "pending":  no compatible comparable pricing could be verified.
 */
type SupportBasis = "verified" | "reused" | "pending";

const REUSED_BENCHMARK_EXPLANATION =
  "A verified benchmark from the same service family, billing unit, region and service period was used because no directly comparable published benchmark exists. All amounts were recalculated using this line's own quantity.";

const PENDING_PRICING_EXPLANATION =
  "Published pricing was located; however, the billed trailer or equipment subtype could not be matched with sufficient confidence. No pricing recommendation is made.";

/** Evidence basis wording. Always subordinate to the market outcome. */
const SUPPORT_BASIS_LABEL: Record<SupportBasis, string> = {
  verified: "Verified Market",
  reused: "Comparable Benchmark Used",
  pending: "Comparable Pricing Not Available",
};


function InterleavedChargeAnalysis({
  pending,
  real,
  findingItems,
}: {
  pending?: boolean;
  real: {
    lineItems: Array<Record<string, unknown>>;
    fees: Array<Record<string, unknown>>;
    findingItems: FindingItem[];
    marketComparisons?: MarketComparisonRow[];
    marketResearch?: MarketResearch[];
    legalResearchCount?: number;
    legalResearch?: LegalResearch[];
  } | null;
  findingItems?: FindingItem[];
}) {
  if (!real) {
    return (
      <EmptyRealState label="Charge-by-charge analysis has not been extracted for this review." />
    );
  }
  const comparisons = real.marketComparisons ?? [];
  const marketRows: MarketRow[] = buildMarketRows(comparisons, findingItems);
  const chargeRows: ChargeRow[] = buildRealChargeRows(real);

  // T3 — Documentation & Mathematical Review. Runs alongside the certified
  // market benchmark engine and never alters it: it only supplies a truthful
  // outcome for lines that carry no verified band, so the queue stops
  // collapsing them into a generic "No Benchmark Available" chip.
  const docReviews = indexReviewsByDescription(
    buildDocumentationReviews(toDocumentationLines(real.lineItems ?? [])),
  );
  const docOutcomeFor = (service: string): string | null =>
    documentationOutcomeLabel(docReviews.get(normalizeDocDescription(service)));

  /* ────────────────────────────────────────────────────────────────
     Presentation source of truth: the SAME normalized invoice charge
     inventory Chapter 02 renders. Benchmark records never create a
     visible row — they only attach as supporting evidence beneath the
     invoice charge they belong to. Calculations are untouched.
     ──────────────────────────────────────────────────────────────── */
  const { canonical: inventory } = buildChargeInventory(real.lineItems ?? []);

  const used = new Set<number>();
  const findChargeFor = (service: string): ChargeRow | null => {
    const key = service.toLowerCase();
    for (let i = 0; i < chargeRows.length; i++) {
      if (used.has(i)) continue;
      const d = chargeRows[i].description.toLowerCase();
      if (!d || !key) continue;
      if (d.includes(key.slice(0, 10)) || key.includes(d.slice(0, 10))) {
        used.add(i);
        return chargeRows[i];
      }
    }
    return null;
  };

  /** ChargeRow lookup keyed by the parsed SERVICE FAMILY of its raw text. */
  const chargeRowQueue = new Map<string, ChargeRow[]>();
  chargeRows.forEach((r) => {
    const k = normalizeChargeKey(parseChargeDescription(r.description).familyName);
    const q = chargeRowQueue.get(k) ?? [];
    q.push(r);
    chargeRowQueue.set(k, q);
  });
  const takeChargeRow = (c: InventoryCharge): ChargeRow | null => {
    const q = chargeRowQueue.get(c.normalized);
    const row = q && q.length ? q.shift()! : null;
    // Canonical Chapter 02 naming — never the raw OCR text.
    return row ? { ...row, description: c.displayName } : null;
  };

  const syntheticChargeRow = (c: InventoryCharge): ChargeRow => ({
    description: c.displayName,
    category: /storage/i.test(c.familyName) ? "Storage" : "Line item",
    amount: c.amount ?? 0,
    assessment: "Needs Documentation",
    severity: "low",
    chip: severityToChip("low"),
    whyItMatters: "No verified market benchmark is on file for this charge yet.",
    evidence: null,
    marketNote: null,
    legalNote: null,
    recommendedAction: "Request supporting documentation for this charge.",
    savingsImpact: null,
  });

  type Block =
    | {
        kind: "matched";
        id: string;
        service: string;
        invoice: number | null;
        assessment: string;
        recovery?: number | null;
        recoveryBasis?: "benchmark" | "non-market" | "none";
        noRecoveryReason?: string;
        /** Chapter 02 line variance (invoice − expected), display only. */
        variance?: number | null;
        /** Subordinate evidence basis wording. */
        basisLabel?: string;
        row: MarketRow;
        charge: ChargeRow | null;
        chargeEvidence: NormalizedChargeEvidence | undefined;
        emphasize: boolean;
        supportBasis?: SupportBasis;
      }
    | {
        kind: "unmatched";
        id: string;
        service: string;
        invoice: number | null;
        assessment: string;
        recovery?: number | null;
        recoveryBasis?: "benchmark" | "non-market" | "none";
        noRecoveryReason?: string;
        variance?: number | null;
        basisLabel?: string;
        charge: ChargeRow;
        isFlagged: boolean;
        supportBasis?: SupportBasis;
      };

  /**
   * Chapter 02 support classification carried into Chapter 03 verbatim, so
   * both chapters agree on which charges are market-supported. No benchmark
   * value, expected amount or recovery is recalculated here.
   */
  type ChargeSupport = {
    assessment: string;
    supported: boolean;
    basis: SupportBasis;
    label: string;
    /** Chapter 02's own computed difference for this line. Read, never derived. */
    variance: number | null;
  };

  const blocks: Block[] = [];

  /** Documented reason a row shows no recovery. Never blank. */
  const reasonFor = (row: MarketRow): string | undefined =>
    row.savingsHigh > 0
      ? undefined
      : !row.hasBenchmark
        ? (docOutcomeFor(row.service) ?? absenceLabel("NOT_EVIDENCED"))
        : row.assessment === "Below Market"
          ? "Below Market"
          : row.assessment === "Within Market"
            ? "Within Market"
            : "No Recovery Calculated";

  const pushMatched = (
    id: string,
    service: string,
    row: MarketRow,
    charge: ChargeRow | null,
    invoice: number | null,
    support?: ChargeSupport,
  ) => {
    const highVariance = typeof row.variancePct === "number" && row.variancePct >= 100;
    const assessment = support?.assessment ?? row.assessment;
    const flagged = assessment === "Above Market" || assessment === "Materially Above Market";
    const unsupported = support ? !support.supported : false;
    blocks.push({
      kind: "matched",
      id,
      service,
      invoice,
      assessment,
      /* An unsupported charge never carries a market recovery figure. */
      recovery: unsupported ? null : row.savingsHigh > 0 ? row.savingsHigh : null,
      recoveryBasis: unsupported ? "none" : row.savingsBasis,
      noRecoveryReason: unsupported ? PENDING_PRICING_EXPLANATION : reasonFor(row),
      row: support ? { ...row, assessment: assessment as MarketRow["assessment"] } : row,
      /* The embedded charge card must state the same outcome as the row. */
      charge:
        charge && support
          ? { ...charge, assessment: assessment as ChargeRow["assessment"] }
          : charge,
      chargeEvidence: deriveChargeEvidence(charge, row, findingItems),
      emphasize: flagged || (!unsupported && highVariance),
      supportBasis: support?.basis,
      variance: unsupported ? null : (support?.variance ?? null),
      basisLabel: support ? SUPPORT_BASIS_LABEL[support.basis] : undefined,
    });
  };

  const pushUnmatched = (
    id: string,
    c: ChargeRow,
    invoice: number | null,
    support?: ChargeSupport,
  ) => {
    const impact = c.savingsImpact ? Math.max(0, Math.round(c.savingsImpact[1])) : 0;
    const recovery = impact > 0 ? Math.min(impact, Math.max(0, c.amount || impact)) : null;
    const unsupported = support ? !support.supported : false;
    const assessment = support?.assessment ?? c.assessment ?? "No Benchmark";
    blocks.push({
      kind: "unmatched",
      id,
      service: c.description,
      invoice,
      assessment,
      recovery: unsupported ? null : recovery,
      recoveryBasis: unsupported ? "none" : recovery ? "non-market" : "none",
      noRecoveryReason: unsupported
        ? PENDING_PRICING_EXPLANATION
        : recovery
          ? undefined
          : (docOutcomeFor(c.description) ?? absenceLabel("NOT_EVIDENCED")),
      charge: support ? { ...c, assessment: assessment as ChargeRow["assessment"] } : c,
      isFlagged: support
        ? assessment === "Above Market" || assessment === "Materially Above Market"
        : c.severity !== "none" && c.severity !== "low",
      supportBasis: support?.basis,
      variance: unsupported ? null : (support?.variance ?? null),
      basisLabel: support ? SUPPORT_BASIS_LABEL[support.basis] : undefined,
    });
  };

  /** How many benchmark records support each invoice charge. */
  const supportingByCharge = new Map<InventoryCharge, number>();
  let verifiedCount = 0;

  if (inventory.length === 0) {
    /* Legacy / preview reports carry no captured invoice lines, so the
       benchmark rows remain the row set exactly as before. */
    marketRows.forEach((row, i) => {
      pushMatched(
        `interleave-${row.service}-${i}`,
        row.service,
        row,
        findChargeFor(row.service),
        row.invoiceCharge ?? null,
      );
      if (row.hasBenchmark) verifiedCount += 1;
    });
    chargeRows.forEach((c, i) => {
      if (used.has(i)) return;
      pushUnmatched(
        `unmatched-charge-${c.description}-${i}`,
        c,
        (c as { amount?: number }).amount ?? null,
      );
    });
  } else {
    /* Bind each benchmark record to its invoice charge. The first record
       bound to a charge supplies its analysis; every additional record is
       merged in as supporting evidence and NEVER becomes another row. */
    const primary = new Map<InventoryCharge, MarketRow>();
    const consumed = new Set<InventoryCharge>();
    const orderedBound: InventoryCharge[] = [];
    const leftovers: number[] = [];

    comparisons.forEach((m, i) => {
      const charge = findInventoryChargeForMarketRow(m, inventory, consumed);
      if (!charge) {
        leftovers.push(i);
        return;
      }
      consumed.add(charge);
      primary.set(charge, marketRows[i]);
      supportingByCharge.set(charge, 1);
      orderedBound.push(charge);
    });

    for (const i of leftovers) {
      const charge = findInventoryChargeForMarketRow(comparisons[i], inventory, new Set());
      if (!charge) continue;
      supportingByCharge.set(charge, (supportingByCharge.get(charge) ?? 0) + 1);
    }

    const ordered = [...orderedBound, ...inventory.filter((c) => !consumed.has(c))];

    /* Support classification is READ from Chapter 02's row builder, never
       recomputed here, so the two chapters can never disagree on which
       charges are market-supported. Row order is identical by construction
       (same inventory, same benchmark binding order). */
    const summaryRows = buildChargeSummaryRows(
      comparisons,
      real.lineItems ?? [],
      real.marketResearch ?? [],
    );
    const aligned = summaryRows.length === ordered.length ? summaryRows : null;

    /* Rows whose customer-facing label would otherwise be identical are
       qualified with a BILLED detail the customer can see on their invoice
       (quantity, unit, rate or service period). Internal line IDs are never
       exposed. */
    const labelCounts = new Map<string, number>();
    ordered.forEach((c) =>
      labelCounts.set(c.displayName, (labelCounts.get(c.displayName) ?? 0) + 1),
    );

    const ORDINALS = ["First", "Second", "Third", "Fourth", "Fifth", "Sixth"];

    /** A customer-visible billed qualifier for an otherwise duplicate label. */
    const billedQualifier = (c: InventoryCharge): string | null => {
      const raw = `${c.description} ${c.displayName}`;
      const unit = /hour|hr\b|hrs\b/i.test(raw)
        ? "Hours"
        : /\bday|daily\b/i.test(raw)
          ? "Days"
          : /\bmile/i.test(raw)
            ? "Miles"
            : null;
      const qty = typeof c.quantity === "number" && c.quantity > 0 ? c.quantity : null;
      if (qty !== null && unit) {
        const n = Number.isInteger(qty) ? qty : Math.round(qty * 100) / 100;
        return `${n} ${n === 1 ? unit.replace(/s$/, "") : unit}`;
      }
      if (qty !== null && typeof c.rate === "number" && c.rate > 0)
        return `${qty} × ${currency(c.rate)}`;
      if (typeof c.rate === "number" && c.rate > 0) return `${currency(c.rate)} Rate`;
      if (c.periodKey) return c.periodKey;
      return null;
    };

    /* Pre-compute qualifiers so collisions fall back to billed order. */
    const qualifierSeen = new Map<string, number>();
    const labelByIndex = new Map<number, string>();
    ordered.forEach((c, i) => {
      const base = c.displayName;
      if ((labelCounts.get(base) ?? 0) < 2) {
        labelByIndex.set(i, base);
        return;
      }
      const q = billedQualifier(c);
      const key = `${base}|${q ?? ""}`;
      const seen = qualifierSeen.get(key) ?? 0;
      qualifierSeen.set(key, seen + 1);
      const dupQualifier = ordered.some(
        (o, j) => j !== i && o.displayName === base && billedQualifier(o) === q,
      );
      const suffix =
        q && !dupQualifier
          ? q
          : q
            ? `${q} · ${ORDINALS[seen] ?? `${seen + 1}th`} Billed Line`
            : `${ORDINALS[seen] ?? `${seen + 1}th`} Billed Line`;
      labelByIndex.set(i, base.includes(suffix) ? base : `${base} — ${suffix}`);
    });

    const labelFor = (_charge: InventoryCharge, index: number): string =>
      labelByIndex.get(index) ?? _charge.displayName;

    const supportFor = (charge: InventoryCharge, index: number): ChargeSupport | undefined => {
      const sr = aligned?.[index];
      if (!sr) return undefined;
      const supported = sr.expectedAmount !== null;
      /* Applied benchmark: the line carries no independently retrieved
         benchmark record of its own — its expected amount comes from a
         compatible verified benchmark on the same claim, recalculated with
         this line's own quantity. */
      const reused =
        Boolean((sr as { benchmarkReusedFromLineId?: string | null }).benchmarkReusedFromLineId) ||
        !primary.has(charge);
      return {
        assessment: supported ? sr.status.label : "Requires Additional Market Support",
        supported,
        basis: supported ? (reused ? "reused" : "verified") : "pending",
        label: sr.sourceLabel ?? "",
        /* Chapter 02's own difference for this line — read, never recomputed. */
        variance:
          supported && typeof sr.difference === "number" && sr.difference > 0
            ? Math.round(sr.difference)
            : null,
      };
    };

    let supportedCount = 0;

    ordered.forEach((charge, i) => {
      const row = primary.get(charge) ?? null;
      const chargeRow = takeChargeRow(charge);
      const support = supportFor(charge, i);
      const service = labelFor(charge, i);
      if (support?.supported ?? row?.hasBenchmark) supportedCount += 1;
      if (row) {
        // Canonical Chapter 02 naming for the visible row. Benchmark math
        // (band, average, variance, recovery) is passed through untouched.
        pushMatched(
          `ch03-${charge.id}`,
          service,
          { ...row, service },
          chargeRow ? { ...chargeRow, description: service } : null,
          charge.amount ?? row.invoiceCharge ?? null,
          support,
        );
        return;
      }
      const fallback = chargeRow ?? syntheticChargeRow(charge);
      pushUnmatched(
        `ch03-${charge.id}-${i}`,
        { ...fallback, description: service },
        charge.amount ?? null,
        support,
      );
    });

    verifiedCount = supportedCount;
  }

  const chargesReviewed = inventory.length || blocks.length;
  const pendingCount = Math.max(0, chargesReviewed - verifiedCount);

  /**
   * Methodology. Implementation detail — always rendered LAST, after the
   * conclusion, evidence and comparison chart.
   */
  const SupportNote = ({ basis }: { basis?: SupportBasis }) => {
    if (basis !== "reused" && basis !== "pending") return null;
    const reused = basis === "reused";
    return (
      <div
        className={`border-t border-border/60 px-5 py-2 ${
          reused ? "bg-trust-soft/30" : "bg-secondary/40"
        }`}
      >
        <div className="text-[9px] font-semibold uppercase tracking-[0.16em] text-muted-foreground/70">
          Methodology
        </div>
        <p className="mt-0.5 whitespace-normal break-words text-[11.5px] leading-snug text-muted-foreground">
          <span className="font-semibold text-navy">
            {reused ? SUPPORT_BASIS_LABEL.reused : SUPPORT_BASIS_LABEL.pending}
          </span>{" "}
          — {reused ? REUSED_BENCHMARK_EXPLANATION : PENDING_PRICING_EXPLANATION}
        </p>
      </div>
    );
  };


  const renderBlock = (b: Block) => {
    if (b.kind === "matched") {
      return (
        <div
          key={b.id}
          className={`overflow-hidden rounded-2xl border bg-trust-soft/25 shadow-card ${
            b.emphasize ? "!border-warn/20 shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "!border-navy/15"
          }`}
          data-pdf-keep-together="true"
        >
          <div className="border-b border-border/80 bg-navy/[0.04]">
            <MarketRowLine
              row={b.row}
              findingItems={findingItems}
              emphasized
              charge={b.charge}
              evidence={b.chargeEvidence}
            />
          </div>
          {b.charge && (
            <div className="flex flex-wrap items-center gap-2 border-b border-border/50 px-5 py-1.5">
              <AssessmentBadge value={b.charge.assessment} severity={b.charge.severity} />
            </div>
          )}
          {b.charge && (
            <div className="px-2 pt-1">

              <ChargeCard
                row={b.charge}
                embedded
                hideIdentity
                hideMetrics
                evidence={b.chargeEvidence}
              />
            </div>
          )}
          <div className="border-t border-dashed border-border/50 px-5 pb-3 pt-2">
            <MarketBar row={b.row} hideTitle />
          </div>
          <SupportNote basis={b.supportBasis} />
        </div>
      );
    }
    const c = b.charge;
    return (
      <div
        key={b.id}
        className={`overflow-hidden rounded-2xl border bg-trust-soft/25 shadow-card ${
          b.isFlagged ? "!border-warn/20 shadow-[0_1px_0_rgba(0,0,0,0.02)]" : "!border-navy/15"
        }`}
        data-pdf-keep-together="true"
      >
        <div
          className={`border-b border-border/80 bg-navy/[0.04] px-5 py-3 border-l-[3px] ${
            b.isFlagged ? "border-l-warn/70" : "border-l-trust/50"
          }`}
        >
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="min-w-0">
              <h4 className="font-display text-[19px] font-semibold leading-snug tracking-tight text-navy">
                {c.description}
              </h4>
              <div className="mt-1 flex flex-wrap items-center gap-2">
                <AssessmentBadge value={c.assessment} severity={c.severity} />
                <span className="text-[10.5px] font-medium uppercase tracking-[0.12em] text-muted-foreground/70">
                  {c.category}
                </span>
              </div>
            </div>
          </div>
        </div>
        <div className="px-2 pt-1">
          <ChargeCard row={c} embedded hideIdentity />
        </div>
        <SupportNote basis={b.supportBasis} />
      </div>
    );
  };

  return (
    <InteractiveChargeAnalysis
      pending={pending}
      blocks={blocks}
      renderBlock={renderBlock}
      chargesReviewed={chargesReviewed}
      supportingSources={comparisons.length}
      verifiedCharges={verifiedCount}
      pendingCharges={pendingCount}
    />
  );
}

/* ────────────────────────────────────────────────────────────────── */
/* Interactive Market Evidence Review                                 */
/* PDF: renders every block sequentially (unchanged behavior).        */
/* Web: charge table (always visible) + toolbar + focused analysis.   */
/* ────────────────────────────────────────────────────────────────── */

type ChargeAnalysisBlock = {
  /** Whether the row carries a market benchmark row (matched) or not. */
  kind?: "matched" | "unmatched";
  id: string;
  service: string;
  invoice: number | null;
  assessment: string;
  recovery?: number | null;
  /** Where the recovery came from — drives the badge wording. */
  recoveryBasis?: "benchmark" | "non-market" | "none";
  /** Documented reason shown when a row has no recovery. Never blank. */
  noRecoveryReason?: string;
  /** Chapter 02 line variance, display only. */
  variance?: number | null;
  /** Subordinate evidence-basis wording (never the outcome). */
  basisLabel?: string;
  /** Evidence classification, used to keep basis wording honest. */
  supportBasis?: "verified" | "reused" | "pending";
};

type QueueGroupKey = "significantly-above" | "above" | "within" | "below" | "no-benchmark";

const QUEUE_GROUP_ORDER: QueueGroupKey[] = [
  "significantly-above",
  "above",
  "within",
  "below",
  "no-benchmark",
];

/**
 * One customer-facing outcome vocabulary. `label` names the group and
 * `outcome` is the badge every row inside that group must state — they are
 * never allowed to diverge.
 */
const QUEUE_GROUP_META: Record<
  QueueGroupKey,
  { label: string; outcome: string; dot: string; accent: string; marketBased: boolean }
> = {
  "significantly-above": {
    label: "Materially Above Market",
    outcome: "Materially Above Market",
    dot: "bg-destructive",
    accent: "border-l-destructive/70",
    marketBased: true,
  },
  above: {
    label: "Above Market",
    outcome: "Above Market",
    dot: "bg-warn",
    accent: "border-l-warn/70",
    marketBased: true,
  },
  within: {
    /* Price-position outcome only. Market support is an evidence
       classification and is reported separately, never as a group. */
    label: "Within Market",
    outcome: "Within Market",
    dot: "bg-trust",
    accent: "border-l-trust/60",
    marketBased: false,
  },
  below: {
    label: "Below Market",
    outcome: "Below Market",
    dot: "bg-trust",
    accent: "border-l-trust/60",
    marketBased: true,
  },
  "no-benchmark": {
    label: "Requires Additional Market Support",
    outcome: "Comparable Pricing Not Available",
    dot: "bg-muted-foreground/50",
    accent: "border-l-border",
    marketBased: false,
  },
};

// Group findings strictly by the market-benchmark comparison result. Recovery
// opportunities from duplicate billing, regulatory caps, or documentation
// issues must NEVER move a finding out of its true market bucket.
function classifyQueueGroup(assessment: string): QueueGroupKey {
  const a = (assessment || "").toLowerCase();
  if (a.includes("materially") || a.includes("significantly")) return "significantly-above";
  if (a.includes("above")) return "above";
  if (a.includes("below")) return "below";
  if (a.includes("within") || a.includes("at market") || a.includes("at-market")) return "within";
  return "no-benchmark";
}

function InteractiveChargeAnalysis<B extends ChargeAnalysisBlock>({
  pending,
  blocks,
  renderBlock,
  chargesReviewed,
  supportingSources,
  verifiedCharges,
  pendingCharges,
}: {
  pending?: boolean;
  blocks: B[];
  /** Unique invoice charges presented (Chapter 02 inventory). */
  chargesReviewed?: number;
  /** Benchmark records backing those charges — evidence, not rows. */
  supportingSources?: number;
  verifiedCharges?: number;
  pendingCharges?: number;
  renderBlock: (b: B) => React.ReactNode;
}) {
  const mode = useRenderMode();
  const rs = useReportSummary();
  const scopedExport = useScopedExportActive();
  const [selectedId, setSelectedId] = useState<string | null>(blocks[0]?.id ?? null);
  /* Web default is "Focus selected": exactly one benchmark finding card is
     visible and Previous/Next moves between findings. "Show all analyses"
     is an explicit customer opt-in. Any export (chapter PDF, full PDF,
     print) overrides the web mode below so a PDF always renders every
     reviewed charge exactly once. */
  const [expandAll, setExpandAll] = useState(false);
  const detailRef = useRef<HTMLDivElement | null>(null);
  const queueId = useId();

  const grouped = QUEUE_GROUP_ORDER.map((key) => ({
    key,
    meta: QUEUE_GROUP_META[key],
    items: blocks.filter((b) => classifyQueueGroup(b.assessment) === key),
  })).filter((g) => g.items.length > 0);

  const [openGroups, setOpenGroups] = useState<Record<string, boolean>>(() =>
    Object.fromEntries(QUEUE_GROUP_ORDER.map((k) => [k, true])),
  );

  useEffect(() => {
    if (!blocks.length) {
      setSelectedId(null);
      return;
    }
    if (!blocks.some((b) => b.id === selectedId)) {
      setSelectedId(blocks[0].id);
    }
  }, [blocks, selectedId]);

  if (!blocks.length) {
    return <EmptyRealState label="Market evidence has not been prepared for this review." />;
  }

  // PDF: sequential — unchanged.
  if (mode === "pdf") {
    return (
      <div className="space-y-7">
        {pending && <PendingBadge label="Prepared during review" />}
        {blocks.map((b) => renderBlock(b))}
      </div>
    );
  }

  const currentIdx = Math.max(
    0,
    blocks.findIndex((b) => b.id === selectedId),
  );
  const selectedBlock = blocks.find((b) => b.id === selectedId) ?? blocks[0];
  // An export always renders the complete review, regardless of focus mode.
  const showAll = expandAll || scopedExport;
  const detailBlocks: B[] = showAll ? blocks : selectedBlock ? [selectedBlock] : [];
  const total = blocks.length;

  const detailAnchorId = (id: string) => `ch03-detail-${id}`;

  // Selection-only navigator. Previous/Next and queue clicks update the
  // selected benchmark and auto-expand a collapsed group, but never move
  // the viewport in either Focus Selected or Show All Analyses mode.
  const selectAndScroll = (id: string) => {
    setSelectedId(id);
    const grp = grouped.find((g) => g.items.some((b) => b.id === id));
    if (grp && !openGroups[grp.key]) {
      setOpenGroups((prev) => ({ ...prev, [grp.key]: true }));
    }
  };

  const goTo = (delta: number) => {
    const next = blocks[currentIdx + delta];
    if (next) selectAndScroll(next.id);
  };

  const setAllGroups = (open: boolean) => {
    setOpenGroups(Object.fromEntries(grouped.map((g) => [g.key, open])));
  };

  // Report-wide counts come from the immutable Report Summary only.
  const summaryStats: Array<{ label: string; field: string; value: number }> = [
    {
      label: "Invoice Charges Reviewed",
      field: "totalInvoiceCharges",
      value: rs.counts.totalInvoiceCharges,
    },
    {
      label: "Market-Supported Charges",
      field: "marketSupportedCharges",
      value: rs.counts.marketSupportedCharges,
    },
    {
      label: "Pending Comparable Pricing",
      field: "pendingComparablePricing",
      value: rs.counts.pendingComparablePricing,
    },
    {
      label: "Supporting Market Sources",
      field: "supportingMarketSources",
      value: rs.counts.supportingMarketSources,
    },
    /* Omitted when the canonical accepted-source register is empty: a "0" tile
       beside the queue's own evidence statement reads as a contradiction. The
       count is never recomputed from another source. */
  ].filter((s) => s.field !== "supportingMarketSources" || s.value > 0);

  return (
    <div className="space-y-5" {...summarySection("Chapter 02 — Market Benchmark Analysis")}>
      {pending && <PendingBadge label="Prepared during review" />}

      {/* Coverage summary — one line per invoice charge, evidence beneath it. */}
      <dl className="grid grid-cols-2 gap-3 lg:grid-cols-4" aria-label="Chapter coverage summary">
        {summaryStats.map((s) => (
          <div
            key={s.label}
            className="rounded-xl border border-border bg-card px-4 py-3 shadow-card"
          >
            <dt className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              {s.label}
            </dt>
            <dd
              className="mt-1 font-display text-2xl leading-none text-navy"
              {...summaryField(s.field, s.value)}
            >
              {s.value}
            </dd>
          </div>
        ))}
      </dl>

      {/* Market Evidence Queue — grouped by benchmark outcome */}
      <div
        className="overflow-hidden rounded-2xl border border-border bg-card shadow-card"
        data-pdf-exclude="true"
        aria-label="Market Evidence Queue"
      >
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border bg-secondary/60 px-5 py-3">
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
              Market Evidence Queue
            </div>
            <div className="mt-0.5 text-sm text-navy">
              Review each invoice charge one at a time.
              <span className="ml-2 text-muted-foreground">
                <span {...chapterMetric("chapter-03", "benchmarkQueueRows", total)}>
                  {total} invoice {total === 1 ? "charge" : "charges"}
                </span>
                {supportingSources
                  ? ` · ${supportingSources} supporting market ${supportingSources === 1 ? "source" : "sources"}`
                  : ""}
                .
              </span>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => setAllGroups(true)}
              aria-label="Expand all queue groups"
              className="rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px] font-semibold text-navy transition hover:bg-secondary"
            >
              Expand All
            </button>
            <button
              type="button"
              onClick={() => setAllGroups(false)}
              aria-label="Collapse all queue groups"
              className="rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px] font-semibold text-navy transition hover:bg-secondary"
            >
              Collapse All
            </button>
          </div>
        </div>

        <div
          id={queueId}
          role="listbox"
          aria-label="Benchmark findings"
          className="divide-y divide-border"
        >
          {grouped.map((g) => {
            const open = openGroups[g.key] ?? true;
            const groupHeaderId = `${queueId}-grp-${g.key}`;
            const groupPanelId = `${queueId}-panel-${g.key}`;
            return (
              <div key={g.key} className={`border-l-[3px] ${g.meta.accent}`}>
                <button
                  type="button"
                  id={groupHeaderId}
                  aria-expanded={open}
                  aria-controls={groupPanelId}
                  onClick={() => setOpenGroups((prev) => ({ ...prev, [g.key]: !prev[g.key] }))}
                  className="flex w-full items-center justify-between gap-3 bg-navy/[0.02] px-5 py-2.5 text-left transition hover:bg-navy/[0.05] focus:outline-none focus-visible:ring-2 focus-visible:ring-navy/40"
                >
                  <div className="flex min-w-0 items-center gap-2.5">
                    <span className={`h-2 w-2 rounded-full ${g.meta.dot}`} aria-hidden />
                    <span className="font-display text-[12px] font-bold uppercase tracking-[0.14em] text-navy">
                      {g.meta.label}
                    </span>
                    <span className="rounded-full bg-white/70 px-2 py-0.5 text-[10px] font-semibold text-muted-foreground">
                      {g.items.length}
                    </span>
                  </div>
                  <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                    {open ? "Hide" : "Show"}
                  </span>
                </button>
                {open && (
                  <ul
                    id={groupPanelId}
                    role="group"
                    aria-labelledby={groupHeaderId}
                    className="divide-y divide-border/70"
                  >
                    {g.items.map((b) => {
                      const selected = b.id === selectedId;
                      const recovery =
                        typeof b.recovery === "number" && b.recovery > 0 ? b.recovery : null;
                      /* The row's outcome is inherited from its group, which
                         itself was assigned from the Chapter 02 status. Row
                         and group can therefore never contradict each other,
                         and process wording never reaches the badge. */
                      const outcome = g.meta.outcome;
                      const outcomeTone =
                        g.key === "significantly-above"
                          ? "bg-destructive/10 text-destructive"
                          : g.key === "above"
                            ? "bg-warn/10 text-warn"
                            : g.key === "no-benchmark"
                              ? "bg-muted text-muted-foreground ring-1 ring-inset ring-border"
                              : "bg-trust/10 text-trust";
                      /* Recovery wording is customer-facing: what the invoice
                         could come down by, not an internal savings term. */
                      /* PHASE 6.4 — REPAIR 2. The certified Chapter 02
                         difference is quantity-adjusted and is the only value
                         allowed under "Total Supported Recovery". A matched
                         row's `recovery` is a PER-UNIT benchmark figure and is
                         labelled as such — never as a total. */
                      const certifiedTotal =
                        typeof b.variance === "number" && b.variance > 0 ? b.variance : null;
                      const chargeLevelRecovery = b.kind === "unmatched" ? recovery : null;
                      const reduction = certifiedTotal ?? chargeLevelRecovery ?? recovery;
                      const reductionLabel =
                        certifiedTotal != null || chargeLevelRecovery != null
                          ? "Total Supported Recovery"
                          : "Per-Unit Benchmark Difference";
                      const marketBased = b.recoveryBasis
                        ? b.recoveryBasis === "benchmark"
                        : g.meta.marketBased;
                      /* A row without a market comparison must never claim a
                         verified market basis; it states its true basis. */
                      const basisLabel =
                        g.key === "no-benchmark" && b.supportBasis !== "pending"
                          ? marketBased
                            ? undefined
                            : "Non-Market Basis"
                          : b.basisLabel;
                      return (
                        <li key={b.id}>
                          <button
                            type="button"
                            role="option"
                            aria-selected={selected}
                            aria-current={selected ? "true" : undefined}
                            onClick={() => selectAndScroll(b.id)}
                            className={`relative flex w-full items-start justify-between gap-3 pl-6 pr-5 py-3 text-left text-sm transition focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-navy/40 ${
                              selected
                                ? "bg-[#FEF6C7] ring-1 ring-inset ring-navy/25"
                                : "hover:bg-secondary/50"
                            }`}
                          >
                            {selected && (
                              <span
                                aria-hidden
                                className="pointer-events-none absolute inset-y-0 left-0 w-[5px] bg-navy"
                              />
                            )}
                            <span className="min-w-0 flex-1">
                              <span
                                className={`block truncate font-display text-[14px] text-navy ${selected ? "font-bold" : "font-semibold"}`}
                              >
                                {b.service}
                              </span>
                              {(reduction != null || basisLabel) && (
                                <span className="mt-0.5 block truncate text-[11px] text-muted-foreground">
                                  {reduction != null
                                    ? `${reductionLabel}: ${formatMoney(reduction)}`
                                    : basisLabel}
                                  {reduction != null && basisLabel ? ` · ${basisLabel}` : ""}
                                </span>
                              )}
                            </span>

                            <span
                              className={`mt-0.5 shrink-0 whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${outcomeTone}`}
                            >
                              {outcome}
                            </span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Toolbar — Prev / Counter / Next + detail mode */}
      <div
        className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-secondary/40 px-4 py-3"
        data-pdf-exclude="true"
      >
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => goTo(-1)}
            disabled={currentIdx === 0}
            aria-label="Previous benchmark"
            className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-navy shadow-sm transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Previous
          </button>
          <div
            className="px-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground"
            aria-live="polite"
          >
            Benchmark Finding {currentIdx + 1} of {total}
          </div>
          <button
            type="button"
            onClick={() => goTo(1)}
            disabled={currentIdx >= total - 1}
            aria-label="Next benchmark"
            className="inline-flex items-center gap-1 rounded-md border border-navy bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground shadow-sm transition hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
          >
            Next <ArrowRight className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setExpandAll(true)}
            disabled={expandAll}
            aria-label="Show all benchmark analyses"
            aria-pressed={expandAll}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px] font-semibold text-navy transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            + Show all analyses
          </button>
          <button
            type="button"
            onClick={() => setExpandAll(false)}
            disabled={!expandAll}
            aria-label="Focus on selected benchmark"
            aria-pressed={!expandAll}
            className="rounded-md border border-border bg-background px-2.5 py-1.5 text-[11px] font-semibold text-navy transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
          >
            − Focus selected
          </button>
        </div>
      </div>

      {/* Detail — the complete review renders every charge exactly once in
          canonical order; Focus Selected narrows the on-screen reading view
          only, and is overridden during any export. */}
      <div ref={detailRef} className="space-y-7 scroll-mt-24">
        {detailBlocks.map((b) => (
          <div
            key={b.id}
            id={detailAnchorId(b.id)}
            {...(b.id === selectedBlock?.id
              ? { "data-selected": "true", "aria-current": "true" as const }
              : {})}
            aria-label={
              b.id === selectedBlock?.id ? "Selected benchmark analysis" : undefined
            }
            data-charge-analysis-card="true"
            className={
              "scroll-mt-24 rounded-xl transition " +
              (showAll && b.id === selectedBlock?.id
                ? "ring-2 ring-navy/40 ring-offset-2 ring-offset-background"
                : "")
            }
          >
            {renderBlock(b)}
          </div>
        ))}
      </div>

    </div>
  );
}

function MarketComparison({
  r,
  pending,
  real,
  findingItems,
  part = "all",
}: {
  r: ReturnType<typeof buildReport>;
  pending?: boolean;
  real?: MarketComparisonRow[] | null;
  findingItems?: FindingItem[];
  /**
   * Render mode. "all" preserves the legacy standalone Section wrapper.
   * The narrative parts skip the Section wrapper so the parent chapter
   * can compose them as a consulting-report flow.
   */
  part?: "all" | "summary" | "table" | "viz";
}) {
  const emptyReal = !!(real && real.length === 0);
  const rows: MarketRow[] = emptyReal ? [] : buildMarketRows(real ?? [], findingItems);
  const zip = real?.[0]?.zip_code ?? real?.[0]?.zip ?? r.zip;
  const state = real?.[0]?.state ?? r.state;

  const flagged = rows.filter(
    (row) => row.assessment === "Above Market" || row.assessment === "Materially Above Market",
  );
  const totalSavingsLow = flagged.reduce((s, x) => s + x.savingsLow, 0);
  const totalSavingsHigh = flagged.reduce((s, x) => s + x.savingsHigh, 0);
  const worst = [...rows].sort(
    (a, b) => (b.variancePct ?? -Infinity) - (a.variancePct ?? -Infinity),
  )[0];

  const summary = emptyReal
    ? ""
    : flagged.length > 0
      ? `Based on ${rows.length} local benchmark${rows.length === 1 ? "" : "s"} for comparable providers within ${zip ? `ZIP ${zip}` : "the invoice region"}${state ? `, ${state}` : ""}, this invoice exceeds typical regional pricing in ${flagged.length} categor${flagged.length === 1 ? "y" : "ies"}. The variance supports a documented case for negotiation.`
      : `Based on ${rows.length} local benchmark${rows.length === 1 ? "" : "s"} for comparable providers within ${zip ? `ZIP ${zip}` : "the invoice region"}${state ? `, ${state}` : ""}, this invoice's charges are broadly consistent with regional pricing.`;

  // Empty state: only render when the chapter would otherwise show nothing.
  if (emptyReal) {
    if (part !== "all" && part !== "summary") return null;
    const emptyBody = (
      <>
        <EmptyRealState label="Market comparison has not been extracted for this review." />
        <MethodologyPanel
          title="How local pricing is benchmarked"
          subtitle="Every charge is priced against comparable providers using a repeatable, evidence-based process."
          items={[
            {
              icon: MapPin,
              label: "Geographic scope",
              description:
                "Comparable operators inside the invoice's ZIP code, expanded to the surrounding region only when the local sample is thin.",
            },
            {
              icon: Database,
              label: "Data sources",
              description:
                "Published rate cards, prior ClaimGuard reviews, and state-filed tariffs — never anecdotal or self-reported quotes.",
            },
            {
              icon: Scale,
              label: "Variance analysis",
              description:
                "Each invoice line is compared to the local median and expected range; only material variances are flagged.",
            },
          ]}
        />
        <CoverageNote message="No comparable benchmarks were returned for this ZIP code during the retrieval window. This is a coverage gap, not a finding of fair pricing — pricing will be re-benchmarked automatically when new comparables become available." />
      </>
    );
    if (part === "summary") return <div>{emptyBody}</div>;
    return (
      <Section
        bare
        number="01"
        eyebrow={`Market Intelligence · ZIP ${r.zip}`}
        title={<>Local Market Comparison{pending && <PendingBadge />}</>}
        intro="Independent price benchmarking against comparable providers in the invoice's ZIP code and region."
      >
        {emptyBody}
      </Section>
    );
  }

  const summaryBlock = (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-7">
      <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-trust">
        <Scale className="h-3.5 w-3.5" /> Analyst summary
        {pending && <PendingBadge />}
      </div>
      <p className="mt-3 max-w-3xl font-display text-xl leading-snug text-navy md:text-2xl">
        {summary}
      </p>
      {worst && worst.variancePct !== null && worst.variancePct > 0 && (
        <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-warn/30 bg-warn/10 px-3 py-1 text-xs font-semibold text-warn">
          <TrendingDown className="h-3.5 w-3.5" />
          Largest variance: {worst.service} · +{Math.round(worst.variancePct)}% above local median
        </div>
      )}
    </div>
  );

  const tableBlock = (
    <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-card">
      <div className="hidden gap-4 border-b border-border bg-secondary/60 px-5 py-3 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground md:grid md:grid-cols-[1.6fr_1fr_1fr_1.2fr_0.9fr_1.3fr_1.1fr]">
        <div>Service</div>
        <div className="text-right">Invoice</div>
        <div className="text-right">Market Avg</div>
        <div className="text-right">Expected Range</div>
        <div className="text-right">Variance</div>
        <div>Supporting Evidence</div>
        <div className="text-right">Savings Opportunity</div>
      </div>
      <div className="divide-y divide-border">
        {rows.map((row, i) => (
          <MarketRowLine key={`${row.service}-${i}`} row={row} findingItems={findingItems} />
        ))}
      </div>
    </div>
  );

  const vizBlock =
    rows.length > 0 ? (
      <div className="rounded-2xl border border-border bg-card p-6 shadow-card md:p-7">
        <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
          <div>
            <div className="eyebrow text-trust">Visualization</div>
            <h3 className="mt-1 font-display text-xl text-navy">Invoice vs. local market</h3>
          </div>
          <div className="flex flex-wrap items-center gap-3 text-[11px] font-medium text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm bg-executive-red" /> Vendor amount
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm bg-executive-blue" /> Market average
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-1 rounded-sm bg-navy" /> Expected max
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="h-2.5 w-4 rounded-sm bg-savings" /> Total Supported Recovery
            </span>
          </div>
        </div>
        <div className="space-y-4">
          {rows.map((row, i) => (
            <MarketBar key={`${row.service}-viz-${i}`} row={row} />
          ))}
        </div>
      </div>
    ) : null;

  const aggregateBlock =
    totalSavingsHigh > 0 ? (
      <div className="flex items-center gap-4 rounded-2xl border border-savings/30 bg-savings-soft px-6 py-5 shadow-card">
        <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-savings text-savings-foreground">
          <TrendingDown className="h-5 w-5" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-[11px] font-semibold uppercase tracking-[0.14em] text-savings/90">
            Market-based recovery
          </div>
          <div className="mt-0.5 font-display text-2xl text-savings">
            {currency(totalSavingsLow)} – {currency(totalSavingsHigh)}
          </div>
        </div>
        <div className="hidden text-xs text-savings/80 sm:block">
          Aggregated from {flagged.length} above-market service{flagged.length === 1 ? "" : "s"}.
        </div>
      </div>
    ) : null;

  if (part === "summary") return summaryBlock;
  if (part === "table") return tableBlock;
  if (part === "viz") {
    return (
      <div className="space-y-6">
        {vizBlock}
        {aggregateBlock}
      </div>
    );
  }

  return (
    <Section
      bare
      number="01"
      eyebrow={`Market Intelligence · ZIP ${zip}${state ? ` · ${state}` : ""}`}
      title={<>Local Market Comparison{pending && <PendingBadge />}</>}
      intro="Independent price benchmarking against comparable providers in the invoice's region."
    >
      <div className="mb-6">{summaryBlock}</div>
      {tableBlock}
      {vizBlock && <div className="mt-6">{vizBlock}</div>}
      {aggregateBlock && <div className="mt-6">{aggregateBlock}</div>}
    </Section>
  );
}

/**
 * Strict evidence applicability: only surface an icon when the underlying
 * data genuinely supports it. Never decorative, never assumed, never
 * placeholder — if a signal isn't present in the row or its matched
 * finding, the icon is omitted.
 */
type EvidenceKind =
  | "law"
  | "regulation"
  | "carrier"
  | "market"
  | "vendor"
  | "docs"
  | "photo"
  | "gps"
  | "timeline"
  | "nova"
  | "human";

interface EvidenceItem {
  key: EvidenceKind;
  label: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  /** Optional anchor id to jump to in the same document. */
  href?: string;
  colorClass: string;
}

const EVIDENCE_META: Record<
  EvidenceKind,
  {
    label: string;
    description: string;
    icon: React.ComponentType<{ className?: string }>;
    colorClass: string;
  }
> = {
  law: {
    label: "Law",
    description: "Applicable statute cited for this charge",
    icon: Scale,
    colorClass: "bg-navy/10 text-navy border-navy/30",
  },
  regulation: {
    label: "Regulation",
    description: "Regulatory limit or rule that governs this charge",
    icon: Landmark,
    colorClass: "bg-orange-100 text-orange-700 border-orange-300",
  },
  carrier: {
    label: "Carrier Guideline",
    description: "Insurance carrier reasonableness guideline",
    icon: ShieldCheck,
    colorClass: "bg-purple-100 text-purple-700 border-purple-300",
  },
  market: {
    label: "Local Market",
    description: "Local market rate comparison",
    icon: TrendingDown,
    colorClass: "bg-blue-100 text-blue-700 border-blue-300",
  },
  vendor: {
    label: "Vendor Intelligence",
    description: "Vendor-specific pricing history or profile",
    icon: Building2,
    colorClass: "bg-teal-100 text-teal-700 border-teal-300",
  },
  docs: {
    label: "Supporting Documentation",
    description: "Document evidence attached to the case",
    icon: FileText,
    colorClass: "bg-gray-100 text-gray-700 border-gray-300",
  },
  photo: {
    label: "Photo Evidence",
    description: "Photo attached to the case supports this finding",
    icon: FileText,
    colorClass: "bg-rose-100 text-rose-700 border-rose-300",
  },
  gps: {
    label: "GPS / Distance",
    description: "GPS or measured distance evidence",
    icon: MapPin,
    colorClass: "bg-green-100 text-green-700 border-green-300",
  },
  timeline: {
    label: "Timeline",
    description: "Time-based sequence supports this finding",
    icon: CalendarDays,
    colorClass: "bg-indigo-100 text-indigo-700 border-indigo-300",
  },
  nova: {
    label: "Nova™ Analysis",
    description: "Detected by Nova™ AI analysis",
    icon: Sparkles,
    colorClass: "bg-cyan-100 text-cyan-700 border-cyan-300",
  },
  human: {
    label: "Human Verified",
    description: "Reviewed and verified by a specialist",
    icon: CheckCircle2,
    colorClass: "bg-emerald-100 text-emerald-700 border-emerald-300",
  },
};

// =========================================================================
// deriveChargeEvidence — SINGLE SOURCE OF TRUTH for Chapter 03 evidence.
//
// Both the Evidence header icons AND the Evidence body rows on a charge
// MUST consume the same normalized object returned here. A body row without
// its matching header icon (or vice versa) is a reconciliation defect.
//
// Every field represents VERIFIED evidence traceable to a real record:
//   - No prose keyword matching outside classifyLegalNote (which drives BOTH
//     the header and the body law/regulation rendering).
//   - No generic source-label triggers.
//   - No inferred categories or fallback flags.
// =========================================================================
export interface NormalizedChargeEvidence {
  market: { present: boolean; text: string | null };
  law: { present: boolean; text: string | null };
  regulation: { present: boolean; text: string | null };
  vendor: boolean;
  carrier: boolean;
  documentation: boolean;
  photo: boolean;
  gps: boolean;
  timeline: boolean;
  nova: boolean;
  humanVerified: boolean;
}

function matchFindingForRow(
  row: MarketRow | null | undefined,
  findingItems: FindingItem[] | undefined,
): FindingItem | undefined {
  if (!row) return undefined;
  const norm = (s: string) =>
    s
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, " ")
      .trim();
  const rowKey = norm(row.service);
  if (!rowKey) return undefined;
  return (findingItems ?? []).find((f) => {
    const t = norm(f.title ?? "");
    if (!t) return false;
    return (
      t.includes(rowKey) ||
      rowKey.includes(t) ||
      rowKey.split(" ").some((w) => w.length > 3 && t.includes(w))
    );
  });
}

export function deriveChargeEvidence(
  charge: ChargeRow | null | undefined,
  row: MarketRow | null | undefined,
  findingItems: FindingItem[] | undefined,
): NormalizedChargeEvidence {
  const legal = (charge?.legalNote ?? "").trim();
  const { showLaw: proseLaw, showReg: proseReg } = classifyLegalNote(legal);

  const match = matchFindingForRow(row, findingItems);
  const evidenceObj = (match?.evidence ?? {}) as Record<string, unknown>;
  const evLaw = hasLawEvidence(evidenceObj);
  const evReg = hasRegulationEvidence(evidenceObj);

  const marketPresent =
    (!!row && row.localAverage !== null && row.localAverage !== undefined) ||
    !!(charge?.marketNote && charge.marketNote.trim().length > 0);

  const verified = match as unknown as { verified?: boolean; reviewed?: boolean } | undefined;

  return {
    market: {
      present: marketPresent,
      text: charge?.marketNote?.trim() || null,
    },
    law: {
      present: proseLaw || evLaw,
      text: proseLaw ? legal : null,
    },
    regulation: {
      present: proseReg || evReg,
      text: proseReg ? legal : null,
    },
    vendor: hasVendorEvidence(evidenceObj),
    carrier: hasCarrierEvidence(evidenceObj),
    documentation: hasDocumentEvidence(evidenceObj),
    photo: hasPhotoEvidence(evidenceObj),
    gps: hasGpsEvidence(evidenceObj),
    timeline: hasTimelineEvidence(evidenceObj),
    nova: hasNovaEvidence(evidenceObj),
    humanVerified: verified?.verified === true || verified?.reviewed === true,
  };
}

function computeEvidenceForRow(
  row: MarketRow,
  findingItems: FindingItem[] | undefined,
  charge?: ChargeRow | null,
  precomputed?: NormalizedChargeEvidence,
): EvidenceItem[] {
  const evidence: EvidenceItem[] = [];
  const push = (key: EvidenceKind, href?: string) => {
    if (evidence.some((e) => e.key === key)) return;
    const m = EVIDENCE_META[key];
    evidence.push({ key, ...m, href });
  };

  const norm = precomputed ?? deriveChargeEvidence(charge, row, findingItems);

  if (norm.market.present) push("market", "#chapter-03");
  if (norm.law.present) push("law", "#chapter-04");
  if (norm.regulation.present) push("regulation", "#chapter-06");
  if (norm.carrier) push("carrier");
  if (norm.vendor) push("vendor");
  if (norm.documentation) push("docs");
  if (norm.photo) push("photo");
  if (norm.gps) push("gps");
  if (norm.timeline) push("timeline");
  if (norm.nova) push("nova");
  if (norm.humanVerified) push("human");

  // Dev-time reconciliation guard — warn if the visible body would render an
  // evidence row that has no matching icon, or vice versa. Never appears in
  // production builds.
  if (import.meta.env.DEV && charge) {
    const has = (k: EvidenceKind) => evidence.some((e) => e.key === k);
    if (norm.market.present !== has("market")) {
      console.warn("[evidence-recon] Market row/icon mismatch", charge.description);
    }
    if (norm.law.present !== has("law")) {
      console.warn("[evidence-recon] Law row/icon mismatch", charge.description);
    }
    if (norm.regulation.present !== has("regulation")) {
      console.warn("[evidence-recon] Regulation row/icon mismatch", charge.description);
    }
  }

  return evidence;
}

function EvidenceIconRow({ items }: { items: EvidenceItem[] }) {
  if (items.length === 0) {
    return <span className="text-xs text-muted-foreground">—</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-1.5" role="list" aria-label="Evidence">
      {items.map((it) => {
        const content = (
          <>
            <it.icon className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sr-only">{it.label}</span>
          </>
        );
        const className = `inline-flex h-7 w-7 items-center justify-center rounded-full border ${it.colorClass}`;
        const title = `${it.label} — ${it.description}`;
        if (it.href) {
          return (
            <a
              key={it.key}
              href={it.href}
              role="listitem"
              title={title}
              aria-label={title}
              className={`${className} transition hover:scale-105`}
            >
              {content}
            </a>
          );
        }
        return (
          <span key={it.key} role="listitem" title={title} aria-label={title} className={className}>
            {content}
          </span>
        );
      })}
    </div>
  );
}

function MarketRowLine({
  row,
  findingItems,
  emphasized = false,
  charge = null,
  evidence,
}: {
  row: MarketRow;
  findingItems: FindingItem[] | undefined;
  emphasized?: boolean;
  charge?: ChargeRow | null;
  evidence?: NormalizedChargeEvidence;
}) {
  const claimVehicleClass = useContext(ClaimVehicleClassContext);
  // Section 6 — name the market family this charge was compared against.
  const benchmarkFamilyLabel = classifyBenchmarkFamily({
    description: row.service,
    vehicleClass: claimVehicleClass,
  }).marketLabel;
  const flagged = row.assessment === "Above Market" || row.assessment === "Materially Above Market";

  /* Presentation-only truth guard: a charge with no comparable market evidence
     has no market average, no range and no variance. Rendering "$0", "$0 – $0"
     and "0%" would state that the market price for this service is zero — a
     claim no source supports. Nothing is computed here; the empty evidence is
     simply not dressed up as a benchmark. */
  const hasMarketAverage = typeof row.localAverage === "number" && row.localAverage > 0;
  const hasMarketRange = typeof row.high === "number" && row.high > 0;
  const hasMarketEvidence = hasMarketAverage || hasMarketRange;

  // Unified color language (final lock):
  //   Invoice / Vendor value + bar → ALWAYS executive red. Never green/blue/orange.
  //   Market Avg → always blue (trust).
  //   Market Range → always neutral.
  //   Recovery Range / Potential Savings → always green (savings).
  //   Variance is the ONLY dynamic value: green within, orange above, red materially above.
  const invoiceToneCls = "text-executive-red";
  const variance =
    row.variancePct === null || !hasMarketAverage
      ? "—"
      : `${row.variancePct > 0 ? "+" : ""}${Math.round(row.variancePct)}%`;
  const varianceCls =
    row.variancePct === null
      ? "text-muted-foreground"
      : row.assessment === "Materially Above Market"
        ? "text-executive-red"
        : row.assessment === "Above Market"
          ? "text-executive-red/80"
          : "text-savings";

  const evidenceItems = computeEvidenceForRow(row, findingItems, charge, evidence);
  const py = emphasized ? "py-3.5" : "py-3";
  const titleCls = emphasized
    ? "font-display font-semibold text-[19px] text-navy leading-snug tracking-tight"
    : "font-display font-semibold text-[15px] text-navy leading-snug tracking-tight";
  const numCls = emphasized ? "text-[15px]" : "";
  const Label = ({ children }: { children: React.ReactNode }) =>
    emphasized ? (
      <div className="hidden lg:block text-[9px] font-medium uppercase tracking-[0.14em] text-muted-foreground/70">
        {children}
      </div>
    ) : null;

  const highVariance = typeof row.variancePct === "number" && row.variancePct >= 100;
  const accentCls =
    flagged || highVariance
      ? "border-l-[3px] border-l-executive-red/60"
      : "border-l-[3px] border-l-trust/50";

  return (
    <div
      className={`grid grid-cols-1 gap-3 px-5 ${py} text-sm lg:grid-cols-[1.9fr_0.85fr_0.75fr_1.1fr_0.75fr_1.05fr_1.2fr] lg:gap-3 lg:items-center ${accentCls}`}
    >
      <div className="flex min-w-0 items-start gap-3">
        {emphasized && (
          <span className="mt-0.5 flex h-8 w-8 flex-none items-center justify-center rounded-full bg-fasttrack/15 text-fasttrack">
            <Waves className="h-3.5 w-3.5" />
          </span>
        )}
        <div className="min-w-0 lg:min-h-[2.75rem]">
          <div className={`${titleCls} line-clamp-1`}>{row.service}</div>
          <div className="mt-0.5 text-[10.5px] leading-snug text-muted-foreground/70 line-clamp-2">
            {benchmarkFamilyLabel}
            {row.source ? ` · ${row.source}` : ""}
          </div>
        </div>
      </div>
      <div className="lg:text-right">
        <Label>Invoice Rate</Label>
        <div className={`tabular-nums lg:hidden`}>
          <span className="text-xs text-muted-foreground mr-2">Invoice:</span>
          <span className={`font-semibold ${invoiceToneCls}`}>
            {formatMoney(row.invoiceCharge)}
          </span>
        </div>
        <div
          className={`hidden lg:block tabular-nums font-semibold ${invoiceToneCls} ${emphasized ? "text-[17px]" : "text-[15px]"}`}
        >
          {formatMoney(row.invoiceCharge)}
        </div>
      </div>
      <div className="lg:text-right">
        <Label>Market Avg</Label>
        <div className={`tabular-nums text-executive-blue ${numCls}`}>
          <span className="lg:hidden text-xs text-muted-foreground mr-2">Market avg:</span>
          <span className="font-semibold">
            {hasMarketAverage ? formatMoney(row.localAverage) : "—"}
          </span>
        </div>
      </div>
      <div className="lg:text-right">
        <Label>Market Range</Label>
        <div className={`tabular-nums text-muted-foreground whitespace-nowrap ${numCls}`}>
          <span className="lg:hidden text-xs text-muted-foreground mr-2">Range:</span>
          {hasMarketRange && row.low !== null && row.high !== null
            ? `${currency(row.low)} – ${currency(row.high)}`
            : NOT_EXTRACTED}
        </div>
      </div>
      <div className="lg:text-right">
        <Label>Variance</Label>
        <div
          className={`font-semibold tabular-nums ${varianceCls} ${emphasized ? "text-[17px]" : "text-[15px]"}`}
        >
          {variance}
        </div>
      </div>
      <div>
        {/* Evidence is rendered only when evidence actually exists — never an
            empty placeholder slot. */}
        {evidenceItems.length > 0 && (
          <>
            <Label>Evidence</Label>
            <div className="mt-0.5">
              <span className="lg:hidden text-xs text-muted-foreground mr-2">Evidence:</span>
              <EvidenceIconRow items={evidenceItems} />
            </div>
          </>
        )}
      </div>

      <div className="lg:text-right">
        <Label>Supported Adjustment Range</Label>
        {row.savingsHigh > 0 ? (
          <div
            className={`font-display font-semibold tabular-nums text-savings whitespace-nowrap ${emphasized ? "text-[17px]" : "text-[15px]"}`}
          >
            {currency(row.savingsLow)} – {currency(row.savingsHigh)}
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">No Adjustment Recommended</span>
        )}
      </div>
    </div>
  );
}

function MarketBar({ row, hideTitle = false }: { row: MarketRow; hideTitle?: boolean }) {
  const inv = row.invoiceCharge ?? 0;
  const avg = row.localAverage ?? 0;
  /* A "$0" average or a "$0 – $0" band is the absence of evidence, not a
     market benchmark. The chart must not draw an expected-max marker at zero,
     print a zero market average, or badge a 0% variance against it. */
  const hasMarketAverage = avg > 0;
  const hasMarketRange = typeof row.high === "number" && row.high > 0;
  const hasRange = hasMarketRange && row.low != null && row.high != null;
  const hi = hasRange ? (row.high as number) : 0;
  const scaleMax = Math.max(hi, inv, avg) * 1.15 || 1;
  const invPct = Math.min(100, (inv / scaleMax) * 100);
  const avgPct = avg > 0 ? Math.min(100, (avg / scaleMax) * 100) : 0;
  const hiPct = hasRange ? Math.min(100, (hi / scaleMax) * 100) : 0;
  const v = row.variancePct ?? 0;
  const materiallyAbove = row.assessment === "Materially Above Market";
  const above = row.assessment === "Above Market" || materiallyAbove;

  // Invoice/Vendor bar + text: ALWAYS executive red, matching the table.
  const invColor = "from-executive-red to-executive-red/70";
  const invTextColor = "text-executive-red";
  // Variance badge: only value that changes color with assessment.
  const varianceBadgeCls = materiallyAbove
    ? "bg-executive-red/12 text-executive-red"
    : above
      ? "bg-executive-red/[0.08] text-executive-red/85"
      : "bg-savings/10 text-savings";

  return (
    <div className={hideTitle ? "" : "rounded-xl border border-border bg-secondary/30 p-4"}>
      <div className="mb-3 flex flex-wrap items-baseline justify-between gap-2">
        {!hideTitle && <div className="font-semibold text-navy">{row.service}</div>}
        <div className="flex items-center gap-3 text-xs tabular-nums">
          <span className="text-muted-foreground">
            Market avg:{" "}
            <span className="text-executive-blue font-semibold">
              {hasMarketAverage ? formatMoney(row.localAverage) : "—"}
            </span>
          </span>
          <span className="text-muted-foreground">
            Invoice:{" "}
            <span className={`${invTextColor} font-semibold`}>
              {formatMoney(row.invoiceCharge)}
            </span>
          </span>
          {hasMarketAverage && row.variancePct !== null && (
            <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${varianceBadgeCls}`}>
              {v > 0 ? "+" : ""}
              {Math.round(v)}%
            </span>
          )}
        </div>
      </div>
      <div className="space-y-2">
        {/* Invoice bar */}
        <div className="flex items-center gap-3">
          <div className="w-24 shrink-0 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80 whitespace-nowrap">
            Invoice
          </div>
          <div className="relative flex-1 h-5 rounded-md bg-white border border-border overflow-hidden">
            <div
              className={`h-full rounded-md bg-gradient-to-r ${invColor} transition-all`}
              style={{ width: `${invPct}%` }}
            />
            {hasRange && (
              <div
                className="absolute top-0 h-full w-[2px] bg-navy"
                style={{ left: `${hiPct}%` }}
                title={`Expected max ${formatMoney(row.high)}`}
              />
            )}
          </div>
          <div
            className={`w-24 shrink-0 text-right text-xs font-semibold tabular-nums ${invTextColor}`}
          >
            {formatMoney(row.invoiceCharge)}
          </div>
        </div>
        {/* Local average bar */}
        <div className="flex items-center gap-3">
          <div className="w-24 shrink-0 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80 whitespace-nowrap">
            Market Avg
          </div>
          <div className="relative flex-1 h-5 rounded-md bg-white border border-border overflow-hidden">
            <div
              className="h-full rounded-md bg-gradient-to-r from-executive-blue to-executive-blue/70 transition-all"
              style={{ width: `${avgPct}%` }}
            />
            {hasRange && (
              <div
                className="absolute top-0 h-full w-[2px] bg-navy"
                style={{ left: `${hiPct}%` }}
              />
            )}
          </div>
          <div className="w-24 shrink-0 text-right text-xs font-semibold tabular-nums text-executive-blue">
            {hasMarketAverage ? formatMoney(row.localAverage) : "—"}
          </div>
        </div>
        {/* Expected max caption — pinned to the marker column */}
        {hasRange && (
          <div className="flex items-center gap-3 pt-0.5">
            <div className="w-24 shrink-0" />
            <div className="relative flex-1">
              <div
                className="absolute -top-1 flex -translate-x-1/2 items-center gap-1 whitespace-nowrap text-[9.5px] font-semibold uppercase tracking-[0.14em] text-navy"
                style={{ left: `${hiPct}%` }}
              >
                <span className="text-navy">↑</span>
                <span>Expected Max · {formatMoney(row.high)}</span>
              </div>
            </div>
            <div className="w-24 shrink-0" />
          </div>
        )}
      </div>
      <div className="mt-4 text-[9.5px] font-semibold uppercase tracking-[0.14em] text-muted-foreground/80">
        {hasRange ? (
          <span>
            Range: {formatMoney(row.low)} – {formatMoney(row.high)}
          </span>
        ) : (
          <span>Market range unavailable</span>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 6. Applicable Laws & Regulations — Legal Intelligence       */
/* ─────────────────────────────────────────────────────────── */

// LAWS / REGS placeholder fixtures were removed to eliminate any risk of
// customer-visible fabricated statutes (e.g. "Reference placeholder · §XXX.XX").
// ApplicableLaws / ApplicableRegulations now render a truthful empty state
// when the legal retrieval pipeline returns no jurisdiction-specific results.
const LAWS: Array<{
  title: string;
  summary: string;
  matters: string;
  impact: string;
  ref: string;
}> = [];
const REGS: Array<{
  title: string;
  summary: string;
  matters: string;
  impact: string;
  ref: string;
}> = [];

interface LegalCardData {
  title: string;
  ref: string;
  plainEnglish: string;
  whyApplies: string;
  customerBenefit: string;
  strength: "Weak" | "Moderate" | "Strong" | "Very Strong" | "Not Rated";
  negotiationValue: "Low" | "Medium" | "High" | "Very High" | "Not Rated";
  /** SAB-004: retrieval relevance 0-100, or null when the authority is unscored. */
  confidence: number | null;
  // Actual invoice charges (line-item / fee categories present on this
  // invoice) that fall within the statute's legal scope. Sourced from
  // invoice data — NOT from findings.
  relatedCharges: string[];
  // Optional grouped presentation of relatedCharges, used by the
  // Regulation card in Chapter 06 to improve readability. When present,
  // it renders instead of the flat list. Statute cards (Chapter 04)
  // continue to render the flat `relatedCharges` list unchanged.
  relatedChargeGroups?: Array<{ label: string; items: string[] }>;
  // Consolidated list of report findings the statute materially supports.
  // Sourced from finding data via the statute-specific bucket rule.
  supportedFindings: string[];
  domains: string[];
  /** Compact legal-category label shown as a badge under the statute citation. */
  legalCategory: string;

  jurisdiction?: string;
  /** SAB-005 — persisted evidence lineage for this authority. */
  lineage: LegalAuthorityLineage;
  regulatoryFacts?: Array<{
    category: string;
    requirement: string;
    valueDisplay: string;
    condition?: string | null;
  }>;
}

// ── Invoice-charge classification ────────────────────────────────
// Distinct from the finding-domain classifier: this reads the actual
// invoice line-item / fee descriptions and buckets each into one of a
// fixed set of charge categories. Used only to source "Related to
// Charges" — the section is grounded in what is really billed on this
// invoice, not in findings.
type ChargeCategory =
  | "tow"
  | "hookup"
  | "storage"
  | "notification"
  | "admin"
  | "lien"
  | "release"
  | "surcharge"
  | "mileage"
  | "dolly"
  | "winching";

const CHARGE_CATEGORY_LABELS: Record<ChargeCategory, string> = {
  tow: "Tow Charge",
  hookup: "Hook-up Fee",
  storage: "Storage Fee",
  notification: "Notification Fee",
  admin: "Administrative Fee",
  lien: "Lien Fee",
  release: "Release Fee",
  surcharge: "Surcharge",
  mileage: "Mileage",
  dolly: "Dolly Fee",
  winching: "Winching / Recovery Fee",
};

function classifyChargeCategory(desc: string): ChargeCategory | null {
  const s = desc.toLowerCase();
  // Order matters: check specific tow-side categories first, then fall
  // through to broader "tow" so a descriptor like "Initial Tow (14
  // miles)" is booked as a tow charge rather than mileage.
  if (/hook[- ]?up/.test(s)) return "hookup";
  if (/\bwinch\w*/.test(s)) return "winching";
  if (/\bdolly\b/.test(s)) return "dolly";
  if (/\btow(ing)?\b|wrecker|flatbed|road ?service/.test(s)) return "tow";
  if (/mileage|per[- ]?mile|\bmiles?\b/.test(s)) return "mileage";
  if (/storage|per[- ]?day|daily rate/.test(s)) return "storage";
  if (/\blien\b/.test(s)) return "lien";
  if (/notification|notice fee|inventory|certified mail/.test(s)) return "notification";
  if (/after[- ]?hours|\brelease\b|\bgate\b/.test(s)) return "release";
  if (/admin|processing|documentation|paperwork/.test(s)) return "admin";
  if (/surcharge|convenience|environmental|\bfuel\b|credit ?card|cleanup/.test(s))
    return "surcharge";
  return null;
}

/**
 * Group invoice line items by charge category, preserving each item's
 * actual invoice description so Chapter 04 can display the customer's
 * real charge names instead of generic category labels. Repeated
 * descriptions (case-insensitive) are consolidated into a single entry
 * with an occurrence count for readability — e.g. "Outdoor Storage
 * (14 days)" for storage lines, otherwise "<desc> (×N)".
 */
function presentInvoiceChargesByCategory(
  lineItems: Array<Record<string, unknown>> | undefined | null,
): Map<ChargeCategory, string[]> {
  type Bucket = { display: string; category: ChargeCategory; count: number };
  const buckets = new Map<string, Bucket>(); // key = cat + '|' + desc.toLowerCase()
  const order: string[] = [];

  for (const li of lineItems ?? []) {
    const desc = String(
      (li as { description?: unknown; name?: unknown; label?: unknown; item?: unknown })
        .description ??
        (li as { name?: unknown }).name ??
        (li as { label?: unknown }).label ??
        (li as { item?: unknown }).item ??
        "",
    ).trim();
    if (!desc) continue;
    const cat = classifyChargeCategory(desc);
    if (!cat) continue;
    const rawQty = (li as { quantity?: unknown }).quantity;
    const qty = typeof rawQty === "number" && rawQty > 0 ? Math.floor(rawQty) : 1;
    const key = `${cat}|${desc.toLowerCase()}`;
    const existing = buckets.get(key);
    if (existing) {
      existing.count += qty;
    } else {
      buckets.set(key, { display: desc, category: cat, count: qty });
      order.push(key);
    }
  }

  const out = new Map<ChargeCategory, string[]>();
  for (const key of order) {
    const b = buckets.get(key)!;
    let label = b.display;
    if (b.count > 1) {
      label =
        b.category === "storage" ? `${b.display} (${b.count} days)` : `${b.display} (×${b.count})`;
    }
    const arr = out.get(b.category) ?? [];
    arr.push(label);
    out.set(b.category, arr);
  }
  return out;
}

// Domain classifier shared by toLegalCard and downstream summaries.
// Tags: tow | storage | notice | fees | deceptive | dueProcess
const LEGAL_DOMAIN_LABELS: Record<string, string> = {
  tow: "towing operations",
  storage: "storage & impound",
  notice: "notice & lien procedure",
  fees: "fee limits & rate rules",
  deceptive: "consumer protection / deceptive practices",
  dueProcess: "due-process & hearing rights",
};
function classifyLegalDomains(raw: string): Set<string> {
  const s = raw.toLowerCase();
  const tags = new Set<string>();
  if (
    /\btow(ing)?\b|hook[- ]?up|winch|road.?service|rotation|dolly|flatbed|mileage|release fee/.test(
      s,
    )
  )
    tags.add("tow");
  if (/\bstorage\b|per[- ]?day|daily rate|impound(?!ment abandonment)|vsf|vehicle storage/.test(s))
    tags.add("storage");
  if (/\bnotice\b|notification|lien|abandon|redemption|certified mail/.test(s)) tags.add("notice");
  if (
    /\bfee(s)?\b|surcharge|administrative|admin fee|gate fee|after[- ]?hours|processing|reasonable|rate|cap|maximum|schedule/.test(
      s,
    )
  )
    tags.add("fees");
  if (/deceptive|misrepresent|dtpa|unfair|false|misleading|consumer protection/.test(s))
    tags.add("deceptive");
  if (/hearing|due process|appeal|challenge|justice of the peace|probable cause/.test(s))
    tags.add("dueProcess");
  return tags;
}

// Lightweight legal-category label for each statute/regulation card. Purely
// presentational orientation aid — first match in priority order wins.
const LEGAL_CATEGORY_RULES: Array<{ label: string; test: RegExp }> = [
  { label: "Abandoned Vehicle", test: /abandon|derelict|unclaimed vehicle/i },
  { label: "Lien Procedures", test: /\blien\b|foreclos|possessory|garagekeeper/i },
  { label: "Notice Requirements", test: /\bnotice\b|notif|certified mail|publication/i },
  {
    label: "Storage & Impound",
    test: /\bstorage\b|impound|vehicle storage facilit|\bvsf\b|per[- ]?day|daily rate/i,
  },
  {
    label: "Consumer Remedies",
    test: /deceptive|dtpa|consumer protection|unfair|remed|hearing|due process|appeal/i,
  },
  { label: "Towing Procedures", test: /\btow(ing)?\b|booting|wrecker|hook[- ]?up|winch/i },
  {
    label: "Fee Regulations",
    test: /fee schedule|maximum (rate|charge)|rate cap|non[- ]?consent|\bfee(s)?\b|surcharge|administrative|\brate\b/i,
  },
  { label: "Vehicle Title", test: /\btitle\b|registration|certificate of title/i },
];

function matchLegalCategory(blob: string): string | null {
  if (!blob.trim()) return null;
  for (const rule of LEGAL_CATEGORY_RULES) {
    if (rule.test.test(blob)) return rule.label;
  }
  return null;
}

/** Title/citation drive the label; body text is only a fallback signal. */
function classifyLegalCategory(...parts: Array<string | null | undefined>): string {
  const clean = parts.filter((p): p is string => !!p && p.trim().length > 0);
  return (
    matchLegalCategory(clean.slice(0, 2).join(" ")) ??
    matchLegalCategory(clean.join(" ")) ??
    "General Regulation"
  );
}



// Finding-side domain tags. Distinct from statute domains: findings are line
// items on THIS invoice, so we need finer buckets that per-statute rules can
// target (tow line item vs storage vs admin fee vs surcharge vs deceptive).
type FindingDomain =
  | "towLineItem"
  | "storage"
  | "notice"
  | "adminFees"
  | "surcharge"
  | "deceptive"
  | "feeCapViolation";

function classifyFindingDomains(f: FindingItem): Set<FindingDomain> {
  const s = [f.title, f.category, f.description].filter(Boolean).join(" ").toLowerCase();
  const tags = new Set<FindingDomain>();
  if (/\btow(ing)?\b|hook[- ]?up|winch|dolly|flatbed|mileage|release fee|road ?service/.test(s))
    tags.add("towLineItem");
  if (/\bstorage\b|per[- ]?day|daily rate|impound|vsf|vehicle storage/.test(s)) tags.add("storage");
  if (/\bnotice\b|notification|lien|abandon|redemption|certified mail|inventory/.test(s))
    tags.add("notice");
  if (/administrative|admin fee|processing|gate fee|after[- ]?hours|documentation/.test(s))
    tags.add("adminFees");
  if (/surcharge|convenience|credit ?card|environmental|fuel fee|recovery fee/.test(s))
    tags.add("surcharge");
  if (/duplicate|deceptive|misleading|misrepresent|unauthorized|false|inflat/.test(s))
    tags.add("deceptive");
  if (
    /exceed|cap|maximum|statutory|excessive|violation|above (the )?(cap|maximum)|over the/.test(s)
  )
    tags.add("feeCapViolation");
  return tags;
}

// Per-statute bucketing rule derived from the legal authority itself, not
// from the finding set. `towDomains` selects which finding-side domains map
// to "Related to Tow"; `invoiceDomains` selects which map to "Related to
// Invoice (as a whole)". `invoiceIsWide` forces every remaining finding into
// invoice-bucket regardless of tags (used for hearing/appeal rights that
// give leverage over the entire bill).
interface StatuteBucketRule {
  towDomains: Set<FindingDomain>;
  invoiceDomains: Set<FindingDomain>;
  invoiceIsWide: boolean;
}

function bucketRuleFor(l: LegalResearch, statuteTags: Set<string>): StatuteBucketRule {
  const cite = (l.citation ?? l.statute ?? "").toLowerCase();
  const title = (l.title ?? l.topic ?? "").toLowerCase();
  const blob = `${cite} ${title}`;

  // ── Known statutes (deterministic mapping keyed off citation) ──
  //
  // Tex. Occ. Code § 2308.452 — Right to a tow hearing. Procedural leverage
  // over the entire invoice; does not govern any specific tow line item.
  if (/2308\.452/.test(blob) || /tow hearing|right to.*hearing/.test(title)) {
    return { towDomains: new Set(), invoiceDomains: new Set(), invoiceIsWide: true };
  }
  // 16 Tex. Admin. Code §§ 85.703–85.710 — VSF rules. Governs storage caps
  // and notification; not the physical tow line items.
  if (/85\.70\d|85\.71\d/.test(blob) || /vehicle storage facilit|vsf\b/.test(blob)) {
    return {
      towDomains: new Set(),
      invoiceDomains: new Set<FindingDomain>(["storage", "notice"]),
      invoiceIsWide: false,
    };
  }
  // Tex. Bus. & Com. Code § 17.41 et seq. — DTPA. Leverage over deceptive,
  // duplicate, or misleading charges; does not govern tow line items.
  if (/17\.41|dtpa|deceptive trade/.test(blob)) {
    return {
      towDomains: new Set(),
      invoiceDomains: new Set<FindingDomain>(["deceptive", "surcharge", "adminFees"]),
      invoiceIsWide: false,
    };
  }
  // Tex. Occ. Code Ch. 2308 (Towing & Booting Act, general). Comprehensive
  // towing statute — governs tow line items directly and provides fee-cap
  // leverage over storage/admin/notice/surcharge findings.
  if (/2308\.001|chapter 2308|towing and booting|texas towing/.test(blob)) {
    return {
      towDomains: new Set<FindingDomain>(["towLineItem"]),
      invoiceDomains: new Set<FindingDomain>([
        "storage",
        "notice",
        "adminFees",
        "surcharge",
        "feeCapViolation",
      ]),
      invoiceIsWide: false,
    };
  }

  // ── Fallback for unknown statutes: derive from statute domain tags ──
  const towDomains = new Set<FindingDomain>();
  const invoiceDomains = new Set<FindingDomain>();
  if (statuteTags.has("tow")) towDomains.add("towLineItem");
  if (statuteTags.has("storage")) invoiceDomains.add("storage");
  if (statuteTags.has("notice")) invoiceDomains.add("notice");
  if (statuteTags.has("fees")) {
    invoiceDomains.add("adminFees");
    invoiceDomains.add("surcharge");
    invoiceDomains.add("feeCapViolation");
  }
  if (statuteTags.has("deceptive")) invoiceDomains.add("deceptive");
  const invoiceIsWide =
    statuteTags.has("dueProcess") && towDomains.size === 0 && invoiceDomains.size === 0;
  return { towDomains, invoiceDomains, invoiceIsWide };
}

function strengthFromRelevance(rel: number | null | undefined): LegalCardData["strength"] {
  // SAB-004: relevance is a retrieval score, not a confidence score. It is
  // mapped through the canonical ladder and is NEVER defaulted — an authority
  // with no relevance score is reported as "Not Rated".
  const canonical = evidenceStrengthFromFraction(rel);
  if (canonical === "Very Strong") return "Very Strong";
  if (canonical === "Strong") return "Strong";
  if (canonical === "Moderate") return "Moderate";
  if (canonical === "Limited") return "Weak";
  return "Not Rated";
}

function negotiationValueFromRelevance(
  rel: number | null | undefined,
): LegalCardData["negotiationValue"] {
  const band = confidenceBand(toConfidencePct(rel));
  if (band === "Very High") return "Very High";
  if (band === "High") return "High";
  if (band === "Moderate") return "Medium";
  if (band === "Limited") return "Low";
  return "Not Rated";
}

// Per-statute charge-scope rule. Returns which invoice charge categories
// the statute genuinely governs, limits, authorizes, or affects. Driven
// from the legal authority (citation), not from the finding set.
// Special values: "none" — statute does not directly regulate any line
// item (procedural rights, general consumer-protection); "wide" reserved
// for future use.
function chargeCategoriesFor(l: LegalResearch): Set<ChargeCategory> | "none" {
  const cite = (l.citation ?? l.statute ?? "").toLowerCase();
  const title = (l.title ?? l.topic ?? "").toLowerCase();
  const blob = `${cite} ${title}`;

  // Tex. Occ. Code § 2308.452 — procedural tow-hearing right.
  if (/2308\.452/.test(blob) || /tow hearing|right to.*hearing/.test(title)) return "none";
  // Tex. DTPA — no direct line-item regulation.
  if (/17\.41|dtpa|deceptive trade/.test(blob)) return "none";
  // 16 TAC §§ 85.703–85.710 — VSF (storage facility) rules.
  if (/85\.70\d|85\.71\d/.test(blob) || /vehicle storage facilit|\bvsf\b/.test(blob)) {
    return new Set<ChargeCategory>(["storage", "notification", "lien", "release"]);
  }
  // Tex. Occ. Code Ch. 2308 — Towing & Booting Act (comprehensive).
  if (/2308\.001|chapter 2308|towing and booting|texas towing/.test(blob)) {
    return new Set<ChargeCategory>([
      "tow",
      "hookup",
      "storage",
      "notification",
      "admin",
      "surcharge",
      "mileage",
      "dolly",
      "winching",
      "release",
      "lien",
    ]);
  }

  // Fallback: derive from statute domain tags on unknown citations.
  const tags = classifyLegalDomains(
    [l.topic, l.title, l.summary, l.why_it_matters].filter(Boolean).join(" "),
  );
  const set = new Set<ChargeCategory>();
  if (tags.has("tow")) {
    set.add("tow");
    set.add("hookup");
    set.add("mileage");
    set.add("dolly");
    set.add("winching");
  }
  if (tags.has("storage")) set.add("storage");
  if (tags.has("notice")) {
    set.add("notification");
    set.add("lien");
  }
  if (tags.has("fees")) {
    set.add("admin");
    set.add("surcharge");
    set.add("release");
  }
  // Statutes that only tag deceptive/dueProcess don't directly regulate
  // any line item — leave the set empty.
  return set;
}

function toLegalCard(
  l: LegalResearch,
  findings?: FindingItem[],
  invoiceLineItems?: Array<Record<string, unknown>>,
): LegalCardData {
  const title = l.title ?? l.topic ?? "Legal reference";
  const ref =
    [l.citation ?? l.statute, l.jurisdiction].filter(Boolean).join(" · ") || "Reference on file";
  // SAB-005 — persisted prose only. Blank persisted content renders an
  // explicit unavailable state; we never substitute generic prose that reads
  // as if it were this authority's recorded finding.
  const plainEnglish = persistedTextOr(l.summary, LINEAGE_UNAVAILABLE.summary);
  const whyApplies = persistedTextOr(l.why_it_matters, LINEAGE_UNAVAILABLE.whyApplies);
  const customerBenefit = persistedTextOr(
    l.potential_negotiation_impact?.trim() ? l.potential_negotiation_impact : l.impact,
    LINEAGE_UNAVAILABLE.customerBenefit,
  );

  // Related to Charges — driven from the invoice, gated by the statute's
  // legal scope. A category is listed only if (1) it actually appears on
  // the invoice, AND (2) the statute materially governs or affects it.
  const statuteBlob = [l.topic, l.title, l.summary, l.why_it_matters].filter(Boolean).join(" ");
  const statuteTags = classifyLegalDomains(statuteBlob);
  const scope = chargeCategoriesFor(l);
  const present = presentInvoiceChargesByCategory(invoiceLineItems);
  const relatedCharges: string[] =
    scope === "none"
      ? []
      : [...present.entries()]
          .filter(([c]) => scope.has(c))
          .flatMap(([c, names]) => (names.length > 0 ? names : [CHARGE_CATEGORY_LABELS[c]]));

  // Grouped presentation for the Regulation card. Order fixed for scan-ability.
  const GROUP_DEFS: Array<{ label: string; cats: ChargeCategory[] }> = [
    { label: "Tow Operations", cats: ["tow", "hookup", "winching", "dolly", "mileage"] },
    { label: "Storage", cats: ["storage"] },
    { label: "Administrative", cats: ["admin"] },
    { label: "Lien / Notice", cats: ["lien", "notification"] },
    { label: "Additional Charges", cats: ["release", "surcharge"] },
  ];
  const relatedChargeGroups =
    scope === "none"
      ? []
      : GROUP_DEFS.map((g) => {
          const items: string[] = [];
          for (const c of g.cats) {
            if (!scope.has(c)) continue;
            const names = present.get(c);
            if (names && names.length > 0) items.push(...names);
            else if (present.has(c)) items.push(CHARGE_CATEGORY_LABELS[c]);
          }
          return { label: g.label, items };
        }).filter((g) => g.items.length > 0);

  // Supported Findings — consolidated list, deduped. Uses the existing
  // statute-specific bucket rule to decide which findings the statute
  // materially supports.
  const rule = bucketRuleFor(l, statuteTags);
  const supportedFindings: string[] = [];
  const seenFindings = new Set<string>();
  for (const f of findings ?? []) {
    const tags = classifyFindingDomains(f);
    const inTow = [...tags].some((t) => rule.towDomains.has(t));
    const inInvoice = rule.invoiceIsWide || [...tags].some((t) => rule.invoiceDomains.has(t));
    if (!(inTow || inInvoice)) continue;
    if (seenFindings.has(f.title)) continue;
    seenFindings.add(f.title);
    supportedFindings.push(f.title);
    if (supportedFindings.length >= 8) break;
  }

  const rawFacts = Array.isArray(l.regulatory_facts) ? l.regulatory_facts : [];
  const regulatoryFacts = rawFacts
    .map((f) => {
      if (!f || typeof f !== "object") return null;
      const requirement = String(f.requirement ?? "").trim();
      const category = String(f.category ?? "").trim();
      if (!requirement) return null;
      const rawValue = (f as { value?: unknown }).value;
      const unit =
        typeof (f as { unit?: unknown }).unit === "string" ? (f as { unit: string }).unit : "";
      let valueDisplay = "";
      if (typeof rawValue === "number" && Number.isFinite(rawValue)) {
        if (/^usd/i.test(unit)) {
          const formatted = currency(rawValue);
          valueDisplay = unit.includes("/") ? `${formatted}${unit.slice(3)}` : formatted;
        } else {
          valueDisplay = unit ? `${rawValue} ${unit}` : String(rawValue);
        }
      } else if (typeof rawValue === "string" && rawValue.trim()) {
        valueDisplay = rawValue.trim();
      }
      return {
        category,
        requirement,
        valueDisplay,
        condition:
          typeof (f as { condition?: unknown }).condition === "string"
            ? (f as { condition: string }).condition
            : null,
      };
    })
    .filter((f): f is NonNullable<typeof f> => !!f)
    .slice(0, 6);

  return {
    title,
    ref,
    plainEnglish,
    whyApplies,
    customerBenefit,
    strength: strengthFromRelevance(l.relevance_score),
    negotiationValue: negotiationValueFromRelevance(l.relevance_score),
    // SAB-004: retrieval relevance, never defaulted. null = not scored.
    confidence: toConfidencePct(l.relevance_score),
    relatedCharges,
    relatedChargeGroups: relatedChargeGroups.length > 0 ? relatedChargeGroups : undefined,
    supportedFindings,
    domains: [...statuteTags],
    legalCategory: classifyLegalCategory(title, ref, plainEnglish),
    jurisdiction: l.jurisdiction ?? undefined,
    lineage: buildAuthorityLineage(l),
    regulatoryFacts: regulatoryFacts.length > 0 ? regulatoryFacts : undefined,
  };

}

function legalFromFixture(f: {
  title: string;
  summary: string;
  matters: string;
  impact: string;
  ref: string;
}): LegalCardData {
  return {
    title: f.title,
    ref: f.ref,
    plainEnglish: f.summary,
    whyApplies: f.matters,
    customerBenefit: f.impact,
    strength: "Not Rated",
    negotiationValue: "Not Rated",
    confidence: null,
    relatedCharges: [],
    supportedFindings: [],
    domains: [],
    legalCategory: classifyLegalCategory(f.title, f.ref, f.summary),
    lineage: buildAuthorityLineage({ citation: f.ref, title: f.title }),
  };

}

function LegalKeyTakeaways({
  items,
  kind = "Statute",
}: {
  items: LegalCardData[];
  kind?: "Statute" | "Regulation";
}) {
  const count = items.length;

  // Aggregate signals from the actual statute set
  const domainCounts = new Map<string, number>();
  const jurisdictions = new Set<string>();
  const chargeSet = new Set<string>();
  const findingSet = new Set<string>();
  let strongCount = 0;
  let highLeverageCount = 0;
  let confidenceSum = 0;
  let scoredCount = 0;

  for (const it of items) {
    for (const d of it.domains) domainCounts.set(d, (domainCounts.get(d) ?? 0) + 1);
    if (it.jurisdiction) jurisdictions.add(it.jurisdiction);
    for (const c of it.relatedCharges) chargeSet.add(c);
    for (const f of it.supportedFindings) findingSet.add(f);
    if (it.strength === "Strong" || it.strength === "Very Strong") strongCount++;
    if (it.negotiationValue === "High" || it.negotiationValue === "Very High") highLeverageCount++;
    // SAB-004: only scored authorities contribute to the average relevance.
    if (typeof it.confidence === "number") {
      confidenceSum += it.confidence;
      scoredCount++;
    }
  }
  const avgConfidence = scoredCount > 0 ? Math.round(confidenceSum / scoredCount) : 0;

  // Top 2 domains by frequency for a theme summary
  const topDomains = [...domainCounts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 2)
    .map(([d]) => LEGAL_DOMAIN_LABELS[d] ?? d);

  const jurisdictionLabel =
    jurisdictions.size === 1
      ? [...jurisdictions][0]
      : jurisdictions.size > 1
        ? `${jurisdictions.size} jurisdictions`
        : null;

  const bullets: string[] = [];

  // 1. Regulation/statute count + jurisdiction (always present when count > 0)
  const noun = kind === "Regulation" ? "regulation" : "statute";
  const isSingular = count === 1;
  const nounForm = isSingular ? noun : `${noun}s`;
  const verb = isSingular ? "was" : "were";
  /* Wording guard (State Law Analysis only): the jurisdiction column often
     literally reads "State", which produced "2 applicable State statutes".
     A generic jurisdiction word is rendered in lower case as a common noun;
     a real jurisdiction name (e.g. "Texas") is left untouched. */
  const statuteScope = jurisdictionLabel && /^(state|statewide)$/i.test(jurisdictionLabel)
    ? "state"
    : (jurisdictionLabel ?? "state");
  /* Regulation Analysis applies the same generic-jurisdiction guard so the
     literal "State" jurisdiction value reads as a common noun. */
  const regulationScope =
    jurisdictionLabel && /^(state|statewide)$/i.test(jurisdictionLabel)
      ? "state"
      : jurisdictionLabel;
  const scopeLabel =
    kind === "Regulation"
      ? regulationScope
        ? `${regulationScope} `
        : ""
      : `${statuteScope} `;

  bullets.push(
    `${count} applicable ${scopeLabel}${nounForm} ${verb} identified for the charges under review.`
      .replace(/\s+/g, " ")
      .trim(),
  );

  // 2. Primary themes — only if domains classified
  if (topDomains.length > 0) {
    const themeLabel = kind === "Regulation" ? "regulatory theme" : "legal theme";
    bullets.push(
      topDomains.length === 1
        ? `Primary ${themeLabel}: ${topDomains[0]}.`
        : `Primary ${themeLabel}s: ${topDomains[0]} and ${topDomains[1]}.`,
    );
  }

  // 3. Coverage — distinguish invoice charges (governed) from report findings
  //    (supported). Only shown when non-zero.
  if (chargeSet.size > 0 || findingSet.size > 0) {
    const subject =
      kind === "Regulation"
        ? isSingular
          ? "This regulation"
          : "The applicable regulations"
        : isSingular
          ? "This statute"
          : "The applicable statutes";
    const chargeClause =
      chargeSet.size > 0
        ? `${isSingular ? "governs" : "directly govern"} ${chargeSet.size} invoice charge${chargeSet.size === 1 ? "" : "s"}`
        : null;
    const findingClause =
      findingSet.size > 0
        ? `${isSingular ? "supports" : "support"} ${findingSet.size} report finding${findingSet.size === 1 ? "" : "s"}`
        : null;
    const clauses = [chargeClause, findingClause].filter(Boolean).join(" and ");
    bullets.push(`${subject} ${clauses}.`);
  }

  // 4. Leverage strength — only if any item scored strong or high leverage
  if (highLeverageCount > 0 || strongCount > 0) {
    const leverageParts: string[] = [];
    if (strongCount > 0) leverageParts.push(`${strongCount} rated Strong or Very Strong`);
    if (highLeverageCount > 0)
      leverageParts.push(`${highLeverageCount} carry High or Very High negotiation leverage`);
    bullets.push(`Leverage profile: ${leverageParts.join("; ")}.`);
  }

  // 5. Overall position — confidence-weighted, always present when count > 0
  if (avgConfidence > 0) {
    const posture =
      avgConfidence >= 85
        ? "well-supported"
        : avgConfidence >= 70
          ? "supported"
          : avgConfidence >= 55
            ? "partially supported"
            : "limited";
    const positionLabel = kind === "Regulation" ? "regulatory position" : "legal position";
    bullets.push(`Overall ${positionLabel} is ${posture} (avg. relevance ${avgConfidence}%).`);
  }

  if (bullets.length === 0) return null;

  const categoryCounts = new Map<string, number>();
  for (const it of items) {
    if (!it.legalCategory) continue;
    categoryCounts.set(it.legalCategory, (categoryCounts.get(it.legalCategory) ?? 0) + 1);
  }
  const categoryEntries = [...categoryCounts.entries()].sort(
    (a, b) => b[1] - a[1] || a[0].localeCompare(b[0]),
  );

  return (
    <div className="mb-5 flex items-start gap-4 rounded-2xl border border-trust/20 bg-trust-soft/40 p-5 print-avoid-break">
      <div
        data-card-padding="tight"
        className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-trust text-trust-foreground shadow-card"
      >
        <Lightbulb className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-trust">
          Key Takeaways
        </div>
        <ul className="mt-2 grid gap-1.5 text-[13px] leading-snug text-navy sm:grid-cols-2">
          {bullets.map((b, i) => (
            <li key={i} className="flex gap-2">
              <span className="mt-1 h-1.5 w-1.5 shrink-0 rounded-full bg-trust" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
        {categoryEntries.length > 0 ? (
          <div className="mt-3 border-t border-trust/15 pt-3">
            <div className="text-[9px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
              Legal categories identified
            </div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {categoryEntries.map(([label, n]) => (
                <span
                  key={label}
                  className="inline-flex items-center gap-1 rounded-full border border-border bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-navy"
                >
                  {label} <span className="text-muted-foreground">({n})</span>
                </span>
              ))}
            </div>
          </div>
        ) : null}
      </div>

    </div>
  );
}

// Look up the chapter that renders immediately after `currentId` in the
// reading order. Registry-backed replacement for the legacy helper.
function nextChapterLabelAfter(currentId: string): string | null {
  return nextChapterLabel(currentId);
}

function AnalystNote({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mt-5 flex items-start gap-4 rounded-2xl border border-fasttrack/30 bg-fasttrack-soft/70 p-5 print-avoid-break"
      data-pdf-include="true"
    >
      <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-fasttrack/15 text-fasttrack">
        <Lightbulb className="h-5 w-5" />
      </div>
      <div className="min-w-0 flex-1">
        <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-fasttrack">
          Analyst Note
        </div>
        <p className="mt-1.5 text-sm leading-relaxed text-navy">{children}</p>
      </div>
    </div>
  );
}

/**
 * Presentation-only helper: how many of these authorities carry an official
 * verification stamp in the persisted record. No data is derived or changed —
 * the persisted `verification_status` is simply read for wording purposes.
 */
function officiallyVerifiedCount(rows: LegalResearch[] | undefined): number {
  if (!rows?.length) return 0;
  return rows.filter((r) => {
    const row = r as unknown as Record<string, unknown>;
    const status = mapVerificationStatus(
      row.verification_status,
      !!safeSourceUrl(row.source_url),
    );
    return (
      status === "Official Source Verified" ||
      status === "Database Verified" ||
      status === "Verified"
    );
  }).length;
}

function authoritySetState(rows: LegalResearch[] | undefined): ReportVerificationState {
  const total = rows?.length ?? 0;
  if (total === 0) return "pending";
  const verified = officiallyVerifiedCount(rows);
  if (verified === total) return "verified";
  return "analyzed";
}

function ApplicableLaws({
  pending,
  real,
  findingItems,
  invoiceLineItems,
}: {
  pending?: boolean;
  real?: LegalResearch[];
  findingItems?: FindingItem[];
  invoiceLineItems?: Array<Record<string, unknown>>;
} = {}) {
  const items: LegalCardData[] = real
    ? real.map((l) => toLegalCard(l, findingItems, invoiceLineItems))
    : LAWS.map(legalFromFixture);
  const state = authoritySetState(real);
  const verified = officiallyVerifiedCount(real);

  return (
    <Section
      bare
      number="01"
      eyebrow="Legal Intelligence"
      title={<>Applicable State Laws{pending && <PendingBadge />}</>}
      intro="Every applicable statute translated into plain English, with the customer benefit and the specific charges it supports. Not legal advice."
    >
      {items.length === 0 ? (
        <>
          <EmptyRealState label="Applicable laws have not been extracted for this review." />
          <MethodologyPanel
            title="How applicable statutes are identified"
            subtitle="Statutory research follows a deterministic pipeline anchored to the invoice's jurisdiction — never a generalized model opinion."
            items={[
              {
                icon: MapPin,
                label: "Jurisdiction lock",
                description:
                  "State is derived from the invoice itself; no cross-jurisdiction citations are permitted.",
              },
              {
                icon: Landmark,
                label: "Authoritative sources",
                description:
                  "Priority is given to official .gov statutes, revised codes, and state administrative publications.",
              },
              {
                icon: ShieldCheck,
                label: "Citation validation",
                description:
                  "Every citation is validated for structure and topical relevance before being surfaced.",
              },
            ]}
          />
          <CoverageNote message="No jurisdiction-specific statutes were returned by the legal retrieval pipeline for this claim. ClaimGuard will not substitute another state's laws; supplemental legal research would be required before drawing a compliance conclusion." />
        </>
      ) : (
        <>
          <VerificationStatusBanner
            className="mb-5"
            state={state}
            badgeLabel={state === "verified" ? "Verified" : "Analyzed"}
            title={
              state === "verified"
                ? "Legal analysis completed and confirmed against official sources"
                : "Legal analysis completed — official database verification pending"
            }
            detail={
              state === "verified"
                ? `All ${items.length} state-law ${items.length === 1 ? "authority" : "authorities"} below were confirmed against an official published source.`
                : `ClaimGuard completed the state-law analysis for this claim and identified ${items.length} applicable ${items.length === 1 ? "authority" : "authorities"}${verified > 0 ? `, ${verified} of which ${verified === 1 ? "has" : "have"} been confirmed against an official published source` : ""}. Confirmation of the remaining ${items.length - verified === 1 ? "authority" : "authorities"} against an official state-law database is pending connection of a licensed state-law provider. This is a limit of source coverage, not an absence of analysis or an adverse finding.`
            }
          />
          <LegalKeyTakeaways items={items} />
          <div className="mt-5 grid gap-5 lg:grid-cols-2">
            {items.map((l, i) => (
              <LegalCard
                key={`statute-${l.ref || l.title}-${i}`}
                data={l}
                icon={getChapterByInternalId("state-law").icon}
                kind="Statute"
              />
            ))}
          </div>
        </>
      )}

    </Section>
  );
}

function ApplicableRegulations({
  pending,
  real,
  findingItems,
  invoiceLineItems,
}: {
  pending?: boolean;
  real?: LegalResearch[];
  findingItems?: FindingItem[];
  invoiceLineItems?: Array<Record<string, unknown>>;
} = {}) {
  const items: LegalCardData[] = real
    ? real.map((l) => toLegalCard(l, findingItems, invoiceLineItems))
    : REGS.map(legalFromFixture);
  // Presentation-only split: federal regulations are retrieved from official
  // federal publishers (GovInfo / eCFR) and carry an official verification
  // stamp; state-level regulations remain analyzed pending a licensed
  // state-law database connection. Grouping only — no rows are filtered out.
  const federalRows = (real ?? []).filter((l) => isFederalAuthority(l));
  const stateRows = (real ?? []).filter((l) => !isFederalAuthority(l));
  const federalItems = federalRows.map((l) => toLegalCard(l, findingItems, invoiceLineItems));
  const stateItems = stateRows.map((l) => toLegalCard(l, findingItems, invoiceLineItems));
  const federalState = authoritySetState(federalRows);
  const stateState = authoritySetState(stateRows);
  const grid = (n: number) => `mt-5 grid gap-5 ${n > 1 ? "lg:grid-cols-2" : "lg:grid-cols-1"}`;
  const cards = (list: LegalCardData[], prefix: string) =>
    list.map((l, i) => (
      <LegalCard
        key={`${prefix}-${l.ref || l.title}-${i}`}
        data={l}
        icon={getChapterByInternalId("regulation").icon}
        kind="Regulation"
      />
    ));

  return (
    <Section
      bare
      number="01"
      eyebrow="Regulatory Intelligence"
      title={<>Applicable Regulations{pending && <PendingBadge />}</>}
      intro="The administrative rules and industry regulations that govern how these charges must be structured and documented."
    >
      {items.length === 0 ? (
        <>
          <EmptyRealState label="Applicable regulations have not been extracted for this review." />
          <MethodologyPanel
            title="How applicable regulations are surfaced"
            subtitle="Regulatory research inspects the administrative rules and industry standards that govern how charges must be structured and documented."
            items={[
              {
                icon: BookOpen,
                label: "Rulebook coverage",
                description:
                  "State administrative codes, agency bulletins, and industry-standard billing rules relevant to the invoice.",
              },
              {
                icon: ClipboardCheck,
                label: "Documentation tests",
                description:
                  "Each rule is mapped to specific documentation requirements the invoice must satisfy — authorization, itemization, and notice.",
              },
              {
                icon: Sparkles,
                label: "Neutral reasoning",
                description:
                  "Findings are stated as conditional obligations, not conclusions of non-compliance, unless direct evidence supports them.",
              },
            ]}
          />
          <CoverageNote message="No jurisdiction-specific regulations were returned for this claim. This is a coverage gap in the current retrieval pipeline, not an affirmative finding that no regulations apply." />
        </>
      ) : !real ? (
        <>
          <LegalKeyTakeaways items={items} kind="Regulation" />
          <div className={grid(items.length)}>{cards(items, "regulation")}</div>
        </>
      ) : (
        <>
          <LegalKeyTakeaways items={items} kind="Regulation" />

          {federalItems.length > 0 ? (
            <section className="mt-6 print-avoid-break" data-regulation-group="federal">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[12.5px] font-bold uppercase tracking-[0.18em] text-trust">
                  Federal Regulations
                </h3>
                <VerificationBadge state={federalState} />
              </div>
              <VerificationStatusBanner
                className="mt-3"
                state={federalState}
                title={
                  federalState === "verified"
                    ? "Confirmed against the official federal publisher"
                    : "Federal regulations identified and analyzed"
                }
                detail={
                  federalState === "verified"
                    ? "Each federal regulation below was retrieved and confirmed against the official federal publication of record."
                    : "Federal regulations below were identified during review. Official publisher confirmation is recorded per authority in the verification details."
                }
              />
              <div className={grid(federalItems.length)}>{cards(federalItems, "fed-regulation")}</div>
            </section>
          ) : null}

          {stateItems.length > 0 ? (
            <section
              className="mt-8 border-t border-border pt-6 print-avoid-break"
              data-regulation-group="state"
            >
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="text-[12.5px] font-bold uppercase tracking-[0.18em] text-navy">
                  State Regulations
                </h3>
                <VerificationBadge state={stateState} />
              </div>
              <VerificationStatusBanner
                className="mt-3"
                state={stateState}
                badgeLabel={stateState === "verified" ? "Verified" : "Analyzed"}
                title={
                  stateState === "verified"
                    ? "Confirmed against the official state publisher"
                    : "State regulations analyzed — official database verification pending"
                }
                detail={
                  stateState === "verified"
                    ? "Each state regulation below was confirmed against an official published source."
                    : "These state-level rules were identified and analyzed against the invoice. Confirmation against an official state regulatory database is pending connection of a licensed state-law provider — a limit of source coverage, not an adverse finding."
                }
              />
              <div className={grid(stateItems.length)}>{cards(stateItems, "state-regulation")}</div>
            </section>
          ) : null}
        </>
      )}

    </Section>
  );
}

function strengthBadgeCls(s: LegalCardData["strength"]): string {
  if (s === "Very Strong") return "border-savings/40 bg-savings-soft text-savings";
  if (s === "Strong") return "border-trust/30 bg-trust-soft text-trust";
  if (s === "Moderate") return "border-fasttrack/40 bg-fasttrack-soft text-fasttrack";
  return "border-border bg-secondary text-muted-foreground";
}

function valueBadgeCls(v: LegalCardData["negotiationValue"]): string {
  if (v === "Very High") return "border-savings/40 bg-savings-soft text-savings";
  if (v === "High") return "border-trust/30 bg-trust-soft text-trust";
  if (v === "Medium") return "border-fasttrack/40 bg-fasttrack-soft text-fasttrack";
  return "border-border bg-secondary text-muted-foreground";
}

function LegalCard({
  data,
  icon: Icon,
  kind,
}: {
  data: LegalCardData;
  icon: React.ComponentType<{ className?: string }>;
  kind: "Statute" | "Regulation";
}) {
  return (
    <article className="flex h-full flex-col overflow-hidden rounded-2xl border border-border bg-card shadow-card transition hover:-translate-y-0.5 hover:shadow-elevated print-avoid-break">
      {/* Header */}
      <header className="border-b border-border bg-gradient-to-br from-trust-soft/60 to-transparent p-6">
        <div className="flex items-start gap-4">
          <div
            data-card-padding="tight"
            className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-trust text-trust-foreground shadow-card"
          >
            <Icon className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-trust">
              {kind}
            </div>
            <h3 className="mt-1 font-display text-lg leading-snug text-navy text-balance hyphens-none">
              {data.title}
            </h3>
            <div className="mt-1.5 flex items-start gap-1.5 text-[11px] font-mono leading-snug text-muted-foreground break-words">
              <Info className="mt-0.5 h-3 w-3 shrink-0" />
              <span className="min-w-0 break-words">{data.ref}</span>
            </div>
            {data.legalCategory ? (
              <div className="mt-2">
                <span className="inline-flex items-center rounded-full border border-border bg-secondary/70 px-2 py-0.5 text-[9px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                  {data.legalCategory}
                </span>
              </div>
            ) : null}
          </div>

        </div>

        {/* Evidence badges */}
        <div className="mt-5 flex flex-wrap gap-2">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${strengthBadgeCls(data.strength)}`}
          >
            <ShieldCheck className="h-3 w-3" /> Evidence: {data.strength}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${valueBadgeCls(data.negotiationValue)}`}
          >
            <TrendingDown className="h-3 w-3" /> Value: {data.negotiationValue}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider text-navy">
            <Sparkles className="h-3 w-3 text-trust" /> Relevance:{" "}
            {data.confidence != null ? `${data.confidence}%` : "Not scored"}
          </span>
        </div>
      </header>

      {/* Body */}
      <div className="flex-1 space-y-4 p-6">
        <div>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            Plain-English summary
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-navy">{data.plainEnglish}</p>
        </div>

        <div className="rounded-xl border-l-2 border-trust bg-trust-soft/60 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-trust">
            Why it applies here
          </div>
          <p className="mt-1.5 text-sm leading-relaxed text-navy">{data.whyApplies}</p>
        </div>

        <div className="rounded-xl border-l-2 border-savings bg-savings-soft/60 p-4">
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-savings/90">
            Potential customer benefit
          </div>
          <p className="mt-1.5 text-sm font-semibold leading-relaxed text-navy">
            {data.customerBenefit}
          </p>
        </div>

        {/* SAB-005 — persisted evidence lineage (source, verification, dates,
            charge linkage). Unavailable fields are shown explicitly. */}
        <LegalLineageStrip lineage={data.lineage} />

        {/* Regulatory facts — only rendered when the citation exposes normalized facts. */}
        {data.regulatoryFacts && data.regulatoryFacts.length > 0 && (
          <div>
            <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
              <BookOpen className="h-3.5 w-3.5" /> Regulatory facts
            </div>
            <ul className="mt-2 space-y-1.5">
              {data.regulatoryFacts.map((f, i) => (
                <li
                  key={`${f.category}-${i}`}
                  className="rounded-lg border border-border bg-white px-3 py-2 text-[12px] leading-snug text-navy"
                >
                  <div className="flex items-baseline justify-between gap-3">
                    <span className="font-semibold">{f.requirement}</span>
                    {f.valueDisplay && (
                      <span className="font-mono text-[11px] font-semibold text-trust">
                        {f.valueDisplay}
                      </span>
                    )}
                  </div>
                  {f.condition && (
                    <div className="mt-0.5 text-[11px] italic text-muted-foreground">
                      {f.condition}
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Applicable charges — split into (a) actual invoice charges the
            statute governs and (b) report findings the statute supports.
            These are two different concepts sourced from two different
            data sets. */}
        <div>
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
            <ClipboardCheck className="h-3.5 w-3.5" /> Applicable Charges
          </div>
          <div className="mt-2 space-y-3">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-trust">
                Invoice Charges
              </div>
              {kind === "Regulation" &&
              data.relatedChargeGroups &&
              data.relatedChargeGroups.length > 0 ? (
                <div className="mt-1.5 space-y-2">
                  {data.relatedChargeGroups.map((g) => (
                    <div key={`grp-${g.label}`}>
                      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                        {g.label}
                      </div>
                      <ul className="mt-0.5 space-y-0.5 text-[12px] leading-snug text-navy">
                        {g.items.map((c) => (
                          <li key={`chg-${g.label}-${c}`} className="flex gap-1.5">
                            <span className="text-muted-foreground">•</span>
                            <span>{c}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              ) : data.relatedCharges.length > 0 ? (
                <ul className="mt-1.5 space-y-0.5 text-[12px] leading-snug text-navy">
                  {data.relatedCharges.map((c: string) => (
                    <li key={`chg-${c}`} className="flex gap-1.5">
                      <span className="text-muted-foreground">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-[11px] italic text-muted-foreground">None identified.</p>
              )}
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-[0.14em] text-trust">
                Report Findings Supported
              </div>
              {data.supportedFindings.length > 0 ? (
                <ul className="mt-1.5 space-y-0.5 text-[12px] leading-snug text-navy">
                  {data.supportedFindings.map((c: string) => (
                    <li key={`fnd-${c}`} className="flex gap-1.5">
                      <span className="text-muted-foreground">•</span>
                      <span>{c}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-1 text-[11px] italic text-muted-foreground">None identified.</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </article>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 8. Timeline Analysis                                        */
/* ─────────────────────────────────────────────────────────── */

type StatuteConfidence = "Documented" | "Calculated" | "Estimated";

interface TimelineStatuteInfo {
  title: string;
  jurisdiction: string;
  authority: string | null;
  confidence: StatuteConfidence;
  reason: string;
  deadline?: string | null;
  evidence?: string | null;
  compliance?: string | null;
}

type CaseTimelineEvent = {
  key: string;
  label: string;
  date: string | null; // ISO or null
  displayDate: string; // formatted date, or the canonical absence label
  displayTime?: string; // HH:MM AM/PM when a real timestamp is present
  icon: React.ComponentType<{ className?: string }>;
  verified: boolean;
  statutory: boolean;
  applicable: boolean;
  phase: "claim" | "review";
  note?: string;
  statute?: TimelineStatuteInfo;
};

function fmtDate(iso: string | null | undefined): string {
  if (!iso) return absenceLabel("NOT_EVIDENCED");
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return absenceLabel("NOT_EVIDENCED");
  return d.toLocaleDateString("en-US", {
    timeZone: "UTC",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

/**
 * Return "9:14 AM" when the ISO timestamp carries a meaningful time
 * component (i.e., not midnight UTC — those come from date-only OCR
 * fields we must NOT fabricate a time for).
 */
function fmtTimeIfPresent(iso: string | null | undefined): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return undefined;
  const isMidnightUTC = d.getUTCHours() === 0 && d.getUTCMinutes() === 0 && d.getUTCSeconds() === 0;
  if (isMidnightUTC) return undefined;
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
}

function pickDate(source: Record<string, unknown> | undefined, keys: string[]): string | null {
  if (!source) return null;
  for (const k of keys) {
    const v = source[k];
    if (typeof v === "string" && v.trim()) {
      const d = new Date(v);
      if (!Number.isNaN(d.getTime())) return d.toISOString();
    }
  }
  return null;
}

function addDaysISO(iso: string | null, days: number | null | undefined): string | null {
  if (!iso || days == null) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  d.setDate(d.getDate() + Math.max(0, Math.floor(days)));
  return d.toISOString();
}

/**
 * Compact statutory information card rendered beneath a Case Timeline
 * milestone. Style is intentionally quiet — small header, muted card,
 * thin border, three badges (statute · jurisdiction · confidence), and
 * a short reason line. Never taller than 5–7 lines of text.
 */
function StatuteCallout({ info }: { info: TimelineStatuteInfo }) {
  // Presentation-only label mapping. Internal confidence engine unchanged.
  const notApplicable = /not applicable/i.test(info.title);
  const confidenceLabel = notApplicable
    ? "Not Applicable"
    : !info.authority
      ? absenceLabel("NOT_EVIDENCED")
      : info.confidence === "Documented"
        ? "Verified"
        : "Estimated";
  const confidenceCls =
    confidenceLabel === "Verified"
      ? "bg-savings-soft text-savings"
      : confidenceLabel === "Estimated"
        ? "bg-trust-soft text-trust"
        : "bg-secondary text-muted-foreground";

  // Short summary describing the legal requirement itself (not evidence).
  const t = info.title.toLowerCase();
  const summary = notApplicable
    ? info.reason
    : t.includes("enforcement") || t.includes("sale") || t.includes("auction")
      ? `State law imposes a waiting period after owner notice before lien enforcement or sale may proceed in ${info.jurisdiction}.`
      : t.includes("certified") || t.includes("publication")
        ? `State law requires certified or published notice as part of the lien enforcement process in ${info.jurisdiction}.`
        : t.includes("notice") || t.includes("dmv") || t.includes("lienholder")
          ? `State law requires owner notification before lien enforcement may proceed in ${info.jurisdiction}.`
          : `Applicable statutory requirement under ${info.jurisdiction} law.`;

  return (
    <div
      className="flex h-full min-h-[9rem] w-full flex-col items-start rounded-lg border border-border/70 bg-muted/40 p-2.5 text-left"
      role="group"
      aria-label={`${info.title} statutory reference`}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-navy/80">
        {info.title}
      </div>
      <div className="mt-1.5 flex flex-wrap gap-1">
        {info.authority ? (
          <span
            className="rounded-sm bg-white px-1.5 py-0.5 font-mono text-[9px] font-medium text-navy ring-1 ring-inset ring-border"
            aria-label={`Authority ${info.authority}`}
          >
            {info.authority}
          </span>
        ) : null}
        <span
          className="rounded-sm bg-white px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-foreground/70 ring-1 ring-inset ring-border"
          aria-label={`Jurisdiction ${info.jurisdiction}`}
        >
          {info.jurisdiction}
        </span>
        <span
          className={`rounded-sm px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${confidenceCls}`}
          aria-label={`Status ${confidenceLabel}`}
        >
          {confidenceLabel}
        </span>
      </div>
      <p className="mt-1.5 text-[11px] leading-snug text-foreground/80">{summary}</p>
    </div>
  );
}

function buildCaseTimeline(
  bundle: FullWorkspaceBundle | null,
  invoiceFields: ReturnType<typeof extractInvoiceReportFields> | null,
  state: string | null,
): {
  events: CaseTimelineEvent[];
  missingStatutory: string[];
  isTowingCase: boolean;
  authorities: StatutoryAuthorityBundle;
} {
  const authorities = getStatutoryAuthorities(state);
  const extracted = (bundle?.invoiceData?.extracted ?? {}) as Record<string, unknown>;
  const events =
    extracted?.events && typeof extracted.events === "object"
      ? (extracted.events as Record<string, unknown>)
      : undefined;
  const merged: Record<string, unknown> = { ...extracted, ...(events ?? {}) };

  const invoiceType = (invoiceFields?.invoiceType ?? "").toLowerCase();
  const lineItemsText = (invoiceFields?.lineItems ?? [])
    .map((li) => String((li as Record<string, unknown>).description ?? ""))
    .join(" ")
    .toLowerCase();
  const isTowingCase =
    /tow|storage|impound|wrecker|lien/.test(invoiceType) ||
    /tow|storage|impound|lien|abandon|auction|notice/.test(lineItemsText);

  // Shared data-source hierarchy (Chapter 05 + Case Timeline single source of truth).
  // Structured fields first, then OCR raw_text fallback; Storage Start falls back to Tow Date.
  const resolvedDates = resolveTimelineDatesISO({
    extracted: extracted as Record<string, unknown>,
  });
  const towDate =
    resolvedDates.towDate ??
    pickDate(merged, [
      "tow_date",
      "towDate",
      "date_of_tow",
      "impound_date",
      "impoundDate",
      "pickup_date",
    ]);
  const storageStart =
    resolvedDates.storageStart ??
    pickDate(merged, [
      "storage_start",
      "storage_start_date",
      "storageStartDate",
      "first_storage_date",
    ]) ??
    towDate;
  /* Release date: structured fields first, then the same OCR raw_text parse
     Chapter 05 uses. Printed invoices routinely carry "Release <date>" with
     no structured counterpart, and omitting it dropped a documented event
     from the chronology. */
  const lawReleaseDate =
    pickDate(merged, [
      "law_enforcement_release_date",
      "police_release_date",
      "release_date",
      "hold_release_date",
    ]) ??
    (() => {
      const parsed = parseTowReleaseFromRawText(extracted.raw_text);
      return parsed.release ? parsed.release.toISOString() : null;
    })();
  const noticeDate = pickDate(merged, [
    "notice_date",
    "first_notice_date",
    "notification_date",
    "owner_notice_date",
  ]);
  const certifiedDate = pickDate(merged, [
    "certified_mail_date",
    "certified_letter_date",
    "certified_notice_date",
  ]);
  const auctionDate = pickDate(merged, ["auction_date", "auction_scheduled_date", "sale_date"]);
  // Potential enforcement window (Case Timeline).
  // Legally distinct from Chapter 05 "Lien Attaches" (possession-based
  // attachment) and Chapter 05 "Auction Eligible" (notice-anchored earliest
  // sale under Tex. Occ. Code § 2303.157). This milestone estimates the
  // earliest point a vendor might pursue lien enforcement after the
  // statutory 30-day waiting period.
  //
  // Calculation hierarchy:
  //   1. Documented notice date + 30 days (§ 2303.157) — preferred
  //   2. Storage start + 30 days — fallback estimate, only when elapsed
  //      calendar days from storage start already meet or exceed 30
  const explicitEnforcementDate = pickDate(merged, [
    "lien_eligible_date",
    "lien_date",
    "lien_start_date",
  ]);
  const daysSince = (iso: string | null): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return null;
    return Math.floor((Date.now() - t) / 86_400_000);
  };
  const elapsedFromStorage = daysSince(storageStart);
  const billedStorageDays =
    typeof merged.storage_days === "number"
      ? (merged.storage_days as number)
      : typeof merged.storageDays === "number"
        ? (merged.storageDays as number)
        : null;

  let enforcementDate: string | null = explicitEnforcementDate;
  let enforcementBasis: "documented" | "notice" | "storage" | "billed" | null =
    explicitEnforcementDate ? "documented" : null;
  if (!enforcementDate && noticeDate) {
    enforcementDate = addDaysISO(noticeDate, 30);
    enforcementBasis = "notice";
  } else if (
    !enforcementDate &&
    storageStart &&
    elapsedFromStorage !== null &&
    elapsedFromStorage >= 30
  ) {
    enforcementDate = addDaysISO(storageStart, 30);
    enforcementBasis = "storage";
  } else if (!enforcementDate && storageStart && billedStorageDays && billedStorageDays >= 30) {
    enforcementDate = addDaysISO(storageStart, 30);
    enforcementBasis = "billed";
  }
  /* A documented release ends possession. Projecting a statutory enforcement
     window that opens after the vehicle was already released would state a
     milestone that never arose on this record, so the estimate is withdrawn
     (documented enforcement dates are never withdrawn). */
  const releasedBeforeEnforcement =
    !!lawReleaseDate &&
    !!enforcementDate &&
    enforcementBasis !== "documented" &&
    new Date(lawReleaseDate).getTime() < new Date(enforcementDate).getTime();
  if (releasedBeforeEnforcement) {
    enforcementDate = null;
    enforcementBasis = null;
  }
  const noticeVerified = !!noticeDate || !!certifiedDate;

  const invoiceDateRaw = invoiceFields?.invoiceDate;
  const invoiceDate =
    invoiceDateRaw && invoiceDateRaw !== NOT_EXTRACTED && invoiceDateRaw !== MISSING_FIELD
      ? (() => {
          const d = new Date(invoiceDateRaw);
          return Number.isNaN(d.getTime()) ? null : d.toISOString();
        })()
      : null;

  /* Each ClaimGuard review activity binds to the record that activity
     produced. Previously all four resolved to the report timestamp, which
     presented one moment as four independently completed stages. */
  const latestOf = (rows: Array<{ created_at?: string | null }> | undefined): string | null => {
    const times = (rows ?? [])
      .map((r) => (r.created_at ? new Date(r.created_at).getTime() : NaN))
      .filter((t) => !Number.isNaN(t));
    return times.length ? new Date(Math.max(...times)).toISOString() : null;
  };
  const submittedDate = bundle?.review?.created_at ?? null;
  const reviewCompletedDate = bundle?.report?.updated_at ?? bundle?.report?.created_at ?? null;
  const reportGeneratedDate = bundle?.report?.created_at ?? reviewCompletedDate;
  const findingsDate = bundle?.findings?.created_at ?? reviewCompletedDate;
  const legalDate = latestOf(bundle?.legalResearch) ?? reviewCompletedDate;
  const marketDate =
    latestOf(bundle?.marketComparisons) ?? latestOf(bundle?.marketResearch) ?? reviewCompletedDate;

  const rows: CaseTimelineEvent[] = [];

  const push = (
    row: Omit<CaseTimelineEvent, "displayDate" | "verified" | "displayTime"> & {
      date: string | null;
    },
  ) => {
    rows.push({
      ...row,
      verified: !!row.date,
      displayDate: fmtDate(row.date),
      displayTime: fmtTimeIfPresent(row.date),
    });
  };

  const authorityLabel = (a: string | null) => a ?? "Applicable state statute";

  if (isTowingCase) {
    push({
      key: "towed",
      label: "Vehicle Towed",
      date: towDate,
      icon: Truck,
      statutory: false,
      applicable: true,
      phase: "claim",
    });
    push({
      key: "storage",
      label: "Vehicle Entered Storage",
      date: storageStart,
      icon: Warehouse,
      statutory: false,
      applicable: true,
      phase: "claim",
    });
    if (lawReleaseDate) {
      /* Only a law-enforcement-specific field justifies the law-enforcement
         label; a printed "Release" line documents release of the vehicle
         without stating who authorised it. */
      const lawSourced = !!pickDate(merged, [
        "law_enforcement_release_date",
        "police_release_date",
        "hold_release_date",
      ]);
      push({
        key: "release",
        label: lawSourced ? "Law Enforcement Release" : "Vehicle Released",
        date: lawReleaseDate,
        icon: ShieldCheck,
        statutory: false,
        applicable: true,
        phase: "claim",
      });
    }

    const noticeDeadlineLabel = authorities.enforcementWaitingDays
      ? `Owner notice required within the applicable statutory window (${authorities.jurisdiction})`
      : "Owner notice required within the applicable statutory window";

    // Notice Sent to Owner — jurisdiction-driven statutory callout.
    const noticeStatute: TimelineStatuteInfo = noticeDate
      ? {
          title: "Notice Requirement",
          jurisdiction: authorities.jurisdiction,
          authority: authorityLabel(authorities.noticeAuthority),
          confidence: "Documented",
          reason: "Owner notice documented in the submitted evidence.",
          deadline: noticeDeadlineLabel,
          evidence: "Owner-notice record on file",
          compliance: "Documented on record",
        }
      : {
          title: "Notice Requirement",
          jurisdiction: authorities.jurisdiction,
          authority: authorityLabel(authorities.noticeAuthority),
          confidence: "Estimated",
          reason:
            "No owner notice documentation was found in the submitted evidence. Timeline position represents the applicable statutory sequence only.",
          deadline: noticeDeadlineLabel,
          evidence: "Not documented",
          compliance: "Cannot be verified from the submitted record",
        };
    rows.push({
      key: "notice",
      label: "Notice Sent to Owner",
      date: noticeDate,
      displayDate: fmtDate(noticeDate),
      displayTime: fmtTimeIfPresent(noticeDate),
      icon: Send,
      verified: !!noticeDate,
      statutory: true,
      applicable: true,
      phase: "claim",
      statute: noticeStatute,
    });

    // Certified Letter Sent — jurisdiction-driven statutory callout.
    const certifiedDeadlineLabel = `Certified mailing required alongside owner notice (${authorities.jurisdiction})`;
    const certifiedStatute: TimelineStatuteInfo = certifiedDate
      ? {
          title: "Certified Mail Requirement",
          jurisdiction: authorities.jurisdiction,
          authority: authorityLabel(authorities.certifiedAuthority ?? authorities.noticeAuthority),
          confidence: "Documented",
          reason: "Certified mailing documentation on file.",
          deadline: certifiedDeadlineLabel,
          evidence: "Certified-mail record on file",
          compliance: "Documented on record",
        }
      : {
          title: "Certified Mail Requirement",
          jurisdiction: authorities.jurisdiction,
          authority: authorityLabel(authorities.certifiedAuthority ?? authorities.noticeAuthority),
          confidence: "Estimated",
          reason:
            "No certified mailing documentation was found. Compliance cannot be independently verified from the submitted record.",
          deadline: certifiedDeadlineLabel,
          evidence: "Not documented",
          compliance: "Cannot be verified from the submitted record",
        };
    rows.push({
      key: "certified",
      label: "Certified Letter Sent",
      date: certifiedDate,
      displayDate: fmtDate(certifiedDate),
      displayTime: fmtTimeIfPresent(certifiedDate),
      icon: Mail,
      verified: !!certifiedDate,
      statutory: true,
      applicable: true,
      phase: "claim",
      statute: certifiedStatute,
    });

    // Potential Enforcement Window — jurisdiction-driven statutory callout.
    const waitDaysLabel = authorities.enforcementWaitingDays
      ? authorities.enforcementConditions
        ? `${authorities.enforcementWaitingDays}-day statutory waiting period (subject to statutory conditions)`
        : `${authorities.enforcementWaitingDays}-day statutory waiting period`
      : "applicable statutory waiting period";
    const enforcementConfidence: StatuteConfidence =
      enforcementBasis === "documented"
        ? "Documented"
        : enforcementBasis === "notice"
          ? "Calculated"
          : "Estimated";
    const enforcementReason = releasedBeforeEnforcement
      ? `The vehicle was released on ${fmtDate(lawReleaseDate)}, before any ${waitDaysLabel} could run. No enforcement window arose on this record.`
      : enforcementBasis === "documented"
        ? "Documented enforcement date on record. Enforcement remains contingent upon verified notice compliance."
        : enforcementBasis === "notice"
          ? `Calculated using the documented notice date plus the ${waitDaysLabel}.`
          : enforcementBasis === "storage" || enforcementBasis === "billed"
            ? "Calculated using the documented storage date because no verified notice date was available."
            : "Notice date and storage date unavailable. Window cannot be calculated on the current record.";
    const enforcementEvidence = releasedBeforeEnforcement
      ? "Documented release date on record"
      : enforcementBasis === "documented"
        ? "Enforcement date on record"
        : enforcementBasis === "notice"
          ? "Derived from documented notice date"
          : enforcementBasis === "storage" || enforcementBasis === "billed"
            ? "Derived from documented storage date"
            : "Not documented";
    const enforcementCompliance = releasedBeforeEnforcement
      ? "Not applicable — possession ended before the statutory window"
      : enforcementBasis === "documented"
        ? "Contingent on verified notice compliance"
        : "Contingent on verified notice and certified-mail compliance";
    const enforcementStatute: TimelineStatuteInfo = {
      title: releasedBeforeEnforcement
        ? "Enforcement Window — Not Applicable"
        : "Estimated Enforcement Window",
      jurisdiction: authorities.jurisdiction,
      authority: authorityLabel(authorities.enforcementAuthority),
      confidence: enforcementConfidence,
      reason: enforcementReason,
      deadline: `Calculated using the ${waitDaysLabel} following vehicle storage`,
      evidence: enforcementEvidence,
      compliance: enforcementCompliance,
    };
    rows.push({
      key: "enforcement",
      label: "Potential Enforcement Window",
      date: enforcementDate,
      displayDate: releasedBeforeEnforcement
        ? "Not applicable — vehicle released"
        : fmtDate(enforcementDate),
      displayTime: fmtTimeIfPresent(enforcementDate),
      icon: Scale,
      verified: !!enforcementDate && noticeVerified && enforcementBasis === "documented",
      statutory: true,
      /* Withdrawn windows are not "missing evidence" — they are milestones
         that never arose, so they are excluded from the coverage gap list. */
      applicable: !releasedBeforeEnforcement,
      phase: "claim",
      statute: enforcementStatute,
    });

    if (auctionDate) {
      push({
        key: "auction",
        label: "Auction Scheduled",
        date: auctionDate,
        icon: Landmark,
        statutory: false,
        applicable: true,
        phase: "claim",
      });
    }
  }

  push({
    key: "invoice",
    label: "Invoice Issued",
    date: invoiceDate,
    icon: Receipt,
    statutory: false,
    applicable: true,
    phase: "claim",
  });
  push({
    key: "submitted",
    label: "Invoice Received",
    date: submittedDate,
    icon: Upload,
    statutory: false,
    applicable: true,
    phase: "review",
  });
  push({
    key: "reviewed",
    label: "AI Review Completed",
    date: findingsDate,
    icon: ClipboardCheck,
    statutory: false,
    applicable: true,
    phase: "review",
  });
  push({
    key: "market",
    label: "Market Comparison Completed",
    date: marketDate,
    icon: BarChart3,
    statutory: false,
    applicable: true,
    phase: "review",
  });
  push({
    key: "legal",
    label: "Legal Research Completed",
    date: legalDate,
    icon: BookOpen,
    statutory: false,
    applicable: true,
    phase: "review",
  });
  push({
    key: "report",
    label: "Professional Report Generated",
    date: reportGeneratedDate,
    icon: BadgeCheck,
    statutory: false,
    applicable: true,
    phase: "review",
  });

  // Chronological sort by known date; unknowns retain their canonical position.
  const withIndex = rows.map((r, i) => ({ r, i }));
  withIndex.sort((a, b) => {
    if (a.r.date && b.r.date) return new Date(a.r.date).getTime() - new Date(b.r.date).getTime();
    if (a.r.date && !b.r.date) return -1;
    if (!a.r.date && b.r.date) return 1;
    return a.i - b.i;
  });
  let sorted = withIndex.map((x) => x.r);

  /* Statutory milestones describe a legally ordered sequence (notice →
     certified mailing → enforcement window). Sorting them by date pushed a
     calculated enforcement window ahead of the undated notices it depends
     on, presenting an impossible sequence. Their legal order is fixed. */
  const STATUTORY_ORDER = ["notice", "certified", "enforcement"];
  const statutorySlots = sorted
    .map((r, i) => ({ r, i }))
    .filter((x) => x.r.phase === "claim" && x.r.statutory);
  if (statutorySlots.length > 1) {
    const ordered = [...statutorySlots.map((x) => x.r)].sort(
      (a, b) => STATUTORY_ORDER.indexOf(a.key) - STATUTORY_ORDER.indexOf(b.key),
    );
    const next = [...sorted];
    statutorySlots.forEach((slot, n) => {
      next[slot.i] = ordered[n]!;
    });
    sorted = next;
  }

  /* A milestone counts as a coverage gap only when the record carries no
     date for it at all. A milestone that is dated (calculated or
     documented) or withdrawn as inapplicable is not "missing". */
  const missingStatutory = sorted
    .filter((r) => r.statutory && r.applicable && !r.date)
    .map((r) => r.label);

  return { events: sorted, missingStatutory, isTowingCase, authorities };
}

function TimelineAnalysis({
  bundle,
  invoiceFields,
  state,
}: {
  bundle: FullWorkspaceBundle | null;
  invoiceFields: ReturnType<typeof extractInvoiceReportFields> | null;
  state: string | null;
}) {
  const { events, missingStatutory, isTowingCase, authorities } = buildCaseTimeline(
    bundle,
    invoiceFields,
    state,
  );
  const reviewEvents = events.filter((e) => e.phase === "review");
  const documentedEvents = events.filter((e) => e.phase === "claim" && !e.statutory);
  const statutoryEvents = events.filter((e) => e.phase === "claim" && e.statutory);

  const renderPhase = (
    phaseEvents: CaseTimelineEvent[],
    eyebrow: string,
    title: string,
    subtitle: string,
    options: {
      showStatuteCallouts?: boolean;
      spacious?: boolean;
      hideTime?: boolean;
      footer?: React.ReactNode;
      relaxed?: boolean;
    } = {},
  ) => {
    if (phaseEvents.length === 0) return null;
    const cols = Math.min(6, Math.max(phaseEvents.length, 1));
    const gridColsClass =
      cols <= 2
        ? "md:grid-cols-2"
        : cols === 3
          ? "md:grid-cols-3"
          : cols === 4
            ? "md:grid-cols-2 lg:grid-cols-4"
            : cols === 5
              ? "md:grid-cols-3 lg:grid-cols-5"
              : "md:grid-cols-3 lg:grid-cols-6";
    const spacious = options.spacious === true;
    const relaxed = options.relaxed === true;
    return (
      <section
        aria-label={`${eyebrow}: ${title}`}
        data-card-padding="tight"
        className={`rounded-2xl border border-border bg-gradient-to-br from-card to-secondary/30 shadow-card ${
          spacious ? "!p-8 !pb-14 md:!p-12 md:!pb-20" : "p-6 md:p-8"
        }`}
      >
        <header className="max-w-3xl">
          <div className="text-[11px] font-semibold uppercase tracking-[0.22em] text-trust">
            {eyebrow}
          </div>
          <h3 className="mt-1.5 font-display text-xl font-semibold text-navy md:text-2xl">
            {title}
          </h3>
          <p className="mt-2 text-sm leading-relaxed text-foreground/70">{subtitle}</p>
        </header>
        <div className={`relative ${spacious ? "mt-14 md:mt-16" : "mt-8"}`}>
          <div
            aria-hidden="true"
            style={{
              left: `calc(100% / ${cols} / 2)`,
              right: `calc(100% / ${cols} / 2)`,
            }}
            className="pointer-events-none absolute top-6 hidden h-px bg-border md:block"
          />

          <ol
            className={`relative grid grid-cols-1 items-stretch gap-x-6 gap-y-8 sm:grid-cols-2 ${gridColsClass}`}
          >
            {phaseEvents.map((t, i) => {
              const isLast = i === phaseEvents.length - 1;
              const unverified = !t.verified;
              /* An inapplicable milestone is not an evidence gap and must not
                 be styled or announced as one. */
              const notApplicable = !t.applicable;
              const statutoryMissing = t.statutory && unverified && !notApplicable;
              const statusLabel = notApplicable
                ? "Statutory milestone — not applicable on this record"
                : statutoryMissing
                  ? "Statutory milestone — not evidenced in file"
                  : unverified
                    ? "Estimated"
                    : isLast
                      ? "Completed"
                      : "Verified";
              return (
                <li
                  key={t.key}
                  className="relative flex h-full flex-col items-start text-left md:items-center md:text-center"
                >
                  <div
                    aria-label={statusLabel}
                    className={`relative z-10 flex h-12 w-12 items-center justify-center rounded-full border-2 shadow-card ring-4 ring-background transition ${
                      statutoryMissing
                        ? "border-amber-500 bg-amber-50 text-amber-700"
                        : unverified
                          ? "border-dashed border-muted-foreground/50 bg-white text-muted-foreground"
                          : isLast
                            ? "border-savings bg-savings text-savings-foreground"
                            : "border-trust bg-white text-trust"
                    }`}
                  >
                    {(() => {
                      const Icon = t.icon as React.ComponentType<{
                        className?: string;
                        strokeWidth?: number;
                        "aria-hidden"?: boolean | "true" | "false";
                      }>;
                      return (
                        <Icon
                          aria-hidden="true"
                          className="h-5 w-5"
                          strokeWidth={t.statutory ? 2.5 : 2}
                        />
                      );
                    })()}
                  </div>
                  <div
                    className={`${relaxed ? "mt-3" : "mt-2"} flex flex-wrap items-center gap-1.5 md:justify-center`}
                  >
                    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                      Step {String(i + 1).padStart(2, "0")}
                    </span>
                    {t.statutory ? (
                      <span
                        className={`rounded-full px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider ${
                          statutoryMissing
                            ? "bg-amber-100 text-amber-800"
                            : "bg-trust-soft text-trust"
                        }`}
                      >
                        Statutory
                      </span>
                    ) : null}
                    {!t.statutory && unverified ? (
                      <span className="rounded-full bg-secondary px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Estimated
                      </span>
                    ) : null}
                  </div>
                  <div
                    className={`${relaxed ? "mt-2" : "mt-1"} text-sm font-semibold leading-snug text-navy`}
                  >
                    {t.label}
                  </div>
                  <div
                    className={`${relaxed ? "mt-1.5" : "mt-0.5"} font-mono text-[11px] tabular-nums ${
                      unverified ? "italic text-muted-foreground/80" : "text-muted-foreground"
                    }`}
                  >
                    {t.displayDate}
                    {t.displayTime && !options.hideTime ? (
                      <span className="ml-1 text-muted-foreground/70">· {t.displayTime}</span>
                    ) : null}
                  </div>

                  {t.note && !options.showStatuteCallouts ? (
                    <div
                      className={`${relaxed ? "mt-2" : "mt-1.5"} text-[11px] leading-relaxed text-muted-foreground/80`}
                    >
                      {t.note}
                    </div>
                  ) : null}
                  {options.showStatuteCallouts && t.statute ? (
                    <div className={`${relaxed ? "mt-4" : "mt-3"} w-full`}>
                      <StatuteCallout info={t.statute} />
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ol>
        </div>
        {options.footer ? <div className="mt-6">{options.footer}</div> : null}
      </section>
    );
  };

  return (
    <Section
      bare
      number="01"
      eyebrow="Chronology"
      title="Case Timeline & Key Events"
      intro={`Three timelines below, read in order: (1) ClaimGuard's review activities on this claim, (2) the documented case timeline reconstructed from the invoice and supporting evidence, and (3) the jurisdictional statutory timeline generated from ${authorities.jurisdiction} lien and notice law.`}
    >
      {events.length === 0 ? (
        <EmptyRealState label="No timeline events could be reconstructed for this claim." />
      ) : (
        <>
          <div className="grid gap-6">
            {renderPhase(
              reviewEvents,
              "Phase 1",
              "ClaimGuard Review",
              "Activities performed by ClaimGuard after the invoice was submitted, including AI analysis, legal research, market benchmarking, and professional report preparation.",
              { spacious: true },
            )}
            {renderPhase(
              documentedEvents,
              "Phase 2",
              "Documented Case Timeline",
              "Chronological reconstruction of documented events extracted from the submitted invoice, supporting documentation, receipts, and verified evidence. Jurisdiction-independent historical facts only.",
              { hideTime: true },
            )}
            {renderPhase(
              statutoryEvents,
              "Phase 3",
              "Jurisdictional Statutory Timeline",
              "Statutory milestones generated from the governing state's towing, storage, lien, and abandonment laws. Requirements automatically adapt to the applicable jurisdiction.",
              {
                showStatuteCallouts: true,
                hideTime: true,
                relaxed: true,
                footer:
                  isTowingCase && missingStatutory.length > 0 ? (
                    <CoverageNote
                      message={`Missing statutory milestones on this claim: ${missingStatutory.join(", ")}. Absence of documented notice or certified mail may affect the vendor's lien eligibility and negotiation posture.`}
                    />
                  ) : null,
              },
            )}
          </div>

          <div className="pt-12">
            <MethodologyPanel
              title="How the case chronology is assembled"
              subtitle="The timeline reconstructs verifiable milestones from the invoice and any customer-supplied documents; undocumented events are surfaced rather than invented."
              items={[
                {
                  icon: Clock,
                  label: "Evidence-anchored dates",
                  description:
                    "Documented milestones are tied to a date on the invoice, receipt, attached correspondence, or ClaimGuard's own processing record. No event date is invented.",
                },
                {
                  icon: Search,
                  label: "Statutory checkpoints",
                  description:
                    "Notice, certified mail, and lien-eligibility windows are mapped against the invoice's jurisdiction and shown in their statutory order, not their calculated order.",
                },
                {
                  icon: ShieldCheck,
                  label: "Verification status bounds",
                  description:
                    "Undocumented milestones are marked ‘Not Evidenced in File’, calculated windows state the date they were derived from, and milestones that never arose are marked not applicable.",
                },
              ]}
            />
          </div>
        </>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 9. Professional Negotiation Playbook                       */
/* ─────────────────────────────────────────────────────────── */

type Priority = "High" | "Medium" | "Low";

type Recommendation = {
  priority: Priority;
  rank: number;
  title: string;
  category: string;
  reason: string;
  invoiceEvidence: string;
  marketEvidence: string;
  legalEvidence: string;
  wording: string;
  followUp: string;
  savingsLabel: string;
  savingsMax: number;
  /** True when a local benchmark row was matched to this charge (Chapter 03). */
  hasMarketEvidence: boolean;
  /** True when a statute/regulation was matched to this charge (Chapters 04–06). */
  hasLegalAuthority: boolean;
};

/* Negotiation selection — shared context so the queue drives the roadmap,
   scripts, and recommended-next-action surfaces without duplicating state. */
type QueueStatus = "not_started" | "in_progress" | "completed" | "followup" | "skipped";
type ScriptTag = "opening" | "storage" | "admin" | "duplicate" | "adjust" | "closing";

type NegotiationSelectionValue = {
  recs: Recommendation[];
  selectedRank: number | null;
  current: Recommendation | null;
  currentIdx: number;
  totalCount: number;
  statuses: Record<number, QueueStatus>;
  completedCount: number;
  select: (rank: number) => void;
  setStatus: (rank: number, next: QueueStatus) => void;
  goTo: (delta: number) => void;
  scriptTagFor: (rec: Recommendation | null) => ScriptTag;
};

const NegotiationSelectionContext = createContext<NegotiationSelectionValue | null>(null);

function scriptTagForRec(rec: Recommendation | null): ScriptTag {
  if (!rec) return "opening";
  const key = `${rec.category ?? ""} ${rec.title ?? ""}`.toLowerCase();
  if (/storage|rate|daily/.test(key)) return "storage";
  if (/admin|processing|handling|paperwork/.test(key)) return "admin";
  if (/duplicate|environmental|overlap/.test(key)) return "duplicate";
  if (/credit\s*card|adjust|surcharge|fuel/.test(key)) return "adjust";
  if (/lien|release|closing|final/.test(key)) return "closing";
  return "adjust";
}

function NegotiationSelectionProvider({
  recs,
  children,
}: {
  recs: Recommendation[];
  children: React.ReactNode;
}) {
  const [selectedRank, setSelectedRank] = useState<number | null>(recs[0]?.rank ?? null);
  const [statuses, setStatuses] = useState<Record<number, QueueStatus>>({});

  const currentIdx = Math.max(
    0,
    recs.findIndex((r) => r.rank === selectedRank),
  );
  const current = recs[currentIdx] ?? recs[0] ?? null;
  const totalCount = recs.length;
  const completedCount = Object.values(statuses).filter((s) => s === "completed").length;

  const select = (rank: number) => setSelectedRank(rank);
  const setStatus = (rank: number, next: QueueStatus) =>
    setStatuses((prev) => ({ ...prev, [rank]: prev[rank] === next ? "not_started" : next }));
  const goTo = (delta: number) => {
    const next = recs[currentIdx + delta];
    if (next) setSelectedRank(next.rank);
  };

  const value: NegotiationSelectionValue = {
    recs,
    selectedRank,
    current,
    currentIdx,
    totalCount,
    statuses,
    completedCount,
    select,
    setStatus,
    goTo,
    scriptTagFor: scriptTagForRec,
  };

  return (
    <NegotiationSelectionContext.Provider value={value}>
      {children}
    </NegotiationSelectionContext.Provider>
  );
}

function useNegotiationSelection(): NegotiationSelectionValue | null {
  return useContext(NegotiationSelectionContext);
}

function wordingFor(category: string, title: string): string {
  const c = (category || "").toLowerCase();
  if (c.includes("storage") || c.includes("rate"))
    return `Based on local benchmarks for comparable facilities in this ZIP code, the daily storage rate on this invoice is materially above the market range. I'd like to bring it in line with the local average — can you adjust the ${title.toLowerCase()} to reflect regional pricing?`;
  if (c.includes("admin") || c.includes("fee"))
    return `I don't see supporting authorization or itemization for the ${title.toLowerCase()}. Under standard billing practice this line needs documentation. Please remove it or send the underlying authorization.`;
  if (c.includes("duplicate") || c.includes("environmental"))
    return `The ${title.toLowerCase()} appears to overlap with charges already itemized elsewhere on this invoice. Please reverse the duplicate and reissue.`;
  if (c.includes("lien") || c.includes("release"))
    return `Please confirm in writing the statutory authority and calculation used for the ${title.toLowerCase()} so I can reconcile it against state requirements.`;
  return `I've reviewed the ${title.toLowerCase()} against comparable invoices and applicable regulations. The supporting evidence indicates this charge should be reduced or removed. Can we resolve it today?`;
}

/**
 * Escalation path. This is a recommended ACTION for the vehicle owner — it
 * never predicts how the vendor will respond, because no canonical source in
 * this package supports a vendor-behaviour forecast.
 */
function followUpFor(priority: Priority): string {
  return priority === "High"
    ? "If declined, request the supporting authorization or tariff in writing, then escalate to a manager citing the authority listed above."
    : priority === "Medium"
      ? "If declined, request the written policy behind the charge and keep the response on file with this report."
      : "If declined, record the response and return to the higher-value items in this playbook.";
}

function findMarketFor(
  title: string,
  category: string,
  market?: MarketComparisonRow[],
): MarketComparisonRow | undefined {
  if (!market?.length) return undefined;
  const t = `${title} ${category}`.toLowerCase();
  return market.find((m) => {
    const key = `${m.service_type ?? ""} ${m.line_item ?? ""}`.toLowerCase();
    if (!key.trim()) return false;
    return key.split(/\s+/).some((w) => w.length > 3 && t.includes(w));
  });
}

function findLegalFor(
  title: string,
  category: string,
  legal?: LegalResearch[],
): LegalResearch | undefined {
  if (!legal?.length) return undefined;
  const t = `${title} ${category}`.toLowerCase();
  return (
    legal.find((l) => {
      const key = `${l.topic ?? ""} ${l.title ?? ""}`.toLowerCase();
      if (!key.trim()) return false;
      return key.split(/\s+/).some((w) => w.length > 3 && t.includes(w));
    }) ?? undefined
  );
}

function invoiceEvidenceFor(amount: number, title: string): string {
  if (amount > 0) return `Line item "${title}" billed at ${currency(amount)} on this invoice.`;
  return `Line item "${title}" appears on this invoice without supporting itemization.`;
}

function marketEvidenceString(m?: MarketComparisonRow): string {
  if (!m)
    return "No local benchmark was established for this line in the Market Benchmark Analysis. Negotiate it on invoice documentation grounds.";
  const inv = m.invoice_amount ?? m.invoice_rate;
  const avg = m.local_average ?? m.market_median;
  const lo = m.typical_low ?? m.market_low;
  const hi = m.typical_high ?? m.market_high;
  const parts: string[] = [];
  if (inv != null && avg != null)
    parts.push(`Invoice ${currency(inv)} vs local average ${currency(avg)}`);
  if (lo != null && hi != null) parts.push(`typical range ${currency(lo)}–${currency(hi)}`);
  if (m.difference_percent != null)
    parts.push(
      `${m.difference_percent > 0 ? "+" : ""}${Math.round(m.difference_percent)}% vs market`,
    );
  return parts.length ? parts.join(" · ") : "Matched to a comparable local benchmark.";
}

function legalEvidenceString(l?: LegalResearch): string {
  if (!l)
    return "No statute or regulation in this claim's legal research maps to this charge. Negotiate this line on invoice and market evidence only.";
  const cite = l.citation ?? l.statute ?? l.title ?? "Applicable statute";
  const why = l.why_it_matters ?? l.summary ?? l.impact ?? "Supports the negotiation position.";
  return `${cite} — ${why}`;
}

function NegotiationOpportunities({
  pending,
  real,
  reportFindings,
  marketComparisons,
  legalResearch,
  vendor,
  report,
}: {
  pending?: boolean;
  real?: FindingItem[] | null;
  reportFindings?: ReturnType<typeof buildReport>["findings"];
  marketComparisons?: MarketComparisonRow[];
  legalResearch?: LegalResearch[];
  vendor?: string;
  report?: ReturnType<typeof buildReport>;
} = {}) {
  const recs: Recommendation[] = buildRecommendations({
    real,
    reportFindings,
    marketComparisons,
    legalResearch,
  });

  // Canonical: every report-wide figure comes from the immutable Report Summary.
  const summary = useReportSummary();
  const strongest = recs[0];
  /* Never present the same position as both the lead and the close-out. */
  const weakest = [...recs]
    .reverse()
    .find((x) => strongest && x.title.toLowerCase() !== strongest.title.toLowerCase());
  const benchmarkedCount = recs.filter((r) => r.hasMarketEvidence).length;
  const authorityCount = recs.filter((r) => r.hasLegalAuthority).length;

  return (
    <Section
      number="01"
      eyebrow="Strategy"
      title={<>Professional Negotiation Playbook{pending && <PendingBadge />}</>}
      intro="A step-by-step strategy built from the findings, benchmarks, and authorities already established in this report — evidence, scripts, and escalation paths included."
    >
      {recs.length === 0 ? (
        <EmptyRealState label="Negotiation opportunities have not been extracted for this review." />
      ) : (
        <NegotiationSelectionProvider recs={recs}>
          <div className="space-y-6">
            <NegotiationStrategySummary
              strongest={strongest}
              weakest={weakest}
              opportunityCount={recs.length}
              benchmarkedCount={benchmarkedCount}
              authorityCount={authorityCount}
              summary={summary}
            />

            <NegotiationRecommendationsSurface recs={recs} vendor={vendor} />

            <NegotiationRoadmap vendor={vendor} />

            <PreparationChecklist />

            <WhatToSay vendor={vendor} report={report} recs={recs} />
          </div>
        </NegotiationSelectionProvider>
      )}
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Negotiation Recommendations — dual-surface renderer.        */
/* PDF: full ranked list (unchanged binder layout).            */
/* Web: guided queue + focus mode (one finding at a time).     */
/* ─────────────────────────────────────────────────────────── */

function NegotiationRecommendationsSurface({
  recs,
  vendor,
}: {
  recs: Recommendation[];
  vendor?: string;
}) {
  const mode = useRenderMode();
  if (mode === "pdf") {
    return <PdfRecommendationsList recs={recs} vendor={vendor} />;
  }
  return <WebNegotiationQueue vendor={vendor} />;
}

function PdfRecommendationsList({ recs, vendor }: { recs: Recommendation[]; vendor?: string }) {
  const topSavings = recs[0]?.savingsMax ?? 0;
  const heroCount =
    recs.length > 0 && (recs[0].priority === "High" || recs[0].savingsMax >= topSavings * 0.6)
      ? 1
      : 0;
  let tailPairable = 0;
  for (let i = recs.length - 1; i >= heroCount + 1; i--) {
    const rec = recs[i];
    const short = (rec.reason?.length ?? 0) < 220;
    if (rec.priority === "Low" && short) tailPairable++;
    else break;
  }
  const pairCount = tailPairable >= 2 ? tailPairable - (tailPairable % 2) : 0;
  const mainEnd = recs.length - pairCount;
  const heroItems = recs.slice(0, heroCount);
  const mainItems = recs.slice(heroCount, mainEnd);
  const pairItems = recs.slice(mainEnd);
  return (
    <div>
      <PlaybookHeader
        eyebrow="Section 1"
        title="Recommendations, ranked"
        subtitle={`${recs.length} negotiation ${recs.length === 1 ? "opportunity" : "opportunities"} prepared, ranked by evidentiary strength and expected recovery.`}
      />
      <div className="mt-4 space-y-3">
        {heroItems.map((rec) => (
          <RecommendationCardPro key={rec.title + rec.rank} rec={rec} vendor={vendor} defaultOpen />
        ))}
        {mainItems.map((rec) => (
          <RecommendationCardPro key={rec.title + rec.rank} rec={rec} vendor={vendor} />
        ))}
        {pairItems.length > 0 && (
          <div className="grid gap-3 md:grid-cols-2 [break-inside:avoid]">
            {pairItems.map((rec) => (
              <RecommendationCardPro key={rec.title + rec.rank} rec={rec} vendor={vendor} compact />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function WebNegotiationQueue({ vendor }: { vendor?: string }) {
  const ctx = useNegotiationSelection();
  const [showHigh, setShowHigh] = useState(true);
  const [showMedium, setShowMedium] = useState(false);
  const [showLow, setShowLow] = useState(false);
  const groupsId = useId();
  const expandAll = () => {
    setShowHigh(true);
    setShowMedium(true);
    setShowLow(true);
  };
  const collapseAll = () => {
    setShowHigh(false);
    setShowMedium(false);
    setShowLow(false);
  };
  const allOpen = showHigh && showMedium && showLow;

  const recs = ctx?.recs ?? [];
  const selectedRank = ctx?.selectedRank ?? null;
  const statuses = ctx?.statuses ?? {};
  const totalCount = ctx?.totalCount ?? 0;
  const completedCount = ctx?.completedCount ?? 0;
  const currentIdx = ctx?.currentIdx ?? 0;
  const current = ctx?.current ?? null;

  const highPriority = recs.filter((r) => r.priority === "High");
  const mediumPriority = recs.filter((r) => r.priority === "Medium");
  const lowPriority = recs.filter((r) => r.priority === "Low");

  useEffect(() => {
    if (!ctx) return;
    const EDITABLE_ROLES = new Set(["textbox", "searchbox", "combobox", "spinbutton"]);
    const isEditableTarget = (target: EventTarget | null) => {
      if (!(target instanceof HTMLElement)) return false;
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return true;
      if (target.isContentEditable) return true;
      const role = target.getAttribute("role");
      if (role && EDITABLE_ROLES.has(role)) return true;
      if (
        target.closest(
          '[contenteditable="true"], [role="textbox"], [role="combobox"], [role="searchbox"], [role="spinbutton"]',
        )
      ) {
        return true;
      }
      return false;
    };
    const handler = (e: KeyboardEvent) => {
      if (isEditableTarget(e.target)) return;
      if (e.key === "ArrowRight") ctx.goTo(1);
      else if (e.key === "ArrowLeft") ctx.goTo(-1);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [ctx]);

  const progressPct = totalCount > 0 ? Math.round((completedCount / totalCount) * 100) : 0;

  if (!current || !ctx) return null;

  return (
    <div className="space-y-6" data-pdf-exclude="true">
      <PlaybookHeader
        eyebrow="Negotiation queue"
        title="Work each finding one at a time"
        subtitle={`${totalCount} opportunit${totalCount === 1 ? "y" : "ies"} prepared. Focus mode keeps the full evidence stack for the finding you're currently negotiating.`}
      />

      {/* Progress bar */}
      <div className="rounded-2xl border border-border bg-card px-4 py-3">
        <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wider text-muted-foreground">
          <span id="negotiation-progress-label">Negotiation progress</span>
          <span className="text-navy">
            {completedCount} of {totalCount} completed
          </span>
        </div>
        <div
          role="progressbar"
          aria-labelledby="negotiation-progress-label"
          aria-valuemin={0}
          aria-valuemax={totalCount}
          aria-valuenow={completedCount}
          aria-valuetext={`${completedCount} of ${totalCount} negotiation findings completed`}
          className="mt-2 h-2 w-full overflow-hidden rounded-full bg-secondary"
        >
          <div
            className="h-full rounded-full bg-savings transition-all"
            style={{ width: `${progressPct}%` }}
          />
        </div>
      </div>

      <div className="grid grid-cols-[minmax(0,1fr)] gap-6 lg:grid-cols-[minmax(0,280px)_minmax(0,1fr)] xl:grid-cols-[minmax(0,300px)_minmax(0,1fr)]">
        {/* Queue rail */}
        <aside className="space-y-3" aria-label="Negotiation queue">
          <div className="flex items-center justify-between gap-2 px-1">
            <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Negotiation queue
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={expandAll}
                disabled={allOpen}
                aria-label="Expand all priority groups"
                className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold text-navy transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                + Expand all
              </button>
              <button
                type="button"
                onClick={collapseAll}
                disabled={!showHigh && !showMedium && !showLow}
                aria-label="Collapse all priority groups"
                className="rounded-md border border-border bg-background px-2 py-1 text-[11px] font-semibold text-navy transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                − Collapse all
              </button>
            </div>
          </div>

          <CollapsibleGroup
            label={`High priority (${highPriority.length})`}
            open={showHigh}
            onToggle={() => setShowHigh((v) => !v)}
            controlsId={`${groupsId}-high`}
          >
            <QueueGroup
              label=""
              tone="high"
              items={highPriority}
              selectedRank={selectedRank}
              statuses={statuses}
              onSelect={ctx.select}
              dense
            />
          </CollapsibleGroup>

          {mediumPriority.length > 0 && (
            <CollapsibleGroup
              label={`Medium priority (${mediumPriority.length})`}
              open={showMedium}
              onToggle={() => setShowMedium((v) => !v)}
              controlsId={`${groupsId}-medium`}
            >
              <QueueGroup
                label=""
                tone="medium"
                items={mediumPriority}
                selectedRank={selectedRank}
                statuses={statuses}
                onSelect={ctx.select}
                dense
              />
            </CollapsibleGroup>
          )}

          {lowPriority.length > 0 && (
            <CollapsibleGroup
              label={`Low priority (${lowPriority.length})`}
              open={showLow}
              onToggle={() => setShowLow((v) => !v)}
              controlsId={`${groupsId}-low`}
            >
              <QueueGroup
                label=""
                tone="low"
                items={lowPriority}
                selectedRank={selectedRank}
                statuses={statuses}
                onSelect={ctx.select}
                dense
              />
            </CollapsibleGroup>
          )}
        </aside>

        {/* Focus stage */}
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-border bg-secondary/40 px-4 py-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Finding {currentIdx + 1} of {totalCount}
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={() => ctx.goTo(-1)}
                disabled={currentIdx === 0}
                className="inline-flex items-center gap-1 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-semibold text-navy shadow-sm transition hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-40"
              >
                <ArrowLeft className="h-3.5 w-3.5" /> Previous
              </button>
              <button
                type="button"
                onClick={() => ctx.goTo(1)}
                disabled={currentIdx >= totalCount - 1}
                className="inline-flex items-center gap-1 rounded-md border border-navy bg-navy px-3 py-1.5 text-xs font-semibold text-navy-foreground shadow-sm transition hover:bg-navy/90 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Next <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>

          <RecommendationCardPro key={current.rank} rec={current} vendor={vendor} defaultOpen />

          <div className="flex flex-wrap items-center gap-2 rounded-2xl border border-border bg-card px-4 py-3">
            <span className="mr-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              Mark as
            </span>
            <StatusPill
              active={statuses[current.rank] === "in_progress"}
              onClick={() => ctx.setStatus(current.rank, "in_progress")}
              tone="muted"
              icon={<ChevronRight className="h-3.5 w-3.5" />}
              label="In progress"
            />
            <StatusPill
              active={statuses[current.rank] === "completed"}
              onClick={() => ctx.setStatus(current.rank, "completed")}
              tone="success"
              icon={<CheckCircle2 className="h-3.5 w-3.5" />}
              label="Completed"
            />
            <StatusPill
              active={statuses[current.rank] === "followup"}
              onClick={() => ctx.setStatus(current.rank, "followup")}
              tone="warn"
              icon={<Flag className="h-3.5 w-3.5" />}
              label="Needs follow-up"
            />
            <StatusPill
              active={statuses[current.rank] === "skipped"}
              onClick={() => ctx.setStatus(current.rank, "skipped")}
              tone="muted"
              icon={<span aria-hidden>–</span>}
              label="Skip"
            />
            <span className="ml-auto text-[11px] text-muted-foreground">
              Tip: use ← / → to move between findings
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

function CollapsibleGroup({
  label,
  open,
  onToggle,
  children,
  controlsId,
}: {
  label: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
  controlsId?: string;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card/60 p-3">
      <button
        type="button"
        onClick={onToggle}
        className="flex w-full items-center justify-between rounded-lg px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wider text-muted-foreground hover:text-navy"
        aria-expanded={open}
        aria-controls={controlsId}
      >
        <span>{label}</span>
        <span className="flex items-center gap-1 text-[10px] normal-case tracking-normal">
          {open ? "Hide" : "Show"}
          <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </span>
      </button>
      {open && (
        <div id={controlsId} className="mt-2">
          {children}
        </div>
      )}
    </div>
  );
}

function QueueGroup({
  label,
  tone,
  items,
  selectedRank,
  statuses,
  onSelect,
  dense,
}: {
  label: string;
  tone: "high" | "medium" | "low";
  items: Recommendation[];
  selectedRank: number | null;
  statuses: Record<number, QueueStatus>;
  onSelect: (rank: number) => void;
  dense?: boolean;
}) {
  if (items.length === 0) return null;
  const toneClass =
    tone === "high" ? "text-danger" : tone === "medium" ? "text-warning" : "text-muted-foreground";
  return (
    <div className={dense ? "" : "rounded-2xl border border-border bg-card p-3"}>
      <div className={`px-1 pb-2 text-[10px] font-bold uppercase tracking-[0.14em] ${toneClass}`}>
        {label}
      </div>
      <ul className="space-y-1.5">
        {items.map((rec) => {
          const active = rec.rank === selectedRank;
          const status = statuses[rec.rank] ?? "not_started";
          const isCompleted = status === "completed";
          const isFollowup = status === "followup";
          const isSkipped = status === "skipped";
          return (
            <li key={rec.rank}>
              <button
                type="button"
                onClick={() => onSelect(rec.rank)}
                aria-current={active ? "true" : undefined}
                className={`group relative flex w-full items-start gap-3 rounded-xl border px-3 py-2.5 text-left transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy/30 ${
                  active
                    ? "border-navy bg-navy/[0.06] shadow-md ring-1 ring-navy/10 -translate-y-px before:absolute before:left-0 before:top-2 before:bottom-2 before:w-1 before:rounded-r-full before:bg-navy"
                    : isCompleted
                      ? "border-transparent opacity-80 hover:opacity-100 hover:bg-secondary/60"
                      : isFollowup
                        ? "border-transparent hover:border-warning/30 hover:bg-warning/[0.04]"
                        : isSkipped
                          ? "border-transparent opacity-70 hover:opacity-100 hover:bg-secondary/60"
                          : "border-transparent hover:border-border hover:bg-secondary/60"
                }`}
              >
                <QueueStateGlyph active={active} status={status} />
                <span className="min-w-0 flex-1">
                  <span className="flex min-w-0 items-start gap-1.5">
                    <span className="mt-0.5 shrink-0 text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                      {String(rec.rank).padStart(2, "0")}
                    </span>
                    <span
                      className={`min-w-0 flex-1 break-words [hyphens:none] [overflow-wrap:break-word] [word-break:normal] text-sm font-semibold leading-snug ${
                        active ? "text-navy" : isCompleted ? "text-muted-foreground" : "text-navy"
                      }`}
                    >
                      {rec.title}
                    </span>
                  </span>
                  {/* Supported recovery / evidence position gets its own full-width
                      line so long statements never wrap into a narrow column. */}
                  <span
                    className={`mt-1 block break-words [hyphens:none] [overflow-wrap:break-word] [word-break:normal] text-[11px] font-semibold leading-snug ${
                      isCompleted ? "text-muted-foreground" : "text-savings"
                    }`}
                  >
                    {rec.savingsLabel}
                  </span>
                  <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[11px] text-muted-foreground">
                    <span>{rec.hasMarketEvidence ? "Benchmarked" : "Documentation"}</span>
                    {status !== "not_started" && (
                      <>
                        <span aria-hidden>·</span>
                        <StatusTag status={status} />
                      </>
                    )}
                  </span>
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function StatusPill({
  active,
  onClick,
  tone,
  icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  tone: "success" | "warn" | "muted";
  icon: React.ReactNode;
  label: string;
}) {
  const activeClass =
    tone === "success"
      ? "border-savings bg-savings-soft text-savings"
      : tone === "warn"
        ? "border-warning bg-warning/10 text-warning"
        : "border-border bg-secondary text-navy";
  return (
    <button
      type="button"
      onClick={onClick}
      className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-[11px] font-semibold transition ${
        active ? activeClass : "border-border bg-background text-muted-foreground hover:text-navy"
      }`}
      aria-pressed={active}
    >
      {icon}
      {label}
    </button>
  );
}

function StatusTag({ status }: { status: QueueStatus }) {
  if (status === "completed") return <span className="font-semibold text-savings">Completed</span>;
  if (status === "followup") return <span className="font-semibold text-warning">Follow-up</span>;
  if (status === "in_progress") return <span className="font-semibold text-navy">In progress</span>;
  if (status === "skipped")
    return <span className="font-semibold text-muted-foreground">Skipped</span>;
  return null;
}

function QueueStateGlyph({ active, status }: { active: boolean; status: QueueStatus }) {
  // Visual state: completed > current (active) > follow-up > skipped > upcoming.
  // Kept intentionally minimal (single glyph) to avoid queue clutter.
  const base =
    "mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[11px] font-bold";
  if (status === "completed") {
    return (
      <span className={`${base} bg-savings text-navy`} aria-label="Completed">
        <CheckCircle2 className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (status === "followup") {
    return (
      <span className={`${base} bg-warning/15 text-warning`} aria-label="Needs follow-up">
        <Flag className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (active) {
    return (
      <span className={`${base} bg-navy text-navy-foreground`} aria-label="Current">
        <ChevronRight className="h-3.5 w-3.5" />
      </span>
    );
  }
  if (status === "skipped") {
    return (
      <span
        className={`${base} border border-dashed border-border bg-transparent text-muted-foreground`}
        aria-label="Skipped"
      >
        –
      </span>
    );
  }
  return (
    <span
      className={`${base} border border-border bg-background text-muted-foreground`}
      aria-label="Upcoming"
    >
      ○
    </span>
  );
}

function buildRecommendations({
  real,
  reportFindings,
  marketComparisons,
  legalResearch,
}: {
  real?: FindingItem[] | null;
  reportFindings?: ReturnType<typeof buildReport>["findings"];
  marketComparisons?: MarketComparisonRow[];
  legalResearch?: LegalResearch[];
}): Recommendation[] {
  const base: {
    priority: Priority;
    title: string;
    category: string;
    reason: string;
    savingsLabel: string;
    savingsMax: number;
    amount: number;
  }[] = reportFindings
    ? [...reportFindings]
        .sort((a, b) => b.savings[1] - a.savings[1])
        .map((f) => ({
          priority:
            f.impact === "High"
              ? ("High" as const)
              : f.impact === "Medium"
                ? ("Medium" as const)
                : ("Low" as const),
          title: f.item,
          category: f.item,
          reason: f.issue,
          savingsLabel: `${currency(f.savings[0])} – ${currency(f.savings[1])}`,
          savingsMax: f.savings[1],
          amount: f.savings[1],
        }))
    : real
      ? [...real]
          .sort(
            (a, b) =>
              (typeof b.amount === "number" ? b.amount : 0) -
              (typeof a.amount === "number" ? a.amount : 0),
          )
          .map((f) => {
            const sev = (f.severity ?? "").toLowerCase();
            const priority: Priority =
              sev === "critical" || sev === "high"
                ? "High"
                : sev === "medium" || sev === "moderate"
                  ? "Medium"
                  : "Low";
            const amt = typeof f.amount === "number" ? f.amount : 0;
            return {
              priority,
              title: f.title,
              category: f.category ?? f.title,
              reason: f.description ?? "Flagged during forensic review.",
              savingsLabel: amt > 0 ? currency(amt) : "Variable",
              savingsMax: amt,
              amount: amt,
            };
          })
      : [];

  /* PHASE 6.4 — REPAIR 4. Deduplicate by canonical charge identity (normalized
     description + billed amount), never by title alone: two genuinely distinct
     billed lines with the same wording but different amounts both survive, while
     the same finding surfaced twice collapses into one position. Ordering is
     preserved; no amount is invented or aggregated. */
  const seenIdentity = new Set<string>();
  const deduped = base.filter((b) => {
    const identity = `${normalizeChargeKey(b.title)}|${b.category.toLowerCase().trim()}|${b.savingsMax}|${b.amount}`;
    if (seenIdentity.has(identity)) return false;
    seenIdentity.add(identity);
    return true;
  });

  return deduped.map((b, i) => {
    const m = findMarketFor(b.title, b.category, marketComparisons);
    const l = findLegalFor(b.title, b.category, legalResearch);
    return {
      priority: b.priority,
      rank: i + 1,
      title: b.title,
      category: b.category,
      reason: b.reason,
      invoiceEvidence: invoiceEvidenceFor(b.amount, b.title),
      marketEvidence: marketEvidenceString(m),
      legalEvidence: legalEvidenceString(l),
      wording: wordingFor(b.category, b.title),
      followUp: followUpFor(b.priority),
      /* A finding with no supported numeric recovery is an evidence position,
         not a monetary one. It never renders "$0 – $0". */
      savingsLabel:
        b.savingsMax > 0 ? b.savingsLabel : "Evidence position — no supported monetary recovery",
      savingsMax: b.savingsMax,
      hasMarketEvidence: Boolean(m),
      hasLegalAuthority: Boolean(l),
    };
  });
}

function PlaybookHeader({
  eyebrow,
  title,
  subtitle,
}: {
  eyebrow: string;
  title: string;
  subtitle?: string;
}) {
  return (
    <div className="border-l-2 border-navy/20 pl-4">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        {eyebrow}
      </div>
      <div className="mt-1 font-display text-xl text-navy md:text-2xl">{title}</div>
      {subtitle && <div className="mt-1 text-sm text-muted-foreground">{subtitle}</div>}
    </div>
  );
}

function PriorityTag({ priority }: { priority: Priority }) {
  const cls =
    priority === "High"
      ? "bg-warn/15 text-warn"
      : priority === "Medium"
        ? "bg-fasttrack-soft text-fasttrack"
        : "bg-secondary text-muted-foreground";
  return (
    <span
      className={`inline-flex w-fit items-center gap-1.5 rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider ${cls}`}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${priority === "High" ? "bg-warn" : priority === "Medium" ? "bg-fasttrack" : "bg-muted-foreground/60"}`}
      />
      {priority} Priority
    </span>
  );
}

function MetaChip({
  label,
  value,
  tone = "neutral",
}: {
  label: string;
  value: string;
  tone?: "neutral" | "savings" | "trust" | "warn";
}) {
  const cls =
    tone === "savings"
      ? "border-savings/30 bg-savings-soft text-savings"
      : tone === "trust"
        ? "border-trust/30 bg-trust/10 text-trust"
        : tone === "warn"
          ? "border-warn/30 bg-warn/10 text-warn"
          : "border-border bg-secondary text-navy";
  return (
    <div className={`flex min-w-0 flex-col rounded-xl border px-3 py-2 ${cls}`}>
      <span className="text-[10px] font-semibold uppercase tracking-wider opacity-70">{label}</span>
      <span className="mt-0.5 break-words [hyphens:none] [overflow-wrap:break-word] [word-break:normal] text-sm font-semibold leading-snug">
        {value}
      </span>
    </div>
  );
}

function RecommendationCardPro({
  rec,
  vendor,
  defaultOpen,
  compact,
}: {
  rec: Recommendation;
  vendor?: string;
  defaultOpen?: boolean;
  compact?: boolean;
}) {
  /* Presentation only — content-aware column sizing so long labels
     ("No Charge-Linked Authority", supported-recovery ranges) wrap at spaces
     instead of collapsing into narrow, mid-word-breaking columns. */
  /* Container queries, not viewport queries: this card renders both full-width
     and beside the negotiation queue rail, so column counts must respond to the
     card's own width. */
  const evidenceCols = compact ? "" : "@[560px]:grid-cols-2";
  const metaCols = compact
    ? "sm:grid-cols-2"
    : "@[420px]:grid-cols-2 @[700px]:grid-cols-[minmax(110px,0.8fr)_minmax(140px,1fr)_minmax(180px,1.35fr)_minmax(145px,1fr)]";
  const followCols = compact ? "" : "md:grid-cols-2";
  return (
    <details
      open={defaultOpen}
      className="group @container overflow-hidden rounded-2xl border border-border bg-card shadow-card open:shadow-elevated"
    >
      <summary className="flex cursor-pointer list-none items-start gap-4 p-4 md:p-5">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-navy text-sm font-bold text-navy-foreground">
          {String(rec.rank).padStart(2, "0")}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <PriorityTag priority={rec.priority} />
            <span className="inline-flex min-w-0 max-w-full items-start gap-1 rounded-full border border-savings/30 bg-savings-soft px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-savings">
              <TrendingDown className="mt-[3px] h-3 w-3 shrink-0" />{" "}
              <span className="min-w-0 break-words [hyphens:none] [word-break:normal] leading-snug">
                {rec.savingsLabel}
              </span>
            </span>
            <span className="inline-flex min-w-0 max-w-full items-start gap-1 rounded-full border border-trust/30 bg-trust/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-trust">
              <BadgeCheck className="mt-[3px] h-3 w-3 shrink-0" />{" "}
              <span className="min-w-0 break-words [hyphens:none] [word-break:normal] leading-snug">
                {rec.hasMarketEvidence ? "Market benchmarked" : "Documentation basis"}
              </span>
            </span>
          </div>
          <div className="mt-1.5 font-display text-lg text-navy md:text-xl">{rec.title}</div>
          <div className="mt-1 text-sm text-muted-foreground">{rec.reason}</div>
        </div>
        <ChevronRight className="mt-1 h-5 w-5 shrink-0 text-muted-foreground transition-transform group-open:rotate-90" />
      </summary>

      <div className="border-t border-border bg-secondary/40 p-4 md:p-5">
        <div className={`grid gap-2.5 ${metaCols}`}>
          <MetaChip label="Priority" value={`${rec.priority} priority`} tone="trust" />
          <MetaChip
            label="Market benchmark"
            value={rec.hasMarketEvidence ? "Established" : "Not established"}
          />
          <MetaChip
            label="Legal authority"
            value={rec.hasLegalAuthority ? "Charge-Linked Authority" : "No Charge-Linked Authority"}
          />
          <MetaChip label="Supported recovery" value={rec.savingsLabel} tone="savings" />
        </div>

        {/* Invoice + Market side by side; Legal Authority spans the full row
            so long statutory explanations never land in the narrowest card. */}
        <div className={`mt-4 grid items-start gap-3 ${evidenceCols}`}>
          <EvidenceBlock
            icon={<Receipt className="h-4 w-4" />}
            label="Invoice evidence"
            body={rec.invoiceEvidence}
          />
          <EvidenceBlock
            icon={<MapPin className="h-4 w-4" />}
            label="Market evidence"
            body={rec.marketEvidence}
          />
          <EvidenceBlock
            icon={<Scale className="h-4 w-4" />}
            label="Legal authority"
            body={rec.legalEvidence}
            className={compact ? undefined : "@[560px]:col-span-2"}
          />
        </div>

        <div className="mt-4 rounded-2xl border border-navy/15 bg-white p-3.5 md:p-4">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-navy">
            Recommended negotiation wording
          </div>
          <p className="mt-1 text-[15px] italic leading-relaxed text-navy">
            &ldquo;{rec.wording}&rdquo;
          </p>
        </div>

        <div className={`mt-3 grid gap-3 ${followCols}`}>
          <div className="rounded-2xl border border-border bg-card p-3.5">
            <div className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
              <ChevronRight className="h-3.5 w-3.5" /> Escalation path with{" "}
              {vendor || "the storage company"}
            </div>
            <p className="mt-1 text-sm text-navy">{rec.followUp}</p>
          </div>
        </div>
      </div>
    </details>
  );
}

function EvidenceBlock({
  icon,
  label,
  body,
  className,
}: {
  icon: React.ReactNode;
  label: string;
  body: string;
  className?: string;
}) {
  return (
    <div
      className={`min-w-0 rounded-2xl border border-border bg-card p-4 ${className ?? ""}`}
    >
      <div className="flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">
        <span className="text-navy">{icon}</span>
        {label}
      </div>
      <p className="mt-1.5 break-words [hyphens:none] [overflow-wrap:break-word] [word-break:normal] text-sm leading-relaxed text-navy">
        {body}
      </p>
    </div>
  );
}

/**
 * Strategy summary. Every figure here is read from the immutable Report
 * Summary or counted directly off the recommendation list — the chapter does
 * not score, rate, or forecast the negotiation.
 */
function NegotiationStrategySummary({
  strongest,
  weakest,
  opportunityCount,
  benchmarkedCount,
  authorityCount,
  summary,
}: {
  strongest?: Recommendation;
  weakest?: Recommendation;
  opportunityCount: number;
  benchmarkedCount: number;
  authorityCount: number;
  summary: ReturnType<typeof useReportSummary>;
}) {
  const fin = summary.financials;
  return (
    <div className="overflow-hidden rounded-3xl border border-navy/15 bg-gradient-to-br from-navy to-navy/90 text-navy-foreground shadow-elevated">
      <div className="grid gap-5 p-5 md:grid-cols-[240px_1fr] md:gap-7 md:p-7">
        <div className="flex flex-col items-center justify-center rounded-2xl border border-white/10 bg-white/5 p-5 text-center">
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-white/60">
            Total Supported Recovery
          </div>
          <div
            className="mt-3 font-display text-4xl leading-none text-white tabular-nums"
            {...summaryField("potentialSavings", fin.potentialSavings)}
          >
            {currency(fin.potentialSavings)}
          </div>
          <div className="mt-1 text-xs text-white/70">Source: Market Benchmark Analysis</div>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-savings px-3 py-1 text-[11px] font-bold uppercase tracking-wider text-savings-foreground">
            <TrendingDown className="h-3 w-3" /> Settlement Goal {currency(fin.settlementGoal)}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <ScoreItem
            label="Opportunities prepared"
            value={`${opportunityCount}`}
            detail={`One position per finding in ${chapterLongRef("market")}`}
            icon={<ClipboardCheck className="h-4 w-4" />}
          />
          <ScoreItem
            label="Strongest position"
            value={strongest?.title ?? "—"}
            detail={strongest ? `${strongest.savingsLabel} supported recovery` : ""}
            icon={<Sparkles className="h-4 w-4" />}
          />
          <ScoreItem
            label="Market-Based Recovery"
            value={currency(fin.marketBasedRecovery)}
            detail={`${benchmarkedCount} position${benchmarkedCount === 1 ? "" : "s"} backed by a local benchmark`}
            icon={<TrendingDown className="h-4 w-4" />}
          />
          <ScoreItem
            label="Non-Market Recovery"
            value={currency(fin.nonMarketRecovery)}
            detail="Documented grounds rather than benchmark variance"
            icon={<Receipt className="h-4 w-4" />}
          />
          <ScoreItem
            label="Negotiation Positions With Legal Support"
            value={`${authorityCount} of ${opportunityCount}`}
            detail={
              authorityCount === 0
                ? `${summary.counts.applicableAuthorities} applicable authorit${summary.counts.applicableAuthorities === 1 ? "y" : "ies"} govern this claim (Chapters 04–06); none is linked to an individual charge.`
                : "Authorities carried from Chapters 04–06"
            }
            icon={<Scale className="h-4 w-4" />}
          />
          <ScoreItem
            label="Close-out position"
            value={weakest?.title ?? "—"}
            detail={weakest ? `${weakest.savingsLabel} · raise last` : ""}
            icon={<Info className="h-4 w-4" />}
          />
        </div>
      </div>
    </div>
  );
}

function ScoreItem({
  label,
  value,
  detail,
  icon,
}: {
  label: string;
  value: string;
  detail?: string;
  icon: React.ReactNode;
}) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-start gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-white/60">
        <span className="mt-px shrink-0 text-white/80">{icon}</span>
        <span className="min-w-0 flex-1 [overflow-wrap:anywhere]">{label}</span>
      </div>
      <div className="mt-1.5 font-display text-base text-white">{value}</div>
      {detail && <div className="mt-0.5 text-xs text-white/70">{detail}</div>}
    </div>
  );
}

function NegotiationRoadmap({ vendor }: { vendor?: string }) {
  const ctx = useNegotiationSelection();
  const steps = [
    {
      title: "Contact the storage facility",
      detail: `Call ${vendor || "the storage company"}'s billing line during business hours and reference this invoice number.`,
    },
    {
      title: "Discuss the strongest issue first",
      detail:
        "Lead with the first item in the ranked recommendations above — it carries the largest supported recovery and anchors the conversation.",
    },
    {
      title: "Present supporting evidence",
      detail:
        "Cite the invoice line, the market benchmark, and the applicable statute from this report.",
    },
    {
      title: "Request removal or reduction",
      detail:
        "Ask for a specific dollar outcome, not a vague 'discount' — a documented number is easier to approve.",
    },
    {
      title: "Escalate if necessary",
      detail:
        "If the front-line rep can't authorize, ask for a supervisor and offer to send the report and citations in writing.",
    },
    {
      title: "Reach settlement",
      detail:
        "Confirm the adjusted balance in writing (email is fine) before making payment or signing a release.",
    },
  ];

  // Progress-driven roadmap:
  //   • completedThroughIdx = the last step considered done for this finding.
  //   • activeIdx = the next step to work on. -1 means "all six complete".
  let completedThroughIdx = -1;
  let activeIdx = 0;
  if (ctx?.current) {
    const status = ctx.statuses[ctx.current.rank];
    if (status === "completed") {
      completedThroughIdx = 5;
      activeIdx = -1;
    } else if (status === "followup") {
      completedThroughIdx = 3;
      activeIdx = 4;
    } else if (status === "in_progress") {
      completedThroughIdx = 1;
      activeIdx = 2;
    } else if (status === "skipped") {
      completedThroughIdx = -1;
      activeIdx = -1;
    } else {
      completedThroughIdx = -1;
      activeIdx = ctx.currentIdx === 0 ? 0 : 1;
    }
  }

  const finishedSubtitle =
    ctx?.current && ctx.statuses[ctx.current.rank] === "completed"
      ? `"${ctx.current.title}" is closed out. Move to the next finding in the queue.`
      : ctx?.current
        ? `Highlighted step reflects where you are with "${ctx.current.title}".`
        : "A six-step progression from first contact to signed settlement.";

  return (
    <div>
      <PlaybookHeader eyebrow="Section 2" title="Negotiation Roadmap" subtitle={finishedSubtitle} />
      <ol className="mt-4 space-y-2.5">
        {steps.map((s, i) => {
          const isDone = i <= completedThroughIdx;
          const isActive = i === activeIdx;
          const isUpcoming = !isDone && !isActive;
          return (
            <li
              key={s.title}
              aria-current={isActive ? "step" : undefined}
              className={`flex gap-4 rounded-2xl border p-4 shadow-card transition-all duration-300 ease-out ${
                isActive
                  ? "border-navy bg-navy/5 ring-1 ring-navy/30 -translate-y-px shadow-md"
                  : isDone
                    ? "border-savings/30 bg-savings-soft/30"
                    : "border-border bg-card"
              }`}
            >
              <div
                className={`relative flex h-10 w-10 shrink-0 items-center justify-center rounded-full font-display text-sm transition-all duration-300 ease-out ${
                  isDone
                    ? "bg-savings text-navy"
                    : isActive
                      ? "bg-savings text-navy ring-2 ring-savings/30 ring-offset-2 ring-offset-background"
                      : "bg-navy/90 text-navy-foreground"
                }`}
              >
                <span
                  key={isDone ? "done" : "num"}
                  className="flex items-center justify-center animate-scale-in"
                >
                  {isDone ? <CheckCircle2 className="h-4 w-4" /> : i + 1}
                </span>
              </div>
              <div className="min-w-0">
                <div
                  className={`font-display text-base transition-colors duration-300 ${
                    isUpcoming ? "text-navy/70" : "text-navy"
                  }`}
                >
                  {s.title}
                  {isActive && (
                    <span className="ml-2 inline-flex rounded-full bg-savings/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-savings animate-fade-in">
                      You are here
                    </span>
                  )}
                  {isDone && !isActive && (
                    <span className="ml-2 inline-flex rounded-full bg-savings/15 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-savings">
                      Done
                    </span>
                  )}
                </div>
                <div
                  className={`mt-0.5 text-sm transition-colors duration-300 ${
                    isUpcoming ? "text-muted-foreground/70" : "text-muted-foreground"
                  }`}
                >
                  {s.detail}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

function PreparationChecklist() {
  const items = [
    {
      label: "Invoice available",
      detail: "Have the original invoice open or printed for reference.",
    },
    {
      label: "Report available",
      detail: "Keep this report open — you'll cite specific findings, benchmarks, and statutes.",
    },
    {
      label: "Supporting evidence ready",
      detail: "Screenshots of market benchmarks and citations of applicable regulations.",
    },
    {
      label: "Desired settlement amount",
      detail: "Know your target reduction and your acceptable floor before you dial.",
    },
    {
      label: "Strongest negotiation points",
      detail: "Rehearse the top two recommendations — those carry most of the recoverable value.",
    },
  ];
  return (
    <div>
      <PlaybookHeader
        eyebrow="Section 3"
        title="Preparation Checklist"
        subtitle="A quick pre-call checklist before you contact the storage company."
      />
      <div className="mt-4 grid gap-3 rounded-2xl border border-border bg-card p-4 shadow-card sm:grid-cols-2">
        {items.map((i) => (
          <div key={i.label} className="flex gap-3">
            <div className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-savings-soft text-savings">
              <CheckCircle2 className="h-4 w-4" />
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-navy">{i.label}</div>
              <div className="text-xs text-muted-foreground">{i.detail}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function WhatToSay({
  vendor,
  report,
  recs,
}: {
  vendor?: string;
  report?: ReturnType<typeof buildReport>;
  recs: Recommendation[];
}) {
  // Canonical: the only settlement target in this package is the Settlement
  // Goal from the immutable Report Summary. Never quote a recovery range here.
  const summary = useReportSummary();
  const settlementGoal = summary.financials.settlementGoal;
  const settlementTarget = settlementGoal > 0 ? currency(settlementGoal) : "the supported amount";
  const storageRec = recs.find((r) => /storage|rate/i.test(r.category + r.title));
  const adminRec = recs.find((r) => /admin|fee/i.test(r.category + r.title));
  const dupRec = recs.find((r) => /duplicate|environmental/i.test(r.category + r.title));

  const scripts: { label: string; body: string }[] = [
    {
      label: "Opening statement",
      body: `Hi, this is regarding invoice from ${vendor || "your facility"}. I've had it independently reviewed and there are a few line items I'd like to discuss and resolve today. I want to be straightforward — I'm not disputing everything, just the specific charges the review flagged.`,
    },
    {
      label: "Discussing storage charges",
      body:
        storageRec?.wording ||
        "The daily storage rate on this invoice is above what comparable facilities in this ZIP code are charging. Based on the local benchmark I have on file, I'd like to bring it in line with the regional average.",
    },
    {
      label: "Discussing administrative fees",
      body:
        adminRec?.wording ||
        "I don't see supporting authorization or itemization for the administrative fee on this invoice. Under standard billing practice this needs documentation — I'd like it removed or supported in writing.",
    },
    {
      label: "Discussing duplicate charges",
      body:
        dupRec?.wording ||
        "One of the line items appears to overlap with a charge already itemized elsewhere on this invoice. It looks like a duplicate — I'd like it reversed.",
    },
    {
      label: "Requesting adjustment",
      body: `Given the items we've discussed, a supported adjusted balance would be ${settlementTarget}. Can you process that adjustment today, or do you need to loop in a supervisor?`,
    },
    {
      label: "Closing the conversation",
      body: `Thanks for working through this with me. Please email me a written confirmation of the adjusted balance before I settle — I want to make sure we're aligned on the final number. I appreciate your time.`,
    },
  ];

  const ctx = useNegotiationSelection();
  const tag = ctx?.scriptTagFor(ctx.current) ?? "opening";
  const tagToIdx: Record<string, number> = {
    opening: 0,
    storage: 1,
    admin: 2,
    duplicate: 3,
    adjust: 4,
    closing: 5,
  };
  const activeIdx = tagToIdx[tag] ?? 0;

  return (
    <div>
      <PlaybookHeader
        eyebrow="Section 4"
        title="What to Say"
        subtitle={
          ctx?.current
            ? `Auto-selected the script that best fits "${ctx.current.title}". Every script stays available below.`
            : "Professional example language — calm, specific, and persuasive. Not a legal letter."
        }
      />
      <div className="mt-4 space-y-2.5">
        {scripts.map((s, i) => (
          <details
            key={`${ctx?.selectedRank ?? "none"}-${s.label}`}
            className="group overflow-hidden rounded-2xl border border-border bg-card shadow-card open:shadow-elevated"
            open={i === activeIdx}
          >
            <summary className="flex cursor-pointer list-none items-center gap-3 p-3.5 md:p-4">
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-bold ${
                  i === activeIdx ? "bg-savings text-navy" : "bg-secondary text-navy"
                }`}
              >
                {i + 1}
              </div>
              <div className="min-w-0 flex-1 font-display text-base text-navy">
                {s.label}
                {i === activeIdx && ctx?.current && (
                  <span className="ml-2 rounded-full bg-savings/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-savings">
                    Suggested for this Finding
                  </span>
                )}
              </div>
              <ChevronRight className="h-4 w-4 text-muted-foreground transition-transform group-open:rotate-90" />
            </summary>
            <div className="border-t border-border bg-secondary/30 p-4">
              <p className="text-[15px] italic leading-relaxed text-navy">&ldquo;{s.body}&rdquo;</p>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 10. Savings & Negotiation Strategy (merged)                 */
/* ─────────────────────────────────────────────────────────── */

function SavingsAndNegotiationStrategy({ r }: { r: ReturnType<typeof buildReport> }) {
  /* PHASE 6.4 — REPAIR 4. Findings are deduplicated by charge identity before
     targets are chosen, and the secondary target must be a different charge. */
  const sorted = [...r.findings].sort((a, b) => b.savings[1] - a.savings[1]);
  const uniqueFindings = sorted.filter((f, i, all) => {
    const key = `${f.item.toLowerCase().trim()}|${f.charge}|${f.savings[1]}`;
    return all.findIndex((o) => `${o.item.toLowerCase().trim()}|${o.charge}|${o.savings[1]}` === key) === i;
  });
  const primary = uniqueFindings[0];
  const secondary = uniqueFindings.find(
    (f) => primary && f.item.toLowerCase().trim() !== primary.item.toLowerCase().trim(),
  );
  // Canonical: every report-wide figure comes from the immutable Report Summary.
  const summary = useReportSummary();
  const fin = summary.financials;
  const savingsMid = fin.potentialSavings || r.estimatedSavings;
  const settlementGoal = fin.settlementGoal;
  // Evidence strength is the canonical negotiation strength carried from the
  // findings engine. No probability is asserted — none is calculated anywhere.
  const evidenceStrength = r.strength;
  const recoveryPct = summary.percentages.recoveryPct;

  return (
    <Section number="02" eyebrow="Executive conclusion" title="Final Negotiation Recommendation">
      <div className="space-y-8">
        {/* Financial Outcome — the numbers, presented once. */}
        <div>
          <SubsectionHeader eyebrow="Section 1" title="Financial Outcome" />
          <div className="mt-3 overflow-hidden rounded-3xl border border-savings/25 bg-savings-soft shadow-card">
            <div className="grid gap-6 p-6 md:grid-cols-4 md:p-8">
              <BigStat
                benchmark
                label="Estimated Fair Payment"
                value={currency(fin.supportedAmount)}
                field="supportedAmount"
                fieldValue={fin.supportedAmount}
              />
              <BigStat
                label="Total Supported Recovery"
                value={currency(savingsMid)}
                field="potentialSavings"
                fieldValue={savingsMid}
                primary
              />
              <BigStat
                label="Settlement Goal"
                value={settlementGoal > 0 ? currency(settlementGoal) : NOT_AVAILABLE}
                field="settlementGoal"
                fieldValue={settlementGoal}
                accent
              />
              <BigStat label="Evidence Strength" value={evidenceStrength} />
            </div>
            <div className="grid grid-cols-2 gap-0 border-t border-savings/20 bg-white/40 text-center text-[11px] uppercase tracking-[0.16em] text-savings/80 md:grid-cols-4">
              {["Supported amount", "Recoverable", "Pay-to amount", "Findings basis"].map((x) => (
                <div key={x} className="border-l border-savings/20 px-4 py-3 first:border-l-0">
                  {x}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Professional Recommendation — targets and approach on one canvas. */}
        <div>
          <SubsectionHeader eyebrow="Section 2" title="Professional Recommendation" />
          <div className="mt-3 rounded-3xl border border-navy/15 bg-gradient-to-br from-navy to-navy/90 p-6 text-navy-foreground shadow-elevated md:p-8">
            <div className="grid gap-4 md:grid-cols-2">
              {primary && (
                <RecItem
                  label="Primary Negotiation Target"
                  title={primary.item}
                  detail="Open with this charge — it carries the largest recoverable value."
                  value={currency(primary.savings[1])}
                  tone="savings"
                />
              )}
              {secondary && (
                <RecItem
                  label="Secondary Negotiation Target"
                  title={secondary.item}
                  detail="Raise once the primary target is resolved to compound the reduction."
                  value={currency(secondary.savings[1])}
                  tone="trust"
                />
              )}
              <RecItem
                label="Suggested Approach"
                title="Negotiate before payment."
                detail={`Contact ${r.vendor} and lead with the cited benchmarks and state regulations.`}
                tone="trust"
              />
              <RecItem
                label="Supported Outcome"
                title={`${Math.round(recoveryPct)}% reduction`}
                detail={`${currency(savingsMid)} of ${currency(fin.invoiceTotal)} is unsupported on the evidence in this report. Evidence strength: ${evidenceStrength}.`}
                tone="savings"
              />
            </div>
          </div>
        </div>

        {/* Executive Bottom Line — one sentence, no numbers repeated. */}
        <div>
          <SubsectionHeader eyebrow="Section 3" title="Executive Bottom Line" />
          <div className="mt-3 rounded-3xl border border-navy/15 bg-navy p-6 text-navy-foreground shadow-elevated md:p-8">
            <div className="max-w-3xl">
              <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60">
                Recommendation
              </div>
              <h3 className="mt-2 font-display text-2xl leading-snug md:text-3xl">
                Proceed with negotiation before remitting payment.
              </h3>
              <p className="mt-3 text-sm text-white/75">
                Findings, benchmarks, and statutes in this report support the Settlement Goal in
                Section 1.
              </p>
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function SubsectionHeader({ eyebrow, title }: { eyebrow: string; title: string }) {
  return (
    <div>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-trust">
        {eyebrow}
      </div>
      <h3 className="mt-1 font-display text-2xl text-navy md:text-3xl">{title}</h3>
    </div>
  );
}

function BigStat({
  label,
  value,
  accent,
  primary,
  benchmark,
  field,
  fieldValue,
}: {
  label: string;
  value: string;
  accent?: boolean;
  primary?: boolean;
  benchmark?: boolean;
  /** Canonical Report Summary field this stat renders. */
  field?: string;
  fieldValue?: number | string | null;
}) {
  const tag = field ? summaryField(field, fieldValue ?? value) : {};
  if (primary) {
    // Ranges ("$1,692 – $1,750") are ~2.4x the width of a single amount and
    // overflowed the metric card at the full hero size. Step them down to the
    // range numeral so they always fit inside the card.
    const isRange = /[–—-]\s*\$/.test(value);
    return (
      <div className="min-w-0" data-metric-card data-summary-component={`BigStat:${label}`}>
        <span className="metric-label text-savings/85">{label}</span>
        <span
          className={`${isRange ? "kpi-range mt-1" : "kpi-hero"} whitespace-nowrap tabular-nums`}
          style={{ color: "var(--color-savings)" }}
          {...tag}
        >
          {value}
        </span>
      </div>
    );
  }

  return (
    <div data-metric-card data-summary-component={`BigStat:${label}`}>
      <div
        className={`text-[11px] font-semibold uppercase tracking-wider ${benchmark ? "text-trust" : "text-savings/80"}`}
      >
        {label}
      </div>
      <div
        className={`mt-3 font-display leading-none ${accent ? "text-4xl md:text-5xl text-savings" : "text-3xl md:text-4xl text-navy"}`}
        {...tag}
      >
        {value}
      </div>
    </div>
  );
}

function RecItem({
  label,
  title,
  detail,
  value,
  tone,
}: {
  label: string;
  title: string;
  detail: string;
  value?: string;
  tone: "savings" | "trust";
}) {
  const chip =
    tone === "savings" ? "bg-savings text-savings-foreground" : "bg-trust text-trust-foreground";
  return (
    <div className="rounded-2xl border border-white/10 bg-white/5 p-4">
      <div className="flex items-center justify-between gap-3">
        <span
          className={`inline-flex items-center rounded-full ${chip} px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-wider`}
        >
          {label}
        </span>
        {value && <span className="font-display text-lg text-white tabular-nums">{value}</span>}
      </div>
      <div className="mt-2 font-display text-lg leading-snug text-white">{title}</div>
      <div className="mt-0.5 text-xs text-white/70">{detail}</div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 12. Downloads & Upsell                                      */
/* ─────────────────────────────────────────────────────────── */

function RecommendationCard({ caseId }: { caseId: string }) {
  const { downloadOrGenerate, busy } = useReportPdfActions(caseId);
  const navigate = useNavigate();
  return (
    <Section eyebrow="Next Steps" title="Your Next Actions" exportable={false}>
      <div className="space-y-6">
        {/* Three clear next actions */}
        <div className="grid gap-4 md:grid-cols-3">
          <button
            onClick={() => downloadOrGenerate("complete")}
            disabled={busy}
            className="group flex flex-col items-center gap-3 rounded-2xl border border-trust/30 bg-trust-soft p-6 text-center transition hover:shadow-elevated disabled:opacity-60"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-trust text-trust-foreground">
              <Download className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-lg text-navy">Download Professional Report</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Save your full findings, citations & ready-to-send letter.
              </div>
            </div>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-lg bg-trust px-4 py-2 text-sm font-semibold text-trust-foreground">
              {busy ? "Preparing…" : "Download Report"}
            </span>
          </button>

          <Link
            to="/submit"
            className="group flex flex-col items-center gap-3 rounded-2xl border border-border bg-white p-6 text-center transition hover:shadow-elevated"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-secondary text-navy">
              <Upload className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-lg text-navy">Upload Another Invoice</div>
              <div className="mt-1 text-sm text-muted-foreground">
                Start a new review and build your case history.
              </div>
            </div>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-border bg-white px-4 py-2 text-sm font-semibold text-navy transition group-hover:border-trust/40 group-hover:text-trust">
              Submit New Invoice <ArrowRight className="h-4 w-4" />
            </span>
          </Link>

          <button
            type="button"
            onClick={() => {
              const el = document.getElementById("fullservice-negotiation-banner");
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }}
            className="group flex flex-col items-center gap-3 rounded-2xl border border-negotiate/30 bg-negotiate-soft p-6 text-center transition hover:shadow-elevated"
          >
            <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-negotiate text-negotiate-foreground">
              <Users className="h-6 w-6" />
            </div>
            <div>
              <div className="font-display text-lg text-navy">Explore Full-Service Negotiation</div>
              <div className="mt-1 text-sm text-muted-foreground">
                See how our specialists take over the negotiation on your behalf.
              </div>
            </div>
            <span className="mt-2 inline-flex items-center gap-1.5 rounded-lg border border-negotiate/40 bg-white px-4 py-2 text-sm font-semibold text-negotiate transition group-hover:bg-negotiate-soft">
              Learn More <ArrowRight className="h-4 w-4" />
            </span>
          </button>
        </div>

        {/* Natural next-step negotiation CTA */}
        <div
          id="fullservice-negotiation-banner"
          className="scroll-mt-24 rounded-3xl border border-negotiate/20 bg-gradient-to-br from-white to-negotiate-soft/30 p-8 shadow-card md:p-10"
        >
          <div className="grid gap-8 md:grid-cols-[1fr_auto] md:items-center">
            <div>
              <div className="text-[11px] font-semibold uppercase tracking-wider text-negotiate">
                Need Us To Handle Everything?
              </div>
              <h3 className="mt-2 font-display text-2xl text-navy md:text-3xl">
                You've already received your Professional AI Review.
              </h3>
              <p className="mt-2 max-w-xl text-sm text-muted-foreground">
                If you'd rather not negotiate yourself, our specialists can take over from here.
              </p>
              <div className="mt-4 grid gap-2 sm:grid-cols-2">
                {[
                  "We contact the vendor",
                  "Negotiate on your behalf",
                  "Keep you updated",
                  "Deliver your final settlement package",
                ].map((item) => (
                  <div key={item} className="flex items-center gap-2 text-sm text-navy">
                    <CheckCircle2 className="h-4 w-4 text-savings" />
                    {item}
                  </div>
                ))}
              </div>
            </div>
            <button
              type="button"
              onClick={() =>
                navigate({
                  to: "/negotiation",
                  search: { review: caseId !== "demo" ? caseId : undefined },
                })
              }
              className="inline-flex shrink-0 items-center gap-2 rounded-lg bg-negotiate px-6 py-3 text-sm font-semibold text-white transition hover:opacity-90"
            >
              <Handshake className="h-4 w-4" />
              Start Full-Service Negotiation
            </button>
          </div>
        </div>

        {/* Primary exit — Return to Dashboard */}
        <div className="flex justify-center pt-2">
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 rounded-lg bg-navy px-8 py-4 text-base font-semibold text-white shadow-sm transition hover:-translate-y-0.5 hover:shadow-elevated"
          >
            <ArrowLeft className="h-4 w-4" />
            Return to Dashboard
          </Link>
        </div>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 11. Supporting Evidence & References (merged)               */
/* ─────────────────────────────────────────────────────────── */

type ReferenceLibraryItem = {
  title: string;
  icon: React.ComponentType<{ className?: string }>;
  hasData: boolean;
  body: React.ReactNode;
};

/* ─────────────────────────────────────────────────────────── */
/* Chapter 11 — Supporting Evidence & References               */
/* Integrity remediation pass:                                 */
/*   • no hard-coded confidence or reliability values          */
/*   • provider status derived from review_jobs + snapshots    */
/*   • real provider labels, deduplicated sources              */
/*   • info-severity findings excluded from Verified counts    */
/*   • jurisdiction-aware legal sources                        */
/*   • honest evidence-quality score (0–100, no floor)         */
/* ─────────────────────────────────────────────────────────── */

type ProviderExecStatus =
  | "verified"
  | "completed"
  | "completed_limited"
  | "fallback"
  | "unavailable"
  | "failed"
  | "not_invoked"
  | "pending"
  | "na";

type ProviderKind =
  | "AI Analysis Provider"
  | "OCR Provider"
  | "External Data Provider"
  | "Public Data Source"
  | "Internal Deterministic Engine"
  | "Customer-Supplied Evidence";

/** Customer-facing label for an internal provider classification. */
const PROVIDER_KIND_LABEL: Record<ProviderKind, string> = {
  "AI Analysis Provider": "AI-Assisted Analysis",
  "OCR Provider": "Document Extraction",
  "External Data Provider": "External Data Source",
  "Public Data Source": "Public Record",
  "Internal Deterministic Engine": "ClaimGuard System",
  "Customer-Supplied Evidence": "Customer Evidence",
};

function providerKindLabel(kind: ProviderKind): string {
  return PROVIDER_KIND_LABEL[kind] ?? kind;
}

interface ProviderTelemetry {
  jobType: string;
  displayName: string;
  purpose: string;
  kind: ProviderKind;
  status: ProviderExecStatus;
  confidence: number | null;
}

const JOB_META: Record<
  string,
  { purpose: string; inferKind: (provider: string | null) => ProviderKind }
> = {
  ocr: {
    purpose: "Optical Character Recognition",
    inferKind: (p) =>
      p && /google|azure|textract|document-ai|tesseract/i.test(p)
        ? "OCR Provider"
        : p && /stub|internal/i.test(p)
          ? "Internal Deterministic Engine"
          : "OCR Provider",
  },
  ai_findings: {
    purpose: "Claims Analysis & Findings",
    inferKind: (p) =>
      p && /stub|internal/i.test(p) ? "Internal Deterministic Engine" : "AI Analysis Provider",
  },
  legal_research: {
    purpose: "Statutory & Regulatory Retrieval",
    inferKind: (p) =>
      p && /stub|internal/i.test(p)
        ? "Internal Deterministic Engine"
        : p && /lovable|openai|anthropic|ai/i.test(p)
          ? "AI Analysis Provider"
          : "External Data Provider",
  },
  market_comparison: {
    purpose: "Market Comparison & Benchmarking",
    inferKind: (p) =>
      p && /stub|internal/i.test(p)
        ? "Internal Deterministic Engine"
        : p && /lovable|openai|anthropic|ai/i.test(p)
          ? "AI Analysis Provider"
          : "External Data Provider",
  },
  invoice_normalization: {
    purpose: "Line-Item Normalization",
    inferKind: (p) =>
      p && /stub|internal/i.test(p) ? "Internal Deterministic Engine" : "AI Analysis Provider",
  },
  negotiation_strategy: {
    purpose: "Negotiation Strategy Assembly",
    inferKind: (p) =>
      p && /stub|internal/i.test(p) ? "Internal Deterministic Engine" : "AI Analysis Provider",
  },
  notification: {
    purpose: "Notification Dispatch",
    inferKind: () => "Internal Deterministic Engine",
  },
  report_generation: {
    purpose: "Report Assembly",
    inferKind: () => "Internal Deterministic Engine",
  },
};

function prettifyProvider(id: string | null | undefined, jobType: string): string {
  void jobType;
  if (!id) return "Internal Deterministic Engine";
  const s = id.trim();
  if (/google-document-ai/i.test(s)) return "Google Document AI";
  if (/^stub-/i.test(s) || /^internal/i.test(s)) return "Internal Deterministic Engine";
  if (/^lovable-ai-findings/i.test(s)) return "ClaimGuard AI Analysis Engine";
  if (/^lovable-legal/i.test(s)) return "ClaimGuard Legal Intelligence Engine";
  if (/^lovable-market/i.test(s)) return "ClaimGuard Market Intelligence Engine";
  if (/^lovable-report/i.test(s)) return "ClaimGuard Report Assembly Engine";
  if (/^lovable-negotiation/i.test(s)) return "ClaimGuard AI Analysis Engine";
  if (/^lovable-/i.test(s)) return "ClaimGuard AI Analysis Engine";
  // Fall back to a classification, never a fabricated brand name.
  return s
    .replace(/^\w/, (c) => c.toUpperCase())
    .replace(/[-_]+/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

function jobExecStatus(status: string | null | undefined): ProviderExecStatus {
  const v = (status ?? "").toLowerCase();
  if (v === "completed" || v === "success" || v === "ok") return "completed";
  if (v === "verified") return "verified";
  if (v === "failed" || v === "error") return "failed";
  if (v === "pending" || v === "queued" || v === "running") return "pending";
  if (v === "unavailable") return "unavailable";
  if (v === "fallback") return "fallback";
  return "completed";
}

function statusChipMeta(s: ProviderExecStatus): { label: string; cls: string } {
  switch (s) {
    case "verified":
      return { label: "Verified", cls: "border-savings/40 bg-savings-soft text-savings" };
    case "completed":
      return { label: "Completed", cls: "border-savings/40 bg-savings-soft text-savings" };
    case "completed_limited":
      return { label: "Completed · Limited Data", cls: "border-warn/40 bg-warn/10 text-warn" };
    case "fallback":
      return { label: "Fallback Used", cls: "border-warn/40 bg-warn/10 text-warn" };
    case "unavailable":
      return {
        label: "Provider Unavailable",
        cls: "border-destructive/40 bg-destructive/10 text-destructive",
      };
    case "failed":
      return { label: "Failed", cls: "border-destructive/40 bg-destructive/10 text-destructive" };
    case "not_invoked":
      return { label: "Not Invoked", cls: "border-border bg-muted text-muted-foreground" };
    case "pending":
      return { label: "Pending", cls: "border-border bg-secondary/60 text-muted-foreground" };
    case "na":
      return {
        label: "Not Applicable",
        cls: "border-border bg-secondary/60 text-muted-foreground",
      };
  }
}

function SupportingEvidenceAndReferences({
  counts,
  files,
  hasReal,
  bundle,
  vendor,
  r,
}: {
  counts: EvidenceCounts;
  files: ReviewFile[] | null;
  hasReal: boolean;
  bundle: FullWorkspaceBundle;
  vendor: string;
  r: ReturnType<typeof buildReport>;
}) {
  void counts;
  void vendor;
  const identity = usePdfPackageIdentity();
  const isCompletePackage = identity.key === "complete";
  const laws = hasReal
    ? bundle.legalResearch.filter((l) => !isRegulatoryAuthority(l))
    : [];
  const regs = hasReal
    ? bundle.legalResearch.filter((l) => isRegulatoryAuthority(l))
    : [];
  const marketRows = hasReal ? bundle.marketComparisons : [];
  const fileList = files ?? [];
  const findingItems = hasReal ? (bundle.findingItems ?? []) : [];
  const jobs = hasReal ? (bundle.jobs ?? []) : [];
  const review = bundle.review as
    | (typeof bundle.review & {
        vendor_snapshot_at?: string | null;
        sos_snapshot_at?: string | null;
        bbb_snapshot_at?: string | null;
        licensing_snapshot_at?: string | null;
        state_licensing_snapshot_at?: string | null;
        notes?: string | null;
        zip?: string | null;
      })
    | null;

  /* ── Jurisdiction (normalized) ───────────────────────────── */
  const jurisdictionState = (() => {
    const raw =
      (bundle.invoiceData?.extracted as { state?: string } | undefined)?.state ?? r.state ?? "";
    return String(raw ?? "")
      .trim()
      .toUpperCase();
  })();
  const isTexas = jurisdictionState === "TX" || jurisdictionState === "TEXAS";

  /* ── Findings classification (H1, H6) ────────────────────── */
  const infoFindings = findingItems.filter((f) => (f.severity ?? "").toLowerCase() === "info");
  const verifiedFindingItems = findingItems.filter((f) => {
    const sev = (f.severity ?? "").toLowerCase();
    if (sev === "info" || sev === "") return false;
    const hasEvidence = Array.isArray(f.evidence) && (f.evidence as unknown[]).length > 0;
    const hasDescription = !!(f.description && f.description.trim().length > 5);
    return hasEvidence || hasDescription;
  });
  const unsupportedFindingItems = findingItems.filter((f) => {
    const sev = (f.severity ?? "").toLowerCase();
    if (sev === "info" || sev === "") return false;
    const hasEvidence = Array.isArray(f.evidence) && (f.evidence as unknown[]).length > 0;
    return !hasEvidence;
  });

  /* ── Provider execution telemetry (H3, H4, M6) ───────────── */
  const jobByType = new Map<string, (typeof jobs)[number]>();
  for (const j of jobs) jobByType.set(j.job_type, j);
  const knownJobTypes = Object.keys(JOB_META);
  const providers: ProviderTelemetry[] = knownJobTypes.map((jt) => {
    const job = jobByType.get(jt);
    const meta = JOB_META[jt];
    if (!job) {
      return {
        jobType: jt,
        displayName: "Not invoked",
        purpose: meta.purpose,
        kind: meta.inferKind(null),
        status: "not_invoked",
        confidence: null,
      };
    }
    return {
      jobType: jt,
      displayName: prettifyProvider(job.provider, jt),
      purpose: meta.purpose,
      kind: meta.inferKind(job.provider),
      status: jobExecStatus(job.status),
      confidence: null,
    };
  });

  // Snapshot-backed public data providers (SoS, BBB, Licensing).
  type SnapshotKey =
    | "vendor_snapshot_at"
    | "sos_snapshot_at"
    | "bbb_snapshot_at"
    | "licensing_snapshot_at"
    | "state_licensing_snapshot_at";
  const snap = (k: SnapshotKey) => (review?.[k] ? "completed" : "not_invoked");
  const snapshotProviders: ProviderTelemetry[] = [
    {
      jobType: "vendor_intelligence",
      displayName: review?.vendor_snapshot_at ? "Google Places" : "Not invoked",
      purpose: "Vendor Business Intelligence",
      kind: "External Data Provider",
      status: snap("vendor_snapshot_at"),
      confidence: null,
    },
    {
      jobType: "sos_intel",
      displayName: review?.sos_snapshot_at ? "Secretary of State Records" : "Not invoked",
      purpose: "Business Registration Verification",
      kind: "Public Data Source",
      status: snap("sos_snapshot_at"),
      confidence: null,
    },
    {
      jobType: "bbb_intel",
      displayName: review?.bbb_snapshot_at ? "Better Business Bureau" : "Not invoked",
      purpose: "Consumer Complaint History",
      kind: "Public Data Source",
      status: snap("bbb_snapshot_at"),
      confidence: null,
    },
    {
      jobType: "licensing_intel",
      displayName: review?.licensing_snapshot_at ? "FMCSA Licensing" : "Not invoked",
      purpose: "Federal Carrier Licensing",
      kind: "Public Data Source",
      status: snap("licensing_snapshot_at"),
      confidence: null,
    },
    {
      jobType: "state_licensing_intel",
      displayName: review?.state_licensing_snapshot_at
        ? "State Licensing Authority"
        : "Not invoked",
      purpose: "State Occupational Licensing",
      kind: "Public Data Source",
      status: snap("state_licensing_snapshot_at"),
      confidence: null,
    },
  ];
  const allProviders = [...providers, ...snapshotProviders];
  const externallyInvokedAI = allProviders.filter(
    (p) =>
      p.kind === "AI Analysis Provider" && (p.status === "completed" || p.status === "verified"),
  );
  const aiProvidersUsed = externallyInvokedAI.length;

  /* ── Availability signals (bundle-driven, honest) ────────── */
  const has = {
    invoice: hasReal && !!bundle.invoiceData,
    charges: (bundle.invoiceData?.line_items?.length ?? 0) > 0,
    market: marketRows.length > 0,
    laws: laws.length > 0,
    regs: regs.length > 0,
    vendor: !!review?.vendor_snapshot_at,
    timeline: (bundle.timeline?.length ?? 0) > 0,
    aiVerified: verifiedFindingItems.length > 0,
    aiAny: findingItems.length > 0,
    files: fileList.length > 0,
    jurisdiction: !!jurisdictionState,
  };

  /* ── Dedupe helpers (M5) ─────────────────────────────────── */
  const uniqueBy = <T,>(arr: T[], key: (v: T) => string) => {
    const seen = new Set<string>();
    const out: T[] = [];
    for (const v of arr) {
      const k = key(v);
      if (k && !seen.has(k)) {
        seen.add(k);
        out.push(v);
      }
    }
    return out;
  };
  const uniqueLegal = uniqueBy(
    [...laws, ...regs],
    (l) =>
      (l.citation ?? l.statute ?? l.title ?? l.id ?? "").trim().toLowerCase() +
      "::" +
      (l.source_url ?? "").trim().toLowerCase(),
  );
  const uniqueMarket = uniqueBy(
    marketRows,
    (m) => `${m.service_type ?? ""}::${m.zip ?? m.zip_code ?? ""}::${m.state ?? ""}`,
  );
  const uniqueFiles = uniqueBy(fileList, (f) => f.file_hash ?? f.id);

  /* ── Reliability counters (H1) ───────────────────────────── */
  // Primary evidence sources: unique customer-supplied primary records
  // (invoice, customer files) + provider results directly consumed.
  const primarySources = [has.invoice ? 1 : 0, uniqueFiles.length].reduce((a, b) => a + b, 0);

  // Secondary references: deduped legal citations + market comparables +
  // vendor/business snapshots that were actually captured.
  const secondaryReferences =
    uniqueLegal.length +
    uniqueMarket.length +
    (review?.vendor_snapshot_at ? 1 : 0) +
    (review?.sos_snapshot_at ? 1 : 0) +
    (review?.bbb_snapshot_at ? 1 : 0) +
    (review?.licensing_snapshot_at ? 1 : 0) +
    (review?.state_licensing_snapshot_at ? 1 : 0);

  // Public references: legal + public snapshots only.
  const publicReferences =
    uniqueLegal.length +
    (review?.sos_snapshot_at ? 1 : 0) +
    (review?.bbb_snapshot_at ? 1 : 0) +
    (review?.licensing_snapshot_at ? 1 : 0) +
    (review?.state_licensing_snapshot_at ? 1 : 0);

  // Missing evidence: only items actually required by this case.
  const missingList: string[] = [];
  if (!has.invoice) missingList.push("Original invoice");
  else if (!bundle.invoiceData?.extracted) missingList.push("Structured invoice extraction");
  if (has.invoice && !bundle.invoiceData?.extracted) missingList.push("Extracted invoice metadata");
  if (has.invoice) {
    const ex = bundle.invoiceData?.extracted as
      | { vendor?: string; invoice_date?: string; state?: string }
      | undefined;
    if (!ex?.vendor) missingList.push("Extracted vendor name");
    if (!ex?.invoice_date) missingList.push("Invoice date");
    if (!ex?.state && !jurisdictionState) missingList.push("Jurisdiction (state)");
  }
  // Only count market/legal as missing when a finding actually depends on it.
  const needsMarket = verifiedFindingItems.some((f) =>
    /market|rate|overcharge|excess|above.market/i.test(
      `${f.category ?? ""} ${f.description ?? ""} ${f.title}`,
    ),
  );
  if (needsMarket && !has.market) missingList.push("Market benchmark data");
  const needsLegal = verifiedFindingItems.some((f) =>
    /statut|regulat|law|code/i.test(`${f.category ?? ""} ${f.description ?? ""} ${f.title}`),
  );
  if (needsLegal && !has.laws && !has.regs) missingList.push("Supporting legal authority");
  const missingEvidence = missingList.length;

  // Manual assumptions: explicitly recorded specialist notes only.
  const manualAssumptions = review?.notes && review.notes.trim().length > 0 ? 1 : 0;

  /* ── Overall confidence (H2, H8) ─────────────────────────── */
  // Only display when it actually exists. `r.confidence` is 0 when the
  // findings provider ran without a confidence signal — treat 0 as "not
  // calculated" rather than silently downgrading the report.
  const rawConfidence =
    typeof bundle.findings?.confidence === "number" ? bundle.findings.confidence : null;
  const overallConfidence =
    rawConfidence != null && rawConfidence > 0 ? Math.round(rawConfidence * 100) : null;

  /* ── Evidence Quality Score (M4) ─────────────────────────── */
  // Documented deterministic formula, 0–100, no floor. Points awarded only
  // for evidence actually present on this review.
  //   Invoice + extraction ......... 20
  //   Line items ................... 10
  //   Verified findings ............ 15
  //   Market coverage .............. 10
  //   Legal coverage ............... 10
  //   Timeline events .............. 5
  //   Vendor snapshot .............. 5
  //   Business (SoS/BBB) ........... 5
  //   Licensing snapshot ........... 5
  //   Customer files ............... 5
  //   Provider job completion ratio  10 (scaled by completed / expected)
  // Penalties: -5 per missing required item, -10 for zero verified findings
  // when the case has any findings surface.
  let score = 0;
  if (has.invoice) score += 20;
  if (has.charges) score += 10;
  if (has.aiVerified) score += 15;
  if (has.market) score += 10;
  if (has.laws || has.regs) score += 10;
  if (has.timeline) score += 5;
  if (review?.vendor_snapshot_at) score += 5;
  if (review?.sos_snapshot_at || review?.bbb_snapshot_at) score += 5;
  if (review?.licensing_snapshot_at || review?.state_licensing_snapshot_at) score += 5;
  if (uniqueFiles.length > 0) score += 5;
  const invokedJobs = allProviders.filter(
    (p) => p.status === "completed" || p.status === "verified",
  ).length;
  const expectedJobs = allProviders.length;
  if (expectedJobs > 0) score += Math.round((invokedJobs / expectedJobs) * 10);
  score -= Math.min(missingList.length, 5) * 5;
  if (findingItems.length > 0 && !has.aiVerified) score -= 10;
  const evidenceQuality = Math.max(0, Math.min(100, score));
  const showEvidenceQuality = hasReal && (has.invoice || has.aiAny || uniqueFiles.length > 0);

  /* ── Carrier guidance availability ───────────────────────── */
  // Verified carrier records only — never inferred from legal/market layers.
  const hasCarrierGuidance = findingItems.some((f) => {
    const ev = (f as { evidence?: Record<string, unknown> | null }).evidence ?? null;
    return !!ev && hasCarrierEvidence(ev);
  });

  /* ── Chapter Validation Matrix (M1, M2) ──────────────────── */
  type ChapterStatus =
    | "validated"
    | "partial"
    | "limited"
    | "pending"
    | "not_available"
    | "not_applicable"
    | "fallback";
  interface ChapterRow {
    num: string;
    name: string;
    icon: LucideIcon;
    status: ChapterStatus;
    evidence: string[];
    systems: string[];
    sources: string[];
    confidence: number | null;
  }
  const chapterStatus = (
    required: boolean[],
    optional: boolean[] = [],
    fallback = false,
  ): ChapterStatus => {
    const req = required.filter(Boolean).length;
    const reqTotal = required.length;
    if (fallback) return "fallback";
    if (reqTotal === 0) return "not_applicable";
    if (req === 0) return "not_available";
    if (req < reqTotal) return "partial";
    if (optional.length > 0 && optional.filter(Boolean).length === 0) return "limited";
    return "validated";
  };

  // Per-chapter validation payload keyed by registry internalId.
  // Chapter identity (number, title, order, count) comes from CHAPTERS —
  // the same registry that drives the sidebar, TOC, headers, and PDF.
  // Do NOT hard-code chapter numbers or titles here.
  type ChapterValidation = {
    status: ChapterStatus;
    evidence: string[];
    systems: string[];
    sources: string[];
    confidence: number | null;
  };

  const validationByInternalId: Record<string, ChapterValidation> = {
    "executive-decision": {
      status: chapterStatus([has.invoice], [has.aiVerified]),
      evidence: [has.invoice ? "Invoice OCR" : "", has.aiAny ? "Findings Digest" : ""].filter(
        Boolean,
      ),
      systems: providers
        .filter(
          (p) => (p.jobType === "ocr" || p.jobType === "ai_findings") && p.status !== "not_invoked",
        )
        .map((p) => p.displayName),
      sources: has.invoice ? ["Original Invoice"] : [],
      confidence: overallConfidence,
    },
    "case-brief": {
      status: chapterStatus([has.invoice], [has.charges]),
      evidence: [
        has.invoice ? "Invoice OCR" : "",
        has.charges ? "Line-Item Normalization" : "",
      ].filter(Boolean),
      systems: providers
        .filter(
          (p) =>
            (p.jobType === "ocr" || p.jobType === "invoice_normalization") &&
            p.status !== "not_invoked",
        )
        .map((p) => p.displayName),
      sources: has.invoice ? ["Original Invoice"] : [],
      confidence: null,
    },
    market: {
      status: chapterStatus([has.market], [has.charges, has.aiVerified]),
      evidence: has.market ? ["ZIP Benchmark", "Regional Rate Tables"] : [],
      systems: providers
        .filter((p) => p.jobType === "market_comparison" && p.status !== "not_invoked")
        .map((p) => p.displayName),
      sources: has.market ? [`${uniqueMarket.length} comparable rate(s)`] : [],
      confidence: null,
    },
    "state-law": {
      status: chapterStatus([has.laws], [has.jurisdiction]),
      evidence: has.laws ? ["State Statutes"] : [],
      systems: providers
        .filter((p) => p.jobType === "legal_research" && p.status !== "not_invoked")
        .map((p) => p.displayName),
      sources: [
        has.laws ? `${laws.length} statute(s)` : "",
        has.jurisdiction ? `Jurisdiction: ${jurisdictionState}` : "",
      ].filter(Boolean),
      confidence: null,
    },
    regulation: {
      status: chapterStatus([has.regs], [has.jurisdiction]),
      evidence: has.regs ? ["Administrative Rules"] : [],
      systems: providers
        .filter((p) => p.jobType === "legal_research" && p.status !== "not_invoked")
        .map((p) => p.displayName),
      sources: has.regs ? [`${regs.length} regulation(s)`] : [],
      confidence: null,
    },
    "ownership-lien": {
      status: chapterStatus([has.invoice, has.laws || has.regs], [has.timeline]),
      evidence: [
        has.laws || has.regs ? "Statutory Notice Rules" : "",
        has.invoice ? "Invoice Dates" : "",
      ].filter(Boolean),
      systems: providers
        .filter((p) => p.jobType === "legal_research" && p.status !== "not_invoked")
        .map((p) => p.displayName),
      sources: has.jurisdiction ? [`Jurisdiction: ${jurisdictionState}`] : [],
      confidence: null,
    },
    // Carrier guidance is only validated by verified carrier records. Statutes,
    // regulations, and market data are never substituted for carrier policy
    // (see Chapter 06 non-substitution declaration).
    carrier: {
      status: chapterStatus([hasCarrierGuidance]),
      evidence: hasCarrierGuidance ? ["Carrier Guideline Records"] : [],
      systems: hasCarrierGuidance
        ? providers
            .filter((p) => p.jobType === "ai_findings" && p.status !== "not_invoked")
            .map((p) => p.displayName)
        : [],
      sources: [],
      confidence: null,
    },
    vendor: {
      status: chapterStatus(
        [!!review?.vendor_snapshot_at],
        [!!review?.sos_snapshot_at, !!review?.bbb_snapshot_at],
      ),
      evidence: [
        review?.vendor_snapshot_at ? "Vendor Profile" : "",
        review?.sos_snapshot_at ? "Business Registration" : "",
        review?.bbb_snapshot_at ? "Complaint History" : "",
      ].filter(Boolean),
      systems: snapshotProviders.filter((p) => p.status === "completed").map((p) => p.displayName),
      sources: [
        review?.vendor_snapshot_at ? "Google Places" : "",
        review?.sos_snapshot_at ? "Secretary of State" : "",
        review?.bbb_snapshot_at ? "BBB" : "",
      ].filter(Boolean),
      confidence: null,
    },
    timeline: {
      status: chapterStatus([has.timeline], [has.invoice]),
      evidence: has.timeline
        ? [
            `${bundle.timeline?.length ?? 0} recorded event(s)`,
            has.invoice ? "Invoice Dates" : "",
          ].filter(Boolean)
        : [],
      systems: ["Internal Timeline Engine"],
      sources: has.jurisdiction ? [`Jurisdictional rules (${jurisdictionState})`] : [],
      confidence: null,
    },
    "supporting-evidence": {
      status: chapterStatus([uniqueFiles.length > 0 || has.invoice], [has.aiAny]),
      evidence: [
        uniqueFiles.length > 0 ? `${uniqueFiles.length} attached file(s)` : "",
        has.invoice ? "Original Invoice" : "",
      ].filter(Boolean),
      systems: Array.from(
        new Set(
          providers.filter((p) => p.status !== "not_invoked").map((p) => p.displayName),
        ),
      ),
      sources: has.invoice ? ["Original Invoice"] : [],
      confidence: null,
    },
    "negotiation-strategy": {
      status: chapterStatus([has.aiVerified], [has.market, has.laws || has.regs]),
      evidence: has.aiVerified ? ["Verified Findings", "Impact Ranking"] : [],
      systems: providers
        .filter((p) => p.jobType === "negotiation_strategy" && p.status !== "not_invoked")
        .map((p) => p.displayName),
      sources: [],
      confidence: null,
    },
    conclusion: {
      status: chapterStatus(
        [has.aiVerified],
        [typeof bundle.findings?.potential_savings === "number"],
      ),
      evidence: has.aiVerified ? ["Verified Findings", "Professional Review"] : [],
      systems: providers
        .filter((p) => p.jobType === "ai_findings" && p.status !== "not_invoked")
        .map((p) => p.displayName),
      sources: [],
      confidence: overallConfidence,
    },
  };

  // Chapter identity is derived from the registry (single source of truth).
  // Number, title, and order stay synchronized with the report automatically;
  // if a chapter is renamed, reordered, or inserted, this matrix updates too.
  // Rows are additionally scoped to the chapters actually included in this
  // package export, so the matrix can never validate a chapter the reader
  // does not have.
  const matrixAllowedAnchors = chaptersForPackage(identity.key);
  const chapterRows: ChapterRow[] = CHAPTERS.filter(
    (c) => !matrixAllowedAnchors || matrixAllowedAnchors.includes(c.anchorId),
  ).map((c) => {
    const v = validationByInternalId[c.internalId] ?? {

      status: "not_available" as ChapterStatus,
      evidence: [],
      systems: [],
      sources: [],
      confidence: null,
    };
    return {
      num: c.paddedDisplayNumber,
      name: c.title,
      icon: c.icon ?? FileText,
      status: v.status,
      evidence: v.evidence,
      systems: v.systems,
      sources: v.sources,
      confidence: v.confidence,
    };
  });

  /* ── Evidence Sources (H4, M3, M5) ───────────────────────── */
  type SourceStatus = "verified" | "unavailable" | "na" | "not-provided";
  type SourceRow = { label: string; status: SourceStatus };
  type SourceGroup = {
    title: string;
    icon: LucideIcon;
    intro: string;
    rows: SourceRow[];
  };
  const statusOf = (present: boolean, notProvidedIfMissing = false): SourceStatus =>
    present ? "verified" : notProvidedIfMissing ? "not-provided" : "unavailable";

  const isImage = (f: ReviewFile) => (f.mime_type ?? "").toLowerCase().startsWith("image/");
  const invoiceFile =
    fileList.find((f) => (f.kind ?? "").toLowerCase() === "invoice") ??
    fileList.find((f) => /invoice|bill|receipt/i.test(f.file_name ?? ""));
  const photoCount = fileList.filter(
    (f) => (f.kind ?? "").toLowerCase() === "photo" || isImage(f),
  ).length;
  // Supporting Documents are counted from their own classification, never
  // from "everything that is not the invoice".
  const attachmentCount =
    selectSupportingDocuments(fileList).length ||
    fileList.filter((f) => f.id !== invoiceFile?.id && !isImage(f)).length;

  // Jurisdiction-aware statute rows: only surface Texas-specific codes for
  // Texas cases. Other jurisdictions get a "State-specific code (per
  // jurisdiction)" row and Texas-only codes render Not Applicable.
  const legalRows: SourceRow[] = [
    { label: "Applicable State Statutes", status: statusOf(has.laws) },
    { label: "Administrative Regulations", status: statusOf(has.regs) },
  ];
  if (isTexas) {
    const codeMentioned = (needle: RegExp) =>
      uniqueLegal.some((l) =>
        needle.test(`${l.citation ?? ""} ${l.statute ?? ""} ${l.title ?? ""}`),
      );
    legalRows.push(
      {
        label: "Property Code (Texas)",
        status: statusOf(codeMentioned(/\bProp\b|Property Code/i)),
      },
      {
        label: "Transportation Code (Texas)",
        status: statusOf(codeMentioned(/\bTransp\b|Transportation Code/i)),
      },
      {
        label: "Occupations Code (Texas)",
        status: statusOf(codeMentioned(/\bOccup\b|Occupations Code/i)),
      },
    );
  } else if (has.jurisdiction) {
    legalRows.push({
      label: `State-specific code (${jurisdictionState})`,
      status: statusOf(has.laws || has.regs),
    });
  }
  legalRows.push({
    label: "Secretary of State Public Records",
    status: statusOf(!!review?.sos_snapshot_at),
  });

  const customerNotesPresent = !!(review?.notes && review.notes.trim().length > 0);

  const sourceGroups: SourceGroup[] = [
    {
      title: "Customer-Submitted Evidence",
      icon: Upload,
      intro: "Materials the customer provided at intake.",
      rows: [
        { label: "Original Invoice", status: statusOf(!!invoiceFile || has.invoice) },
        { label: "Vehicle & Scene Photos", status: statusOf(photoCount > 0, true) },
        { label: "Supporting Documents", status: statusOf(attachmentCount > 0, true) },
        { label: "Customer Notes", status: statusOf(customerNotesPresent, true) },
      ],
    },
    {
      title: "AI Analysis Systems",
      icon: Sparkles,
      intro: "Analysis systems that contributed to this review.",

      rows: providers.map((p) => ({
        label: `${p.displayName} — ${p.purpose}`,
        status:
          p.status === "completed" || p.status === "verified"
            ? ("verified" as SourceStatus)
            : p.status === "not_invoked"
              ? ("na" as SourceStatus)
              : ("unavailable" as SourceStatus),
      })),
    },
    {
      title: "Legal Research Sources",
      icon: Landmark,
      intro: has.jurisdiction
        ? `Authoritative legal materials matched to ${jurisdictionState}.`
        : "Authoritative legal materials matched to this claim.",
      rows: legalRows,
    },
    {
      title: "Market Intelligence",
      icon: BarChart3,
      intro: "Local and regional pricing signals used for comparison.",
      rows: [
        { label: "ZIP Market Benchmarks", status: statusOf(has.market) },
        { label: "Regional Rate Coverage", status: statusOf(uniqueMarket.length > 1) },
        {
          label: "Vendor Intelligence Profile",
          status: statusOf(!!review?.vendor_snapshot_at),
        },
        {
          label: "Business Complaint History",
          status: statusOf(!!review?.bbb_snapshot_at),
        },
      ],
    },
    {
      title: "Methodology",
      icon: ClipboardCheck,
      intro:
        "Every charge is extracted from the source invoice, cross-checked against the AI Findings engine, benchmarked against local market data (when available), and validated against applicable statutes and regulations for the resolved jurisdiction. Confidence is calculated from the number of independent sources that agree on a given charge; observations without corroborating evidence are labeled Informational and are excluded from verified findings.",
      rows: [
        { label: "Multi-Source Cross-Check", status: has.invoice ? "verified" : "unavailable" },
        {
          label: "Jurisdictional Applicability Filter",
          status: has.jurisdiction ? "verified" : "unavailable",
        },
        {
          label: "Human Specialist Review",
          status: counts.humanReview > 0 ? "verified" : "na",
        },
      ],
    },
  ];

  /* ── Provider Verification Table — Production Architecture ───
     Customer-facing names only. Internal telemetry identifiers
     (lovable-*, JOB_META keys, model routes) are intentionally
     hidden. Only systems that materially contributed are shown. */
  const jobStatusFor = (jt: string): ProviderExecStatus => {
    const j = jobByType.get(jt);
    // No job record means the system never ran for this claim. Never imply
    // completion without telemetry.
    return j ? jobExecStatus(j.status) : "not_invoked";
  };
  const providerRows: ProviderTelemetry[] = [
    {
      jobType: "orchestration",
      displayName: "Review Coordination",
      purpose: "Managed the review workflow and quality checks.",
      kind: "Internal Deterministic Engine",
      status: "completed",
      confidence: null,
    },
    {
      jobType: "customer_evidence",
      displayName: "Customer-Submitted Evidence",
      purpose: "Invoice, photographs, documents, and notes you provided.",
      kind: "Customer-Supplied Evidence",
      status: "completed",
      confidence: null,
    },
    {
      jobType: "ocr",
      displayName: "Google Document AI",
      purpose: "Extracted invoice data, line items, dates, and totals.",
      kind: "OCR Provider",
      status: jobStatusFor("ocr"),
      confidence: null,
    },
    {
      jobType: "invoice_normalization",
      displayName: "Invoice Standardization",
      purpose: "Organized invoice charges into a comparable format.",
      kind: "Internal Deterministic Engine",
      status: jobStatusFor("invoice_normalization"),
      confidence: null,
    },
    {
      jobType: "market_comparison",
      displayName: "Market Intelligence",
      purpose: "Compared local benchmark pricing for each charge.",
      kind: "Internal Deterministic Engine",
      status: jobStatusFor("market_comparison"),
      confidence: null,
    },
    {
      jobType: "legal_research",
      displayName: "ClaimGuard Legal Intelligence",
      purpose: "Matched applicable statutes and regulations.",
      kind: "Internal Deterministic Engine",
      status: jobStatusFor("legal_research"),
      confidence: null,
    },
    {
      jobType: "ai_findings",
      displayName: "Assisted Findings Analysis",
      purpose: "Identified findings and supporting evidence for review.",
      kind: "AI Analysis Provider",
      status: jobStatusFor("ai_findings"),
      confidence: null,
    },
    {
      jobType: "report_generation",
      displayName: "Report Assembly",
      purpose: "Generated the final report package.",
      kind: "Internal Deterministic Engine",
      status: "completed",
      confidence: null,
    },
    {
      jobType: "notification",
      displayName: "Notifications",
      purpose: "Workflow and completion updates.",
      kind: "Internal Deterministic Engine",
      status: "completed",
      confidence: null,
    },
    ...(review?.vendor_snapshot_at
      ? [
          {
            jobType: "vendor_intelligence",
            displayName: "Google Places",
            purpose: "Verified vendor location and business profile.",
            kind: "External Data Provider" as ProviderKind,
            status: "completed" as ProviderExecStatus,
            confidence: null,
          },
        ]
      : []),
    ...(review?.sos_snapshot_at
      ? [
          {
            jobType: "sos_intel",
            displayName: "Secretary of State",
            purpose: "Business registration verification.",
            kind: "Public Data Source" as ProviderKind,
            status: "completed" as ProviderExecStatus,
            confidence: null,
          },
        ]
      : []),
    ...(review?.bbb_snapshot_at
      ? [
          {
            jobType: "bbb_intel",
            displayName: "Better Business Bureau",
            purpose: "Consumer reputation reference.",
            kind: "External Data Provider" as ProviderKind,
            status: "completed" as ProviderExecStatus,
            confidence: null,
          },
        ]
      : []),
    ...(review?.licensing_snapshot_at
      ? [
          {
            jobType: "licensing_intel",
            displayName: "FMCSA",
            purpose: "Federal operating authority.",
            kind: "Public Data Source" as ProviderKind,
            status: "completed" as ProviderExecStatus,
            confidence: null,
          },
        ]
      : []),
    ...(review?.state_licensing_snapshot_at
      ? [
          {
            jobType: "state_licensing_intel",
            displayName: "State Licensing",
            purpose: "License verification.",
            kind: "Public Data Source" as ProviderKind,
            status: "completed" as ProviderExecStatus,
            confidence: null,
          },
        ]
      : []),
  ];


  /* ── Finding Traceability Matrix (H5, H6) ────────────────── */
  interface TraceRow {
    key: string;
    finding: string;
    issue: string;
    severity: string;
    isInfo: boolean;
    evidence: string[];
    chapters: string[];
    confidence: number | null;
  }
  const traceabilityRows: TraceRow[] = findingItems.map((f, i) => {
    const sev = (f.severity ?? "").toLowerCase();
    const isInfo = sev === "info" || sev === "";
    const evidence: string[] = [];
    const pushEv = (s: string | null | undefined) => {
      const t = (s ?? "").trim();
      if (!t) return;
      if (!evidence.some((e) => e.toLowerCase() === t.toLowerCase())) evidence.push(t);
    };

    // 1. Explicit supporting_evidence entries (array shape from AI output).
    if (Array.isArray(f.evidence)) {
      for (const e of f.evidence as Array<{ label?: string; citation?: string }>) {
        if (!e) continue;
        if (e.citation && e.label) pushEv(`${e.label} — ${e.citation}`);
        else if (e.label) pushEv(e.label);
        else if (e.citation) pushEv(e.citation);
      }
    } else if (f.evidence && typeof f.evidence === "object") {
      // 2. Evidence object keys → human-readable evidence items (deterministic).
      const ev = f.evidence as Record<string, unknown>;
      const nonEmpty = (v: unknown) =>
        v != null && v !== "" && !(Array.isArray(v) && v.length === 0);
      const keyMap: Array<{ re: RegExp; label: string }> = [
        {
          re: /^(law|statute|statute_reference|law_reference|citation)$/i,
          label: "Applicable State Statute",
        },
        {
          re: /^(regulation|regulation_reference|cfr|administrative_rule|rule_reference)$/i,
          label: "Administrative Regulation",
        },
        {
          re: /^(carrier|carrier_guideline|insurer_guideline|insurance_guideline)$/i,
          label: "Carrier Guideline",
        },
        {
          re: /vendor_history|vendor_profile|vendor_records|public_record|licens|complaint|enforcement/i,
          label: "Vendor Records",
        },
        {
          re: /^(document|document_url|doc_url|attachment|file|file_url|receipt|invoice_image|contract|uploaded_document)$/i,
          label: "Supporting Document",
        },
        { re: /^(photo|photos|photo_url|image|images)$/i, label: "Customer Submitted Photo" },
        { re: /^(gps|distance|mileage|miles|coordinates)$/i, label: "GPS / Location Evidence" },
        {
          re: /^(timeline|timestamp|start_time|end_time|event_date|activity_log)$/i,
          label: "Timeline Analysis",
        },
        { re: /invoice_line|line_item|line_index/i, label: "Invoice Line Item" },
        { re: /market|benchmark|zip_rate|regional_rate/i, label: "Market Benchmark" },
      ];
      for (const [k, v] of Object.entries(ev)) {
        if (!nonEmpty(v)) continue;
        const hit = keyMap.find((m) => m.re.test(k));
        if (hit) pushEv(hit.label);
      }
    }

    // 3. Contextual evidence — only added when the underlying signal exists.
    // Chapter references come from the Master Chapter Registry so numbers and
    // titles can never drift from the report the reader is holding.
    const hay = `${f.category ?? ""} ${f.title} ${f.description ?? ""}`.toLowerCase();
    const chapterTag = (internalId: string) => {
      const c = getChapterByInternalId(internalId);
      return `${c.paddedDisplayNumber} ${c.title}`;
    };
    const chapters: string[] = [];
    if (/duplicate|charge|overcharge|line|invoice/i.test(hay)) {
      chapters.push(chapterTag("case-brief"));
      if (invoiceFile) pushEv("Original Invoice");
    }
    if (/market|rate|above.market|benchmark/i.test(hay) && has.market) {
      chapters.push(chapterTag("market"));
      pushEv("Market Benchmark");
      pushEv("ClaimGuard Market Intelligence Engine");
    }
    if (/statut|law|code/i.test(hay) && has.laws) {
      chapters.push(chapterTag("state-law"));
      pushEv("ClaimGuard Legal Intelligence Engine");
    }
    if (/regulat|rule/i.test(hay) && has.regs) {
      chapters.push(chapterTag("regulation"));
      pushEv("Administrative Regulation");
    }
    if (/lien|abandon|notice|ownership/i.test(hay)) chapters.push(chapterTag("ownership-lien"));
    if (/storage|day|duration|timeline/i.test(hay) && has.timeline) {
      chapters.push(chapterTag("timeline"));
      pushEv("Storage Timeline");
    }
    return {
      key: `${f.id ?? f.title}-${i}`,
      finding: f.title,
      issue: f.description ?? "",
      severity: sev || "info",
      isInfo,
      evidence,
      chapters: Array.from(new Set(chapters)),
      confidence: null,
    };
  });

  /* ── Submitted files (H7, L3, L4) ────────────────────────── */
  const fileLifecycleStatus = (f: ReviewFile): { label: string; cls: string } => {
    const kind = (f.kind ?? "").toLowerCase();
    if (kind === "invoice" && bundle.invoiceData) {
      const hasExtraction =
        bundle.invoiceData?.extracted && Object.keys(bundle.invoiceData.extracted).length > 0;
      return hasExtraction
        ? { label: "OCR Processed", cls: "border-savings/40 bg-savings-soft text-savings" }
        : { label: "OCR Partial", cls: "border-warn/40 bg-warn/10 text-warn" };
    }
    if (kind === "report") {
      return { label: "Generated", cls: "border-trust/40 bg-trust/10 text-trust" };
    }
    return { label: "Uploaded", cls: "border-border bg-secondary/60 text-muted-foreground" };
  };

  const isGps = (f: ReviewFile) => /(gps|location|geo|coord)/i.test(f.file_name ?? "");
  const isCorrespondence = (f: ReviewFile) =>
    /(email|letter|correspond|message|reply|note)/i.test(f.file_name ?? "");
  const originalInvoice = invoiceFile ?? null;
  const photos = fileList.filter(
    (f) => ((f.kind ?? "").toLowerCase() === "photo" || isImage(f)) && f.id !== originalInvoice?.id,
  );
  const gps = fileList.filter((f) => isGps(f) && f.id !== originalInvoice?.id);
  const correspondence = fileList.filter(
    (f) => isCorrespondence(f) && f.id !== originalInvoice?.id,
  );
  const claimed = new Set(
    [originalInvoice, ...photos, ...gps, ...correspondence]
      .filter((f): f is ReviewFile => !!f)
      .map((f) => f.id),
  );
  const additional = fileList.filter((f) => !claimed.has(f.id));

  const caseFileGroups: Array<{ title: string; items: ReviewFile[] }> = [
    {
      title: originalInvoice ? "Original Invoice" : "Original invoice not identified",
      items: originalInvoice ? [originalInvoice] : [],
    },
    { title: "Uploaded Photos", items: photos },
    { title: "GPS / Location Evidence", items: gps },
    { title: "Correspondence", items: correspondence },
    { title: "Additional Attachments", items: additional },
  ].filter((g) => g.items.length > 0);

  /* ── Reliability metric tiles (H1) — grouped ─────────────── */
  type Tile = { label: string; value: string | number; hint?: string };
  const metricGroups: Array<{ title: string; tiles: Tile[] }> = [
    {
      title: "Evidence Quality",
      tiles: [
        {
          label: "Overall Evidence Quality",
          value: showEvidenceQuality ? `${evidenceQuality}%` : "Not Calculated",
          hint: "Weighted 0–100 score from records, providers, and citations captured for this review.",
        },
        {
          label: ANALYSIS_CONFIDENCE_LABEL,
          value: overallConfidence != null ? `${overallConfidence}%` : CONF_NOT_CALCULATED,
          hint: "Findings-provider confidence signal. Only shown when reported by the analysis engine.",
        },
      ],
    },
    {
      title: "Validation",
      tiles: [
        {
          label: "Verified Findings",
          value: verifiedFindingItems.length,
          hint: "Findings backed by captured evidence.",
        },
        {
          label: "Informational Observations",
          value: infoFindings.length,
          hint: "Notes only — not counted as verified findings.",
        },
        {
          label: "Unsupported Findings",
          value: unsupportedFindingItems.length,
          hint: "Findings lacking a corroborating evidence record.",
        },
      ],
    },
    {
      title: "Evidence Sources",
      tiles: [
        {
          label: "Primary Evidence Sources",
          value: primarySources,
          hint: "Original invoice and customer-submitted files.",
        },
        {
          label: "Secondary References",
          value: secondaryReferences,
          hint: "Legal citations, market comparables, and business snapshots.",
        },
        {
          label: "Public References",
          value: publicReferences,
          hint: "Publicly available legal and registry records.",
        },
        {
          label: "AI Providers Used",
          value: aiProvidersUsed,
          hint: "External AI providers that completed at least one job.",
        },
      ],
    },
    {
      title: "Exceptions",
      tiles: [
        {
          label: "Missing Evidence",
          value: missingEvidence,
          hint: "Records this review expected but did not receive.",
        },
        {
          label: "Manual Assumptions",
          value: manualAssumptions,
          hint: "Specialist notes recorded outside the automated pipeline.",
        },
      ],
    },
  ];

  /* ── Chapter status chip helper ──────────────────────────── */
  const chapterChip = (s: ChapterStatus) => {
    const map: Record<ChapterStatus, { label: string; cls: string; icon: React.ReactNode }> = {
      validated: {
        label: "Validated",
        cls: "border-savings/40 bg-savings-soft text-savings",
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      partial: {
        label: "Partially Validated",
        cls: "border-warn/40 bg-warn/10 text-warn",
        icon: <AlertTriangle className="h-3 w-3" />,
      },
      limited: {
        label: "Limited Evidence",
        cls: "border-warn/40 bg-warn/10 text-warn",
        icon: <Info className="h-3 w-3" />,
      },
      pending: {
        label: absenceLabel("VERIFICATION_PENDING"),
        cls: "border-border bg-secondary/60 text-muted-foreground",
        icon: <Info className="h-3 w-3" />,
      },
      not_available: {
        label: absenceLabel("COVERAGE_BOUNDARY"),
        cls: "border-border bg-muted text-muted-foreground",
        icon: <Info className="h-3 w-3" />,
      },
      not_applicable: {
        label: "Not Applicable",
        cls: "border-border bg-secondary/60 text-muted-foreground",
        icon: <Info className="h-3 w-3" />,
      },
      fallback: {
        label: "Fallback Used",
        cls: "border-warn/40 bg-warn/10 text-warn",
        icon: <AlertTriangle className="h-3 w-3" />,
      },
    };
    const c = map[s];
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.cls}`}
      >
        {c.icon}
        {c.label}
      </span>
    );
  };

  const sourceChip = (s: SourceStatus) => {
    const map: Record<SourceStatus, { label: string; cls: string; icon: React.ReactNode }> = {
      verified: {
        label: "Verified",
        cls: "border-savings/40 bg-savings-soft text-savings",
        icon: <CheckCircle2 className="h-3 w-3" />,
      },
      unavailable: {
        label: "Unavailable",
        cls: "border-border bg-muted text-muted-foreground",
        icon: <Info className="h-3 w-3" />,
      },
      na: {
        label: "Not Applicable",
        cls: "border-border bg-secondary/60 text-muted-foreground",
        icon: <Info className="h-3 w-3" />,
      },
      "not-provided": {
        label: "Not Provided",
        cls: "border-warn/30 bg-warn/10 text-warn",
        icon: <AlertTriangle className="h-3 w-3" />,
      },
    };
    const c = map[s];
    return (
      <span
        className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${c.cls}`}
      >
        {c.icon}
        {c.label}
      </span>
    );
  };

  /* ── Evidence Grade derived from chapter status (M2 polish) */
  const chapterGrade = (
    s: ChapterStatus,
    confidence: number | null,
  ): { label: string; cls: string } | null => {
    switch (s) {
      case "validated":
        if (confidence != null && confidence >= 90)
          return { label: "Grade A+", cls: "border-savings/40 bg-savings-soft text-savings" };
        return { label: "Grade A", cls: "border-savings/40 bg-savings-soft text-savings" };
      case "partial":
        return { label: "Grade B", cls: "border-warn/40 bg-warn/10 text-warn" };
      case "limited":
        return { label: "Grade B−", cls: "border-warn/40 bg-warn/10 text-warn" };
      case "fallback":
        return { label: "Grade C", cls: "border-warn/40 bg-warn/10 text-warn" };
      case "not_available":
        return null;
      case "not_applicable":
        return null;
      case "pending":
        return null;
    }
  };

  return (
    <Section
      bare
      number="01"
      eyebrow="Appendix"
      title="Supporting Evidence & References"
      intro="Report conclusions are mapped to the supporting records, provider results, and references available for this review. Every row below reflects real execution telemetry — nothing here implies verification, confidence, or provider usage unless the underlying record supports it."
    >
      {/* ── Report Reliability Summary ─────────────────────── */}
      <div
        className="rounded-3xl border border-navy/15 bg-gradient-to-br from-navy to-navy/90 p-8 text-navy-foreground shadow-elevated md:p-10 print-avoid-break"
        data-pdf-include="true"
      >
        <div className="flex flex-wrap items-center gap-3">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-navy-foreground/20 bg-navy-foreground/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
            <ShieldCheck className="h-3.5 w-3.5" />
            Report Reliability Summary
          </span>
          <span className="text-[11px] uppercase tracking-wider text-navy-foreground/60">
            Evidence-backed · Cross-checked · Limited by source availability
          </span>
        </div>
        <h3 className="mt-4 font-display text-3xl leading-tight md:text-4xl">
          Every conclusion is mapped to the records, provider results, and citations captured for
          this review.
        </h3>
        <div className="mt-8 space-y-6">
          {isCompletePackage ? (
            metricGroups.map((group) => (
              <div key={group.title} className="print-avoid-break">
                <div className="mb-2 flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-navy-foreground/60">
                  <span className="h-px w-6 bg-navy-foreground/25" />
                  {group.title}
                </div>
                <div
                  className={`grid gap-3 ${
                    group.tiles.length <= 2
                      ? "grid-cols-1 sm:grid-cols-2"
                      : group.tiles.length === 3
                        ? "grid-cols-1 sm:grid-cols-3"
                        : "grid-cols-2 md:grid-cols-4"
                  }`}
                >
                  {group.tiles.map((m) => (
                    <div
                      key={m.label}
                      className="rounded-2xl border border-navy-foreground/15 bg-navy-foreground/5 p-4"
                      title={m.hint}
                    >
                      <div className="font-display text-2xl tabular-nums md:text-3xl">{m.value}</div>
                      <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-navy-foreground/70">
                        {m.label}
                      </div>
                      {m.hint && (
                        <div className="mt-1.5 text-[11px] leading-snug text-navy-foreground/55">
                          {m.hint}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))
          ) : (
            <div className="grid gap-3 grid-cols-2 md:grid-cols-5 print-avoid-break">
              {[
                {
                  label: ANALYSIS_CONFIDENCE_LABEL,
                  value: overallConfidence != null ? `${overallConfidence}%` : CONF_NOT_CALCULATED,
                },
                { label: "Chapters Reviewed", value: chapterRows.length },
                { label: "Findings Validated", value: verifiedFindingItems.length },
                {
                  label: "Sources Cited",
                  value: primarySources + secondaryReferences,
                },
                {
                  label: "Evidence Verified",
                  value: sourceGroups.reduce(
                    (n, g) => n + g.rows.filter((r) => r.status === "verified").length,
                    0,
                  ),
                },
              ].map((m) => (
                <div
                  key={m.label}
                  className="rounded-2xl border border-navy-foreground/15 bg-navy-foreground/5 p-4"
                >
                  <div className="font-display text-2xl tabular-nums md:text-3xl">{m.value}</div>
                  <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-navy-foreground/70">
                    {m.label}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {isCompletePackage && missingList.length > 0 && (
          <div className="mt-6 rounded-2xl border border-navy-foreground/15 bg-navy-foreground/5 p-4 text-[12px] leading-relaxed text-navy-foreground/85 print-avoid-break">
            <div className="mb-2 text-[10px] font-semibold uppercase tracking-wider text-navy-foreground/70">
              Missing evidence tracked
            </div>
            <ul className="grid grid-cols-1 gap-x-6 gap-y-1 sm:grid-cols-2 list-disc pl-5 marker:text-navy-foreground/50">
              {missingList.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>

      <div className="mt-10 space-y-12">
        {/* ── Section 1 — Claim Validation Matrix ───────────── */}
        <div>
          <SubsectionHeader eyebrow="Section 1" title="Claim Validation Matrix" />
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Each report chapter and the systems, evidence, and sources that supported it. Status
            reflects the evidence actually captured for this review.
          </p>
          <div className="mt-5 space-y-3">
            {chapterRows.map((row) => {
              const RowIcon: LucideIcon = row.icon ?? FileText;
              const g = chapterGrade(row.status, row.confidence);
              return (
                <ReportAccordion
                  key={row.num}
                  className="print-avoid-break"
                  bodyClassName="p-5 md:p-6"
                  summary={
                    <div className="flex flex-wrap items-center gap-3">
                      <span
                        aria-hidden="true"
                        className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-secondary text-navy"
                      >
                        <RowIcon aria-hidden="true" className="h-4 w-4" strokeWidth={2} />
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[10px] font-mono font-semibold uppercase tracking-widest text-fasttrack">
                          Chapter {row.num}
                        </span>
                        <span className="mt-0.5 block font-semibold text-navy">{row.name}</span>
                      </span>
                      <span className="ml-auto flex flex-wrap items-center gap-2">
                        {g ? (
                          <span
                            className={`inline-flex items-center rounded-md border px-2.5 py-1 text-xs font-bold uppercase tracking-wide ${g.cls}`}
                          >
                            {g.label}
                          </span>
                        ) : null}
                        {chapterChip(row.status)}
                        <span className="font-mono text-sm tabular-nums text-navy">
                          {row.confidence != null ? `${row.confidence}%` : "—"}
                        </span>
                      </span>
                    </div>
                  }
                >
                  <dl className="grid gap-4 md:grid-cols-2">
                    {[
                      { k: "Supporting Evidence", v: row.evidence },
                      { k: "Systems Invoked", v: row.systems },
                      { k: "External Sources", v: row.sources },
                    ].map((f) => (
                      <div key={f.k}>
                        <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                          {f.k}
                        </dt>
                        <dd className="mt-1 text-[13px] leading-snug text-foreground/80">
                          {f.v.length ? f.v.join(" · ") : "—"}
                        </dd>
                      </div>
                    ))}
                    <div>
                      <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        {ANALYSIS_CONFIDENCE_LABEL}
                      </dt>
                      <dd className="mt-1 font-mono text-sm tabular-nums text-navy">
                        {row.confidence != null ? `${row.confidence}%` : "—"}
                      </dd>
                    </div>
                  </dl>
                </ReportAccordion>
              );
            })}
          </div>
        </div>

        {/* ── Section 2 — Evidence Sources & Research References ── */}
        <div>
          <SubsectionHeader eyebrow="Section 2" title="Evidence Sources & Research References" />
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            A complete catalogue of the evidence, legal sources, market data, and analysis
            referenced throughout this review.
          </p>

          {/* SAB-005 — legal authority evidence lineage. Persisted metadata
              only; unavailable fields render explicit unavailable states. */}
          <div className="mt-5">
            <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground">
              Legal Authority Evidence Lineage
            </div>
            {uniqueLegal.length > 0 ? (
              <div className="mt-3 space-y-3">
                {uniqueLegal.map((l, i) => (
                  <div key={`ev-lineage-${l.id ?? i}-${i}`} className="print-avoid-break">
                    <div className="text-[12px] font-semibold text-navy break-words">
                      {(l.citation ?? l.statute ?? l.title ?? "Legal authority").trim()}
                    </div>
                    <LegalLineageStrip className="mt-1.5" lineage={buildAuthorityLineage(l)} />
                  </div>
                ))}
              </div>
            ) : (
              <p className="mt-2 text-sm italic text-muted-foreground">
                No legal authorities were persisted for this review.
              </p>
            )}
          </div>

          <div className="mt-5 grid gap-4 md:grid-cols-2">
            {(isCompletePackage
              ? sourceGroups
              : [
                  {
                    title: "Research & Reference Sources",
                    icon: Landmark,
                    intro:
                      "Legal research, market benchmarks, supporting documentation, and review methodology referenced throughout this report.",
                    rows: [
                      { label: "Legal Research", status: statusOf(has.laws || has.regs) },
                      { label: "Market Research", status: statusOf(has.market) },
                      {
                        label: "Supporting Documentation",
                        status: statusOf(uniqueFiles.length > 0, true),
                      },
                      { label: "Methodology", status: "verified" as SourceStatus },
                    ],
                  },
                  {
                    title: "Analytical Systems",
                    icon: Sparkles,
                    intro:
                      "Systems used to extract, analyze, validate, and assemble the findings in this report.",
                    rows: [
                      {
                        label: "OCR",
                        status: statusOf(!!bundle.invoiceData?.extracted || has.invoice),
                      },
                      { label: "AI Analysis", status: statusOf(has.aiAny) },
                      {
                        label: "Deterministic Processing",
                        status: "verified" as SourceStatus,
                      },
                      { label: "Report Assembly", status: "verified" as SourceStatus },
                    ],
                  },
                ]
            ).map((group) => {
              const Icon: LucideIcon = group.icon ?? FileSearch;
              return (
                <details
                  key={group.title}
                  className="group rounded-2xl border border-border bg-card p-5 shadow-card open:shadow-elevated"
                  open
                >
                  <summary className="flex cursor-pointer list-none items-start justify-between gap-4">
                    <div className="flex items-start gap-3">
                      <div
                        data-card-padding="tight"
                        className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-secondary text-navy"
                      >
                        <Icon
                          aria-hidden="true"
                          className="h-5 w-5 shrink-0 text-navy"
                          strokeWidth={2}
                        />
                      </div>
                      <div>
                        <div className="font-semibold text-navy">{group.title}</div>
                        <div className="mt-0.5 text-xs text-muted-foreground">
                          {group.rows.length} reference{group.rows.length === 1 ? "" : "s"}
                        </div>
                      </div>
                    </div>
                    <ChevronRight
                      aria-hidden="true"
                      className="pdf-hide mt-2 h-4 w-4 shrink-0 text-muted-foreground transition group-open:rotate-90"
                    />
                  </summary>

                  <div className="mt-4 border-t border-border pt-4">
                    {group.title === "Methodology" ||
                    group.title === "Research & Reference Sources" ||
                    group.title === "Analytical Systems" ? (
                      <p className="mb-3 text-[13px] leading-relaxed text-muted-foreground">
                        {group.intro}
                      </p>
                    ) : (
                      <p className="mb-3 text-xs uppercase tracking-wider text-muted-foreground">
                        {group.intro}
                      </p>
                    )}
                    <ul className="space-y-2">
                      {group.rows.map((row) => (
                        <li
                          key={row.label}
                          className="flex items-center justify-between gap-3 rounded-lg border border-border/60 bg-secondary/30 px-3 py-2"
                        >
                          <span className="text-sm text-navy">{row.label}</span>
                          {sourceChip(row.status)}
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              );
            })}
          </div>
        </div>

        {/* ── Section 3 — Systems Used During This Review ── */}
        {isCompletePackage ? (
          <div>
            <SubsectionHeader eyebrow="Section 3" title="Systems Used During This Review" />
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              The systems and sources below contributed to this investigation. Each is listed with
              its purpose and verification status so every conclusion in this report can be traced
              back to the evidence behind it.
            </p>
            <div className="mt-4 flex flex-wrap gap-2 text-[11px]">
              {[
                { label: "Customer Evidence", desc: "Documents and information you submitted" },
                { label: "Document Extraction", desc: "Reads data from submitted documents" },
                { label: "ClaimGuard System", desc: "ClaimGuard review and analysis system" },
                { label: "AI-Assisted Analysis", desc: "Reviewed analysis support" },
                { label: "Public Record", desc: "Government or public registry" },
                { label: "External Data Source", desc: "Independent third-party data" },
              ].map((k) => (
                <span
                  key={k.label}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-secondary/40 px-2.5 py-1 font-semibold text-navy"
                  aria-label={`${k.label}: ${k.desc}`}
                  title={k.desc}
                >

                  <span aria-hidden="true" className="h-1.5 w-1.5 rounded-full bg-navy/60" />
                  {k.label}
                </span>
              ))}
            </div>
            <div className="mt-4 space-y-3">
              {providerRows
                .filter((p) => p.status !== "not_invoked")
                .map((p) => {
                  const chip = statusChipMeta(p.status);
                  return (
                    <ReportAccordion
                      key={`${p.jobType}-${p.displayName}`}
                      className="print-avoid-break"
                          bodyClassName="p-5 md:p-6"
                      summary={
                        <div className="flex flex-wrap items-center gap-3">
                          <span className="min-w-0 font-semibold text-navy break-words">
                            {p.displayName}
                          </span>
                          <span className="ml-auto flex flex-wrap items-center gap-2">
                            <span className="text-[12px] text-muted-foreground">
                              {providerKindLabel(p.kind)}
                            </span>
                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${chip.cls}`}
                            >
                              {chip.label}
                            </span>
                          </span>
                        </div>
                      }
                    >
                      <dl className="grid gap-4 md:grid-cols-2">
                        <div>
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Purpose
                          </dt>
                          <dd className="mt-1 text-[13px] leading-snug text-foreground/80">
                            {p.purpose}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Type
                          </dt>
                          <dd className="mt-1 text-[13px] leading-snug text-foreground/80">
                            {providerKindLabel(p.kind)}
                          </dd>
                        </div>
                        <div>
                          <dt className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Status
                          </dt>
                          <dd className="mt-1">
                            <span
                              className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${chip.cls}`}
                            >
                              {chip.label}
                            </span>
                          </dd>
                        </div>
                      </dl>
                    </ReportAccordion>
                  );
                })}
            </div>

            <div className="mt-4 rounded-xl border border-border bg-secondary/30 p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wider text-navy">
                Provider Transparency
              </div>
              <p className="mt-1.5 text-xs leading-relaxed text-muted-foreground">
                ClaimGuard combines submitted documentation, public records, legal research, market
                benchmarking, and verified AI-assisted analysis to produce this report. Internal
                quality controls validate every finding before the report is finalized. Only
                systems that contributed to this review are listed.
              </p>

            </div>
          </div>
        ) : (
          <div>
            <SubsectionHeader eyebrow="Section 3" title="How This Report Was Produced" />
            <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
              Every conclusion in this Professional Review Report is produced through a disciplined,
              multi-stage pipeline that combines document intake, expert analytical review, legal
              and market research, and formal report assembly.
            </p>
            <div className="mt-5 grid gap-3 md:grid-cols-2">
              {[
                {
                  title: "Document Intake & Extraction",
                  body: "Your invoice and supporting files are ingested, structured, and cross-checked so every line item, fee, and date is captured accurately.",
                },
                {
                  title: "Analytical Review",
                  body: "Charges are analyzed against jurisdictional statutes, regulatory frameworks, and market benchmarks to identify overcharges, procedural defects, and evidentiary gaps.",
                },
                {
                  title: "Legal & Market Research",
                  body: "Applicable state and federal authorities, licensing records, and local rate comparables are compiled and verified against authoritative public sources.",
                },
                {
                  title: "Report Assembly & Certification",
                  body: "Findings are assembled into this deliverable, reviewed for internal consistency, and formally certified prior to release.",
                },
              ].map((step) => (
                <div
                  key={step.title}
                  className="rounded-2xl border border-border bg-card p-5 shadow-card print-avoid-break"
                >
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-navy/70">
                    {step.title}
                  </div>
                  <p className="mt-2 text-[13px] leading-relaxed text-muted-foreground">
                    {step.body}
                  </p>
                </div>
              ))}
            </div>
            <p className="mt-4 text-[11px] leading-relaxed text-muted-foreground">
              Internal system routing, provider identifiers, credentials, prompts, and proprietary
              implementation details are intentionally omitted from this deliverable. A full
              provider verification table is available in the Complete Package edition.
            </p>
          </div>
        )}

        {/* ── Section 4 — Finding Traceability Matrix ───────── */}
        <div>
          <SubsectionHeader eyebrow="Section 4" title="Finding Traceability Matrix" />
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Every recorded observation, its captured evidence, and the chapters where it is
            analyzed. Informational observations are labeled and excluded from verified findings.
          </p>
          {traceabilityRows.length === 0 ? (
            <div className="mt-5 rounded-2xl border border-dashed border-border bg-secondary/30 p-6 text-sm text-muted-foreground">
              No findings were recorded for this claim, so no traceability rows are generated.
            </div>
          ) : (
            <table className="mt-5 w-full border-collapse">
              <caption className="sr-only">Finding traceability matrix</caption>
              <thead className="sr-only">
                <tr>
                  <th scope="col">
                    Finding, severity, supporting evidence, and referenced chapters
                  </th>
                </tr>
              </thead>
              <tbody className="block space-y-4">
                {traceabilityRows.map((row, i) => (
                  <tr key={row.key} className="block">
                    <td className="block rounded-2xl border border-border bg-card p-5 shadow-card print-avoid-break">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="flex items-start gap-3">
                          <span className="mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-fasttrack/10 font-mono text-xs font-semibold text-fasttrack">
                            {String(i + 1).padStart(2, "0")}
                          </span>
                          <div>
                            <div className="font-semibold text-navy">{row.finding}</div>
                            {row.issue && (
                              <div className="mt-1 text-[13px] leading-snug text-muted-foreground">
                                {row.issue}
                              </div>
                            )}
                            {row.isInfo && (
                              <div className="mt-2 rounded-lg border border-warn/30 bg-warn/5 p-2 text-[12px] leading-relaxed text-warn">
                                <span className="font-semibold uppercase tracking-wider">
                                  Informational Observation
                                </span>{" "}
                                — AI-assisted findings were unavailable. This item is flagged for
                                further review and does not represent a verified discrepancy.
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded-full border border-border bg-secondary/60 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-navy">
                            Severity: {row.severity}
                          </span>
                          {!row.isInfo && (
                            <span className="rounded-full border border-savings/40 bg-savings-soft px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-savings">
                              {row.evidence.length > 0 ? "Evidence-backed" : "Limited Support"}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="mt-4 grid gap-3 md:grid-cols-2">
                        <div className="rounded-xl border border-border/60 bg-secondary/30 p-3">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Supporting Evidence
                          </div>
                          {row.evidence.length === 0 ? (
                            <div className="mt-1.5 text-[13px] italic text-muted-foreground">
                              Supporting evidence not available
                            </div>
                          ) : (
                            <ul className="mt-1.5 space-y-1 text-[13px] text-navy">
                              {row.evidence.map((e, ei) => (
                                <li
                                  key={`${row.key}-e-${ei}`}
                                  className="flex items-center gap-1.5"
                                >
                                  <CheckCircle2
                                    aria-hidden="true"
                                    className="h-3 w-3 text-savings"
                                  />
                                  {e}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                        <div className="rounded-xl border border-border/60 bg-secondary/30 p-3">
                          <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                            Referenced In
                          </div>
                          {row.chapters.length === 0 ? (
                            <div className="mt-1.5 text-[13px] italic text-muted-foreground">
                              Not mapped to a specific chapter
                            </div>
                          ) : (
                            <ul className="mt-1.5 flex flex-wrap gap-1.5">
                              {row.chapters.map((c) => (
                                <li
                                  key={`${row.key}-c-${c}`}
                                  className="rounded-full border border-border bg-card px-2 py-0.5 text-[11px] font-semibold text-navy"
                                >
                                  {c}
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* ── Section 5 — Submitted Case Files (H7, L3) ─────── */}
        <div>
          <SubsectionHeader eyebrow="Section 5" title="Submitted Case Files" />
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">
            Files submitted with this claim and their lifecycle status. Verified status is only
            shown when an actual verification event exists.
          </p>
          {caseFileGroups.length === 0 ? (
            <div className="mt-4 rounded-2xl border border-dashed border-border bg-secondary/30 p-6 text-sm text-muted-foreground">
              No case files were submitted with this review.
            </div>
          ) : (
            <div className="mt-4 space-y-4">
              {caseFileGroups.map((group) => (
                <div
                  key={group.title}
                  className="rounded-2xl border border-border bg-card p-5 shadow-card"
                >
                  <div className="flex items-center justify-between gap-3">
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
                      {group.title}
                    </div>
                    <span className="rounded-full bg-secondary px-2.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-navy">
                      {group.items.length} item{group.items.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <ul className="mt-3 divide-y divide-border">
                    {group.items.map((f) => {
                      const uploaded = (f as { created_at?: string | null }).created_at;
                      const st = fileLifecycleStatus(f);
                      const isInvoiceRow = (f.kind ?? "").toLowerCase() === "invoice";
                      const inv = isInvoiceRow ? bundle.invoiceData : null;
                      const extracted = inv?.extracted as Record<string, unknown> | undefined;
                      const entityCount = extracted
                        ? Object.values(extracted).filter(
                            (v) => v != null && !(typeof v === "string" && v.trim() === ""),
                          ).length
                        : 0;
                      const ocrConf = (inv as { ocr_confidence?: number | null } | null)
                        ?.ocr_confidence;
                      return (
                        <li
                          key={f.id}
                          className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 py-2.5 text-sm md:grid-cols-[1fr_auto_auto_auto] md:items-center"
                        >
                          <div className="flex min-w-0 items-center gap-2">
                            <FileText className="h-4 w-4 shrink-0 text-trust" />
                            <div className="min-w-0">
                              <div className="truncate font-medium text-navy">{f.file_name}</div>
                              {isInvoiceRow && inv && isCompletePackage && (
                                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
                                  <span>OCR: {extracted ? "Processed" : "Partial"}</span>
                                  {entityCount > 0 && <span>{entityCount} entities extracted</span>}
                                  {typeof ocrConf === "number" && ocrConf > 0 && (
                                    <span>Confidence: {Math.round(ocrConf * 100)}%</span>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                          <span className="hidden text-xs text-muted-foreground md:inline">
                            {uploaded ? new Date(uploaded).toLocaleDateString() : "—"}
                          </span>
                          <span className="text-xs tabular-nums text-muted-foreground">
                            {f.file_size ? `${Math.round(f.file_size / 1024)} KB` : "—"}
                          </span>
                          <span
                            className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${st.cls}`}
                          >
                            {st.label}
                          </span>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* ── Evidence Certification ──────────────────────────── */}
        <div
          className="rounded-3xl border border-navy/15 bg-gradient-to-br from-navy to-navy/90 p-8 text-navy-foreground shadow-elevated md:p-10 print-avoid-break"
          data-pdf-include="true"
        >
          <div className="flex flex-wrap items-center gap-3">
            <span className="inline-flex items-center gap-1.5 rounded-full border border-navy-foreground/20 bg-navy-foreground/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider">
              <BadgeCheck className="h-3.5 w-3.5" />
              Evidence Certification
            </span>
          </div>
          <p className="mt-4 max-w-3xl font-display text-xl leading-snug md:text-2xl print-avoid-break">
            Every material conclusion presented within this {identity.title} is traceable to one or
            more supporting records, provider responses, independently validated calculations, or
            publicly available reference materials collected during this review.
          </p>
          <p className="mt-4 max-w-3xl text-sm leading-relaxed text-navy-foreground/80 print-avoid-break">
            Observations lacking sufficient corroborating evidence are intentionally excluded from
            verified findings and identified separately when applicable. This chapter serves as the
            evidentiary foundation supporting every conclusion contained throughout this report.
            Provider names and execution status reflect actual telemetry; no result is implied
            unless the underlying record supports it.
          </p>
        </div>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* 13. Professional Review Conclusion                        */
/* ─────────────────────────────────────────────────────────── */


function ProfessionalReviewConclusion({
  r,
  caseId,
  versionEntries,
}: {
  r: ReturnType<typeof buildReport>;
  caseId: string;
  versionEntries?: VersionEntry[] | null;
}) {
  const topFinding = [...r.findings].sort((a, b) => b.savings[1] - a.savings[1])[0];
  /* Canonical Report Summary — this closing chapter summarizes; it never
     calculates. Every figure below is read from the immutable summary. */
  const rs = useReportSummary();
  const fin = rs.financials;
  /* Verdict mirrors the Chapter 01 Executive Verdict predicate exactly
     (supported recovery ⇒ negotiate before payment). No local thresholds. */
  const isNegotiable = fin.potentialSavings > 0;
  const verdict = {
    label: isNegotiable ? "Negotiate Before Payment" : "Payment Aligned",
    explanation: isNegotiable
      ? `The evidence assembled in this report supports a Total Supported Recovery of ${currency(fin.potentialSavings)} against an invoice of ${currency(fin.invoiceTotal)}. Negotiate to the Settlement Goal of ${currency(fin.settlementGoal)} before remitting payment.`
      : "No material overcharges were identified. This invoice aligns with the market and regulatory evidence reviewed in the preceding chapters.",
  };

  const verdictChip = isNegotiable
    ? "border-warn/40 bg-warn/10 text-warn"
    : "border-savings/40 bg-savings-soft text-savings";

  /* Completion summary — each row reflects whether the underlying record
     exists for THIS claim. Nothing is asserted as completed by default. */
  const completionItems = [
    { label: "Invoice analyzed", icon: FileCheck, done: fin.invoiceTotal > 0 },
    {
      label: "Charges reviewed",
      icon: ClipboardCheck,
      done: rs.counts.totalInvoiceCharges > 0,
    },
    {
      label: "Market comparison completed",
      icon: Scale,
      done: rs.counts.marketSupportedCharges > 0,
    },
    {
      label: "Applicable laws and regulations reviewed",
      icon: Landmark,
      done: rs.counts.applicableAuthorities > 0,
    },
    { label: "Findings generated", icon: FileText, done: rs.counts.findingsCount > 0 },
    {
      label: "Negotiation strategy prepared",
      icon: ClipboardCheck,
      done: rs.counts.findingsCount > 0,
    },
    { label: "Report ready for action", icon: BadgeCheck, done: true },
  ];

  const nextSteps = [
    {
      num: "01",
      title: "Download the Professional Findings Report",
      body: "Keep a complete copy with all evidence, citations, and the ready-to-send negotiation letter for your records.",
      icon: Download,
      guidance: {
        label: "TIP",
        text: "Save both the complete package and the negotiation-only PDF for quick reference.",
        tone: "blue" as const,
      },
    },
    {
      num: "02",
      title: "Save a copy of the original invoice",
      body: "Preserve the unmodified invoice and any related correspondence as supporting documentation.",
      icon: FileText,
      guidance: {
        label: "IMPORTANT",
        text: "Keep the untouched original — it is your baseline evidence during any dispute.",
        tone: "amber" as const,
      },
    },
    {
      num: "03",
      title: "Contact the storage company",
      body: `Reach out to ${r.vendor} with confidence. Reference the specific findings and market benchmarks in this report.`,
      icon: Phone,
      guidance: {
        label: "RECOMMENDED",
        text: "Lead with the benchmark data and cite the strongest finding first.",
        tone: "green" as const,
      },
    },
    {
      num: "04",
      title: "Use the negotiation playbook",
      body: "Follow the prepared scripts, evidence chain, and recommended sequence to maximize your recovery.",
      icon: Handshake,
      guidance: {
        label: "NOTE",
        text: "Follow the scripts in order — each step builds on the evidence established before it.",
        tone: "purple" as const,
      },
    },
    {
      num: "05",
      title: "Request written confirmation of any adjustment",
      body: "Always obtain an emailed or mailed confirmation of the revised balance before remitting payment.",
      icon: ClipboardCheck,
      guidance: {
        label: "REMINDER",
        text: "Do not remit payment until the revised balance is confirmed in writing.",
        tone: "neutral" as const,
      },
    },
  ];

  const guidanceToneStyles: Record<string, { wrap: string; label: string }> = {
    blue: { wrap: "border-trust/25 bg-trust-soft/60", label: "text-trust" },
    amber: { wrap: "border-warn/30 bg-warn/10", label: "text-warn" },
    green: { wrap: "border-savings/30 bg-savings-soft/70", label: "text-savings" },
    purple: {
      wrap: "border-[color:oklch(0.75_0.12_300)]/30 bg-[color:oklch(0.97_0.03_300)]",
      label: "text-[color:oklch(0.45_0.18_300)]",
    },
    neutral: { wrap: "border-border bg-muted/50", label: "text-muted-foreground" },
  };

  return (
    <Section
      bare
      number="01"
      eyebrow="Final assessment"
      title="Professional Review Conclusion"
      intro="A concise executive verdict on the invoice, the completion of our review, and your clear path forward."
    >
      {/* Verdict card */}
      <div className="rounded-3xl border border-navy/15 bg-gradient-to-br from-navy to-navy/90 p-8 text-navy-foreground shadow-elevated md:p-10">
        <div className="flex flex-wrap items-center gap-3">
          <span
            className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold ${verdictChip}`}
          >
            <ShieldCheck className="h-3.5 w-3.5" /> {verdict.label}
          </span>
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/20 bg-white/10 px-3 py-1 text-xs font-semibold text-white/80">
            <Gauge className="h-3.5 w-3.5" /> Evidence strength: {r.strength}
          </span>
        </div>

        <p className="mt-6 max-w-3xl font-display text-2xl leading-snug md:text-[26px]">
          {verdict.explanation}
        </p>

        <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
              Invoice Total
            </div>
            <div
              className="mt-2 font-display text-2xl text-white tabular-nums"
              {...summaryField("invoiceTotal", fin.invoiceTotal)}
            >
              {currency(fin.invoiceTotal)}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
              Estimated Fair Payment
            </div>
            <div
              className="mt-2 font-display text-2xl text-white tabular-nums"
              {...summaryField("supportedAmount", fin.supportedAmount)}
            >
              {currency(fin.supportedAmount)}
            </div>
          </div>
          <div className="rounded-2xl border border-savings/40 bg-savings/10 p-5 ring-1 ring-savings/25">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-savings/90">
              Total Supported Recovery
            </div>
            <div
              className="mt-2 font-display text-2xl tabular-nums"
              style={{ color: "var(--color-savings)" }}
              {...summaryField("potentialSavings", fin.potentialSavings)}
            >
              {currency(fin.potentialSavings)}
            </div>
          </div>
          <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
              Settlement Goal
            </div>
            <div
              className="mt-2 font-display text-2xl text-white tabular-nums"
              {...summaryField("settlementGoal", fin.settlementGoal)}
            >
              {currency(fin.settlementGoal)}
            </div>
          </div>
        </div>

        {topFinding && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-5">
            <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-white/60">
              Largest supported recovery
            </div>
            <div className="mt-2 font-display text-lg text-white">{topFinding.item}</div>
            <div className="mt-1 text-xs text-white/70">{topFinding.issue}</div>
            <div className="mt-2 text-[11px] uppercase tracking-wider text-white/50">
              Detailed in {chapterLongRef("market")} and {chapterLongRef("negotiation-strategy")}
            </div>
          </div>
        )}

        <div className="mt-6 rounded-2xl border border-white/15 bg-white/5 p-5">
          <div className="text-[11px] font-semibold uppercase tracking-wider text-white/60">
            Recommended next action
          </div>
          <p className="mt-1.5 text-sm text-white/90">
            {isNegotiable
              ? `Open with the strongest supported finding and anchor the discussion on the Settlement Goal of ${currency(fin.settlementGoal)}, as set out in ${chapterLongRef("negotiation-strategy")}.`
              : "No negotiation is indicated. Remit payment as invoiced."}
          </p>
        </div>
      </div>

      {/* Publication Record — relocated from between chapters so
          publication metadata reinforces (not interrupts) the signed
          conclusion. Interactive-only; PDF export is unchanged. */}
      {versionEntries && versionEntries.length > 0 && (
        <div className="no-print mt-6">
          <VersionHistory
            entries={versionEntries}
            title="Publication Record"
            eyebrow="Certification"
            intro="Formal publication history for this Claim Intelligence Package™. Every revision is versioned, dated, and attributed."
          />
        </div>
      )}

      {/* Completion checklist */}
      <div className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card md:p-8">
        <div className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-trust">
          <BadgeCheck className="h-3.5 w-3.5" /> Review Completion Summary
        </div>
        <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {completionItems.map((item) => (
            <div
              key={item.label}
              className="flex items-center gap-3 rounded-xl border border-border/70 bg-white p-4"
            >
              <div
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full ${
                  item.done ? "bg-savings/10 text-savings" : "bg-muted text-muted-foreground"
                }`}
              >
                <item.icon className="h-4 w-4" />
              </div>
              <div
                className={`flex items-center gap-2 text-sm font-semibold ${
                  item.done ? "text-navy" : "text-muted-foreground"
                }`}
              >
                {item.done ? (
                  <CheckCircle2 className="h-4 w-4 text-savings" />
                ) : (
                  <MinusCircle className="h-4 w-4 text-muted-foreground" />
                )}
                {item.label}
                {!item.done && (
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                    Not available
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Download & next steps */}
      <div className="mt-6 overflow-hidden rounded-2xl border border-navy/15 bg-card shadow-card">
        {/* Chapter-style navy header band */}
        <div className="relative bg-gradient-to-br from-navy via-navy to-navy/85 px-6 py-3 text-navy-foreground md:px-8 md:py-4">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-gold">
            <ClipboardCheck className="h-3.5 w-3.5 flex-none" /> Download & Next Steps
          </div>
          <h3 className="mt-1 font-display text-xl leading-tight text-white md:text-2xl">
            Recommended Next Steps
          </h3>
          <p className="mt-1 max-w-2xl text-sm text-white/75">
            Use these recommendations to guide your actions after reviewing the Professional Review
            Package.
          </p>
          <div className="mt-2 h-px w-16 bg-gold/70" />
        </div>

        {/* Steps */}
        <div className="divide-y divide-border/70">
          {nextSteps.map((step) => {
            const tone = guidanceToneStyles[step.guidance.tone];
            return (
              <div
                key={step.num}
                className="relative grid grid-cols-1 items-center gap-3 px-6 py-5 transition-all duration-200 ease-out hover:-translate-y-0.5 hover:shadow-card hover:ring-1 hover:ring-trust/20 md:grid-cols-[auto_minmax(0,1fr)_minmax(0,18rem)] md:gap-4 md:px-8"
              >
                {/* LEFT: icon + step number badge */}
                <div className="flex items-center gap-2">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-trust-soft text-trust ring-1 ring-trust/20">
                    <step.icon className="h-5 w-5 flex-none" size={20} />
                  </div>
                  <span className="inline-flex h-7 min-w-7 items-center justify-center rounded-md bg-navy px-1.5 text-[11px] font-bold tabular-nums text-white">
                    {step.num}
                  </span>
                </div>

                {/* CENTER: title + description */}
                <div className="min-w-0">
                  <h4 className="font-display text-base font-semibold leading-snug text-navy md:text-[17px]">
                    {step.title}
                  </h4>
                  <p className="mt-1 text-sm leading-relaxed text-muted-foreground">{step.body}</p>
                </div>

                {/* RIGHT: guidance box */}
                <div
                  className={`flex h-full flex-col justify-center rounded-lg border px-4 py-3 ${tone.wrap}`}
                >
                  <div
                    className={`text-[10px] font-bold uppercase tracking-[0.14em] ${tone.label}`}
                  >
                    {step.guidance.label}
                  </div>
                  <p className="mt-1 text-[12.5px] leading-snug text-navy/85">
                    {step.guidance.text}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Case File Appendices — always append the original invoice  */
/* ─────────────────────────────────────────────────────────── */

type AppendixGroup = {
  letter: string;
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  files: ReviewFile[];
};

function classifyAppendixFiles(files: ReviewFile[]): AppendixGroup[] {
  const invoices: ReviewFile[] = [];
  const photos: ReviewFile[] = [];
  const repairs: ReviewFile[] = [];
  const towReceipts: ReviewFile[] = [];
  const additional: ReviewFile[] = [];

  for (const f of files) {
    const name = (f.file_name ?? "").toLowerCase();
    const kind = (f.kind ?? "").toLowerCase();
    const mime = (f.mime_type ?? "").toLowerCase();
    if (kind === "invoice") {
      invoices.push(f);
    } else if (kind === "photo" || mime.startsWith("image/")) {
      // Route repair/tow images to their categories when the filename says so.
      if (/repair|estimate|body\s*shop/.test(name)) repairs.push(f);
      else if (/tow|receipt|impound|release/.test(name)) towReceipts.push(f);
      else photos.push(f);
    } else if (/repair|estimate|body\s*shop/.test(name)) {
      repairs.push(f);
    } else if (/tow|receipt|impound|release/.test(name)) {
      towReceipts.push(f);
    } else {
      additional.push(f);
    }
  }

  // Fallback: if we found no explicitly-tagged invoice but the customer
  // uploaded exactly one document, treat it as the invoice so Appendix A is
  // never empty when files exist.
  if (invoices.length === 0 && files.length > 0) {
    const candidates = [...photos, ...additional];
    if (candidates.length > 0) {
      const primary = candidates[0];
      invoices.push(primary);
      const idx = photos.indexOf(primary);
      if (idx >= 0) photos.splice(idx, 1);
      const jdx = additional.indexOf(primary);
      if (jdx >= 0) additional.splice(jdx, 1);
    }
  }

  const groups: AppendixGroup[] = [
    {
      letter: "A",
      title: "Original Submitted Invoice",
      subtitle: "The primary case document as received from the customer. Always included.",
      icon: Receipt,
      files: invoices,
    },
  ];
  if (photos.length) {
    groups.push({
      letter: "B",
      title: "Supporting Photos",
      subtitle: "Vehicle, damage, and on-site photos submitted with this case.",
      icon: ImageIcon,
      files: photos,
    });
  }
  if (repairs.length) {
    groups.push({
      letter: "C",
      title: "Repair Estimate",
      subtitle: "Repair or body-shop estimates supplied by the customer.",
      icon: Wrench,
      files: repairs,
    });
  }
  if (towReceipts.length) {
    groups.push({
      letter: "D",
      title: "Tow Receipt",
      subtitle: "Tow, impound, or release receipts supplied by the customer.",
      icon: Truck,
      files: towReceipts,
    });
  }
  if (additional.length) {
    groups.push({
      letter: "E",
      title: "Additional Attachments",
      subtitle: "Other correspondence and documents included with this case.",
      icon: Paperclip,
      files: additional,
    });
  }

  return groups;
}

function CaseFileAppendices({ files }: { files: ReviewFile[] | null }) {
  // Supplementary appendices only (Appendix B and later). The Original
  // Submitted Invoice (Appendix A) now renders as a top-of-report section
  // immediately after the Table of Contents.
  const groups = classifyAppendixFiles(files ?? []).filter((g) => g.letter !== "A");
  if (groups.length === 0) return null;

  return (
    <Section
      id="section-appendices"
      eyebrow="Case File Appendices"
      title="Supplementary Documents & Attachments"
      intro="Additional documents the customer submitted, in the order they were received. The original invoice is presented as a separate source-document section at the front of the report."
      exportable={false}
    >
      <div className="space-y-6">
        {groups.map((g) => (
          <AppendixCard key={g.letter} group={g} />
        ))}
      </div>
    </Section>
  );
}

/**
 * Original Submitted Invoice — source-document section that renders
 * immediately after the Table of Contents. Not a chapter: no chapter
 * banner, no export controls, no AI overlays. Shows only the customer's
 * original invoice file(s) as uploaded.
 */
function OriginalSubmittedInvoice({ files }: { files: ReviewFile[] | null }) {
  const invoiceGroup = classifyAppendixFiles(files ?? []).find((g) => g.letter === "A");
  const openRef = useRef<((index: number) => void) | null>(null);
  if (!invoiceGroup) return null;
  const hasFiles = invoiceGroup.files.length > 0;

  return (
    <Section
      id="section-original-invoice"
      eyebrow="Source Document"
      title={
        hasFiles ? (
          <button
            type="button"
            onClick={() => openRef.current?.(0)}
            className="group inline-flex items-baseline gap-2 rounded-md text-left transition hover:text-trust focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
            aria-label="Open the original submitted invoice in the document viewer"
          >
            <span className="underline-offset-4 group-hover:underline">
              Original Submitted Invoice
            </span>
            <Maximize2 className="h-4 w-4 shrink-0 self-center text-trust opacity-0 transition group-hover:opacity-100" />
          </button>
        ) : (
          "Original Submitted Invoice"
        )
      }
      exportTitle="Original Submitted Invoice"
      intro="The invoice as submitted — the source document for this review, shown with no annotations, overlays, or AI findings. All analysis in the following chapters is derived from this document. Select any preview to open it full size."
      exportable={false}
    >
      <AppendixCard
        group={invoiceGroup}
        registerOpen={(fn) => {
          openRef.current = fn;
        }}
      />
    </Section>
  );
}

/**
 * Resolves short-lived signed URLs for a set of case files. Read-only: it
 * never mutates the underlying review record.
 */
function useSignedFileUrls(files: ReviewFile[]) {
  const [urls, setUrls] = useState<Record<string, string>>({});
  const key = files.map((f) => `${f.bucket}:${f.storage_path}`).join("|");
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const entries = await Promise.all(
        files.map(async (f) => {
          try {
            const signed = await fileService.signedUrl(f.bucket, f.storage_path, 3600);
            return [f.id, signed] as const;
          } catch {
            return [f.id, ""] as const;
          }
        }),
      );
      if (cancelled) return;
      setUrls(Object.fromEntries(entries.filter(([, v]) => v)));
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);
  return urls;
}

function AppendixCard({
  group,
  registerOpen,
}: {
  group: AppendixGroup;
  registerOpen?: (fn: (index: number) => void) => void;
}) {
  const Icon = group.icon;
  const urls = useSignedFileUrls(group.files);
  const [openIndex, setOpenIndex] = useState<number | null>(null);
  const triggerRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const cardTriggerRef = useRef<HTMLButtonElement | null>(null);
  const lastTriggerRef = useRef<HTMLElement | null>(null);

  const open = useCallback((index: number) => {
    lastTriggerRef.current =
      triggerRefs.current[index] ?? cardTriggerRef.current ?? (document.activeElement as HTMLElement);
    setOpenIndex(index);
  }, []);

  useEffect(() => {
    registerOpen?.(open);
  }, [registerOpen, open]);

  const close = useCallback(() => {
    setOpenIndex(null);
    // Restore focus to the element that opened the viewer.
    window.setTimeout(() => lastTriggerRef.current?.focus?.(), 0);
  }, []);

  const documents: ViewerDocument[] = group.files.map((f) => ({
    id: f.id,
    name: f.file_name ?? "Document",
    url: urls[f.id] ?? null,
    mime: f.mime_type ?? null,
    bucket: f.bucket,
    storagePath: f.storage_path,
  }));

  const hasFiles = group.files.length > 0;

  return (
    <div className="print-avoid-break rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-trust-soft text-trust">
            <Icon className="h-5 w-5" />
          </div>
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-trust">
              Appendix {group.letter}
            </div>
            {hasFiles ? (
              <button
                ref={cardTriggerRef}
                type="button"
                onClick={() => open(0)}
                className="group mt-0.5 inline-flex items-center gap-2 rounded-md text-left font-display text-xl text-navy transition hover:text-trust focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust focus-visible:ring-offset-2"
                aria-label={`Open ${group.title} in the document viewer`}
              >
                <span className="underline-offset-4 group-hover:underline">{group.title}</span>
                <Maximize2 className="h-4 w-4 shrink-0 text-trust opacity-0 transition group-hover:opacity-100" />
              </button>
            ) : (
              <h3 className="mt-0.5 font-display text-xl text-navy">{group.title}</h3>
            )}
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{group.subtitle}</p>
          </div>
        </div>
        <span className="rounded-full border border-border bg-white px-2.5 py-0.5 text-[11px] font-semibold text-navy">
          {group.files.length} document{group.files.length === 1 ? "" : "s"}
        </span>
      </div>

      {!hasFiles ? (
        <div className="mt-5 rounded-xl border border-dashed border-border bg-secondary/40 p-5 text-center text-sm text-muted-foreground">
          The original invoice will appear here once it has been received.
        </div>
      ) : (
        <div className="mt-5 grid gap-4 sm:grid-cols-2">
          {group.files.map((f, i) => (
            <AppendixDocument
              key={f.id}
              file={f}
              url={urls[f.id] ?? null}
              index={i}
              total={group.files.length}
              onOpen={() => open(i)}
              triggerRef={(el) => {
                triggerRefs.current[i] = el;
              }}
            />
          ))}
        </div>
      )}

      {openIndex !== null && (
        <InvoiceViewer documents={documents} startIndex={openIndex} onClose={close} />
      )}
    </div>
  );
}

function AppendixDocument({
  file,
  url,
  index,
  total,
  onOpen,
  triggerRef,
}: {
  file: ReviewFile;
  url: string | null;
  index: number;
  total: number;
  onOpen: () => void;
  triggerRef: (el: HTMLButtonElement | null) => void;
}) {
  const [failed, setFailed] = useState(false);
  const mime = (file.mime_type ?? "").toLowerCase();
  const isImage = mime.startsWith("image/");
  const isPdf = mime === "application/pdf" || /\.pdf$/i.test(file.file_name ?? "");
  const sizeLabel = file.file_size
    ? file.file_size >= 1024 * 1024
      ? `${(file.file_size / (1024 * 1024)).toFixed(1)} MB`
      : `${Math.round(file.file_size / 1024)} KB`
    : "—";
  const docLabel = total > 1 ? ` (document ${index + 1} of ${total})` : "";

  return (
    <figure className="group overflow-hidden rounded-xl border border-border bg-white transition duration-200 hover:-translate-y-0.5 hover:border-trust/50 hover:shadow-card">
      <div className="flex items-center justify-between gap-3 border-b border-border bg-secondary/40 px-4 py-2.5 text-xs">
        <div className="flex min-w-0 items-center gap-2">
          {isImage ? (
            <ImageIcon className="h-3.5 w-3.5 shrink-0 text-trust" />
          ) : isPdf ? (
            <FileText className="h-3.5 w-3.5 shrink-0 text-trust" />
          ) : (
            <Paperclip className="h-3.5 w-3.5 shrink-0 text-trust" />
          )}
          <button
            type="button"
            onClick={onOpen}
            className="min-w-0 truncate rounded font-medium text-navy underline-offset-4 hover:text-trust hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust"
            aria-label={`Open ${file.file_name}${docLabel} in the document viewer`}
          >
            {file.file_name}
          </button>
        </div>
        <span className="shrink-0 tabular-nums text-muted-foreground">{sizeLabel}</span>
      </div>

      {isImage && url && !failed ? (
        <button
          ref={triggerRef}
          type="button"
          onClick={onOpen}
          className="relative block w-full cursor-zoom-in focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-trust"
          aria-label={`Open ${file.file_name}${docLabel} in the document viewer`}
        >
          <img
            src={url}
            alt={`${file.file_name}${docLabel}`}
            crossOrigin="anonymous"
            onError={() => setFailed(true)}
            className="block max-h-[560px] w-full bg-white object-contain"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute inset-0 bg-navy/0 transition group-hover:bg-navy/[0.06]"
          />
          <span
            aria-hidden
            className="no-print pointer-events-none absolute bottom-3 right-3 inline-flex translate-y-1 items-center gap-1.5 rounded-full bg-navy/85 px-3 py-1.5 text-[11px] font-semibold text-white opacity-0 shadow-card transition group-hover:translate-y-0 group-hover:opacity-100"
          >
            <Maximize2 className="h-3.5 w-3.5" /> Open invoice
          </span>
        </button>
      ) : (
        <div className="flex flex-col items-center justify-center gap-3 px-6 py-10 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-trust-soft text-trust">
            {isPdf ? (
              <FileText className="h-6 w-6" />
            ) : isImage ? (
              <ImageIcon className="h-6 w-6" />
            ) : (
              <Paperclip className="h-6 w-6" />
            )}
          </div>
          <div className="text-sm font-semibold text-navy">
            {isPdf
              ? "PDF document included in the case file"
              : isImage
                ? failed
                  ? "Image preview unavailable"
                  : "Loading preview…"
                : "Document included in the case file"}
          </div>
          <div className="max-w-xs text-xs text-muted-foreground">
            The full document is attached to this case and available on your dashboard.
          </div>
          {url && (
            <div className="no-print flex flex-wrap items-center justify-center gap-2">
              <button
                ref={triggerRef}
                type="button"
                onClick={onOpen}
                className="inline-flex items-center gap-1.5 rounded-md border border-border bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-secondary focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-trust"
                aria-label={`Open ${file.file_name}${docLabel} in the document viewer`}
              >
                <Maximize2 className="h-3.5 w-3.5" /> Open invoice
              </button>
              <button
                type="button"
                onClick={() => {
                  void (async () => {
                    const ok = await downloadOriginalFile({
                      url,
                      fileName: file.file_name,
                      bucket: file.bucket,
                      storagePath: file.storage_path,
                      mime: file.mime_type,
                      fallbackLabel: `Invoice_${index + 1}`,
                    });
                    if (!ok) toast.error("Download failed. Please try again.");
                  })();
                }}
                className="inline-flex items-center gap-1.5 rounded-md bg-navy px-3 py-1.5 text-xs font-semibold text-white hover:opacity-90"
              >
                <Download className="h-3.5 w-3.5" /> Download Invoice
              </button>
            </div>
          )}
        </div>
      )}
    </figure>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Closing page — final page of the printed / downloaded PDF   */
/* ─────────────────────────────────────────────────────────── */

function ClosingPage(_props: { mode?: RenderMode } = {}) {
  const identity = usePdfPackageIdentity();
  return (
    <section className="print-avoid-break relative mt-8 overflow-hidden rounded-[28px] border border-white/10 bg-navy text-navy-foreground shadow-elevated aspect-[3/4] md:aspect-[4/5]">
      {/* Full-bleed cinematic photograph — softened for a quiet back-cover feel.
          The cover uses a bold diagonal reveal; the back cover lets the image
          settle into darkness so the eye rests on the brand mark and message. */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <img
          src={coverTruck.url}
          alt=""
          crossOrigin="anonymous"
          className="h-full w-full object-cover object-[center_58%] scale-[1.08] opacity-[0.28]"
        />
        {/* Deep vignette so text reads as if pressed into cloth binding */}
        <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(9,17,40,0.72)_0%,rgba(9,17,40,0.94)_62%,rgba(9,17,40,0.98)_100%)]" />
        <div className="absolute inset-0 bg-navy/55" />
      </div>

      {/* Twin gold rules frame the composition like the endpapers of a bound book */}
      <div
        aria-hidden
        className="absolute inset-x-10 top-10 h-px bg-fasttrack/45 md:inset-x-16 md:top-14"
      />
      <div
        aria-hidden
        className="absolute inset-x-10 bottom-16 h-px bg-fasttrack/45 md:inset-x-16 md:bottom-20"
      />

      {/* Centered, calm composition — one axis, generous negative space */}
      <div className="relative flex h-full flex-col items-center justify-between px-10 pt-16 pb-24 text-center md:px-16 md:pt-20 md:pb-28">
        {/* Top — brand lockup */}
        <div className="flex flex-col items-center">
          <div className="flex items-center gap-2.5">
            <ShieldCheck className="h-7 w-7 text-fasttrack md:h-8 md:w-8" />
            <span className="font-display text-[22px] font-bold tracking-[0.02em] text-white md:text-[26px]">
              CLAIMGUARD
            </span>
            <span className="align-super text-[10px] font-semibold text-white/70 md:text-[11px]">
              ™
            </span>
          </div>
          <div className="mt-3 text-[10px] font-semibold uppercase tracking-[0.32em] text-white/55 md:text-[11px]">
            Professional Review Services
          </div>
        </div>

        {/* Middle — the message. Sits alone with breathing room. */}
        <div className="flex max-w-3xl flex-col items-center">
          <h2 className="font-display text-[52px] font-normal leading-[0.95] tracking-tight text-white md:text-[72px] lg:text-[80px]">
            Thank you.
          </h2>

          <div aria-hidden className="mt-8 h-px w-16 bg-fasttrack md:mt-10 md:w-20" />

          <p className="mt-10 max-w-[44rem] text-[15px] font-light leading-relaxed text-white/80 md:mt-12 md:text-[17px]">
            {identity.thankYouMessage}
          </p>


          {/* Certification ribbon — restated as a closing seal of quality,
              positioned as a signature line below the message. */}
          <div className="mt-16 inline-flex flex-col items-center bg-fasttrack px-8 py-3.5 md:mt-20 md:px-10 md:py-4">
            <div className="text-[13px] font-bold uppercase tracking-[0.28em] text-navy md:text-[15px]">
              {identity.closingRibbonPrimary}
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-[0.24em] text-navy/75 md:text-[12px]">
              {identity.closingRibbonSecondary}
            </div>

          </div>
        </div>

        {/* Bottom — small centered seal, publisher's mark scale */}
        <div className="flex flex-col items-center gap-4">
          <div className="relative h-[88px] w-[88px] rounded-full bg-fasttrack p-[6px] shadow-[0_14px_36px_rgba(0,0,0,0.5)] md:h-[110px] md:w-[110px]">
            <div className="relative flex h-full w-full items-center justify-center overflow-hidden rounded-full border border-fasttrack bg-navy">
              <div
                aria-hidden
                className="absolute inset-[10%] rounded-full border border-fasttrack/60"
                style={{
                  background:
                    "repeating-conic-gradient(from 0deg, rgba(212,162,74,0.32) 0deg 1deg, rgba(212,162,74,0.06) 1deg 7deg)",
                }}
              />
              <div aria-hidden className="absolute inset-[26%] rounded-full bg-navy/90" />
              <div className="relative flex h-[36%] w-[36%] items-center justify-center rounded-[28%] bg-fasttrack shadow-inner">
                <div className="absolute inset-[15%] rounded-[24%] bg-navy" />
                <svg aria-hidden viewBox="0 0 40 40" className="relative h-[70%] w-[70%]">
                  <path
                    d="M 10 20 L 17 28 L 31 12"
                    fill="none"
                    stroke="#ffffff"
                    strokeWidth="4"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </div>
          </div>
          <div className="text-center text-[10px] font-semibold tracking-[0.2em] text-white/60 md:text-[11px]">
            <div>Certified by ClaimGuard</div>
            <div className="text-[11px] tracking-[0.18em] text-white/70 md:text-[12px]">
              Professional Review Services
            </div>
          </div>
        </div>
      </div>

      {/* Footer band — quiet confidentiality strip, matches cover treatment */}
      <div className="absolute inset-x-0 bottom-0 border-t border-white/10 bg-navy/85 py-3 text-center text-[10px] font-semibold uppercase tracking-[0.28em] text-white/60 backdrop-blur md:text-[12px]">
        Confidential — {identity.closingRibbonPrimary}
      </div>
    </section>
  );
}

function ReportFooter({ caseId, real = false }: { caseId: string; real?: boolean }) {
  return (
    <div className="mt-8 space-y-3">
      <div className="rounded-2xl border border-trust/20 bg-trust-soft/40 p-5 text-center text-xs text-navy">
        This package is prepared for review, negotiation support, and customer documentation.
      </div>
      <div className="flex flex-col items-start justify-between gap-4 rounded-2xl border border-border bg-secondary/40 p-6 text-xs text-muted-foreground md:flex-row md:items-center">
        <div className="flex items-start gap-2">
          <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-trust" />
          <span>
            Prepared by ClaimGuard · File {caseId} · Confidential. This package is for informational
            and negotiation-support purposes and does not constitute legal advice.
          </span>
        </div>
        {!real && (
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-3.5 w-3.5 text-warn" />
            <span>Sample values shown for demonstration.</span>
          </div>
        )}
      </div>
    </div>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Invoice Risk Score gauge                                    */
/* ─────────────────────────────────────────────────────────── */

function InvoiceRiskScore({ r }: { r: ReturnType<typeof buildReport> }) {
  const pct = Math.max(0, Math.min(100, r.savingsPct[1] * 2.5));
  const band =
    pct >= 70
      ? {
          label: "Critical",
          tone: "text-destructive",
          ring: "border-destructive/40 bg-destructive/5",
        }
      : pct >= 45
        ? { label: "High Risk", tone: "text-warn", ring: "border-warn/40 bg-warn/5" }
        : pct >= 20
          ? {
              label: "Moderate Risk",
              tone: "text-fasttrack",
              ring: "border-fasttrack/40 bg-fasttrack-soft/40",
            }
          : {
              label: "Low Risk",
              tone: "text-savings",
              ring: "border-savings/40 bg-savings-soft/40",
            };
  return (
    <section id="risk-score" className="scroll-mt-24 report-rise print-avoid-break">
      <div className={`rounded-3xl border p-6 shadow-card md:p-8 ${band.ring}`}>
        <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
          <div className="max-w-lg">
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
              Overall Invoice Risk Score
            </div>
            <div className={`mt-2 font-display text-3xl ${band.tone}`}>{band.label}</div>
            <p className="mt-2 text-sm text-muted-foreground">
              Derived from the severity and volume of findings identified across this invoice.
              Higher risk indicates stronger grounds to negotiate before payment.
            </p>
          </div>
          <div className="w-full md:w-80">
            <div className="mb-2 flex items-center justify-between text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              <span>Low</span>
              <span>Moderate</span>
              <span>High</span>
              <span>Critical</span>
            </div>
            <div className="relative h-3 overflow-hidden rounded-full bg-gradient-to-r from-savings via-fasttrack via-warn to-destructive">
              <div
                className="absolute top-1/2 h-5 w-1.5 -translate-y-1/2 rounded-full bg-navy shadow-elevated ring-2 ring-white"
                style={{ left: `calc(${pct}% - 3px)` }}
              />
            </div>
            <div className="mt-3 flex items-center justify-between text-xs text-muted-foreground">
              <span>Risk index</span>
              <span className={`font-semibold ${band.tone}`}>{Math.round(pct)} / 100</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ─────────────────────────────────────────────────────────── */
/* Floating Table of Contents (desktop only)                   */
/* ─────────────────────────────────────────────────────────── */

// Reading-order navigation is driven entirely by the Master Chapter
// Registry (`CHAPTERS`). Anchor ids remain stable (`chapter-01`..`chapter-12`)
// so PDF bookmarks and deep links keep working, while the displayed
// number is always the reading-order position (1..12).

function FloatingTOC() {
  const { active, emptyIds } = useChapterNavState();
  return (
    <aside className="report-toc hidden h-full xl:block no-print">
      <nav
        aria-label="Report chapters"
        className="sticky top-24 rounded-2xl border border-border bg-card/80 p-3 shadow-card backdrop-blur 2xl:p-4"
      >
        <div className="mb-3 flex items-center justify-between gap-2">
          <div className="text-[10px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
            On this page
          </div>
          <div className="shrink-0 text-[10px] font-mono text-muted-foreground/70">
            {TOTAL_CHAPTERS} chapters
          </div>
        </div>
        <ChapterNavList active={active} emptyIds={emptyIds} />
      </nav>
    </aside>
  );

}

/**
 * MobileChapterJump — a compact <select> shown below the toolbar on
 * screens without the sidebar TOC (< lg). Preserves the same anchor
 * targets so navigation stays consistent across viewports.
 */
function MobileChapterJump() {
  return (
    <div className="mt-2 lg:hidden">
      <label htmlFor="mobile-chapter-jump" className="sr-only">
        Jump to chapter
      </label>
      <div className="relative">
        <select
          id="mobile-chapter-jump"
          onChange={(e) => {
            const id = e.target.value;
            if (!id) return;
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
            e.target.value = "";
          }}
          className="w-full appearance-none rounded-md border border-border bg-white py-2 pl-3 pr-9 text-sm text-navy shadow-sm focus:outline-none focus:ring-2 focus:ring-trust"
          defaultValue=""
        >
          <option value="" disabled>
            Jump to chapter…
          </option>
          {CHAPTERS.map((c) => (
            <option key={c.anchorId} value={c.anchorId}>
              {c.paddedDisplayNumber} · {c.shortTitle}
            </option>
          ))}
        </select>
        <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
      </div>
    </div>
  );
}

/**
 * BackToTop — floating pill that appears once the reader scrolls past
 * the first viewport. Interactive-only; hidden from PDF exports.
 * Also toggles the sticky toolbar's shadow via a data attribute so the
 * toolbar visually detaches from the page on scroll.
 */
function BackToTop() {
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    if (typeof window === "undefined") return;
    const bar = document.querySelector<HTMLElement>("[data-report-toolbar]");
    const publishHeight = () => {
      if (!bar) return;
      const h = Math.round(bar.getBoundingClientRect().height);
      if (h > 0) {
        document.documentElement.style.setProperty("--report-toolbar-h", `${h}px`);
      }
    };
    publishHeight();
    const ro =
      bar && typeof ResizeObserver !== "undefined" ? new ResizeObserver(publishHeight) : null;
    if (ro && bar) ro.observe(bar);
    const onScroll = () => {
      const scrolled = window.scrollY > 400;
      setVisible(scrolled);
      if (bar) bar.dataset.scrolled = window.scrollY > 24 ? "true" : "false";
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", publishHeight);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", publishHeight);
      ro?.disconnect();
    };
  }, []);
  return (
    <button
      type="button"
      aria-label="Back to top"
      data-visible={visible ? "true" : "false"}
      onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
      className="report-back-to-top no-print"
    >
      <ArrowLeft className="h-3.5 w-3.5 rotate-90" />
      Top
    </button>
  );
}

function BoldStat({ text }: { text: string }) {
  const statPattern =
    /(\$\d{1,3}(?:,\d{3})*(?:\.\d+)?(?:\s*[KkMmBb])?|\d+(?:\.\d+)?%|\d+(?:\.\d+)?\s+(?:fee\s+categor(?:y|ies)|applicable\s+statute[s]?|statute[s]?|agency\s+regulation[s]?|document[s]?|charge[s]?|law[s]?|finding[s]?|benchmark[s]?|ZIP-level\s+benchmark[s]?|item[s]?|overcharge[s]?|saving[s]?|recovery|month[s]?|day[s]?|year[s]?|provider[s]?|vendor[s]?|service[s]?|line[s]?)|\d+(?:\.\d+)?(?:[xX])?)/;
  const match = text.match(statPattern);
  if (!match || match.index == null) {
    return <span className="text-[13.5px] leading-snug text-navy">{text}</span>;
  }
  const before = text.slice(0, match.index);
  const stat = match[0];
  const after = text.slice(match.index + stat.length);
  return (
    <span className="text-[13.5px] leading-snug text-navy">
      {before}
      <strong className="font-bold text-navy">{stat}</strong>
      {after}
    </span>
  );
}
